import { LEGAL_EFFECTIVE_DATE, LEGAL_ENTITY, PRIVACY_POLICY, TERMS_OF_SERVICE, type LegalDoc } from '@/constants/legal';
import { callClaude } from '@/lib/anthropic';
import { scheduleReminders, scheduleScoreTease } from '@/lib/notifications';
import { initSounds, playSound } from '@/lib/sounds';
import { buyHumanPhoto, buyPro, configurePurchases, hasOrderedHumanPhoto, isHumanPhotoAvailable, isProActive, purchasesReady, restorePro, type ProPlan } from '@/lib/purchases';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useIsFocused } from '@react-navigation/native';
import * as AppleAuthentication from 'expo-apple-authentication';
import * as Haptics from 'expo-haptics';
import * as ImagePicker from 'expo-image-picker';
// Loaded lazily — native module may not be in the current dev build binary.
// No-ops gracefully until the next production build includes it.
let StoreReview: { requestReview: () => Promise<void> } | null = null;
try { StoreReview = require('expo-store-review'); } catch { StoreReview = null; }
import * as Location from 'expo-location';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import {
  ActivityIndicator,
  Alert,
  Animated,
  AppState,
  Dimensions,
  Easing,
  InteractionManager,
  KeyboardAvoidingView,
  Linking,
  PanResponder,
  Platform,
  ScrollView,
  StyleSheet, Text, TextInput, TouchableOpacity, View
} from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import Svg, { Circle, Defs, Ellipse, G, Line, LinearGradient as SvgLinearGradient, Mask, Path, Rect, Stop, Text as SvgText } from 'react-native-svg';
import { Camera as VisionCamera, useCameraDevice, useCameraPermission, usePhotoOutput } from 'react-native-vision-camera';
// Live head-pose detection for hands-free auto-capture. Nitro-based CameraOutput
// (like usePhotoOutput) with a plain JS onFacesDetected callback — NOT the old
// per-frame Skia frame processor that re-rendered the screen and glitched the
// preview. Requires a fresh EAS dev build (native module).
import { useFaceDetectorOutput, type Face } from 'react-native-vision-camera-face-detector';

const { width, height } = Dimensions.get('window');

// ── PULSE palette (2026-07-20 redesign, direction "2b") ──
// Near-black canvas; the cyan→magenta gradient is confined to rings, strokes,
// bars and one CTA per screen — everything else stays quiet. Single source of
// truth. Legacy names (PRIMARY_BLUE, ACCENT, FLAME…) are kept and repointed so
// existing usages across the app pick up the new palette automatically.
const BG = '#0a0714';                       // app background
const SURFACE = '#0c0918';                  // raised surface / tab bar / card base
const SURFACE_2 = '#12101f';                // slightly raised row
const CARD_FILL = 'rgba(236,232,248,0.02)'; // near-invisible card wash over BG
const BORDER = 'rgba(236,232,248,0.09)';    // hairline card border
const TRACK = 'rgba(236,232,248,0.08)';     // idle bar/ring track
const CYAN = '#43e0ff';                     // data / STRONG tier / primary accent
const MAGENTA = '#ff4dd2';                  // action / PRIORITY tier / streak
const PURPLE_MID = '#9a6bff';               // BALANCED tier
const PRIMARY_BLUE = CYAN;                  // legacy name — now the PULSE cyan
const ACCENT = CYAN;                        // the everyday accent
const ACCENT_SOFT = 'rgba(67,224,255,0.10)';
const FLAME = MAGENTA;                       // streak chip is magenta in PULSE
const TEXT = '#ece8f8';
const TEXT_DIM = 'rgba(236,232,248,0.55)';
const TEXT_FAINT = 'rgba(236,232,248,0.40)';

// Type system (loaded in app/_layout.tsx).
// Inter carries EVERY body/UI string — task copy, descriptions, scores, XP,
// percentiles, buttons, labels — because it stays legible at small sizes on
// the dark background and has the cleanest numerals.
const FONT = 'Inter_400Regular';
const FONT_MED = 'Inter_500Medium';
const FONT_SEMI = 'Inter_600SemiBold';
const FONT_BOLD = 'Inter_700Bold';
// Fraunces (soft display serif) is reserved for display moments only — big
// headers like "Your Program", week names, the paywall headline. Never use it
// for small text: its character is the point at large sizes and it gets muddy
// below ~15px, where Inter is strictly more legible on the dark background.
const SERIF_SEMI = 'Fraunces_600SemiBold';
const SERIF_BOLD = 'Fraunces_700Bold';
// Brand gradient stops (cyan → magenta). Used by SVG gradients app-wide.
const GRAD_FROM = CYAN;
const GRAD_TO = MAGENTA;

// ── PULSE gradient primitives (react-native-svg only — no native modules,
// so the whole redesign hot-reloads onto the existing dev build) ──────────

// Unique, stable SVG gradient id per component instance. react-native-svg
// resolves `url(#id)` against a GLOBAL id space, so duplicate ids would make
// every gauge/pill inherit the first one's stops. A per-instance id avoids it.
let _gradSeq = 0;
function useGradId(prefix: string): string {
  const ref = useRef<string>('');
  if (!ref.current) ref.current = `${prefix}${_gradSeq++}`;
  return ref.current;
}

// Gradient-clipped text (wordmark, hero numbers). SVG so it needs no extra
// native module; caller passes an explicit box so it never clips.
function GradientText({ text, size, width, height, family = FONT_BOLD, letterSpacing = 0, align = 'left', from = GRAD_FROM, to = GRAD_TO, style }: {
  text: string; size: number; width: number; height?: number; family?: string;
  letterSpacing?: number; align?: 'left' | 'center' | 'right'; from?: string; to?: string; style?: any;
}) {
  const id = useGradId('gt');
  const h = height ?? Math.round(size * 1.28);
  const anchor = align === 'center' ? 'middle' : align === 'right' ? 'end' : 'start';
  const x = align === 'center' ? width / 2 : align === 'right' ? width : 0;
  return (
    <Svg width={width} height={h} style={style}>
      <Defs>
        <SvgLinearGradient id={id} x1="0" y1="0" x2="1" y2="0">
          <Stop offset="0" stopColor={from} />
          <Stop offset="1" stopColor={to} />
        </SvgLinearGradient>
      </Defs>
      <SvgText fill={`url(#${id})`} fontSize={size} fontFamily={family} letterSpacing={letterSpacing} x={x} y={size} textAnchor={anchor}>
        {text}
      </SvgText>
    </Svg>
  );
}

// Circular progress gauge. `gradient` → cyan→magenta stroke; otherwise a solid
// `strokeColor`. Optional soft `glow` (iOS shadow) in the arc's color.
function RingGauge({ size, stroke, progress, gradient = false, strokeColor = CYAN, glowColor, children }: {
  size: number; stroke: number; progress: number; gradient?: boolean;
  strokeColor?: string; glowColor?: string; children?: React.ReactNode;
}) {
  const id = useGradId('ring');
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <View
        style={glowColor ? { shadowColor: glowColor, shadowOffset: { width: 0, height: 0 }, shadowRadius: 16, shadowOpacity: 0.5 } : undefined}
      >
        <Svg width={size} height={size}>
          <Defs>
            <SvgLinearGradient id={id} x1="0" y1="0" x2="1" y2="1">
              <Stop offset="0" stopColor={GRAD_FROM} />
              <Stop offset="1" stopColor={GRAD_TO} />
            </SvgLinearGradient>
          </Defs>
          <Circle cx={c} cy={c} r={r} stroke={TRACK} strokeWidth={stroke} fill="none" />
          <Circle
            cx={c} cy={c} r={r}
            stroke={gradient ? `url(#${id})` : strokeColor}
            strokeWidth={stroke} fill="none" strokeLinecap="round"
            strokeDasharray={`${circ}`} strokeDashoffset={circ * (1 - Math.max(0, Math.min(1, progress)))}
            transform={`rotate(-90 ${c} ${c})`}
          />
        </Svg>
      </View>
      {children != null && <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center' }}>{children}</View>}
    </View>
  );
}

// Full-width gradient CTA pill with a soft colored glow. Measures its own width
// (onLayout) so the SVG fill is pixel-exact and the rounded corners are clean.
function GradientPill({ label, onPress, icon, height = 52, glowColor = MAGENTA, disabled, style }: {
  label: string; onPress?: () => void; icon?: React.ReactNode; height?: number;
  glowColor?: string; disabled?: boolean; style?: any;
}) {
  const id = useGradId('pill');
  const [w, setW] = useState(0);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      disabled={disabled}
      onLayout={(e) => setW(e.nativeEvent.layout.width)}
      style={[{
        height, borderRadius: height / 2, justifyContent: 'center', alignItems: 'center',
        shadowColor: glowColor, shadowOffset: { width: 0, height: 0 }, shadowRadius: 18, shadowOpacity: disabled ? 0 : 0.45,
        opacity: disabled ? 0.6 : 1,
      }, style]}
    >
      {w > 0 && (
        <Svg width={w} height={height} style={StyleSheet.absoluteFill}>
          <Defs>
            <SvgLinearGradient id={id} x1="0" y1="0" x2="1" y2="0">
              <Stop offset="0" stopColor={GRAD_FROM} />
              <Stop offset="1" stopColor={GRAD_TO} />
            </SvgLinearGradient>
          </Defs>
          <Rect x={0} y={0} width={w} height={height} rx={height / 2} fill={`url(#${id})`} />
        </Svg>
      )}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {icon}
        <Text style={{ color: BG, fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 1.3 }}>{label}</Text>
      </View>
    </TouchableOpacity>
  );
}

// Module-level so the object identity is stable — usePhotoOutput memoizes on
// it and would tear down / recreate the camera output every render otherwise.
// 16:9 (not 4:3): the session matches the preview format to the photo output,
// and a 16:9 preview fills a tall phone screen the way it did before this
// output existed — a 4:3 target made the preview crop in and look zoomed.
const SCAN_PHOTO_RESOLUTION = { width: 1080, height: 1920 };

// Illinois' Biometric Information Privacy Act (BIPA) carries statutory damages
// of $1,000–$5,000 per violation with NO proof of harm required, and is the
// single biggest litigation risk for any face-scanning app. To stay clear of
// it, face analysis is disabled for users located in Illinois. We do a one-time
// GPS fix + reverse-geocode at scan time, read only the state/region, and never
// store the coordinates. Returns 'ok' to allow, 'blocked' for Illinois, or
// 'denied' when we can't verify (permission off / no fix) — and we fail closed.
const BLOCKED_REGION_NAMES = ['Illinois'];
const BLOCKED_REGION_CODES = ['IL'];

async function verifyRegion(): Promise<'ok' | 'blocked' | 'denied'> {
  try {
    const { status } = await Location.requestForegroundPermissionsAsync();
    if (status !== 'granted') return 'denied';
    const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Low });
    const places = await Location.reverseGeocodeAsync({
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
    });
    const place = places[0];
    if (!place) return 'denied';
    const inUS = place.isoCountryCode === 'US' || place.country === 'United States';
    const region = place.region ?? '';
    // Only ever block inside the US — other countries can't be in Illinois.
    if (inUS && (BLOCKED_REGION_NAMES.includes(region) || BLOCKED_REGION_CODES.includes(region))) {
      return 'blocked';
    }
    return 'ok';
  } catch (e) {
    console.error('Region check error:', e);
    return 'denied';
  }
}

const TOP_BAR_HEIGHT = 110;
const NAV_BAR_HEIGHT = 38;
// Left-to-right order of the bottom tabs — used to derive the slide direction
// when switching tabs (content enters from the side the new tab sits on).
const TAB_ORDER = ['home', 'results', 'scan', 'guides', 'profile'];
const BOTTOM_INSET = 28;
const BUBBLE_LARGE = 112;
const BUBBLE_SMALL = 88;

const NAV_TOTAL = NAV_BAR_HEIGHT + BOTTOM_INSET;
const BUBBLES_BOTTOM = NAV_TOTAL + 8;
const OVERALL_BUBBLE_TOP_FROM_BOTTOM = BUBBLES_BOTTOM + 20 + BUBBLE_LARGE;
const SCAN_BAR_BOTTOM = OVERALL_BUBBLE_TOP_FROM_BOTTOM + 24;

// The on-screen face-oval geometry (all inputs are constants, so this is
// deterministic at module scope). Used both to draw the oval AND to gate
// auto-capture on the detected face actually being inside it.
const FACE_AVAIL_TOP = TOP_BAR_HEIGHT + 60;
const FACE_AVAIL_SPACE = height - FACE_AVAIL_TOP - (SCAN_BAR_BOTTOM + 60);
const FACE_OVAL_H = Math.min(FACE_AVAIL_SPACE * 0.96, height * 0.49);
const FACE_OVAL_W = FACE_OVAL_H * 0.7;
const FACE_OVAL_TOP = FACE_AVAIL_TOP + (FACE_AVAIL_SPACE - FACE_OVAL_H) / 2 + 20;

// Is the detected face reasonably inside the oval (screen coords via the
// detector's autoMode scaling)? Deliberately GENEROUS tolerances — the goal is
// to stop clearly off-center/too-far captures (which produce garbage scores),
// not to frustrate people with pixel-perfect alignment. The oval is
// horizontally centered, so this check is also immune to front-camera
// mirroring (a mirrored x-center stays the same distance from screen center).
function faceInOval(b: { x: number; y: number; width: number; height: number }): boolean {
  const cx = b.x + b.width / 2;
  const cy = b.y + b.height / 2;
  return (
    Math.abs(cx - width / 2) <= FACE_OVAL_W * 0.45 &&
    Math.abs(cy - (FACE_OVAL_TOP + FACE_OVAL_H / 2)) <= FACE_OVAL_H * 0.45 &&
    b.width >= FACE_OVAL_W * 0.35 // face too far away = too small to score well
  );
}

const CATEGORIES = [
  { key: 'skin', label: 'SKIN' },
  { key: 'jawline', label: 'JAWLINE' },
  { key: 'eyes', label: 'EYES' },
  { key: 'cheekbones', label: 'CHEEKBONES' },
  { key: 'masculinity', label: 'STRUCTURE' },
  { key: 'hair', label: 'HAIR' },
];

// ---------------------------------------------------------------------------
// Program data + generation
// ---------------------------------------------------------------------------

type ProgramTaskTemplate = { id: string; label: string; icon: string; xp: number };
type ProgramTask = { instanceId: string; templateId: string; label: string; icon: string; category: string; xp: number; day: number; desc?: string };
type ProgramWeek = {
  week: number;
  name: string;
  icon: string;
  gradient: string[];
  priorities: string[];
  tasks: ProgramTask[];
};
type Program = {
  generatedAt: number;
  priorities: string[];
  rawScores: Record<string, number>;
  weeks: ProgramWeek[];
};

const CATEGORY_KEYS = ['skin', 'jawline', 'eyes', 'cheekbones', 'masculinity', 'hair'];
const TASKS_PER_DAY = 5; // each program day has this many tasks (× 7 days/week)
const CATEGORY_LABEL: Record<string, string> = {
  skin: 'Skin',
  jawline: 'Jawline',
  eyes: 'Eyes',
  cheekbones: 'Cheekbones',
  masculinity: 'Structure',
  hair: 'Hair',
};

// Large pre-written library — at least 10 unique tasks per category so weeks
// rarely repeat the same task back-to-back.
const TASK_LIBRARY: Record<string, ProgramTaskTemplate[]> = {
  skin: [
    { id: 'am-skin',    label: 'AM Cleanse + Moisturize',         icon: '🧴', xp: 20 },
    { id: 'pm-skin',    label: 'PM Repair Routine',               icon: '🌙', xp: 20 },
    { id: 'spf',        label: 'Shield Up — Apply SPF 30+',       icon: '☀️', xp: 15 },
    { id: 'water-skin', label: 'Hydration Protocol: 8 glasses',   icon: '💧', xp: 15 },
    { id: 'no-sugar',   label: 'Zero Sugar Day (fights glycation)',icon: '🚫', xp: 35 },
    { id: 'omega3',     label: 'Omega-3 Meal (skin barrier)',      icon: '🐟', xp: 20 },
    { id: 'sleep-skin', label: 'Skin Recovery Sleep: 8 hrs',      icon: '😴', xp: 30 },
    { id: 'cold-rinse', label: 'Cold Rinse — Tighten Pores',      icon: '🧊', xp: 15 },
    { id: 'exfoliate',  label: 'Resurface — Exfoliate Tonight',   icon: '✨', xp: 25 },
    { id: 'vit-c',      label: 'Vitamin C Serum (AM)',            icon: '🍊', xp: 25 },
    { id: 'no-dairy',   label: 'No Dairy Day (reduce inflammation)',icon: '🥛', xp: 30 },
    { id: 'green-tea',  label: 'Drink Green Tea (antioxidants)',   icon: '🍵', xp: 15 },
  ],
  jawline: [
    { id: 'mew',          label: 'Mewing: Tongue Up All Day',           icon: '👅', xp: 35 },
    { id: 'gum',          label: 'Mastic Gum Drill: 20 min',           icon: '🦷', xp: 25 },
    { id: 'neck-curls',   label: 'Neck Curl-Ups: 3 × 15',             icon: '💪', xp: 30 },
    { id: 'low-sodium',   label: 'Sodium Cut — Reduce Face Bloat',     icon: '🧂', xp: 20 },
    { id: 'chin-tucks',   label: 'Chin Tucks: 3 × 12 reps',           icon: '📐', xp: 25 },
    { id: 'water-jaw',    label: 'Hydration: 8 glasses minimum',       icon: '💧', xp: 15 },
    { id: 'tongue-check', label: 'Tongue Posture Checks × 5',          icon: '✅', xp: 25 },
    { id: 'jaw-massage',  label: 'Lymph Drain Jaw Massage: 5 min',     icon: '👐', xp: 20 },
    { id: 'hard-chew',    label: 'Hard Food Challenge (raw veggies)',   icon: '🥕', xp: 20 },
    { id: 'gua-sha-jaw',  label: 'Gua Sha Jawline Sculpt: 5 min',      icon: '🪨', xp: 25 },
    { id: 'lean-cardio',  label: 'Cardio: Lower Body Fat %',           icon: '🏃', xp: 35 },
    { id: 'no-soda',      label: 'Zero Soda Today (cuts bloat)',        icon: '🚱', xp: 20 },
  ],
  eyes: [
    { id: 'eye-cream',     label: 'Eye Cream: AM + PM Application',    icon: '👁️', xp: 20 },
    { id: 'no-screen',     label: 'Screen Blackout: 1 hr before bed',  icon: '📵', xp: 30 },
    { id: 'sleep-eyes',    label: 'REM Protocol: Sleep 8 hrs',         icon: '😴', xp: 30 },
    { id: 'cold-compress', label: 'Cold Compress: De-puff Under Eyes', icon: '🧊', xp: 20 },
    { id: 'low-salt',      label: 'Low Sodium Day (reduce puffiness)', icon: '🧂', xp: 20 },
    { id: 'eye-breaks',    label: '20-20-20 Rule: Every Hour',         icon: '👀', xp: 15 },
    { id: 'hydrate-eyes',  label: 'Hydration Protocol: 8 glasses',    icon: '💧', xp: 15 },
    { id: 'sleep-back',    label: 'Back Sleeper Protocol',             icon: '🛏️', xp: 25 },
    { id: 'no-alcohol',    label: 'Alcohol-Free Day',                  icon: '🚱', xp: 35 },
    { id: 'brow-groom',    label: 'Brow Shape + Clean-Up',             icon: '✂️', xp: 20 },
    { id: 'zinc-eyes',     label: 'Zinc-Rich Meal (eye health)',       icon: '🥜', xp: 20 },
    { id: 'sunglasses',    label: 'Wear Sunglasses Outdoors',          icon: '🕶️', xp: 10 },
  ],
  cheekbones: [
    { id: 'face-yoga',    label: 'Face Yoga Circuit: 5 min',           icon: '🧘', xp: 25 },
    { id: 'mew-mid',      label: 'Mewing Focus: Midface Expansion',    icon: '👅', xp: 30 },
    { id: 'cardio-cut',   label: 'Cardio Shred: 25 min',               icon: '🏃', xp: 35 },
    { id: 'gua-sha-chk',  label: 'Gua Sha Cheek Lift: 5 min',          icon: '🪨', xp: 25 },
    { id: 'sodium-cut',   label: 'Sodium Cut — Sculpt Cheeks',         icon: '🧂', xp: 20 },
    { id: 'hydrate-chk',  label: 'Hydration: 8 glasses minimum',       icon: '💧', xp: 15 },
    { id: 'bilateral',    label: 'Bilateral Chewing (even growth)',     icon: '🦷', xp: 20 },
    { id: 'cheek-massage',label: 'Cheek Contour Massage: 3 min',       icon: '👐', xp: 20 },
    { id: 'clean-meal',   label: 'Whole Food Clean Meal',               icon: '🥗', xp: 20 },
    { id: 'chin-level',   label: 'Posture: Chin Level at All Times',   icon: '🧍', xp: 15 },
    { id: 'face-steam',   label: 'Steam + Massage Session: 10 min',    icon: '♨️', xp: 20 },
    { id: 'caloric-def',  label: 'Stay in Caloric Deficit Today',      icon: '⚖️', xp: 30 },
  ],
  masculinity: [
    { id: 'lift',        label: 'Heavy Compound Lifts: 45 min', icon: '🏋️', xp: 45 },
    { id: 'protein',     label: 'Hit Protein Target: 1g/lb',   icon: '🥩', xp: 25 },
    { id: 'sunlight',    label: 'Morning Sunlight: 20 min',     icon: '☀️', xp: 25 },
    { id: 'cold-shower', label: 'Cold Shower Protocol',         icon: '🚿', xp: 35 },
    { id: 'sleep-t',     label: 'Deep Recovery Sleep: 8 hrs',   icon: '😴', xp: 30 },
    { id: 'squat-dead',  label: 'Squat or Deadlift Priority',   icon: '💪', xp: 40 },
    { id: 'no-porn',     label: 'Dopamine Detox: Cut the Doomscroll', icon: '🚫', xp: 45 },
    { id: 'leg-day',     label: 'Leg Day: Squats + Deadlifts',  icon: '🦵', xp: 40 },
    { id: 'zinc-mag',    label: 'Zinc + Magnesium Rich Meal',   icon: '🥜', xp: 20 },
    { id: 'posture',     label: 'Power Posture All Day',        icon: '🧍', xp: 15 },
    { id: 'intermit',    label: 'Intermittent Fasting (16:8)',  icon: '⏱️', xp: 35 },
    { id: 'no-junk',     label: 'Zero Processed Food Today',    icon: '🥦', xp: 30 },
  ],
  hair: [
    { id: 'scalp-mass', label: 'Scalp Stimulation: 5 min',           icon: '💆', xp: 20 },
    { id: 'biotin',     label: 'Biotin + Hair Vitamin Stack',         icon: '💊', xp: 20 },
    { id: 'sulf-free',  label: 'Sulfate-Free Wash Protocol',          icon: '🧴', xp: 15 },
    { id: 'no-tight',   label: 'No Tight Styles (cut traction loss)', icon: '🎀', xp: 15 },
    { id: 'protein-h',  label: 'Protein Meal (keratin building)',     icon: '🥚', xp: 20 },
    { id: 'rosemary',   label: 'Rosemary Oil: Apply to Scalp',        icon: '🌿', xp: 25 },
    { id: 'no-heat',    label: 'Zero Heat Styling Today',             icon: '🔥', xp: 20 },
    { id: 'silk-pill',  label: 'Silk Pillowcase Protocol',            icon: '🛏️', xp: 15 },
    { id: 'hydrate-h',  label: 'Hydration Protocol: 8 glasses',       icon: '💧', xp: 15 },
    { id: 'derma-roll', label: 'Derma Roll (per schedule)',            icon: '🧷', xp: 35 },
    { id: 'iron-meal',  label: 'Iron-Rich Meal (prevent loss)',        icon: '🥩', xp: 20 },
    { id: 'low-stress', label: 'Stress-Free Day (cortisol kills hair)',icon: '🧘', xp: 30 },
  ],
};

// One plain-language explainer per task (keyed by template id) — what to
// actually do and why it helps. Shown under each task in the program so the
// plan teaches, not just instructs. Kept factual and non-overpromising.
const TASK_DESC: Record<string, string> = {
  // skin
  'am-skin': 'Gentle cleanser to clear overnight oil, then moisturizer on damp skin to lock in hydration for the day.',
  'pm-skin': "Cleanse off the day's sweat and grime, then apply your treatment and night cream so skin repairs while you sleep.",
  'spf': 'UV is the #1 driver of aging and uneven tone. Apply SPF 30+ every morning — even indoors or when cloudy.',
  'water-skin': 'Aim for ~2L of water today. Dehydrated skin looks dull and shows fine lines more.',
  'no-sugar': 'Skip added sugar today. Excess sugar drives glycation, which stiffens collagen and speeds skin aging.',
  'omega3': 'Eat salmon, sardines, walnuts or flax. Omega-3s strengthen the skin barrier and calm inflammation.',
  'sleep-skin': 'Most skin repair and collagen production happens in deep sleep. Protect a full 8 hours tonight.',
  'cold-rinse': 'Finish your wash with 20–30s of cool water to de-puff and temporarily tighten the look of pores.',
  'exfoliate': 'Use a gentle chemical exfoliant (AHA/BHA) tonight to clear dead cells. 2–3× a week, never daily.',
  'vit-c': 'Apply vitamin C after cleansing, before SPF. It brightens tone and defends against daytime free radicals.',
  'no-dairy': 'Skip milk and whey today. For many people dairy triggers breakouts and facial puffiness.',
  'green-tea': 'Swap a drink for green tea. Its polyphenols are anti-inflammatory and support clearer skin.',
  // jawline
  'mew': 'Rest your whole tongue flat on the roof of your mouth, teeth lightly together, lips sealed. Consistent tongue posture supports jaw and midface tone.',
  'gum': 'Chew firm mastic or nootka gum for 20 min to work the masseter — the muscle that adds width and definition to the jaw.',
  'neck-curls': 'Lying on your back, tuck your chin and curl your head up. Builds the neck so the jawline reads sharper from the side.',
  'low-sodium': 'Keep sodium low today. Excess salt holds water in your face, softening jaw and cheek definition.',
  'chin-tucks': "Pull your chin straight back (make a 'double chin'), hold 3s, release. Counters forward-head posture that blurs the jawline.",
  'water-jaw': 'Drink ~2L today. Staying hydrated actually helps flush the water retention that puffs up your face.',
  'tongue-check': 'Five times today, catch your tongue and place it flat on your palate. Builds the mewing habit until it is automatic.',
  'jaw-massage': 'Massage from the center of your face outward and down the neck to drain lymph fluid and de-puff the jaw.',
  'hard-chew': 'Eat raw carrots, apples, nuts or jerky. Chewing tougher food trains and grows the jaw muscles.',
  'gua-sha-jaw': 'Glide a gua sha tool along the jaw and neck to drain fluid for a temporarily sharper, more contoured look.',
  'lean-cardio': '20–30 min of cardio. Lower body fat is what reveals the bone structure under your jaw and cheeks.',
  'no-soda': 'Cut soda today. The sodium and sugar drive the bloating and glycation that soften your features.',
  // eyes
  'eye-cream': 'Pat a little eye cream around the orbital bone morning and night to hydrate thin under-eye skin and reduce shadowing.',
  'no-screen': 'No screens for the hour before bed. Blue light delays melatonin and worsens dark circles and eye fatigue.',
  'sleep-eyes': 'Full sleep is the single biggest fix for dark circles and puffy, tired-looking eyes. Aim for 8 hours.',
  'cold-compress': 'Hold something cold (a chilled spoon or gel mask) under the eyes for a few minutes to constrict vessels and cut puffiness.',
  'low-salt': 'High salt shows up as morning eye puffiness. Keep sodium down today to reduce fluid retention.',
  'eye-breaks': 'Every 20 min, look 20 feet away for 20 seconds. Reduces the strain that makes eyes look tired and red.',
  'hydrate-eyes': 'Dehydration deepens under-eye hollows and shadows. Drink ~2L across the day.',
  'sleep-back': 'Sleep on your back tonight. Face-down sleeping lets fluid pool under the eyes and creases the skin.',
  'no-alcohol': 'Skip alcohol. It dehydrates you and dilates vessels, worsening under-eye darkness and puffiness.',
  'brow-groom': 'Trim strays and clean up the lower brow line to open and frame the eyes. Go light — never over-pluck.',
  'zinc-eyes': 'Eat zinc-rich food (shellfish, pumpkin seeds, beef). Zinc supports repair of the delicate skin around the eyes.',
  'sunglasses': 'Wear UV sunglasses outside. Protects thin eye-area skin from aging and stops you squinting in lines.',
  // cheekbones
  'face-yoga': 'Run through targeted facial-muscle exercises for 5 min to tone the midface and support a lifted look.',
  'mew-mid': 'Focus your tongue pressure toward the front of your palate. Supports the midface that frames the cheekbones.',
  'cardio-cut': '25 min of cardio to lower body fat — the fastest way to reveal the cheekbones you already have.',
  'gua-sha-chk': 'Sweep a gua sha from beside the nose out to the ear along the cheekbone to drain fluid and lift the midface.',
  'sodium-cut': 'Low sodium today reduces the water retention that rounds out and hides your cheekbones.',
  'hydrate-chk': 'Drink ~2L today. Proper hydration flushes the facial bloat that softens cheek definition.',
  'bilateral': 'Chew evenly on both sides of your mouth. One-sided chewing builds uneven muscle and facial asymmetry.',
  'cheek-massage': 'Massage upward and outward across the cheeks to boost circulation and temporarily lift the midface.',
  'clean-meal': 'Eat one meal of whole, unprocessed food. Cuts the inflammation and bloat that blur your features.',
  'chin-level': 'Keep your chin level and neck long all day. Good posture presents your cheekbones and jaw at their best angle.',
  'face-steam': 'Steam the face then massage to boost circulation and lymph drainage for a fresher, more sculpted look.',
  'caloric-def': 'Hold a slight calorie deficit today. Sustained fat loss is what uncovers cheekbone and jaw structure.',
  // structure (masculinity)
  'lift': 'Train heavy compound lifts (squat, bench, row, press). Builds your frame and drives the hormones behind a stronger look.',
  'protein': 'Eat ~1g of protein per pound of bodyweight to build muscle and keep skin and hair strong.',
  'sunlight': 'Get 20 min of sunlight early. Sets your circadian rhythm, improves sleep, and supports testosterone.',
  'cold-shower': 'Finish your shower with 1–2 min of cold water to boost alertness, circulation and recovery.',
  'sleep-t': 'Testosterone and growth hormone peak in deep sleep. Protect a full 8 hours tonight.',
  'squat-dead': 'Make squats or deadlifts the focus of today. They are the biggest lifts for whole-body strength and hormones.',
  'no-porn': 'Cut porn and endless scrolling today. Resets your dopamine baseline so real life feels rewarding again.',
  'leg-day': 'Do not skip legs. Training your largest muscles drives the biggest strength and hormonal response.',
  'zinc-mag': 'Eat zinc- and magnesium-rich food (beef, eggs, pumpkin seeds, leafy greens). Both support testosterone and sleep.',
  'posture': 'Shoulders back, chest open, stand tall. Confident posture instantly changes how strong and capable you read.',
  'intermit': 'Keep eating within an 8-hour window today to help control body fat that hides your structure. Skip it if it hurts training.',
  'no-junk': 'Avoid processed and fast food today. It drives the fat gain and inflammation that soften your features.',
  // hair
  'scalp-mass': 'Massage your scalp with your fingertips for 5 min to boost blood flow to the follicles.',
  'biotin': 'Take your hair vitamins with food. Biotin and friends support the keratin your hair is built from.',
  'sulf-free': 'Use a sulfate-free shampoo. Harsh sulfates strip natural oils and leave hair dry and brittle.',
  'no-tight': 'Skip tight buns, braids or hats today. Constant tension pulls hair out at the hairline (traction loss).',
  'protein-h': 'Eat a solid protein meal. Hair is mostly keratin — a protein — so low intake shows up as weak growth.',
  'rosemary': 'Massage diluted rosemary oil into the scalp. Studies suggest it rivals minoxidil for stimulating growth.',
  'no-heat': 'Skip the blow dryer and hot tools today. Heat weakens the shaft and causes breakage over time.',
  'silk-pill': 'Sleep on silk tonight. Far less friction than cotton means less breakage and frizz.',
  'hydrate-h': 'Drink ~2L today. Hydration supports healthy follicles from the inside out.',
  'derma-roll': 'Use your derma roller on schedule (not daily), cleaned first. Microneedling can stimulate the scalp and boost absorption.',
  'iron-meal': 'Eat iron-rich food (red meat, spinach, lentils). Low iron is a common, fixable cause of hair shedding.',
  'low-stress': 'Actively de-stress today — walk, breathe, unplug. Chronic cortisol pushes hair into the shedding phase.',
};

// Task detail sheet content: numbered how-to steps + the mechanism behind each
// task, shown when a timeline card is tapped (see TaskDetailReader). Covers
// every task that's a genuine physical technique (mewing, gua sha, lifts,
// massage, posture...); pure diet/sleep/habit tasks keep their existing
// TASK_DESC one-liner and the sheet falls back to that.
const TASK_HOWTO: Record<string, { steps: string[]; why: string; illustration?: string }> = {
  // ── jawline ──
  'mew': {
    steps: [
      'Close your lips gently, teeth lightly touching (not clenched).',
      'Rest your whole tongue — tip to back — flat against the roof of your mouth.',
      "Don't press hard; this is a resting posture, not a flex.",
      'Breathe through your nose, not your mouth.',
      'Hold this as your default posture throughout the day — it becomes automatic with practice.',
    ],
    why: 'Correct tongue posture keeps light, constant upward pressure on the palate and supports the muscles around the jaw and midface. Mouth-breathing or a low-resting tongue does the opposite over time, letting the face grow longer and less defined.',
    illustration: 'mew',
  },
  'gum': {
    steps: [
      'Take a piece of mastic or nootka gum — firmer than regular gum.',
      'Chew evenly on both sides, not just your dominant one.',
      'Keep a steady, moderate pace for the full 20 minutes — don’t rush it.',
      'Stop if you feel jaw fatigue or clicking; ease off and try shorter sessions.',
    ],
    why: 'The masseter is the muscle that adds width and definition at the back of the jaw. Firm, sustained resistance chewing works it the way any other muscle grows — under repeated load.',
    illustration: 'chew',
  },
  'neck-curls': {
    steps: [
      'Lie flat on your back on a mat or bed.',
      'Tuck your chin slightly, as if nodding "yes."',
      'Curl your head up off the surface a few inches, leading with the chin tuck, not the forehead.',
      'Hold briefly, then lower with control — don’t drop.',
      'Do 3 sets of 15 reps.',
    ],
    why: 'Building the front neck muscles tightens the neck-to-jaw line, which is what makes a jawline read as sharp from the side — this is the resistance-training counterpart to chin tucks.',
    illustration: 'neck-curl',
  },
  'chin-tucks': {
    steps: [
      'Sit or stand tall with your shoulders back and relaxed.',
      "Without tilting your head down, glide your chin straight back — like making a deliberate double chin.",
      'Hold the retracted position for 2–3 seconds.',
      'Release slowly back to neutral.',
      'Do 3 sets of 12 reps, resting ~30s between sets.',
    ],
    why: 'Forward-head posture (common from looking down at phones) pulls the jaw forward and slack, blurring its edge. Chin tucks retrain the deep neck flexors to hold the head — and jaw — in a position that reads sharper from the side.',
    illustration: 'chin-tucks',
  },
  'tongue-check': {
    steps: [
      'Set 5 random reminders through the day (phone alarms work well).',
      'Each time one fires, check: are your lips closed and tongue on the roof of your mouth?',
      'If not, reset into position immediately — don’t judge it, just correct it.',
      'Over a few weeks these checks make the posture automatic between checks too.',
    ],
    why: 'Mewing only works if the posture holds outside of the moments you’re actively thinking about it. Random spot-checks catch the unconscious slips a single daily reminder would miss.',
    illustration: 'mew',
  },
  'jaw-massage': {
    steps: [
      'Warm your hands, then find the angle of your jaw with your fingertips.',
      'Using light pressure, sweep from under the ear down toward the collarbone.',
      'Repeat the sweep 10–15 times per side.',
      'Finish with a few sweeps from chin to ear along the jawline itself.',
    ],
    why: 'This follows the lymphatic drainage path along the jaw and neck, helping move fluid that would otherwise sit and puff the lower face, especially after a salty day or poor sleep.',
    illustration: 'massage',
  },
  'gua-sha-jaw': {
    steps: [
      'Apply a few drops of facial oil so the tool glides without tugging skin.',
      'Angle the gua sha tool almost flat against your skin (15–20°).',
      'Starting at the chin, sweep outward and slightly up along the jawline to the ear.',
      'Repeat each side 8–10 times with light, even pressure — this isn’t meant to hurt.',
    ],
    why: 'The sweeping motion moves surface fluid and relaxes the masseter, giving a temporarily sharper, less puffy jaw contour — most visible right after and building slightly with consistency.',
    illustration: 'gua-sha',
  },
  // ── cheekbones ──
  'face-yoga': {
    steps: [
      'Puff your cheeks with air and hold for 5 seconds, then release.',
      'Suck cheeks in (fish face) and hold for 5 seconds.',
      'Smile wide without showing teeth, hold, release.',
      'Repeat the full circuit 5 times, about 5 minutes total.',
    ],
    why: 'These are light resistance drills for the muscles around the cheeks — the same underlying idea as any facial muscle work, done consistently rather than in one heavy session.',
    illustration: 'face-yoga',
  },
  'mew-mid': {
    steps: [
      'Same tongue posture as mewing: whole tongue flat on the roof of the mouth.',
      'Today, focus specifically on the middle third of the tongue pressing into the mid-palate.',
      'Hold light, constant pressure — don’t clench.',
      'Maintain through normal activity; don’t set aside separate "practice time."',
    ],
    why: 'Pressure on the mid-palate is the part of tongue posture most linked to midface support — it’s the same habit as mewing, just with attention on where the pressure is landing.',
    illustration: 'mew',
  },
  'gua-sha-chk': {
    steps: [
      'Apply facial oil so the tool moves smoothly.',
      'Angle the tool flat against the skin over the cheek.',
      'Sweep from the side of the nose outward toward the top of the ear, following the cheekbone.',
      'Repeat 8–10 times per side with light pressure.',
    ],
    why: 'Sweeping along the cheekbone’s ridge highlights its contour by moving surface puffiness and briefly relaxing the muscle sitting over it.',
    illustration: 'gua-sha',
  },
  'bilateral': {
    steps: [
      'Notice which side you usually chew on — most people have a dominant side.',
      'Deliberately alternate bites between left and right through each meal.',
      'If one side feels weak or awkward at first, that confirms the imbalance — keep going gently.',
    ],
    why: 'Chewing on one side disproportionately builds that side’s masseter, which can read as facial asymmetry over years. Balancing it out lets both sides develop evenly.',
    illustration: 'chew',
  },
  'cheek-massage': {
    steps: [
      'Warm your hands and apply light facial oil or moisturizer.',
      'Using knuckles or fingertips, make small upward circles over the cheeks.',
      'Work from the corners of the mouth up toward the top of the cheekbone.',
      'Continue for 3 minutes, keeping pressure light throughout.',
    ],
    why: 'Circulation and light lymphatic movement in the midface can reduce puffiness that sits over the cheekbone and blurs its contour.',
    illustration: 'massage',
  },
  'chin-level': {
    steps: [
      'Set a few reminders through the day to check your posture.',
      'Each check: is your chin parallel to the floor, not tilted down or jutting forward?',
      'Reset gently — shoulders back, chin level, ears over shoulders.',
      'Hold the corrected position as your new default, not just for the check itself.',
    ],
    why: 'A dropped or jutting chin changes the angle the cheekbones and jaw are viewed at, and over time reinforces forward-head posture that softens the whole midface line.',
    illustration: 'posture',
  },
  'face-steam': {
    steps: [
      'Fill a bowl with hot (not scalding) water, or use a facial steamer.',
      'Lean over it with a towel draped over your head, 8–10 inches from the water.',
      'Steam for 5–7 minutes to open pores and soften tissue.',
      'Follow immediately with 3 minutes of gentle upward cheek massage while skin is warm.',
    ],
    why: 'Warmth increases blood flow and makes tissue more responsive to massage, so pairing the two gets more out of the massage than doing it cold.',
    illustration: 'steam',
  },
  // ── eyes ──
  'eye-breaks': {
    steps: [
      'Every hour, find something ~20 feet away.',
      'Look at it for a full 20 seconds, letting your eyes fully relax focus.',
      'Blink a few times before returning to your screen.',
      'Repeat every hour you’re on a screen.',
    ],
    why: 'Sustained close-up focus tires the muscles around the eyes and dries them out, which shows up as tension lines and puffiness. Regular distance breaks reset that strain.',
    illustration: 'eye-break',
  },
  'cold-compress': {
    steps: [
      'Wrap ice or a cold spoon in a thin cloth — never apply ice directly to skin.',
      'Gently press under each eye for 30–60 seconds.',
      'Repeat 2–3 times per eye if puffiness is noticeable.',
    ],
    why: 'Cold constricts the small blood vessels under the eyes, quickly reducing the fluid pooling that causes puffiness — a fast, temporary fix rather than a long-term one.',
    illustration: 'cold',
  },
  'brow-groom': {
    steps: [
      'Brush brow hairs upward and outward to see their natural shape.',
      'Only remove obvious strays — the gap between brows and hairs well outside the natural line.',
      'Trim any long hairs by brushing up and snipping just the tips.',
      'Stop there — don’t thin or reshape the arch itself.',
    ],
    why: 'Overgrown brows read as unkempt; over-plucked ones read as surprised or thin. A light clean-up frames the eyes without changing the face’s natural structure.',
    illustration: 'brow',
  },
  // ── masculinity ──
  'lift': {
    steps: [
      'Pick 3–4 compound lifts: squat, deadlift, bench, overhead press, or rows.',
      'Warm up with a light set before your working weight.',
      'Train each in the 5–8 rep range for 3–4 sets, with good form over ego weight.',
      'Total session: 45 minutes including warm-up and rest between sets.',
    ],
    why: 'Compound lifts build the most muscle per minute of any training style, and overall muscle mass — especially shoulders and traps — is one of the strongest visual drivers of a masculine frame.',
    illustration: 'lift',
  },
  'cold-shower': {
    steps: [
      'Start your shower at normal temperature to clean up.',
      'For the last 30–60 seconds, turn the water fully cold.',
      'Breathe steadily through it rather than tensing up and holding your breath.',
      'Build up duration over time if 30 seconds feels like a lot at first.',
    ],
    why: 'Cold exposure is a well-documented, mild stressor that’s linked to alertness and discipline-building — the physical challenge is the point, not a specific facial effect.',
    illustration: 'cold',
  },
  'squat-dead': {
    steps: [
      'Pick either the squat or the deadlift as today’s priority lift.',
      'Warm up with 2 light sets before working weight.',
      'Work up to 3–4 heavy sets of 5–6 reps with a spotter or safety bars if available.',
      'Prioritize depth and form over adding weight too fast.',
    ],
    why: 'Squats and deadlifts are the two biggest total-body compound lifts — they drive the most overall muscle and strength growth of any exercise pair.',
    illustration: 'squat',
  },
  'leg-day': {
    steps: [
      'Start with squats: 4 sets of 6–8 reps.',
      'Follow with deadlifts or Romanian deadlifts: 3 sets of 6–8 reps.',
      'Finish with 2 accessory movements (lunges, leg press, calf raises).',
      'Rest 2–3 minutes between heavy sets.',
    ],
    why: 'Legs are the largest muscle group in the body — training them hard drives more overall growth hormone response than upper-body-only training, and a proportionate lower body reads as more athletic.',
    illustration: 'squat',
  },
  'posture': {
    steps: [
      'Stand or sit with feet/hips stable, weight even.',
      'Roll shoulders back and down, away from your ears.',
      'Lift through the crown of your head, lengthening the spine.',
      'Keep this as your default rather than a held pose — relax into it, don’t brace.',
    ],
    why: 'Upright posture immediately reads as more confident and commanding, and takes pressure off the neck and jaw line that slouching drags forward and down.',
    illustration: 'posture',
  },
  // ── hair ──
  'scalp-mass': {
    steps: [
      'Use your fingertips (not nails) at the hairline.',
      'Press firmly and move the scalp in small circles — don’t just slide over hair.',
      'Work systematically across the whole scalp: front, sides, crown, back.',
      'Continue for 5 minutes total.',
    ],
    why: 'Massage increases blood flow to hair follicles, which several small studies link to improved hair thickness over consistent, months-long use.',
    illustration: 'scalp',
  },
  'rosemary': {
    steps: [
      'Dilute rosemary essential oil in a carrier oil (like jojoba or coconut) — never apply undiluted.',
      'Part hair in sections and apply directly to the scalp with a dropper or fingertips.',
      'Massage in for 2–3 minutes.',
      'Leave on for at least 20 minutes, or overnight, before washing out.',
    ],
    why: 'A well-cited study found rosemary oil performed comparably to minoxidil for hair density over 6 months, likely through improved scalp circulation.',
    illustration: 'rosemary',
  },
  'derma-roll': {
    steps: [
      'Only use on your scheduled day — over-rolling damages the scalp.',
      'Clean the roller and scalp with rubbing alcohol before use.',
      'Roll gently in vertical, horizontal, and diagonal directions, section by section — light pressure only.',
      'Apply a scalp serum immediately after; skip anything with fragrance today.',
    ],
    why: 'Microneedling creates controlled micro-injury that triggers a healing response in the scalp and improves how well topicals like minoxidil or rosemary oil absorb.',
    illustration: 'derma-roll',
  },
};

// Shared instructional line-art, same 24x24-grid hand-drawn style as
// CATEGORY_GLYPH. Many tasks describe the same physical action (mewing,
// chewing, a cold-water reset) so illustrations are defined once here and
// reused by id across TASK_ILLUSTRATION below, rather than redrawn per task.
const TASK_ART: Record<string, { d: string[]; dim?: string[]; dot?: [number, number, number][] }> = {
  // Head in profile mid-tuck: jaw pulled back over the neck, with a faint
  // "forward" ghost position showing the motion being corrected.
  chinTucks: {
    d: [
      'M9 4.5c2.6 0 4.4 2 4.4 4.6 0 1-.3 1.8-.8 2.6l1.6 2.6c.3.5 0 1.1-.6 1.1h-1.6v3.4c0 .7-.6 1.2-1.2 1.2H8.4c-.6 0-1.1-.5-1.1-1.1V16c-1.8-.5-3-2-3-3.9V9.4C4.3 6.7 6.4 4.5 9 4.5z',
      'M7.6 10.2c.2.5.7.8 1.2.6',
    ],
    dim: ['M15 8.5c1.6.6 2.6 2 2.6 3.8'],
  },
  // Side profile with tongue posture indicated by a short arc pressed to the
  // palate, mouth gently closed.
  mew: {
    d: [
      'M8.6 5c3 0 5.2 2.4 5.2 5.6 0 1.3-.4 2.4-1 3.3l1.6 2.7c.3.5 0 1.1-.6 1.1h-1.4v2.7c0 .6-.5 1.1-1.1 1.1H8.2c-.6 0-1-.5-1-1.1v-2c-2-.6-3.4-2.3-3.4-4.6V10c0-2.8 2-5 4.8-5z',
      'M7.4 11.2c.9-.5 2.2-.5 3 0',
    ],
    dim: ['M6.4 13.6h4'],
  },
  // Side-profile jaw with molar marks, mid-chew.
  chew: {
    d: [
      'M6 9c0-3 3-5.4 6-5.4s6 2.4 6 5.4c0 2.6-1 4.6-2.6 6l.8 3a1 1 0 0 1-1 1.3H9.8a1 1 0 0 1-1-1.1l.4-2.6C7.2 14.4 6 12 6 9z',
      'M9.6 9.6h1.2M13.2 9.6h1.2',
    ],
    dim: ['M8.6 12.4c1 .8 2.2 1.2 3.4 1.2s2.4-.4 3.4-1.2'],
  },
  // Reclined head with a curved arrow indicating the curl-up motion.
  neckCurl: {
    d: [
      'M4.4 14.8c0-2.4 1.8-3.6 3.6-3.6h2.2c2.6 0 4.6 2 4.6 4.4',
      'M9.4 11.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4z',
    ],
    dim: ['M12.4 16c1.6-1 3.4-1 5 0', 'M18.6 14.2l1 1.8-1.8 1'],
  },
  // Face outline with a hand motif sweeping the jaw/cheek — shared by jaw
  // massage and cheek massage.
  massage: {
    d: [
      'M8.4 4.6c2.8 0 5 2.4 5 5.6 0 4-2.4 8.4-5 8.4s-5-4.4-5-8.4c0-3.2 2.2-5.6 5-5.6z',
      'M11.6 12.4c1.6.2 2.8 1 3.6 2.2.6 1 .8 2.2.6 3.2',
    ],
    dim: ['M12.2 11c.8-.4 1.8-.4 2.6 0'],
  },
  // Curved jaw sweep with a small gua sha stone at the leading edge.
  guaSha: {
    d: [
      'M5 15.4c1.6-4.6 5-8.4 9-9.4',
      'M14 5.2l2.4-.8 1 2.2-2.2 1.2z',
    ],
    dim: ['M5.6 17.2c1.4-.6 2.6-1.4 3.6-2.4'],
  },
  // Round face with cheek/smile lines — face-yoga's puff-and-hold.
  faceYoga: {
    d: [
      'M12 4.4c4 0 6.6 3.4 6.6 7.6 0 5-3.2 9-6.6 9s-6.6-4-6.6-9c0-4.2 2.6-7.6 6.6-7.6z',
      'M8.6 12.2c.8.6 1.8.6 2.6 0M14.8 12.2c.8.6 1.8.6 2.6 0',
    ],
    dim: ['M9.4 16.2c1.6 1.2 3.6 1.2 5.2 0'],
  },
  // Face + wavy steam lines rising from below.
  steam: {
    d: [
      'M7 20c0-4.2 2.6-7.6 5.8-7.6s5.8 3.4 5.8 7.6',
    ],
    dim: ['M9 8c-.6-1 0-2.4 1.2-2.4', 'M12.4 7c-.6-1.2 0-2.8 1.4-2.8', 'M15.6 8c-.6-1-.2-2.2.8-2.4'],
  },
  // Eye with a small arrow gazing toward the distance (20-20-20 rule).
  eyeBreak: {
    d: [
      'M3.4 12.4c2.8-3.6 12-3.6 14.8 0-2.8 3.6-12 3.6-14.8 0z',
      'M10.8 12.4a2 2 0 1 0 4 0 2 2 0 0 0-4 0z',
    ],
    dim: ['M17 8.2l3.4-2M19 5.2l1.4.6-.6 1.4'],
  },
  // Brow arc crossed by a pair of tweezers.
  brow: {
    d: [
      'M5 11c2.4-2.6 8-2.6 10.4 0',
      'M17 8.4l3-3M17.6 9l2.4-3.6',
    ],
  },
  // Snowflake over a shoulder/collar base — shared by cold shower, cold
  // rinse, cold compress.
  cold: {
    d: [
      'M8.4 20c0-4.6 2.6-8.2 5.6-8.2s5.6 3.6 5.6 8.2',
    ],
    dim: ['M14 4v6M11.2 5.6l5.6 3.2M11.2 8.8l5.6-3.2'],
  },
  // Barbell across bent legs — shared by squat/deadlift/leg-day and the
  // general compound-lift task.
  squat: {
    d: [
      'M4 8.4h4M16 8.4h4M6.4 6.8v3.2M17.6 6.8v3.2',
      'M9 8.4c0 1.6.6 2.6 1.4 3.4l-1.2 4.6M15 8.4c0 1.6-.6 2.6-1.4 3.4l1.2 4.6',
    ],
    dim: ['M9.2 19.4h1.6M13.2 19.4h1.6'],
  },
  // Standing side-profile figure with a straight spine — posture cue.
  posture: {
    d: [
      'M12 4.2a2.1 2.1 0 1 1 0 4.2 2.1 2.1 0 0 1 0-4.2z',
      'M12 8.6v6.6',
      'M12 15.2l-2.6 4.6M12 15.2l2.6 4.6',
    ],
    dim: ['M8.4 10.6h7.2', 'M6 20.4h12'],
  },
  // Head outline with three fingertip points marking a scalp massage.
  scalp: {
    d: [
      'M6 13.4C6 8 9.2 4 13 4s7 4 7 9.4c0 1-.1 1.9-.3 2.6',
    ],
    dim: ['M8.8 8.4c1.6-1.4 4-2.2 6.2-1.6'],
    dot: [[9, 7.4, 0.9], [12, 6, 0.9], [15, 7.2, 0.9]],
  },
  // Droplet above a scalp curve.
  rosemary: {
    d: [
      'M14 4.2c1.8 2 2.8 4 2.8 5.8a2.8 2.8 0 1 1-5.6 0c0-1.8 1-3.8 2.8-5.8z',
    ],
    dim: ['M6.6 19c0-5 3-8.8 6.6-8.8'],
  },
  // Roller wheel with needle dots + handle, mid-roll over a surface line.
  dermaRoll: {
    d: [
      'M4.4 15.4h6.4',
      'M8.6 11.4a3 3 0 1 1 0 6 3 3 0 0 1 0-6z',
      'M14.6 8.4l4-4',
    ],
    dot: [[7.4, 13, 0.5], [8.6, 10.6, 0.5], [9.8, 13, 0.5], [9.4, 15.6, 0.5], [7.8, 15.6, 0.5]],
  },
};

// Task id -> shared illustration (see TASK_ART). Tasks not listed here fall
// back to their category glyph in TaskDetailReader.
const TASK_ILLUSTRATION: Record<string, { d: string[]; dim?: string[]; dot?: [number, number, number][] }> = {
  'mew': TASK_ART.mew,
  'mew-mid': TASK_ART.mew,
  'tongue-check': TASK_ART.mew,
  'chin-tucks': TASK_ART.chinTucks,
  'gum': TASK_ART.chew,
  'hard-chew': TASK_ART.chew,
  'bilateral': TASK_ART.chew,
  'neck-curls': TASK_ART.neckCurl,
  'jaw-massage': TASK_ART.massage,
  'cheek-massage': TASK_ART.massage,
  'gua-sha-jaw': TASK_ART.guaSha,
  'gua-sha-chk': TASK_ART.guaSha,
  'face-yoga': TASK_ART.faceYoga,
  'face-steam': TASK_ART.steam,
  'eye-breaks': TASK_ART.eyeBreak,
  'cold-compress': TASK_ART.cold,
  'cold-rinse': TASK_ART.cold,
  'cold-shower': TASK_ART.cold,
  'brow-groom': TASK_ART.brow,
  'lift': TASK_ART.squat,
  'squat-dead': TASK_ART.squat,
  'leg-day': TASK_ART.squat,
  'posture': TASK_ART.posture,
  'chin-level': TASK_ART.posture,
  'scalp-mass': TASK_ART.scalp,
  'rosemary': TASK_ART.rosemary,
  'derma-roll': TASK_ART.dermaRoll,
};

const WEEK_NAMES = [
  'Foundation Week',
  'Momentum Week',
  'Breakthrough Week',
  'Discipline Week',
  'Ascension Week',
  'Relentless Week',
  'Sharpening Week',
  'Peak Week',
];
const WEEK_ICONS = ['🌱', '⚡', '💥', '🎯', '🚀', '🔥', '🗡️', '👑'];
const WEEK_GRADIENTS = [
  ['#00C9FF', '#0077B6'],
  ['#56CCF2', '#2F80ED'],
  ['#F2994A', '#F2C94C'],
  ['#EB5757', '#C0392B'],
  ['#9D50BB', '#6E48AA'],
  ['#11998E', '#38EF7D'],
  ['#FC5C7D', '#6A82FB'],
  ['#F7971E', '#FFD200'],
];
// Maps a raw score to an approximate percentile among typical users.
// Simulated from a roughly normal distribution centred on score ~65.
function scoreToPercentile(raw: number): number {
  if (raw <= 25) return 5;
  if (raw <= 35) return 12;
  if (raw <= 45) return 22;
  if (raw <= 55) return 35;
  if (raw <= 62) return 45;
  if (raw <= 68) return 55;
  if (raw <= 74) return 65;
  if (raw <= 80) return 75;
  if (raw <= 87) return 85;
  if (raw <= 93) return 92;
  return 97;
}

// Allocates 8 weekly focus-pairs so that lower-percentile categories appear
// in more weeks, proportional to their need weight.
function buildWeekFocusPairs(rawScores: Record<string, number>): [string, string][] {
  const needs = Object.fromEntries(
    CATEGORY_KEYS.map(k => [k, Math.max(1, 100 - scoreToPercentile(rawScores[k] ?? 65))])
  );
  const totalNeed = CATEGORY_KEYS.reduce((s, k) => s + needs[k], 0);

  const SLOTS = 24;
  const rawCounts = Object.fromEntries(
    CATEGORY_KEYS.map(k => [k, needs[k] / totalNeed * SLOTS])
  );
  const counts: Record<string, number> = Object.fromEntries(
    CATEGORY_KEYS.map(k => [k, Math.max(1, Math.round(rawCounts[k]))])
  );

  let total = CATEGORY_KEYS.reduce((s, k) => s + counts[k], 0);
  let iter = 0;
  while (total > SLOTS && iter++ < 20) {
    const k = CATEGORY_KEYS.reduce((b, k) =>
      counts[k] - rawCounts[k] > counts[b] - rawCounts[b] ? k : b
    );
    if (counts[k] > 1) { counts[k]--; total--; } else break;
  }
  iter = 0;
  while (total < SLOTS && iter++ < 20) {
    const k = CATEGORY_KEYS.reduce((b, k) =>
      rawCounts[k] - counts[k] > rawCounts[b] - counts[b] ? k : b
    );
    counts[k]++; total++;
  }

  // Build flat pool of 24 slots (8 weeks × 3 focus categories).
  const pool: string[] = [];
  CATEGORY_KEYS.forEach(k => { for (let i = 0; i < counts[k]; i++) pool.push(k); });
  const shuffled = shuffle(pool);

  // Group into triplets; fix duplicate categories within a triplet by swapping
  // with the next available different item further in the pool.
  const pairs: [string, string][] = [];
  for (let i = 0; i < 24; i += 3) {
    const triple = [shuffled[i], shuffled[i + 1], shuffled[i + 2]];
    for (let slot = 1; slot < 3; slot++) {
      let tries = 0;
      while (triple.slice(0, slot).includes(triple[slot]) && tries++ < 10) {
        for (let j = i + slot + 1; j < shuffled.length; j++) {
          if (!triple.slice(0, slot).includes(shuffled[j])) {
            [shuffled[i + slot], shuffled[j]] = [shuffled[j], shuffled[i + slot]];
            triple[slot] = shuffled[i + slot];
            break;
          }
        }
      }
    }
    pairs.push([triple[0], triple[1], triple[2]] as unknown as [string, string]);
  }

  return pairs;
}

const STORAGE = {
  program: 'maxify_program',
  programRescans: 'maxify_program_rescans', // rescans since the current program was built — gates week unlocks (finish a week + rescan to open the next)
  streak: 'maxify_streak',
  tasks: (d: string) => `maxify_tasks_${d}`,
  tasksAwarded: (d: string) => `maxify_tasks_awarded_${d}`, // which instances have paid out XP/lifetime credit today (uncheck+recheck must not pay twice)
  firstScan: 'maxify_first_scan_done',
  scanCount: 'maxify_scan_count',
  bestScore: 'maxify_best_score',
  lastScanDate: 'maxify_last_scan_date',
  subscribed: 'maxify_is_subscribed',
  history: 'maxify_scan_history',
  lastScores: 'maxify_last_scores', // raw scores of the most recent scan

  account: 'maxify_account',
  session: 'maxify_session',
  guidesRead: 'maxify_guides_read',
  tutorialDone: 'maxify_tutorial_done',
  scanConsent: 'maxify_scan_consent', // user's biometric/AI-processing consent
  journeyStart: 'maxify_journey_start', // ts of first scan — anchors the over-time boost
  lifetimeTasks: 'maxify_lifetime_tasks', // cumulative tasks ever completed — feeds the boost
  lastReviewPrompt: 'maxify_last_review_prompt', // date key of last App Store review request
  recommendations: 'maxify_recommendations', // AI-tailored style/grooming fixes from the last scan
  subtraits: 'maxify_subtraits', // per-category named sub-traits from the last scan
  harmony: 'maxify_harmony', // facial ratio/proportion metrics from the last scan
  profile: 'maxify_profile', // onboarding questionnaire answers (captured pre-login)
  scanHowTo: 'maxify_scan_howto_seen', // first-time Wii-style "how to scan" card shown
  potentialAgeOk: 'maxify_potential_age_ok', // one-time 18+ confirmation for the human-edited photo order
  frontPhoto: 'maxify_front_photo', // most recent scan photo (local-only) so results survive relaunch
  frontFaceBox: 'maxify_front_face_box', // detected face bounds for frontPhoto (JSON FaceBox) — anchors the biometric face map
  accountNudged: 'maxify_account_nudged', // post-teaser account prompt shown once, ever
  promoUnlocked: 'maxify_promo_unlocked', // locally-granted Pro via promo code (bypasses RevenueCat)
  xp: 'maxify_xp', // lifetime XP from tasks + guides — drives the global Level
  tipDismissed: 'maxify_tip_dismissed', // dateKey when today's tip was dismissed
  day1Photo: 'maxify_day1_photo', // FIRST scan photo, kept for the before/after journey
  lastRecap: 'maxify_last_recap', // dateKey the Sunday recap was last shown
  milestonesShown: 'maxify_milestones_shown', // [30,60,90] journey milestones already celebrated
  badges: 'maxify_badges', // unlocked achievement badge ids
  streakFreeze: 'maxify_streak_freeze', // { available, usedWeek } — weekly one-miss insurance
};

// Onboarding profile captured before login. Feeds the Claude scoring prompt +
// tailored recommendations so analysis accounts for the user's demographics/goals.
type Profile = {
  gender: 'male' | 'female' | 'other';
  skinType: string;
  ethnicity: string;
  goals: string[];
  completedAt: number;
};

// One entry per scan, storing DISPLAY scores (already boosted) for the graph.
type ScanRecord = { ts: number; overall: number; potential: number; cats: Record<string, number> };

// A tailored, appearance-changing "style fix" Claude derives from the actual
// face (e.g. round face → angular goatee). `feature` tags which area it targets
// (used for the icon/accent). These are things the user can change NOW — grooming,
// hair, brows, styling, framing — not habits or surgery.
type Recommendation = { title: string; action: string; reason: string; feature: string };

// Five-step trait scale, best → worst. Our own wording (not Qoves'): the lowest
// tier is "priority" (where to focus), never a demoralizing "too low"/"weak".
// A trait is a "concern" when its tier is 'developing' or 'priority'.
type TraitTier = 'ideal' | 'strong' | 'balanced' | 'developing' | 'priority';
// The exact non-surgical fix for a concern, with clinical-style metadata.
type TraitProtocol = { fix: string; difficulty: 'Easy' | 'Medium' | 'Hard'; timeline: string; cost: string };
// A named anatomical sub-trait inside a category (Qoves-style report). `percentile`
// ranks it vs the user's demographic; `why`/`protocol` are only present on concerns.
type SubTrait = {
  name: string;
  tier: TraitTier;
  percentile?: number;
  why?: string;
  protocol?: TraitProtocol;
};
// A facial proportion/ratio reading (the "scientific" layer). `flag` = off-ideal.
type HarmonyMetric = { name: string; value: string; status: 'ideal' | 'balanced' | 'flag' };

// True for the two lowest tiers — the traits that get a why + protocol.
function isConcern(t: TraitTier): boolean {
  return t === 'developing' || t === 'priority';
}

// Normalize Claude's free-text tier into our 5-step scale (it sometimes says
// "suboptimal"/"good"/"needs work" instead of the exact tokens). Order matters.
function normTier(t: any): TraitTier {
  const s = String(t || '').toLowerCase();
  if (s.startsWith('ideal') || s.includes('excellent')) return 'ideal';
  if (s.includes('strong') || s.includes('above') || s.includes('good')) return 'strong';
  if (s.includes('priority') || s.includes('weak') || s.includes('poor') || s.includes('too low') || s.includes('focus')) return 'priority';
  if (s.includes('develop') || s.includes('suboptimal') || s.includes('improv') || s.includes('below') || s.includes('need') || s.includes('low')) return 'developing';
  return 'balanced';
}
function normStatus(t: any): HarmonyMetric['status'] {
  const s = String(t || '').toLowerCase();
  if (s.startsWith('ideal') || s.includes('strong') || s.includes('good') || s.includes('pass')) return 'ideal';
  if (s.includes('flag') || s.includes('off') || s.includes('weak') || s.includes('poor')) return 'flag';
  return 'balanced';
}
function normDifficulty(d: any): TraitProtocol['difficulty'] {
  const s = String(d || '').toLowerCase();
  if (s.includes('hard') || s.includes('difficult')) return 'Hard';
  if (s.includes('med')) return 'Medium';
  return 'Easy';
}
function difficultyColor(d: TraitProtocol['difficulty']): string {
  return d === 'Hard' ? MAGENTA : d === 'Medium' ? PURPLE_MID : CYAN;
}
// "72" → "72nd" etc. Used for percentile labels.
function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
}
// PULSE tier colors: cyan (STRONG/ideal) → purple (BALANCED) → magenta
// (developing/PRIORITY). Distinction comes from the label; the palette stays to
// the three brand hues.
function tierColor(t: string): string {
  switch (t) {
    case 'ideal': return CYAN;
    case 'strong': return CYAN;
    case 'balanced': return PURPLE_MID;
    case 'developing': return MAGENTA;
    case 'priority': return MAGENTA;
    case 'flag': return MAGENTA;
    default: return PURPLE_MID;
  }
}
function tierLabel(t: string): string {
  switch (t) {
    case 'ideal': return 'IDEAL';
    case 'strong': return 'STRONG';
    case 'balanced': return 'BALANCED';
    case 'developing': return 'DEVELOPING';
    case 'priority': return 'PRIORITY';
    case 'flag': return 'FLAG';
    default: return 'BALANCED';
  }
}

// Top-percent label for a DISPLAY score. Only returns a label for genuinely
// strong scores (top third) so the callout always reads as a compliment — weak
// categories communicate themselves via color + the AI reason, so we never
// surface a demoralizing "bottom X%".
function topPercentLabel(display: number): string | null {
  if (display >= 92) return 'Top 3%';
  if (display >= 88) return 'Top 7%';
  if (display >= 84) return 'Top 12%';
  if (display >= 80) return 'Top 18%';
  if (display >= 76) return 'Top 25%';
  if (display >= 72) return 'Top 32%';
  return null;
}

// Vector icon for a recommendation based on the facial area it targets.
function recIcon(feature: string): keyof typeof Ionicons.glyphMap {
  const f = (feature || '').toLowerCase();
  if (f.includes('jaw') || f.includes('chin') || f.includes('beard') || f.includes('facial hair')) return 'man-outline';
  if (f.includes('hair')) return 'cut-outline';
  if (f.includes('brow') || f.includes('eye')) return 'eye-outline';
  if (f.includes('skin')) return 'water-outline';
  if (f.includes('cheek')) return 'diamond-outline';
  if (f.includes('glass') || f.includes('frame') || f.includes('style')) return 'glasses-outline';
  if (f.includes('masc') || f.includes('structure') || f.includes('body')) return 'barbell-outline';
  return 'star-outline';
}

// Shared Pro-gate overlay used by EVERY locked results section (breakdown,
// harmony, face map, potential, category grid) so every lock looks identical —
// one frosted card with an icon, title, sub, and CTA.
function ProLock({ title, sub, onUnlock, icon = 'lock-closed', cta = 'UPGRADE TO PRO' }: {
  title: string; sub: string; onUnlock: () => void; icon?: keyof typeof Ionicons.glyphMap; cta?: string;
}) {
  return (
    <View style={styles.proLockOverlay}>
      <Ionicons name={icon} size={26} color="#fff" style={{ marginBottom: 4 }} />
      <Text style={styles.lockTitle}>{title}</Text>
      <Text style={styles.lockSub}>{sub}</Text>
      <TouchableOpacity style={styles.categoryLockBtn} onPress={onUnlock} activeOpacity={0.85}>
        <Text style={styles.categoryLockBtnText}>{cta}</Text>
      </TouchableOpacity>
    </View>
  );
}

// Concave boost shown to the user everywhere a score appears. Raw scores are
// only ever used internally (priority detection).
// Base curve: maps a raw 0–100 into a friendlier band, lifting low scores a
// little so a bad photo/lighting doesn't read as brutal — but the lift is kept
// small (slope stays close to 1) specifically so real differences between
// faces still show up in the displayed number instead of getting compressed
// into a narrow "everyone's basically a 75" band.
// `boost` is the small over-time assist added on top (see computeBoost) — the
// base read stays honest; the boost is what makes a returning user's number tick
// up. Always clamped to 100.
function displayScore(raw: number, boost = 0): number {
  return Math.min(100, Math.round(raw + 12 * (1 - raw / 100) + boost));
}

// Biometric face-map dimensions (responsive, capped) — used for the landmark
// mesh overlaid on the captured front photo.
const FACE_MAP_W = Math.min(300, width - 72);
const FACE_MAP_H = Math.round(FACE_MAP_W * 1.32);

// Facial landmark points in NORMALIZED coords (0-1) over a centered front-facing
// face. We don't run live face-tracking (it glitched the preview), so these are
// approximate anatomical positions — the guided scan centers the face in the
// frame, so they line up well enough to read as a real biometric mesh. Each
// point is tagged with the category whose score colors it (red→green by score).
const FACE_LANDMARKS: { x: number; y: number; cat: string }[] = [
  { x: 0.50, y: 0.07, cat: 'hair' },        // 0  hairline center
  { x: 0.35, y: 0.11, cat: 'hair' },        // 1  hairline L
  { x: 0.65, y: 0.11, cat: 'hair' },        // 2  hairline R
  { x: 0.43, y: 0.17, cat: 'hair' },        // 3  forehead L
  { x: 0.57, y: 0.17, cat: 'hair' },        // 4  forehead R
  { x: 0.29, y: 0.23, cat: 'masculinity' }, // 5  temple L
  { x: 0.71, y: 0.23, cat: 'masculinity' }, // 6  temple R
  { x: 0.37, y: 0.275, cat: 'eyes' },       // 7  brow L out
  { x: 0.45, y: 0.255, cat: 'eyes' },       // 8  brow L in
  { x: 0.55, y: 0.255, cat: 'eyes' },       // 9  brow R in
  { x: 0.63, y: 0.275, cat: 'eyes' },       // 10 brow R out
  { x: 0.35, y: 0.345, cat: 'eyes' },       // 11 eye L out
  { x: 0.45, y: 0.34, cat: 'eyes' },        // 12 eye L in
  { x: 0.55, y: 0.34, cat: 'eyes' },        // 13 eye R in
  { x: 0.65, y: 0.345, cat: 'eyes' },       // 14 eye R out
  { x: 0.31, y: 0.45, cat: 'cheekbones' },  // 15 cheekbone L
  { x: 0.69, y: 0.45, cat: 'cheekbones' },  // 16 cheekbone R
  { x: 0.50, y: 0.37, cat: 'skin' },        // 17 nose bridge
  { x: 0.50, y: 0.47, cat: 'skin' },        // 18 mid nose
  { x: 0.38, y: 0.50, cat: 'cheekbones' },  // 19 cheek L mid
  { x: 0.62, y: 0.50, cat: 'cheekbones' },  // 20 cheek R mid
  { x: 0.50, y: 0.55, cat: 'skin' },        // 21 nose tip
  { x: 0.45, y: 0.565, cat: 'skin' },       // 22 nostril L
  { x: 0.55, y: 0.565, cat: 'skin' },       // 23 nostril R
  { x: 0.27, y: 0.55, cat: 'masculinity' }, // 24 jaw angle L
  { x: 0.73, y: 0.55, cat: 'masculinity' }, // 25 jaw angle R
  { x: 0.40, y: 0.60, cat: 'skin' },        // 26 cheek skin L
  { x: 0.60, y: 0.60, cat: 'skin' },        // 27 cheek skin R
  { x: 0.50, y: 0.62, cat: 'skin' },        // 28 philtrum
  { x: 0.50, y: 0.69, cat: 'jawline' },     // 29 lip line
  { x: 0.31, y: 0.66, cat: 'jawline' },     // 30 jaw L
  { x: 0.69, y: 0.66, cat: 'jawline' },     // 31 jaw R
  { x: 0.38, y: 0.77, cat: 'jawline' },     // 32 lower jaw L
  { x: 0.62, y: 0.77, cat: 'jawline' },     // 33 lower jaw R
  { x: 0.50, y: 0.84, cat: 'jawline' },     // 34 chin
];

// Edges connecting landmarks into a triangulated wireframe (index pairs).
const FACE_MESH_EDGES: [number, number][] = [
  [1, 3], [3, 0], [0, 4], [4, 2], [1, 5], [2, 6],
  [5, 7], [7, 8], [8, 9], [9, 10], [10, 6],
  [7, 11], [8, 12], [9, 13], [10, 14], [11, 12], [13, 14],
  [3, 8], [4, 9], [0, 17], [12, 17], [13, 17],
  [5, 11], [6, 14], [5, 15], [6, 16], [11, 15], [14, 16],
  [17, 18], [18, 21], [21, 22], [21, 23], [22, 28], [23, 28], [18, 19], [18, 20],
  [15, 19], [16, 20], [19, 26], [20, 27], [15, 24], [16, 25], [19, 24], [20, 25],
  [26, 28], [27, 28], [26, 22], [27, 23],
  [24, 30], [25, 31], [30, 32], [31, 33], [32, 34], [33, 34],
  [26, 30], [27, 31], [28, 29], [29, 34], [26, 32], [27, 33], [29, 32], [29, 33],
  [24, 26], [25, 27],
];

// Real detected face position/size for a given scan, normalized to 0-1
// fractions of the capture frame. When present, FACE_LANDMARKS (a fixed
// anatomical template) gets affine-mapped onto this box instead of being
// drawn at its literal template coordinates — so the mesh/pins track the
// actual face in the photo rather than assuming a fixed framing.
type FaceBox = { x: number; y: number; w: number; h: number };

// The implicit bounding region the FACE_LANDMARKS template already assumes
// (computed once from the table itself, not hand-maintained).
const TEMPLATE_BOX = (() => {
  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const p of FACE_LANDMARKS) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, width: maxX - minX, height: maxY - minY };
})();

// Map a template-relative point onto the real detected face box for this
// scan, preserving the template's hand-tuned proportions while anchoring
// position/scale to the actual photo. Falls back to the raw template point
// when no box was captured (e.g. detection missed that scan).
function mapPoint(p: { x: number; y: number }, box: FaceBox | null): { x: number; y: number } {
  if (!box) return p;
  return {
    x: box.x + ((p.x - TEMPLATE_BOX.minX) / TEMPLATE_BOX.width) * box.w,
    y: box.y + ((p.y - TEMPLATE_BOX.minY) / TEMPLATE_BOX.height) * box.h,
  };
}

// The biometric landmark mesh drawn over the front photo: faint blue wireframe +
// score-colored nodes (red=focus area → green=strong). Sizes scale to w/h so it
// fits any map dimensions.
function FaceMesh({ w, h, scores, boost, box }: {
  w: number; h: number; scores: Record<string, number>; boost: number; box: FaceBox | null;
}) {
  return (
    <Svg width={w} height={h} style={StyleSheet.absoluteFill} pointerEvents="none">
      {FACE_MESH_EDGES.map(([a, b], i) => {
        const pa = mapPoint(FACE_LANDMARKS[a], box);
        const pb = mapPoint(FACE_LANDMARKS[b], box);
        return (
          <Line
            key={`e${i}`}
            x1={pa.x * w} y1={pa.y * h}
            x2={pb.x * w} y2={pb.y * h}
            stroke="rgba(0,191,255,0.30)" strokeWidth={0.8}
          />
        );
      })}
      {FACE_LANDMARKS.map((raw, i) => {
        const p = mapPoint(raw, box);
        const has = typeof scores[raw.cat] === 'number';
        const color = has ? getScoreColor(displayScore(scores[raw.cat], boost)) : 'rgba(255,255,255,0.55)';
        return (
          <G key={`p${i}`}>
            <Circle cx={p.x * w} cy={p.y * h} r={7} fill={color} opacity={0.16} />
            <Circle cx={p.x * w} cy={p.y * h} r={2.8} fill={color} />
            <Circle cx={p.x * w} cy={p.y * h} r={2.8} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={0.5} />
          </G>
        );
      })}
    </Svg>
  );
}

// ── Interactive face map ────────────────────────────────────────────────────
// Places each named sub-trait pin on the actual anatomy it describes. Keyword
// match first (e.g. "Gonial angle" → jaw corner), else the category anchor.
const CAT_ANCHOR: Record<string, { x: number; y: number }> = {
  hair: { x: 0.50, y: 0.10 },
  eyes: { x: 0.50, y: 0.34 },
  cheekbones: { x: 0.69, y: 0.45 },
  skin: { x: 0.40, y: 0.55 },
  jawline: { x: 0.50, y: 0.80 },
  masculinity: { x: 0.29, y: 0.30 },
};
const TRAIT_POS: { kw: string; x: number; y: number }[] = [
  { kw: 'canthal', x: 0.35, y: 0.345 }, { kw: 'eye spacing', x: 0.50, y: 0.34 },
  { kw: 'under-eye', x: 0.42, y: 0.385 }, { kw: 'upper-lid', x: 0.45, y: 0.33 },
  { kw: 'lid', x: 0.45, y: 0.33 }, { kw: 'brow', x: 0.45, y: 0.26 },
  { kw: 'gonial', x: 0.27, y: 0.60 }, { kw: 'ramus', x: 0.27, y: 0.53 },
  { kw: 'chin', x: 0.50, y: 0.84 }, { kw: 'submental', x: 0.50, y: 0.80 },
  { kw: 'jaw', x: 0.31, y: 0.66 }, { kw: 'zygomatic', x: 0.69, y: 0.45 },
  { kw: 'ogee', x: 0.66, y: 0.50 }, { kw: 'malar', x: 0.62, y: 0.50 },
  { kw: 'cheek', x: 0.62, y: 0.50 }, { kw: 'nasal', x: 0.50, y: 0.47 },
  { kw: 'nose', x: 0.50, y: 0.50 }, { kw: 'philtrum', x: 0.50, y: 0.62 },
  { kw: 'lip', x: 0.50, y: 0.69 }, { kw: 'clarity', x: 0.40, y: 0.55 },
  { kw: 'tone', x: 0.60, y: 0.55 }, { kw: 'pore', x: 0.40, y: 0.48 },
  { kw: 'texture', x: 0.60, y: 0.60 }, { kw: 'hairline', x: 0.50, y: 0.11 },
  { kw: 'density', x: 0.50, y: 0.07 }, { kw: 'temple', x: 0.35, y: 0.13 },
  { kw: 'width', x: 0.29, y: 0.40 }, { kw: 'thirds', x: 0.50, y: 0.40 },
  { kw: 'symmetry', x: 0.50, y: 0.45 }, { kw: 'bone', x: 0.31, y: 0.45 },
];
function traitPinPos(name: string, cat: string): { x: number; y: number } {
  const n = name.toLowerCase();
  for (const t of TRAIT_POS) if (n.includes(t.kw)) return { x: t.x, y: t.y };
  return CAT_ANCHOR[cat] ?? { x: 0.5, y: 0.5 };
}
type FaceMapPin = { n: number; cat: string; trait: SubTrait; x: number; y: number };
function buildPins(subs: Record<string, SubTrait[]>): FaceMapPin[] {
  const pins: FaceMapPin[] = [];
  let n = 1;
  for (const cat of CATEGORY_KEYS) {
    const arr = subs[cat];
    if (!arr) continue;
    for (const trait of arr) {
      let { x, y } = traitPinPos(trait.name, cat);
      // Nudge apart pins that would otherwise stack on the same spot.
      let guard = 0;
      while (pins.some(p => Math.abs(p.x - x) < 0.045 && Math.abs(p.y - y) < 0.045) && guard < 10) {
        x += 0.05; if (x > 0.88) { x = 0.22; y += 0.05; }
        guard++;
      }
      pins.push({ n: n++, cat, trait, x, y });
    }
  }
  return pins;
}

// A zoomed circular crop of the user's face centered on a normalized point, so
// each trait/category can show the exact region we're talking about. Reuses the
// already-decoded front photo (ExpoImage caches by URI) to stay memory-light.
function FaceCrop({ photo, ax, ay, size = 52, zoom = 2.6 }: {
  photo: string; ax: number; ay: number; size?: number; zoom?: number;
}) {
  const S = size * zoom;
  return (
    <View style={{
      width: size, height: size, borderRadius: size / 2, overflow: 'hidden',
      borderWidth: 1, borderColor: 'rgba(255,255,255,0.25)', backgroundColor: '#000',
    }}>
      <ExpoImage
        source={{ uri: `data:image/jpeg;base64,${photo}` }}
        style={{ position: 'absolute', width: S, height: S, left: -(ax * S - size / 2), top: -(ay * S - size / 2) }}
        contentFit="cover"
        cachePolicy="memory"
      />
    </View>
  );
}

// Drag-to-compare before/after: the original photo on the left of the divider,
// the AI "potential" on the right. Both images are identical dimensions from
// the same camera frame, so they align pixel-perfect; dragging just moves the
// clip edge. The photo claims ANY touch that starts on it (any direction) and
// never yields it, so a diagonal drag stays smooth instead of getting
// cancelled by the parent ScrollView the moment it detects vertical motion.
// Currently unused — the $40 human-edited photo is delivered by email (v1),
// not shown in-app. Kept for the planned v2 in-app delivery UI, which would
// show this same before/after comparison for the delivered result.
function BeforeAfterSlider({ before, after, w, h }: {
  before: string; after: string; w: number; h: number;
}) {
  // Driven as an Animated.Value so drags update the native views directly,
  // with no React re-render per frame — that reconciliation is what made the
  // old setState version stutter. The overlay children are pointerEvents="none"
  // so every touch reports locationX relative to THIS wrapper (a finger crossing
  // a child view used to reset the reference and make the divider jump).
  const clamp = (x: number) => Math.max(16, Math.min(w - 16, x));
  const divX = useRef(new Animated.Value(w / 2)).current;
  const pan = useRef(
    PanResponder.create({
      // Claim the touch at the CAPTURE phase — before the parent results-sheet
      // ScrollView's native scroll gesture ever gets to negotiate for it — and
      // never release it once granted. Without this, any vertical component in
      // the drag (which is nearly always present) let the ScrollView steal the
      // gesture mid-drag and cancel the slider, making it feel "interrupted."
      // This lets the divider track a diagonal drag smoothly: horizontal moves
      // it, vertical is simply ignored, and the page itself never scrolls
      // while a finger is down on the photo.
      onStartShouldSetPanResponderCapture: () => true,
      onMoveShouldSetPanResponderCapture: () => true,
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderTerminationRequest: () => false,
      onShouldBlockNativeResponder: () => true,
      onPanResponderGrant: (e) => { divX.setValue(clamp(e.nativeEvent.locationX)); },
      onPanResponderMove: (e) => { divX.setValue(clamp(e.nativeEvent.locationX)); },
    })
  ).current;
  return (
    <View {...pan.panHandlers} style={[styles.baWrap, { width: w, height: h }]}>
      {/* AFTER (potential) fills the frame; BEFORE is clipped to the left side */}
      <ExpoImage
        source={{ uri: `data:image/jpeg;base64,${after}` }}
        style={{ width: w, height: h }}
        contentFit="cover"
        cachePolicy="memory"
        pointerEvents="none"
      />
      <Animated.View style={[styles.baClip, { width: divX, height: h }]} pointerEvents="none">
        <ExpoImage
          source={{ uri: `data:image/jpeg;base64,${before}` }}
          style={{ width: w, height: h }}
          contentFit="cover"
          cachePolicy="memory"
        />
      </Animated.View>
      <Animated.View
        style={[styles.baDivider, { left: 0, height: h, transform: [{ translateX: Animated.subtract(divX, 1) }] }]}
        pointerEvents="none"
      />
      <Animated.View
        style={[styles.baHandle, { left: 0, top: h / 2 - 17, transform: [{ translateX: Animated.subtract(divX, 17) }] }]}
        pointerEvents="none"
      >
        <Text style={styles.baHandleText}>◂▸</Text>
      </Animated.View>
      <View style={[styles.baTag, { left: 10 }]} pointerEvents="none"><Text style={styles.baTagText}>YOU</Text></View>
      <View style={[styles.baTag, { right: 10, borderColor: '#ff4dd266' }]} pointerEvents="none">
        <Text style={[styles.baTagText, { color: '#ff9ae8' }]}>POTENTIAL ✦</Text>
      </View>
    </View>
  );
}

// One consistent section header for the results sheet: centered title, a short
// colored accent bar, and a one-line subtitle. Gives each section the same
// designed rhythm instead of bare uppercase labels.
function SectionHeader({ title, sub, accent = PRIMARY_BLUE }: { title: string; sub?: string; accent?: string }) {
  return (
    <View style={styles.sectionHead}>
      <Text style={styles.sectionHeadTitle}>{title}</Text>
      <View style={[styles.sectionAccentBar, { backgroundColor: accent }]} />
      {sub ? <Text style={styles.sectionHeadSub}>{sub}</Text> : null}
    </View>
  );
}

// A slim percentile bar — fills to the ranking, colored by tier. The visual that
// turns a plain number into a "metric".
function PercentBar({ pct, color }: { pct: number; color: string }) {
  const w = Math.max(4, Math.min(100, pct));
  return (
    <View style={styles.pbTrack}>
      <View style={[styles.pbFill, { width: `${w}%`, backgroundColor: color }]} />
    </View>
  );
}

// The interactive annotated face map: numbered pins over the photo + a live
// callout for the selected trait. Only rendered for subscribers.
function InteractiveFaceMap({ photo, pins, box }: { photo: string; pins: FaceMapPin[]; box: FaceBox | null }) {
  const [sel, setSel] = useState(pins.length ? pins[0].n : 0);
  const selected = pins.find(p => p.n === sel) ?? pins[0];
  return (
    <>
      <View style={[styles.faceMapWrapper, { width: FACE_MAP_W, height: FACE_MAP_H }]}>
        <ExpoImage
          source={{ uri: `data:image/jpeg;base64,${photo}` }}
          style={styles.faceMapPhoto}
          contentFit="cover"
          cachePolicy="memory"
        />
        {/* faint wireframe underlay for the sci-fi read */}
        <Svg width={FACE_MAP_W} height={FACE_MAP_H} style={StyleSheet.absoluteFill} pointerEvents="none">
          {FACE_MESH_EDGES.map(([a, b], i) => {
            const pa = mapPoint(FACE_LANDMARKS[a], box);
            const pb = mapPoint(FACE_LANDMARKS[b], box);
            return (
              <Line
                key={`e${i}`}
                x1={pa.x * FACE_MAP_W} y1={pa.y * FACE_MAP_H}
                x2={pb.x * FACE_MAP_W} y2={pb.y * FACE_MAP_H}
                stroke="rgba(0,191,255,0.22)" strokeWidth={0.7}
              />
            );
          })}
        </Svg>
        {/* numbered, tappable trait pins */}
        {pins.map((raw) => {
          const p = mapPoint(raw, box);
          const c = tierColor(raw.trait.tier);
          const active = raw.n === sel;
          const d = active ? 24 : 17;
          return (
            <TouchableOpacity
              key={raw.n}
              activeOpacity={0.8}
              onPress={() => setSel(raw.n)}
              style={[styles.mapPin, {
                left: p.x * FACE_MAP_W - d / 2,
                top: p.y * FACE_MAP_H - d / 2,
                width: d, height: d, borderRadius: d / 2,
                backgroundColor: c,
                borderColor: active ? '#fff' : 'rgba(255,255,255,0.65)',
                borderWidth: active ? 2 : 1,
              }]}
            >
              <Text style={[styles.mapPinText, active && { fontSize: 11 }]}>{raw.n}</Text>
            </TouchableOpacity>
          );
        })}
        <View style={[styles.mapCorner, styles.mapCornerTL]} pointerEvents="none" />
        <View style={[styles.mapCorner, styles.mapCornerTR]} pointerEvents="none" />
        <View style={[styles.mapCorner, styles.mapCornerBL]} pointerEvents="none" />
        <View style={[styles.mapCorner, styles.mapCornerBR]} pointerEvents="none" />
      </View>

      {selected && (
        <View style={[styles.pinCallout, { borderColor: tierColor(selected.trait.tier) }]}>
          <View style={styles.pinCalloutHead}>
            <View style={[styles.pinCalloutNum, { backgroundColor: tierColor(selected.trait.tier) }]}>
              <Text style={styles.pinCalloutNumText}>{selected.n}</Text>
            </View>
            <Text style={styles.pinCalloutName} numberOfLines={1}>{selected.trait.name}</Text>
            <View style={[styles.tierTag, { borderColor: tierColor(selected.trait.tier), backgroundColor: tierColor(selected.trait.tier) + '22' }]}>
              <Text style={[styles.tierTagText, { color: tierColor(selected.trait.tier) }]}>{tierLabel(selected.trait.tier)}</Text>
            </View>
          </View>
          {typeof selected.trait.percentile === 'number' && (
            <View style={styles.pinCalloutBarRow}>
              <PercentBar pct={selected.trait.percentile} color={tierColor(selected.trait.tier)} />
              <Text style={[styles.pinCalloutPct, { color: tierColor(selected.trait.tier) }]}>{ordinal(selected.trait.percentile)}</Text>
            </View>
          )}
          {selected.trait.why ? <Text style={styles.pinCalloutWhy}>{selected.trait.why}</Text> : null}
          {selected.trait.protocol ? (
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 7 }}>
              <View style={{ width: 7, height: 7, borderRadius: 4, marginTop: 5, backgroundColor: difficultyColor(selected.trait.protocol.difficulty) }} />
              <Text style={[styles.pinCalloutFix, { flex: 1 }]}>{selected.trait.protocol.fix}</Text>
            </View>
          ) : null}
        </View>
      )}
      <Text style={styles.mapHint}>Tap any point to inspect that trait</Text>
    </>
  );
}

// The over-time "feel good" assist. Grows mostly with real effort (tasks
// completed) plus a gentle weekly floor, so even a quiet user drifts up a little
// — but it's capped, so scores never run away from reality. Anchored to the
// user's first scan (journeyStart) so it survives the program regenerating on
// every scan.
// ── XP Levels ───────────────────────────────────────────────────────────────
// One global level from lifetime XP (tasks + guides). Square-root curve: the
// first levels land fast (early wins), later ones demand real consistency.
// L2 @ 60 XP (~first day), L5 @ 960 (~2 weeks), L10 @ 4860 (~7 weeks).
function levelForXp(xp: number): number {
  return Math.floor(Math.sqrt(Math.max(0, xp) / 60)) + 1;
}
function xpAtLevel(level: number): number {
  return 60 * (level - 1) * (level - 1);
}

// Daily tip bank — one surfaces per day on the scan screen (rotates by date).
// Short, concrete, drawn from the same knowledge as the Guides.
const DAILY_TIPS = [
  'Sunscreen every morning is the single biggest anti-aging move — 80% of visible aging is UV.',
  'Sleep on your back with your head slightly elevated — less face puffiness by morning.',
  'Drink water first thing. Dehydration shows in your under-eyes before you feel it.',
  'Change your pillowcase twice a week — it touches your face 8 hours a night.',
  'Mewing is posture, not magic: tongue flat on the roof of your mouth, lips sealed.',
  'A sharp jawline is mostly low body fat. The gym cuts your face too.',
  'Don’t pop pimples — the scar lasts months, the pimple lasts days.',
  'Cold water on your face in the morning tightens and de-puffs in 30 seconds.',
  'Brows frame your eyes. Clean up strays — don’t reshape, just define.',
  'High protein + 8h sleep beats any supplement for skin and hair.',
  'Squinting at screens builds tension lines. Screen slightly below eye level.',
  'Walk 20+ minutes daily — circulation literally gives your face color.',
  'Trim your neckline weekly even when growing a beard. Sharp edges read as intentional.',
  'A fresh cut every 2–3 weeks does more than any product.',
  'Salt at night = puffy face in the morning. Go easy at dinner.',
  'Consistency beats intensity: 10 minutes daily beats a 2-hour Sunday routine.',
];

const BOOST_CAP = 8;
function computeBoost(journeyStart: number | null, lifetimeTasks: number): number {
  const weeks = journeyStart ? (Date.now() - journeyStart) / (7 * 86400000) : 0;
  return Math.max(0, Math.min(weeks * 0.4 + lifetimeTasks * 0.15, BOOST_CAP));
}

function dateKey(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function daysBetweenKeys(a: string, b: string): number {
  const da = new Date(`${a}T00:00:00`).getTime();
  const db = new Date(`${b}T00:00:00`).getTime();
  return Math.round((db - da) / 86400000);
}

// On load: streak survives if the last active day was today or yesterday,
// otherwise a day was missed and it resets to 0.
function computeStreakOnLoad(
  stored: { count: number; lastDate: string | null } | null,
  today: string
): number {
  if (!stored?.lastDate) return 0;
  const diff = daysBetweenKeys(stored.lastDate, today);
  return diff === 0 || diff === 1 ? stored.count : 0;
}

// Monday-anchored week key — the streak freeze refills each new week.
function weekAnchorKey(dk: string): string {
  const d = new Date(`${dk}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // back to Monday
  return dateKey(d);
}

// ── Achievement badges — collectible unlocks keyed to data the app already
// tracks. Checked reactively; earned ids persist to STORAGE.badges. ──
type BadgeCtx = {
  scanCount: number;
  streakDays: number;
  lifetimeTasks: number;
  level: number;
  hasPotential: boolean;
  perfectDay: boolean;
  weekConquered: boolean;
};
type BadgeDef = { id: string; icon: keyof typeof Ionicons.glyphMap; title: string; desc: string; color: string; earned: (c: BadgeCtx) => boolean };
const BADGES: BadgeDef[] = [
  { id: 'first_scan', icon: 'scan', title: 'First Scan', desc: 'Complete your first face scan', color: '#00BFFF', earned: c => c.scanCount >= 1 },
  { id: 'scan_5', icon: 'analytics', title: 'Data Driven', desc: 'Complete 5 scans', color: '#00BFFF', earned: c => c.scanCount >= 5 },
  { id: 'streak_3', icon: 'flame', title: 'Heating Up', desc: 'Hit a 3-day streak', color: '#FF6600', earned: c => c.streakDays >= 3 },
  { id: 'streak_7', icon: 'bonfire', title: 'On Fire', desc: 'Hit a 7-day streak', color: '#FF6600', earned: c => c.streakDays >= 7 },
  { id: 'streak_30', icon: 'rocket', title: 'Unstoppable', desc: 'Hit a 30-day streak', color: '#FF6600', earned: c => c.streakDays >= 30 },
  { id: 'tasks_10', icon: 'checkmark-circle', title: 'Grinder', desc: 'Complete 10 tasks', color: '#43e0ff', earned: c => c.lifetimeTasks >= 10 },
  { id: 'tasks_50', icon: 'barbell', title: 'Machine', desc: 'Complete 50 tasks', color: '#43e0ff', earned: c => c.lifetimeTasks >= 50 },
  { id: 'perfect_day', icon: 'star', title: 'Perfect Day', desc: 'Finish every task in one day', color: '#FFD700', earned: c => c.perfectDay },
  { id: 'level_5', icon: 'trophy', title: 'Level 5', desc: 'Reach Level 5', color: '#ff4dd2', earned: c => c.level >= 5 },
  { id: 'week_done', icon: 'ribbon', title: 'Week Conquered', desc: 'Finish a full program week', color: '#ff4dd2', earned: c => c.weekConquered },
  { id: 'potential', icon: 'sparkles', title: 'Saw the Vision', desc: 'Ordered your hand-edited photo', color: '#ff4dd2', earned: c => c.hasPotential },
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Encode raw image bytes to base64 in pure JS, so the scan photo can be sent
// to the API straight from memory — the app never writes it to device storage.
const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function bytesToBase64(bytes: Uint8Array): string {
  const out: string[] = [];
  const len = bytes.length;
  let i = 0;
  for (; i + 3 <= len; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out.push(B64_CHARS[(n >> 18) & 63] + B64_CHARS[(n >> 12) & 63] + B64_CHARS[(n >> 6) & 63] + B64_CHARS[n & 63]);
  }
  const rem = len - i;
  if (rem === 1) {
    const n = bytes[i] << 16;
    out.push(B64_CHARS[(n >> 18) & 63] + B64_CHARS[(n >> 12) & 63] + '==');
  } else if (rem === 2) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out.push(B64_CHARS[(n >> 18) & 63] + B64_CHARS[(n >> 12) & 63] + B64_CHARS[(n >> 6) & 63] + '=');
  }
  return out.join('');
}

// The 2 lowest RAW category scores are the user's priorities (under the hood).
function detectPriorities(rawScores: Record<string, number>): string[] {
  return [...CATEGORY_KEYS]
    .sort((a, b) => (rawScores[a] ?? 100) - (rawScores[b] ?? 100))
    .slice(0, 2);
}

function generateProgram(rawScores: Record<string, number>): Program {
  const priorities = detectPriorities(rawScores);
  const focusPairs = buildWeekFocusPairs(rawScores);

  const decks: Record<string, ProgramTaskTemplate[]> = {};
  const ptr: Record<string, number> = {};
  CATEGORY_KEYS.forEach(k => {
    decks[k] = shuffle(TASK_LIBRARY[k]);
    ptr[k] = 0;
  });

  const pick = (cat: string, n: number, week: number): ProgramTask[] => {
    const deck = decks[cat];
    const out: ProgramTask[] = [];
    for (let i = 0; i < n; i++) {
      const idx = ptr[cat];
      ptr[cat]++;
      const t = deck[idx % deck.length];
      // include the draw index so the id is unique even if the deck cycles
      out.push({ instanceId: `w${week}-${cat}-${t.id}-${idx}`, templateId: t.id, label: t.label, icon: t.icon, category: cat, xp: t.xp, day: 0, desc: TASK_DESC[t.id] });
    }
    return out;
  };

  const weeks: ProgramWeek[] = focusPairs.map((triple, i) => {
    const [catA, catB, catC] = triple as unknown as [string, string, string];
    const week = i + 1;

    // TASKS_PER_DAY tasks for each of the 7 days. Weighted toward the week's
    // top focus category, shuffled so the categories mix across each day
    // rather than clustering, then chunked into days 1–7.
    const raw = [...pick(catA, 12, week), ...pick(catB, 12, week), ...pick(catC, 11, week)];
    const tasks: ProgramTask[] = shuffle(raw).map((t, idx) => ({ ...t, day: Math.floor(idx / TASKS_PER_DAY) + 1 }));

    return {
      week,
      name: WEEK_NAMES[i],
      icon: WEEK_ICONS[i],
      gradient: WEEK_GRADIENTS[i],
      priorities: [catA, catB, catC].map(k => CATEGORY_LABEL[k]),
      tasks,
    };
  });

  return { generatedAt: Date.now(), priorities, rawScores, weeks };
}

function getScoreColor(score: number): string {
  if (score >= 80) return '#43e0ff';
  if (score >= 60) return '#FFD700';
  if (score >= 40) return '#FF6600';
  return '#FF0000';
}

const CONFETTI_COLORS = [CYAN, MAGENTA, PURPLE_MID, '#FFFFFF', '#6f9dff'];
const CONFETTI_COUNT = 22;

function ConfettiBurst() {
  const { width: sw, height: sh } = Dimensions.get('window');
  const particles = useRef(
    Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
      x: Math.random() * sw,
      size: 6 + Math.random() * 7,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: Math.random() * 280,
      rotate: Math.random() * 360,
      anim: new Animated.Value(0),
    }))
  ).current;

  useEffect(() => {
    const anims = particles.map(p =>
      Animated.sequence([
        Animated.delay(p.delay),
        Animated.timing(p.anim, { toValue: 1, duration: 1300, useNativeDriver: true }),
      ])
    );
    Animated.parallel(anims).start();
  }, []);

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      {particles.map((p, i) => {
        const translateY = p.anim.interpolate({ inputRange: [0, 1], outputRange: [-20, sh + 40] });
        const opacity = p.anim.interpolate({ inputRange: [0, 0.7, 1], outputRange: [1, 0.9, 0] });
        const rotate = p.anim.interpolate({ inputRange: [0, 1], outputRange: [`${p.rotate}deg`, `${p.rotate + 540}deg`] });
        return (
          <Animated.View
            key={i}
            style={{
              position: 'absolute',
              left: p.x,
              top: 0,
              width: p.size,
              height: p.size * 0.5,
              backgroundColor: p.color,
              borderRadius: 2,
              opacity,
              transform: [{ translateY }, { rotate }],
            }}
          />
        );
      })}
    </View>
  );
}

function ScoreBubble({ label, score, arcColor, size, onPress, pulse, glowRing, boost = 0, floor }: {
  label: string;
  score: number | string;
  arcColor?: string;
  size: 'large' | 'small' | 'medium' | 'xlarge';
  onPress?: () => void;
  pulse?: Animated.Value;
  glowRing?: Animated.Value;
  boost?: number;        // over-time assist added to the displayed score
  floor?: number | null; // ratchet — never show below this (used for OVERALL)
}) {
  const isXLarge = size === 'xlarge';
  const isLarge = size === 'large';
  const isMedium = size === 'medium';
  const dim = isXLarge ? 148 : isLarge ? BUBBLE_LARGE : isMedium ? 118 : BUBBLE_SMALL;

  // Arc radius scales with the bubble so it sits between the number and the
  // outer ring instead of crowding the digits.
  const r = Math.round(dim * 0.37);
  const cx = dim / 2;
  const cy = dim / 2;

  const sweepDeg = 200;
  const startDeg = 90 + (360 - sweepDeg) / 2;
  const endDeg = 90 - (360 - sweepDeg) / 2;
  const toRad = (d: number) => d * (Math.PI / 180);
  const x1 = cx + r * Math.cos(toRad(startDeg));
  const y1 = cy + r * Math.sin(toRad(startDeg));
  const x2 = cx + r * Math.cos(toRad(endDeg));
  const y2 = cy + r * Math.sin(toRad(endDeg));

  const arcPath = `M${x1.toFixed(2)} ${y1.toFixed(2)} A${r} ${r} 0 1 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  const arcLength = 2 * Math.PI * r * (sweepDeg / 360);

  // Always show the boosted score to the user; raw values never reach the UI.
  // The ratchet floor keeps the number from ever appearing to go backward.
  let numScore = typeof score === 'number' ? displayScore(score, boost) : null;
  if (numScore !== null && typeof floor === 'number') numScore = Math.max(numScore, Math.min(100, floor));
  const fillColor = arcColor || (numScore !== null ? getScoreColor(numScore) : 'rgba(255,255,255,0.15)');
  const dashOffset = numScore !== null ? arcLength * (1 - numScore / 100) : arcLength;
  const strokeW = isXLarge ? 6 : isLarge ? 4 : isMedium ? 5 : 4;

  // Count-up animation: counts from 0 to numScore when score first appears.
  const countAnim = useRef(new Animated.Value(0)).current;
  const prevScore = useRef<number | null>(null);
  const [displayCount, setDisplayCount] = useState<number | null>(null);
  useEffect(() => {
    if (numScore !== null && prevScore.current === null) {
      setDisplayCount(0);
      const id = countAnim.addListener(({ value }) => setDisplayCount(Math.round(value)));
      countAnim.setValue(0);
      Animated.timing(countAnim, { toValue: numScore, duration: 700, useNativeDriver: false }).start(() => {
        countAnim.removeListener(id);
        setDisplayCount(numScore);
      });
    } else if (numScore !== null && prevScore.current !== numScore) {
      countAnim.setValue(numScore);
      setDisplayCount(numScore);
    }
    prevScore.current = numScore;
  }, [numScore]);

  const animatedTransform = pulse
    ? { transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.06] }) }] }
    : {};

  const glowStyle = glowRing
    ? {
        shadowColor: PRIMARY_BLUE,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: glowRing.interpolate({ inputRange: [0, 1], outputRange: [0.5, 1] }),
        shadowRadius: glowRing.interpolate({ inputRange: [0, 1], outputRange: [10, 25] }),
      }
    : {};

  const content = (
    <Animated.View style={[styles.bubble, { width: dim, height: dim, borderRadius: dim / 2 }, animatedTransform, glowStyle]}>
      {/* The outer blue ring is drawn INSIDE the same SVG as the arc so both
          share the exact same center — an RN border shifted the fill box and
          left the arc visibly off-center from the ring. */}
      <Svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} style={StyleSheet.absoluteFill}>
        <Circle cx={cx} cy={cy} r={dim / 2 - 1.5} fill="none" stroke={PRIMARY_BLUE} strokeWidth={2.5} />
        <Path d={arcPath} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={strokeW} strokeLinecap="round" />
        <Path d={arcPath} fill="none" stroke={fillColor} strokeWidth={strokeW} strokeLinecap="round" strokeDasharray={arcLength} strokeDashoffset={dashOffset} />
      </Svg>
      <View style={styles.bubbleTextContainer}>
        <Text style={[{ fontSize: isXLarge ? 40 : isLarge ? 30 : isMedium ? 28 : 22, fontFamily: FONT_BOLD, color: fillColor }]}>
          {numScore !== null ? (displayCount ?? numScore) : '--'}
        </Text>
        <Text style={[styles.bubbleLabel, { fontSize: isXLarge ? 11 : isLarge ? 9 : isMedium ? 9 : 8 }]}>{label}</Text>
      </View>
    </Animated.View>
  );

  if (onPress) return <TouchableOpacity onPress={onPress} activeOpacity={0.8}>{content}</TouchableOpacity>;
  return content;
}

// Full-screen spotlight — dark everywhere except the face oval (no rectangular boundary)
function SilhouetteSpotlight({
  screenW, screenH, faceLeft, faceTop, faceW, faceH,
}: { screenW: number; screenH: number; faceLeft: number; faceTop: number; faceW: number; faceH: number }) {
  const sx = faceW / 240;
  const sy = faceH / 340;
  return (
    <Svg width={screenW} height={screenH} style={StyleSheet.absoluteFill}>
      <Defs>
        <Mask id="fullMask">
          <Rect x="0" y="0" width={screenW} height={screenH} fill="white" />
          <G transform={`translate(${faceLeft},${faceTop}) scale(${sx},${sy})`}>
            <Path d="M120 12 C85 12, 52 30, 38 62 C24 92, 23 128, 28 158 C33 185, 44 205, 58 222 C68 234, 80 243, 92 250 C100 255, 110 258, 120 258 C130 258, 140 255, 148 250 C160 243, 172 234, 182 222 C196 205, 207 185, 212 158 C217 128, 216 92, 202 62 C188 30, 155 12, 120 12 Z" fill="black" />
          </G>
        </Mask>
      </Defs>
      <Rect x="0" y="0" width={screenW} height={screenH} fill="rgba(0,0,0,0.62)" mask="url(#fullMask)" />
    </Svg>
  );
}

function FaceSilhouette({ width: w, height: h }: { width: number; height: number }) {
  // Steady vertical cyan→purple gradient at all times. (A green "locked-in"
  // recolor used to live here, but switching the whole SVG's stroke on every
  // face-detect frame caused a visible glitch, so it was removed.) userSpaceOnUse
  // so the gradient spans the whole silhouette — cyan up top, magenta at the
  // chin; top corner brackets read cyan, bottom brackets read purple.
  const stroke = 'url(#faceGrad)';
  return (
    <Svg width={w} height={h} viewBox="0 0 240 340">
      <Defs>
        <SvgLinearGradient id="faceGrad" x1="0" y1="0" x2="0" y2="270" gradientUnits="userSpaceOnUse">
          <Stop offset="0" stopColor="#00E0FF" />
          <Stop offset="0.5" stopColor="#2E9BFF" />
          <Stop offset="1" stopColor="#B026FF" />
        </SvgLinearGradient>
      </Defs>
      <Path d="M120 12 C85 12, 52 30, 38 62 C24 92, 23 128, 28 158 C33 185, 44 205, 58 222 C68 234, 80 243, 92 250 C100 255, 110 258, 120 258 C130 258, 140 255, 148 250 C160 243, 172 234, 182 222 C196 205, 207 185, 212 158 C217 128, 216 92, 202 62 C188 30, 155 12, 120 12 Z" fill="none" stroke={stroke} strokeWidth="14" opacity="0.13" />
      <Path d="M120 12 C85 12, 52 30, 38 62 C24 92, 23 128, 28 158 C33 185, 44 205, 58 222 C68 234, 80 243, 92 250 C100 255, 110 258, 120 258 C130 258, 140 255, 148 250 C160 243, 172 234, 182 222 C196 205, 207 185, 212 158 C217 128, 216 92, 202 62 C188 30, 155 12, 120 12 Z" fill="none" stroke={stroke} strokeWidth="2.5" />
      <Path d="M58 222 C65 240, 78 253, 92 258 C100 262, 110 264, 120 264 C130 264, 140 262, 148 258 C162 253, 175 240, 182 222" fill="none" stroke={stroke} strokeWidth="2.2" opacity="0.9" />
      <Path d="M40 185 C45 202, 51 214, 58 222" fill="none" stroke={stroke} strokeWidth="2" opacity="0.8" />
      <Path d="M200 185 C195 202, 189 214, 182 222" fill="none" stroke={stroke} strokeWidth="2" opacity="0.8" />
      <Line x1="120" y1="20" x2="120" y2="260" stroke={stroke} strokeWidth="1"   opacity="0.4" strokeDasharray="4,6" />
      <Line x1="38"  y1="108" x2="202" y2="108" stroke={stroke} strokeWidth="1"   opacity="0.4" strokeDasharray="4,6" />
      <Line x1="42"  y1="172" x2="198" y2="172" stroke={stroke} strokeWidth="1"   opacity="0.4" strokeDasharray="4,6" />
      <Line x1="50"  y1="205" x2="190" y2="205" stroke={stroke} strokeWidth="1"   opacity="0.4" strokeDasharray="4,6" />
      <Line x1="38"  y1="28"  x2="38"  y2="52"  stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
      <Line x1="38"  y1="28"  x2="62"  y2="28"  stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
      <Line x1="202" y1="28"  x2="202" y2="52"  stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
      <Line x1="202" y1="28"  x2="178" y2="28"  stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
      <Line x1="58"  y1="256" x2="34"  y2="256" stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
      <Line x1="38"  y1="256" x2="38"  y2="232" stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
      <Line x1="182" y1="256" x2="206" y2="256" stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
      <Line x1="202" y1="256" x2="202" y2="232" stroke={stroke} strokeWidth="2.5" opacity="0.8" strokeLinecap="round" />
    </Svg>
  );
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// The 4 positions of the guided scan. Each is captured manually: the user taps
// CAPTURE, a 3-2-1 countdown runs, then the photo is taken — so first-timers know
// exactly when the shot happens and have time to hold the pose. `dir` drives the
// on-screen directional arrow (the front camera preview is mirrored, so a "turn
// to your left" arrow points screen-left, matching the mirror). Index 0 is the
// front-facing shot we annotate later.
const SCAN_STEPS: { instruction: string; hint: string; dir: 'front' | 'left' | 'right' | 'down' }[] = [
  { instruction: 'Look straight ahead', hint: 'Center your face in the oval and hold still', dir: 'front' },
  { instruction: 'Turn your head to your left', hint: 'Full profile — hold it there', dir: 'left' },
  { instruction: 'Turn your head to your right', hint: 'Full profile — hold it there', dir: 'right' },
];

// ── Auto-capture head-pose detection ──
// How long the target pose must be held (ms) before the countdown auto-fires —
// long enough to reject a mid-turn blur or a transient, short enough to feel
// responsive. Halved (was 450) as part of the 2×-faster scan.
const HEAD_HOLD_MS = 225;
// MLKit yaw is positive/negative about the vertical axis; pitch about the
// horizontal. Combined with the FRONT camera's mirrored preview, the mapping of
// "your left / your right / chin down" to a sign is empirically finicky (same
// class of issue as the on-screen arrow direction). If left/right trigger
// backwards on-device, flip YAW_LEFT_SIGN; if chin-down never fires, flip
// PITCH_DOWN_SIGN. These two constants are the ONLY things to touch to correct it.
const YAW_LEFT_SIGN = -1; // flipped 2026-07-07: device testing showed "turn left" only fired on a right turn
const PITCH_DOWN_SIGN = 1;
const YAW_TURN_DEG = 22;   // how far the head must rotate to count as a full turn
const FRONT_TOL_DEG = 14;  // how centered "look straight" must be

// Whether the current head angles satisfy the target pose for a guided step.
function poseMet(dir: 'front' | 'left' | 'right' | 'down', yaw: number, pitch: number): boolean {
  switch (dir) {
    case 'front': return Math.abs(yaw) < FRONT_TOL_DEG && Math.abs(pitch) < 20;
    case 'left':  return yaw * YAW_LEFT_SIGN > YAW_TURN_DEG;
    case 'right': return yaw * YAW_LEFT_SIGN < -YAW_TURN_DEG;
    // Deliberately lenient (was 14°): a slight chin dip should trigger it —
    // this angle matters least for scoring, and users weren't being detected.
    case 'down':  return pitch * PITCH_DOWN_SIGN > 6;
  }
}

// Detect the REAL image format from base64 magic bytes. Camera captures are
// always JPEG, but "Import from Photos" can hand us a PNG (screenshots) or
// HEIC-converted formats — declaring those as image/jpeg makes the Claude API
// reject the request ("image appears to be image/png"). Base64 prefixes:
// JPEG "/9j/", PNG "iVBOR", WebP "UklGR", GIF "R0lGOD".
function b64MediaType(b64: string): 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif' {
  if (b64.startsWith('iVBOR')) return 'image/png';
  if (b64.startsWith('UklGR')) return 'image/webp';
  if (b64.startsWith('R0lGOD')) return 'image/gif';
  return 'image/jpeg';
}

// Turns Claude's rejection reason into a specific, actionable coaching line so a
// failed scan teaches the user exactly what to fix instead of a generic retry.
function badScanMessage(issue?: string): string {
  switch (issue) {
    case 'dark': return 'Too dark — face a window or turn on a light, then RESCAN';
    case 'blurry': return 'Came out blurry — wipe your lens, hold still, then RESCAN';
    case 'framing': return 'Center your face and fill the oval, then RESCAN';
    case 'obstructed': return 'Clear your face (hair, glasses, hand) then RESCAN';
    default: return 'No clear face found — get well-lit and centered, then RESCAN';
  }
}

const AnimatedEllipse = Animated.createAnimatedComponent(Ellipse);

// DoorDash/Cash-App-style progress ring that wraps the oval face frame. `progress`
// (0→1) drives a blue stroke that fills clockwise as the 4 positions are captured;
// `flash` pulses a green ring each time a position is locked in.
function GuideRing({ width: w, height: h, progress, flash }: {
  width: number; height: number; progress: Animated.Value; flash: Animated.Value;
}) {
  const sw = 8;
  const cx = w / 2;
  const cy = h / 2;
  const rx = w / 2 - sw;
  const ry = h / 2 - sw;
  // Ramanujan approximation of the ellipse perimeter — used as the dash length.
  const perim = Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
  const dashoffset = progress.interpolate({ inputRange: [0, 1], outputRange: [perim, 0] });
  return (
    <Svg width={w} height={h}>
      <Ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={sw} />
      <AnimatedEllipse
        cx={cx} cy={cy} rx={rx} ry={ry} fill="none"
        stroke={PRIMARY_BLUE} strokeWidth={sw} strokeLinecap="round"
        strokeDasharray={`${perim} ${perim}`} strokeDashoffset={dashoffset}
        // Fill COUNTER-clockwise from the top. The horizontal mirror
        // (translate+scale(-1,1)) reverses SVG's default clockwise stroke
        // direction; the top start-point is on the mirror axis so it stays put.
        // Clockwise fill read to users like a "turn your head right" cue.
        transform={`translate(${2 * cx} 0) scale(-1 1) rotate(-90 ${cx} ${cy})`}
      />
      <AnimatedEllipse
        cx={cx} cy={cy} rx={rx} ry={ry} fill="none"
        stroke="#43e0ff" strokeWidth={sw + 2} opacity={flash}
      />
    </Svg>
  );
}

// Big directional cue shown during a guided scan: an animated chevron that
// slides toward the way the user should turn their head, so the instruction is
// unmistakable. `front` shows a steady "look here" reticle instead of an arrow.
function ScanDirectionCue({ dir }: { dir: 'front' | 'left' | 'right' | 'down' }) {
  const slide = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    slide.setValue(0);
    const loop = Animated.loop(Animated.sequence([
      Animated.timing(slide, { toValue: 1, duration: 650, useNativeDriver: true }),
      Animated.timing(slide, { toValue: 0, duration: 650, useNativeDriver: true }),
    ]));
    loop.start();
    return () => loop.stop();
  }, [dir, slide]);

  if (dir === 'front') {
    const pulse = slide.interpolate({ inputRange: [0, 1], outputRange: [1, 1.12] });
    return (
      <Animated.View style={[styles.scanCueReticle, { transform: [{ scale: pulse }] }]}>
        <Text style={styles.scanCueReticleText}>◎</Text>
      </Animated.View>
    );
  }

  const glyph = dir === 'left' ? '‹' : dir === 'right' ? '›' : '⌄';
  const axis = dir === 'down' ? 'translateY' : 'translateX';
  const range = dir === 'left' ? [-4, -18] : dir === 'right' ? [4, 18] : [4, 18];
  const move = slide.interpolate({ inputRange: [0, 1], outputRange: range });
  return (
    <Animated.View style={[styles.scanCueArrow, { transform: [{ [axis]: move } as any] }]}>
      <Text style={styles.scanCueArrowText}>{glyph}</Text>
    </Animated.View>
  );
}

// Duolingo-style celebratory haptic: a quick escalating triple-buzz capped with
// a success notification — fires a little dopamine hit when a streak advances.
function fireStreakHaptic() {
  const steps: [number, () => Promise<unknown>][] = [
    [0,   () => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light)],
    [60,  () => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium)],
    [130, () => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy)],
    [250, () => Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)],
  ];
  steps.forEach(([delay, fn]) => setTimeout(() => { fn().catch(() => {}); }, delay));
}

function streakMessage(n: number): string {
  if (n >= 30) return "Unstoppable. You're built different.";
  if (n >= 14) return 'Two weeks straight. The grind is real.';
  if (n >= 7) return "A full week! You're locking in.";
  if (n >= 3) return "Momentum's building. Don't break it now.";
  return "Day one of many. Let's go.";
}

// Full-screen Duolingo-style streak celebration with rotating rays, a bouncing
// flame, the streak count, and a haptic burst.
function StreakCelebration({ count, onClose }: { count: number; onClose: () => void }) {
  const fade = useRef(new Animated.Value(0)).current;
  const flameScale = useRef(new Animated.Value(0)).current;
  const numberPop = useRef(new Animated.Value(0)).current;
  const rayRot = useRef(new Animated.Value(0)).current;
  const flare = useRef(new Animated.Value(0)).current;  // burst glow behind the flame

  useEffect(() => {
    fireStreakHaptic();
    playSound('streak');
    Animated.timing(fade, { toValue: 1, duration: 200, useNativeDriver: true }).start();
    // Ignite: flame springs up from nothing with a flare burst behind it.
    Animated.parallel([
      Animated.spring(flameScale, { toValue: 1, friction: 3.2, tension: 80, useNativeDriver: true }),
      Animated.sequence([
        Animated.timing(flare, { toValue: 1, duration: 260, useNativeDriver: true }),
        Animated.timing(flare, { toValue: 0, duration: 520, useNativeDriver: true }),
      ]),
    ]).start();
    Animated.sequence([
      Animated.delay(260),
      Animated.spring(numberPop, { toValue: 1, friction: 3.5, tension: 130, useNativeDriver: true }),
    ]).start();
    // Swirl: rays whip around fast for the first turn, then settle to a slow
    // idle loop — one continuous value so the transform stays reactive.
    Animated.sequence([
      Animated.timing(rayRot, { toValue: 1, duration: 850, useNativeDriver: true }),
      Animated.loop(Animated.timing(rayRot, { toValue: 6, duration: 60000, useNativeDriver: true })),
    ]).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spin = rayRot.interpolate({ inputRange: [0, 1, 6], outputRange: ['0deg', '380deg', '2180deg'] });
  const flareScale = flare.interpolate({ inputRange: [0, 1], outputRange: [0.3, 2.4] });
  const flareOpacity = flare.interpolate({ inputRange: [0, 1], outputRange: [0, 0.5] });

  return (
    <Animated.View style={[styles.streakOverlay, { opacity: fade }]}>
      <Animated.View style={[styles.streakRays, { transform: [{ rotate: spin }] }]}>
        <Svg width={340} height={340} viewBox="0 0 340 340">
          {Array.from({ length: 12 }).map((_, i) => (
            <Line
              key={i}
              x1="170" y1="170" x2="170" y2="6"
              stroke="#FF8A00" strokeWidth="16" opacity={i % 2 === 0 ? 0.18 : 0.1}
              transform={`rotate(${i * 30} 170 170)`} strokeLinecap="round"
            />
          ))}
        </Svg>
      </Animated.View>

      <View style={styles.streakFlameWrap}>
        <Animated.View style={[styles.streakFlare, { transform: [{ scale: flareScale }], opacity: flareOpacity }]} pointerEvents="none" />
        <Animated.View style={{ transform: [{ scale: flameScale }] }}>
          <Ionicons name="flame" size={72} color={FLAME} />
        </Animated.View>
      </View>
      <Animated.Text style={[styles.streakNumber, { transform: [{ scale: numberPop }] }]}>{count}</Animated.Text>
      <Text style={styles.streakDayLabel}>DAY STREAK</Text>
      <Text style={styles.streakSubtext}>{streakMessage(count)}</Text>

      <TouchableOpacity style={styles.streakContinueBtn} onPress={onClose} activeOpacity={0.85}>
        <Text style={styles.streakContinueText}>KEEP IT GOING</Text>
      </TouchableOpacity>
    </Animated.View>
  );
}

// The "pull the lever" moment — a full-screen suspense build, then the overall
// score slams in with a glow + haptic, then the delta vs last scan / new-best /
// first-scan line fades in. Makes every scan feel like an event.
function ScoreRevealOverlay({ score, delta, isFirst, isBest, onClose }: {
  score: number; delta: number; isFirst: boolean; isBest: boolean; onClose: () => void;
}) {
  const fade = useRef(new Animated.Value(0)).current;
  const numScale = useRef(new Animated.Value(0.5)).current;
  const glow = useRef(new Animated.Value(0)).current;
  const ringPulse = useRef(new Animated.Value(0)).current;
  const detail = useRef(new Animated.Value(0)).current;
  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState<'suspense' | 'reveal'>('suspense');
  const color = getScoreColor(score);

  useEffect(() => {
    Animated.timing(fade, { toValue: 1, duration: 260, useNativeDriver: true }).start();
    Haptics.selectionAsync().catch(() => {});
    const pulse = Animated.loop(Animated.sequence([
      Animated.timing(ringPulse, { toValue: 1, duration: 620, useNativeDriver: true }),
      Animated.timing(ringPulse, { toValue: 0, duration: 620, useNativeDriver: true }),
    ]));
    pulse.start();

    const t = setTimeout(() => {
      pulse.stop();
      setPhase('reveal');
      const countAnim = new Animated.Value(0);
      const id = countAnim.addListener(({ value }) => setCount(Math.round(value)));
      countAnim.setValue(0);
      Animated.timing(countAnim, { toValue: score, duration: 950, useNativeDriver: false }).start(() => {
        countAnim.removeListener(id);
        setCount(score);
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
        Animated.parallel([
          Animated.spring(numScale, { toValue: 1, friction: 3.5, tension: 140, useNativeDriver: true }),
          Animated.timing(glow, { toValue: 1, duration: 320, useNativeDriver: true }),
          Animated.timing(detail, { toValue: 1, duration: 420, delay: 120, useNativeDriver: true }),
        ]).start();
      });
      Animated.timing(numScale, { toValue: 0.94, duration: 950, useNativeDriver: true }).start();
    }, 1150);
    return () => { clearTimeout(t); pulse.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pulseScale = ringPulse.interpolate({ inputRange: [0, 1], outputRange: [0.9, 1.12] });
  const pulseOpacity = ringPulse.interpolate({ inputRange: [0, 1], outputRange: [0.25, 0.6] });
  const glowRadius = glow.interpolate({ inputRange: [0, 1], outputRange: [0, 34] });

  return (
    <Animated.View style={[styles.revealOverlay, { opacity: fade }]}>
      {isBest && phase === 'reveal' && <ConfettiBurst />}
      {phase === 'suspense' ? (
        <>
          <Animated.View style={[styles.revealPulseRing, { borderColor: PRIMARY_BLUE, transform: [{ scale: pulseScale }], opacity: pulseOpacity }]} />
          <Text style={styles.revealSuspenseText}>REVEALING YOUR SCORE</Text>
        </>
      ) : (
        <>
          <Text style={styles.revealLabel}>{isFirst ? 'YOUR FIRST SCORE' : 'YOUR OVERALL'}</Text>
          <Animated.Text
            style={[
              styles.revealNumber,
              { color, transform: [{ scale: numScale }], textShadowColor: color, textShadowRadius: glowRadius as any },
            ]}
          >
            {count}
          </Animated.Text>
          <Animated.View style={{ opacity: detail, alignItems: 'center' }}>
            {isFirst ? (
              <Text style={styles.revealDelta}>Your journey starts now</Text>
            ) : isBest ? (
              <Text style={[styles.revealDelta, { color: '#43e0ff' }]}>NEW PERSONAL BEST</Text>
            ) : delta > 0 ? (
              <Text style={[styles.revealDelta, { color: '#43e0ff' }]}>+{delta} since last scan</Text>
            ) : (
              <Text style={[styles.revealDelta, { color: 'rgba(255,255,255,0.7)' }]}>Holding strong — keep building</Text>
            )}
            <TouchableOpacity style={styles.revealBtn} onPress={onClose} activeOpacity={0.85}>
              <Text style={styles.revealBtnText}>SEE FULL RESULTS</Text>
            </TouchableOpacity>
          </Animated.View>
        </>
      )}
    </Animated.View>
  );
}

// Video-game level-up moment — fires when lifetime XP crosses a level bound.
function LevelUpOverlay({ level, onClose }: { level: number; onClose: () => void }) {
  const fade = useRef(new Animated.Value(0)).current;
  const pop = useRef(new Animated.Value(0)).current;
  const ringGlow = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    playSound('level');
    Animated.timing(fade, { toValue: 1, duration: 220, useNativeDriver: true }).start();
    Animated.sequence([
      Animated.delay(120),
      Animated.spring(pop, { toValue: 1, friction: 3.2, tension: 120, useNativeDriver: true }),
    ]).start();
    Animated.loop(
      Animated.sequence([
        Animated.timing(ringGlow, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(ringGlow, { toValue: 0, duration: 900, useNativeDriver: true }),
      ])
    ).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Animated.View style={[styles.levelUpOverlay, { opacity: fade }]}>
      <ConfettiBurst />
      <Text style={styles.levelUpLabel}>LEVEL UP</Text>
      <Animated.View
        style={[
          styles.levelUpBadge,
          {
            transform: [{ scale: pop }],
            shadowOpacity: ringGlow.interpolate({ inputRange: [0, 1], outputRange: [0.5, 1] }),
          },
        ]}
      >
        <Text style={styles.levelUpNumber}>{level}</Text>
      </Animated.View>
      <Text style={styles.levelUpSub}>Every task and guide stacks XP. Keep climbing.</Text>
      <TouchableOpacity style={styles.streakContinueBtn} onPress={onClose} activeOpacity={0.85}>
        <Text style={styles.streakContinueText}>LET&apos;S GO</Text>
      </TouchableOpacity>
    </Animated.View>
  );
}

// Achievement-unlocked celebration — same beat as LevelUpOverlay but themed by
// the badge's own color. `extra` = how many more unlocked in the same moment.
function BadgeUnlockOverlay({ badge, extra, onClose }: { badge: BadgeDef; extra: number; onClose: () => void }) {
  const fade = useRef(new Animated.Value(0)).current;
  const pop = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    playSound('badge');
    Animated.timing(fade, { toValue: 1, duration: 220, useNativeDriver: true }).start();
    Animated.sequence([
      Animated.delay(120),
      Animated.spring(pop, { toValue: 1, friction: 3.2, tension: 120, useNativeDriver: true }),
    ]).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Animated.View style={[styles.levelUpOverlay, { opacity: fade }]}>
      <ConfettiBurst />
      <Text style={[styles.levelUpLabel, { color: badge.color }]}>ACHIEVEMENT UNLOCKED</Text>
      <Animated.View
        style={[
          styles.badgeUnlockRing,
          { borderColor: badge.color, shadowColor: badge.color, transform: [{ scale: pop }] },
        ]}
      >
        <Ionicons name={badge.icon} size={58} color={badge.color} />
      </Animated.View>
      <Text style={styles.badgeUnlockTitle}>{badge.title}</Text>
      <Text style={styles.levelUpSub}>
        {badge.desc}{extra > 0 ? `  ·  +${extra} more unlocked!` : ''}
      </Text>
      <TouchableOpacity style={styles.streakContinueBtn} onPress={onClose} activeOpacity={0.85}>
        <Text style={styles.streakContinueText}>COLLECT</Text>
      </TouchableOpacity>
    </Animated.View>
  );
}

// Full-screen stats moment — powers the Sunday recap and 30/60/90-day journey
// milestones: a title, a few big stat rows, and a continue button.
type StatsMoment = { title: string; sub: string; rows: { icon: keyof typeof Ionicons.glyphMap; label: string; value: string }[]; confetti?: boolean };
function StatsMomentOverlay({ moment, onClose }: { moment: StatsMoment; onClose: () => void }) {
  const fade = useRef(new Animated.Value(0)).current;
  const slide = useRef(new Animated.Value(30)).current;
  useEffect(() => {
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: 260, useNativeDriver: true }),
      Animated.spring(slide, { toValue: 0, friction: 7, tension: 60, useNativeDriver: true }),
    ]).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Animated.View style={[styles.momentOverlay, { opacity: fade }]}>
      {moment.confetti && <ConfettiBurst />}
      <Animated.View style={[styles.momentCard, { transform: [{ translateY: slide }] }]}>
        <Text style={styles.momentTitle}>{moment.title}</Text>
        <Text style={styles.momentSub}>{moment.sub}</Text>
        {moment.rows.map((r, i) => (
          <View key={i} style={styles.momentRow}>
            <Ionicons name={r.icon} size={20} color={ACCENT} style={styles.momentRowIcon} />
            <Text style={styles.momentRowLabel}>{r.label}</Text>
            <Text style={styles.momentRowValue}>{r.value}</Text>
          </View>
        ))}
        <TouchableOpacity style={styles.streakContinueBtn} onPress={onClose} activeOpacity={0.85}>
          <Text style={styles.streakContinueText}>KEEP BUILDING</Text>
        </TouchableOpacity>
      </Animated.View>
    </Animated.View>
  );
}

function shortDate(ts: number): string {
  const d = new Date(ts);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

// Smooth-ish line graph of overall score over the recorded scans.
function ScoreGraph({ history }: { history: ScanRecord[] }) {
  // null = no manual selection yet → default to the most recent scan so a
  // tooltip is always visible. Tapping/dragging picks a specific point.
  const [sel, setSel] = useState<number | null>(null);

  const W = width - 72;
  const H = 196;
  const padL = 28, padR = 12, padT = 30, padB = 26;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const pts = history.map(h => h.overall);
  const maxY = Math.max(100, ...pts);
  const minY = Math.max(0, Math.min(...pts) - 8);
  const span = Math.max(1, maxY - minY);

  const xFor = (i: number) => padL + (history.length <= 1 ? plotW / 2 : (i / (history.length - 1)) * plotW);
  const yFor = (v: number) => padT + plotH - ((v - minY) / span) * plotH;

  const linePath = history.map((h, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(h.overall).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${xFor(history.length - 1).toFixed(1)} ${(padT + plotH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(padT + plotH).toFixed(1)} Z`;

  const gridVals = [0, 0.25, 0.5, 0.75, 1];

  // Touch → nearest data point. dataRef keeps the responder closure fresh
  // without recreating the PanResponder mid-gesture.
  const dataRef = useRef({ history, padL, plotW, last: -1 });
  dataRef.current = { history, padL, plotW, last: dataRef.current.last };
  const pick = (locX: number) => {
    const d = dataRef.current;
    if (d.history.length === 0) return;
    const ratio = (locX - d.padL) / d.plotW;
    const i = Math.max(0, Math.min(d.history.length - 1, Math.round(ratio * (d.history.length - 1))));
    if (i !== d.last) {
      d.last = i;
      Haptics.selectionAsync().catch(() => {});
    }
    setSel(i);
  };
  const responder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (e) => pick(e.nativeEvent.locationX),
      onPanResponderMove: (e) => pick(e.nativeEvent.locationX),
    })
  ).current;

  const active = history.length === 0 ? -1 : (sel === null ? history.length - 1 : sel);

  // Tooltip geometry (clamped to stay inside the chart)
  let tip: { x: number; y: number; tw: number; th: number; ax: number; ay: number; rec: ScanRecord } | null = null;
  if (active >= 0) {
    const rec = history[active];
    const ax = xFor(active), ay = yFor(rec.overall);
    const tw = 78, th = 40;
    const tx = Math.max(padL, Math.min(W - padR - tw, ax - tw / 2));
    const ty = ay - th - 12 < 0 ? ay + 12 : ay - th - 12;
    tip = { x: tx, y: ty, tw, th, ax, ay, rec };
  }

  return (
    <View style={{ width: W, height: H }} {...responder.panHandlers}>
      <Svg width={W} height={H}>
        <Defs>
          <SvgLinearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor={CYAN} stopOpacity="0.32" />
            <Stop offset="1" stopColor={CYAN} stopOpacity="0" />
          </SvgLinearGradient>
        </Defs>

        {gridVals.map((g, i) => {
          const y = padT + plotH - g * plotH;
          const label = Math.round(minY + g * span);
          return (
            <G key={i}>
              <Line x1={padL} y1={y} x2={W - padR} y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <SvgText x={padL - 6} y={y + 3} fontSize="9" fill="rgba(255,255,255,0.35)" textAnchor="end">{label}</SvgText>
            </G>
          );
        })}

        {history.length > 1 && <Path d={areaPath} fill="url(#areaFill)" />}
        {history.length > 1 && <Path d={linePath} fill="none" stroke={CYAN} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}

        {/* Selection guide line */}
        {tip && (
          <Line x1={tip.ax} y1={padT} x2={tip.ax} y2={padT + plotH}
            stroke="rgba(67,224,255,0.45)" strokeWidth="1" strokeDasharray="3 3" />
        )}

        {history.map((h, i) => (
          <G key={i}>
            <Circle cx={xFor(i)} cy={yFor(h.overall)} r={i === active ? 6.5 : 4.5}
              fill={i === active ? CYAN : BG} stroke={CYAN} strokeWidth="2.5" />
            {(i === 0 || i === history.length - 1 || history.length <= 5) && (
              <SvgText x={xFor(i)} y={H - 8} fontSize="9" fill="rgba(255,255,255,0.4)" textAnchor="middle">{shortDate(h.ts)}</SvgText>
            )}
          </G>
        ))}

        {/* Tooltip bubble */}
        {tip && (
          <G>
            <Rect x={tip.x} y={tip.y} width={tip.tw} height={tip.th} rx="9"
              fill={BG} stroke={CYAN} strokeWidth="1.2" />
            <SvgText x={tip.x + tip.tw / 2} y={tip.y + 18} fontSize="16" fontWeight="900"
              fill={CYAN} textAnchor="middle">{tip.rec.overall}</SvgText>
            <SvgText x={tip.x + tip.tw / 2} y={tip.y + 32} fontSize="9"
              fill="rgba(255,255,255,0.55)" textAnchor="middle">{shortDate(tip.rec.ts)}</SvgText>
          </G>
        )}
      </Svg>
    </View>
  );
}

function ResultsTab({ history, isSubscribed, onSubscribe, day1Photo, latestPhoto, canViewBreakdown, onViewBreakdown, subtraits }: {
  history: ScanRecord[];
  isSubscribed: boolean;
  onSubscribe: () => void;
  day1Photo: string | null;
  latestPhoto: string | null;
  canViewBreakdown: boolean;
  onViewBreakdown: () => void;
  subtraits: Record<string, SubTrait[]>;
}) {
  // The graph, journey photos (two full-res base64 decodes), and history list
  // are heavy enough that mounting them in the SAME frame as the tab-switch
  // slide/fade caused a visible freeze right at the switch. Deferring them
  // one tick past the interaction (same trick already used for the results
  // sheet's heavy content) lets the switch animation land smoothly first —
  // the StaggerIn fade-in then makes the short delay read as part of the
  // entrance, not a stall. Declared before the early return below so hook
  // order stays stable regardless of whether there's any history yet.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(() => setReady(true));
    return () => task.cancel();
  }, []);

  if (history.length === 0) {
    return (
      <View style={styles.programLocked}>
        <Ionicons name="stats-chart" size={54} color="rgba(255,255,255,0.35)" style={styles.programLockIcon} />
        <Text style={styles.programLockTitle}>NO SCANS YET</Text>
        <Text style={styles.programLockText}>
          Complete your first scan to start tracking your progress over time.
        </Text>
      </View>
    );
  }

  const latest = history[history.length - 1];
  const first = history[0];
  const best = Math.max(...history.map(h => h.overall));
  const change = latest.overall - first.overall;
  const changeColor = change > 0 ? CYAN : change < 0 ? '#FF6B6B' : TEXT_DIM;
  const changeStr = `${change > 0 ? '+' : ''}${change}`;

  return (
    <ScrollView style={styles.programContainer} contentContainerStyle={{ paddingBottom: 40, paddingTop: 8 }}>
      <StaggerIn index={0}>
        <Text style={styles.resultsTabHeadline}>Your Progress</Text>
        <Text style={styles.resultsTabSub}>{history.length} scan{history.length > 1 ? 's' : ''} tracked</Text>
      </StaggerIn>

      {!ready ? null : (
        <>
      {/* Open the full scored breakdown (the results sheet) directly from here,
          so it isn't reachable only by a swipe-up on the Scan tab. */}
      {canViewBreakdown && (
        <PressScale style={styles.viewBreakdownBtn} onPress={onViewBreakdown} activeOpacity={0.85}>
          <Ionicons name="reader-outline" size={17} color="#00131c" />
          <Text style={styles.viewBreakdownText}>VIEW FULL BREAKDOWN</Text>
        </PressScale>
      )}

      {/* Overall-score trend leads the tab — the first thing the user sees. */}
      <StaggerIn index={1}>
      <View style={styles.graphCard}>
        <View style={styles.graphTitleRow}>
          <Text style={styles.graphTitle}>Overall Score</Text>
          {history.length > 1 && <Text style={styles.graphHint}>tap or drag to inspect</Text>}
        </View>
        <ScoreGraph history={history} />
      </View>
      </StaggerIn>

      <StaggerIn index={2}>
      <View style={styles.statsRow}>
        <View style={[styles.statBox, { borderColor: `${CYAN}50` }]}>
          <Ionicons name="trending-up" size={20} color={CYAN} style={styles.statIcon} />
          <Text style={[styles.statNum, { color: CYAN }]}>{latest.overall}</Text>
          <Text style={[styles.statLabel, { color: CYAN }]}>LATEST</Text>
        </View>
        <View style={[styles.statBox, { borderColor: `${MAGENTA}50` }]}>
          <Ionicons name="trophy" size={20} color={MAGENTA} style={styles.statIcon} />
          <Text style={[styles.statNum, { color: MAGENTA }]}>{best}</Text>
          <Text style={[styles.statLabel, { color: MAGENTA }]}>BEST</Text>
        </View>
        <View style={[styles.statBox, { borderColor: `${changeColor}50` }]}>
          <Ionicons name={change >= 0 ? 'arrow-up' : 'arrow-down'} size={20} color={changeColor} style={styles.statIcon} />
          <Text style={[styles.statNum, { color: changeColor }]}>{changeStr}</Text>
          <Text style={[styles.statLabel, { color: changeColor }]}>SINCE START</Text>
        </View>
      </View>
      </StaggerIn>

      {/* Day-1 vs Today — the emotional payoff of the journey. Shows once a
          second scan exists; before that, a teaser that Day 1 is locked in. */}
      {day1Photo && latestPhoto && history.length > 1 ? (
        <View style={styles.journeyCard}>
          <Text style={styles.journeyTitle}>Your Journey</Text>
          <View style={styles.journeyRow}>
            <View style={styles.journeyCol}>
              <ExpoImage source={{ uri: `data:image/jpeg;base64,${day1Photo}` }} style={styles.journeyPhoto} contentFit="cover" cachePolicy="memory" />
              <Text style={styles.journeyTag}>DAY 1</Text>
              <Text style={styles.journeyScore}>{first.overall}</Text>
            </View>
            <View style={styles.journeyMid}>
              <Ionicons name="arrow-forward" size={22} color="rgba(255,255,255,0.5)" />
              {change > 0 && <Text style={styles.journeyDelta}>+{change}</Text>}
            </View>
            <View style={styles.journeyCol}>
              <ExpoImage source={{ uri: `data:image/jpeg;base64,${latestPhoto}` }} style={styles.journeyPhoto} contentFit="cover" cachePolicy="memory" />
              <Text style={[styles.journeyTag, { color: CYAN }]}>TODAY</Text>
              <Text style={[styles.journeyScore, { color: CYAN }]}>{latest.overall}</Text>
            </View>
          </View>
        </View>
      ) : day1Photo ? (
        <View style={styles.journeyCard}>
          <Text style={styles.journeyTitle}>Your Journey</Text>
          <Text style={styles.journeyTeaser}>Day 1 is locked in. Rescan after a week of work to see your before &amp; after here.</Text>
        </View>
      ) : null}

      {/* CONCERNS & PROTOCOL no longer dumps here as one wall — each concern
          now surfaces as a per-day insight row on Home's program timeline, so
          the scan's findings arrive gradually alongside the work. */}

      <Text style={styles.historyHeader}>Scan History</Text>
      {[...history].reverse().map((h, i) => {
        const realIdx = history.length - 1 - i;
        const prev = realIdx > 0 ? history[realIdx - 1] : null;
        const delta = prev ? h.overall - prev.overall : null;
        return (
          <View key={h.ts} style={styles.historyRow}>
            <View style={styles.historyDateCol}>
              <Text style={styles.historyDate}>{shortDate(h.ts)}</Text>
              {i === 0 && <Text style={styles.historyLatestTag}>LATEST</Text>}
            </View>
            <View style={styles.historyScoreWrap}>
              <Text style={styles.historyScore}>{h.overall}</Text>
              {delta !== null && (
                <Text style={[styles.historyDelta, { color: delta > 0 ? CYAN : delta < 0 ? '#FF6B6B' : TEXT_FAINT }]}>
                  {delta > 0 ? '+' : ''}{delta}
                </Text>
              )}
            </View>
          </View>
        );
      })}

      {!isSubscribed && (
        <PressScale style={styles.resultsUpsell} onPress={onSubscribe} activeOpacity={0.85}>
          <Text style={styles.resultsUpsellTitle}>Unlock Category Trends</Text>
          <Text style={styles.resultsUpsellSub}>Go Pro to track skin, jawline, eyes & more over time</Text>
        </PressScale>
      )}
        </>
      )}
    </ScrollView>
  );
}

// Rotating status lines shown while the scan is being analyzed — makes the
// wait feel like real work is happening (it is: a Claude vision call).
const ANALYZE_STEPS = [
  'Mapping your facial structure…',
  'Measuring proportions & symmetry…',
  'Assessing skin, eyes & jawline…',
  'Scoring across all six categories…',
  'Building your personalized plan…',
];

// Full-screen loading state shown over the scan tab while analysis runs. It
// deliberately does NOT cover the bottom nav bar (which carries a higher
// zIndex), so the user can leave and come back — analysis keeps running in the
// background and the results are waiting when they return.
function ScanAnalyzingOverlay() {
  const spin = useRef(new Animated.Value(0)).current;
  const spin2 = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(0)).current;
  const msgFade = useRef(new Animated.Value(1)).current;
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    const a = Animated.loop(Animated.timing(spin, { toValue: 1, duration: 2600, easing: Easing.linear, useNativeDriver: true }));
    const b = Animated.loop(Animated.timing(spin2, { toValue: 1, duration: 3400, easing: Easing.linear, useNativeDriver: true }));
    const c = Animated.loop(Animated.sequence([
      Animated.timing(pulse, { toValue: 1, duration: 950, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      Animated.timing(pulse, { toValue: 0, duration: 950, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
    ]));
    a.start(); b.start(); c.start();
    // Cross-fade through the status lines every ~2.3s.
    const id = setInterval(() => {
      Animated.timing(msgFade, { toValue: 0, duration: 260, useNativeDriver: true }).start(() => {
        setMsgIdx(i => (i + 1) % ANALYZE_STEPS.length);
        Animated.timing(msgFade, { toValue: 1, duration: 260, useNativeDriver: true }).start();
      });
    }, 2300);
    return () => { a.stop(); b.stop(); c.stop(); clearInterval(id); };
  }, [spin, spin2, pulse, msgFade]);

  const rot = spin.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });
  const rot2 = spin2.interpolate({ inputRange: [0, 1], outputRange: ['360deg', '0deg'] });
  const coreScale = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.9, 1.12] });
  const coreOpacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.55, 1] });

  return (
    <View style={styles.analyzeOverlay} pointerEvents="auto">
      <View style={styles.analyzeRingWrap}>
        {/* Outer arc — spins clockwise */}
        <Animated.View style={{ position: 'absolute', transform: [{ rotate: rot }] }}>
          <Svg width={132} height={132}>
            <Circle cx={66} cy={66} r={58} stroke="rgba(0,191,255,0.14)" strokeWidth={3} fill="none" />
            <Circle
              cx={66} cy={66} r={58} stroke={ACCENT} strokeWidth={3} fill="none" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 58 * 0.28} ${2 * Math.PI * 58 * 0.72}`}
            />
          </Svg>
        </Animated.View>
        {/* Inner arc — spins counter-clockwise, purple */}
        <Animated.View style={{ position: 'absolute', transform: [{ rotate: rot2 }] }}>
          <Svg width={98} height={98}>
            <Circle
              cx={49} cy={49} r={40} stroke="#ff4dd2" strokeWidth={2.5} fill="none" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 40 * 0.18} ${2 * Math.PI * 40 * 0.82}`}
            />
          </Svg>
        </Animated.View>
        {/* Pulsing core */}
        <Animated.View style={[styles.analyzeCore, { transform: [{ scale: coreScale }], opacity: coreOpacity }]}>
          <Ionicons name="scan" size={30} color={ACCENT} />
        </Animated.View>
      </View>

      <Text style={styles.analyzeTitle}>ANALYZING YOUR SCAN</Text>
      <Animated.Text style={[styles.analyzeStatus, { opacity: msgFade }]}>{ANALYZE_STEPS[msgIdx]}</Animated.Text>
      <Text style={styles.analyzeLeaveHint}>This takes a few seconds. You can keep using the app — your results will be ready here.</Text>
    </View>
  );
}

// Four flat tabs + Scan as a raised center button — the one "do the thing"
// action gets FAB treatment instead of competing as an equal tab. (Program is
// no longer a tab; the plan lives on Home.)
function NavBar({ tab, setTab }: { tab: string; setTab: (t: string) => void }) {
  // PULSE: outline glyph when inactive (faint), filled cyan when active. Center
  // scan is a 56px cyan→magenta gradient FAB with a magenta glow.
  const fabId = useGradId('scanfab');
  // Sliding active indicator: a small cyan bar at the top edge of the bar that
  // glides to the active item's center. Item centers are captured via onLayout;
  // the indicator hides while Scan is active (the FAB has its own glow cue).
  const itemCenters = useRef<Record<string, number>>({});
  const indX = useRef(new Animated.Value(-100)).current;
  const indOpacity = useRef(new Animated.Value(0)).current;
  const indPlaced = useRef(false);
  const moveIndicator = (key: string) => {
    const cx = itemCenters.current[key];
    if (key === 'scan' || cx == null) {
      Animated.timing(indOpacity, { toValue: 0, duration: 140, useNativeDriver: true }).start();
      return;
    }
    const x = cx - 8; // bar is 16px wide
    if (!indPlaced.current) {
      indPlaced.current = true;
      indX.setValue(x);
    } else {
      Animated.spring(indX, { toValue: x, speed: 20, bounciness: 6, useNativeDriver: true }).start();
    }
    Animated.timing(indOpacity, { toValue: 1, duration: 140, useNativeDriver: true }).start();
  };
  useEffect(() => { moveIndicator(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const items: { key: string; icon: keyof typeof Ionicons.glyphMap; iconActive: keyof typeof Ionicons.glyphMap; label: string }[] = [
    { key: 'home', icon: 'home-outline', iconActive: 'home', label: 'HOME' },
    { key: 'results', icon: 'stats-chart-outline', iconActive: 'stats-chart', label: 'RESULTS' },
    { key: 'scan', icon: 'scan', iconActive: 'scan', label: 'Scan' }, // rendered as the center FAB
    { key: 'guides', icon: 'book-outline', iconActive: 'book', label: 'GUIDES' },
    { key: 'profile', icon: 'person-outline', iconActive: 'person', label: 'PROFILE' },
  ];
  return (
    <View style={styles.navBar}>
      <Animated.View
        pointerEvents="none"
        style={[styles.navIndicator, { opacity: indOpacity, transform: [{ translateX: indX }] }]}
      />
      {items.map((item) => item.key === 'scan' ? (
        <TouchableOpacity
          key="scan"
          style={[styles.navScanFab, tab === 'scan' && styles.navScanFabActive]}
          onPress={() => {
            if (tab !== 'scan') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
            setTab('scan');
          }}
          activeOpacity={0.85}
        >
          <Svg width={56} height={56} style={StyleSheet.absoluteFill}>
            <Defs>
              <SvgLinearGradient id={fabId} x1="0" y1="0" x2="1" y2="1">
                <Stop offset="0" stopColor={GRAD_FROM} />
                <Stop offset="1" stopColor={GRAD_TO} />
              </SvgLinearGradient>
            </Defs>
            <Circle cx={28} cy={28} r={28} fill={`url(#${fabId})`} />
          </Svg>
          <Ionicons name="scan" size={24} color={BG} />
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          key={item.key}
          style={styles.navItem}
          onLayout={(e) => {
            itemCenters.current[item.key] = e.nativeEvent.layout.x + e.nativeEvent.layout.width / 2;
            if (item.key === tab) moveIndicator(item.key);
          }}
          onPress={() => {
            if (tab !== item.key) Haptics.selectionAsync().catch(() => {});
            setTab(item.key);
          }}
        >
          <Ionicons
            name={tab === item.key ? item.iconActive : item.icon}
            size={21}
            color={tab === item.key ? CYAN : TEXT_FAINT}
          />
          <Text style={[styles.navLabel, tab === item.key && styles.navLabelActive]}>{item.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

// Press-in scale: tappable cards/CTAs dip to ~0.97 while pressed and spring
// back on release, instead of only dimming. Drop-in TouchableOpacity swap for
// the high-touch surfaces (task cards, tiles, CTAs, settings rows).
const AnimatedTouchable = Animated.createAnimatedComponent(TouchableOpacity);
function PressScale({ onPress, onLongPress, style, children, disabled, hitSlop, scaleTo = 0.97, activeOpacity = 0.9 }: {
  onPress?: () => void;
  onLongPress?: () => void;
  style?: ComponentProps<typeof TouchableOpacity>['style'];
  children?: ReactNode;
  disabled?: boolean;
  hitSlop?: ComponentProps<typeof TouchableOpacity>['hitSlop'];
  scaleTo?: number;
  activeOpacity?: number;
}) {
  const scale = useRef(new Animated.Value(1)).current;
  // The transform goes on the touchable itself (not a wrapper view) so layout
  // styles like `width: '48%'` or `flex: 1` keep resolving against the same
  // parent they did as a plain TouchableOpacity.
  return (
    <AnimatedTouchable
      onPress={onPress}
      onLongPress={onLongPress}
      disabled={disabled}
      hitSlop={hitSlop}
      activeOpacity={activeOpacity}
      style={[style, { transform: [{ scale }] }]}
      onPressIn={() => Animated.spring(scale, { toValue: scaleTo, speed: 40, bounciness: 0, useNativeDriver: true }).start()}
      onPressOut={() => Animated.spring(scale, { toValue: 1, speed: 40, bounciness: 5, useNativeDriver: true }).start()}
    >
      {children}
    </AnimatedTouchable>
  );
}

// Staggered entrance: on mount, fades in and rises 8px after `index * 40ms`.
// Wrapped around the first few top-level sections of each tab so opening a tab
// reads as choreography (composes with the tab slide — container slides,
// sections cascade inside it). Tabs remount on every visit, so it replays.
function StaggerIn({ index, children, style }: { index: number; children?: ReactNode; style?: ComponentProps<typeof View>['style'] }) {
  const fade = useRef(new Animated.Value(0)).current;
  const rise = useRef(new Animated.Value(8)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: 260, delay: index * 40, easing: Easing.out(Easing.quad), useNativeDriver: true }),
      Animated.timing(rise, { toValue: 0, duration: 260, delay: index * 40, easing: Easing.out(Easing.quad), useNativeDriver: true }),
    ]).start();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Animated.View style={[style, { opacity: fade, transform: [{ translateY: rise }] }]}>
      {children}
    </Animated.View>
  );
}

// One consistent back affordance for every drill-in view (guide reader, week
// detail, weeks list, category detail) — a chevron + label, same size/placement
// everywhere. Replaces the four different ad-hoc "← Back"/"‹ X" links.
function BackHeader({ label, onPress, color = 'rgba(255,255,255,0.7)' }: { label: string; onPress: () => void; color?: string }) {
  return (
    <TouchableOpacity style={styles.backHeader} onPress={onPress} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
      <Ionicons name="chevron-back" size={20} color={color} />
      <Text style={[styles.backHeaderText, { color }]}>{label}</Text>
    </TouchableOpacity>
  );
}

// Returns which week (0-based) and day within that week (1-7) the user is on
// based purely on calendar days elapsed since the program was generated.
function programCurrentDay(generatedAt: number): { weekIdx: number; dayInWeek: number } {
  const daysSince = Math.max(0, Math.floor((Date.now() - generatedAt) / 86400000));
  return {
    weekIdx: Math.min(Math.floor(daysSince / 7), 7),
    dayInWeek: (daysSince % 7) + 1,
  };
}

// (HomeTaskCard + its particle constants were removed with the standalone
// "Today's Tasks" list — daily tasks now live only inside the current week of
// YOUR PROGRAM, so there's a single place to check them off.)

// ─────────────────────────────────────────────────────────────────────────
//  DAY TIMELINE — Home's core surface
//  The program is no longer behind a "Foundation Week" bubble: the selected
//  day's tasks render directly on Home as a vertical timeline. Each task is a
//  card; the fill-in completion circle sits OUTSIDE the card on a rail, joined
//  by a connecting line, so the day reads as one continuous path.
// ─────────────────────────────────────────────────────────────────────────

// Per-category identity. All six hues stay inside the brand's cool
// cyan→purple→magenta family so the timeline reads as one system rather than a
// bag of colors.
const CATEGORY_STYLE: Record<string, { color: string }> = {
  skin:        { color: '#43e0ff' },
  jawline:     { color: '#9a6bff' },
  eyes:        { color: '#ff4dd2' },
  cheekbones:  { color: '#7ab8ff' },
  masculinity: { color: '#c77dff' },
  hair:        { color: '#4dffd2' },
};
const catStyle = (c: string) => CATEGORY_STYLE[c] ?? { color: ACCENT };

// Custom line-art glyphs, one per category — drawn on a shared 24×24 grid at a
// matched stroke weight so the six read as one set. Ionicons had nothing that
// actually depicts facial anatomy (jawline was a literal triangle), so these
// are purpose-built: an anatomical feature per category, not a generic symbol.
// `dim` paths are structural guides (proportion lines, head sides) drawn at
// lower opacity so the subject of each glyph stays dominant.
const CATEGORY_GLYPH: Record<string, { d: string[]; dim?: string[]; dot?: [number, number, number][] }> = {
  // Droplet + inner shine — hydration/care.
  skin: {
    d: [
      'M12 4.2C12 4.2 6.8 10.4 6.8 14a5.2 5.2 0 0 0 10.4 0c0-3.6-5.2-9.8-5.2-9.8z',
      'M9.7 14.5a2.6 2.6 0 0 0 2.5 2.5',
    ],
  },
  // Side-profile jaw sweep from temple to chin, with the ear behind it.
  jawline: {
    d: ['M16.8 5.5c.6 4 .4 7.7-1.6 10.7s-5.6 4.2-8.8 3.7'],
    dim: ['M13.9 8.3a1.9 1.9 0 1 0 .2 3.4'],
  },
  // Almond eye + iris, brow arc above.
  eyes: {
    d: [
      'M3.4 13.4c3.2-4.2 14-4.2 17.2 0-3.2 4.2-14 4.2-17.2 0z',
      'M5.6 7.4c3-2.4 9.8-2.4 12.8 0',
    ],
    dot: [[12, 13.4, 2.5]],
  },
  // Face outline with the zygomatic contour called out across it.
  cheekbones: {
    d: ['M6.6 10.4c2.8 2.2 8 2.2 10.8 0'],
    dim: ['M12 3.4c4.4 0 6.8 3 6.8 7.2 0 5-3.2 9.8-6.8 9.8s-6.8-4.8-6.8-9.8c0-4.2 2.4-7.2 6.8-7.2z'],
  },
  // Front skull frame with a squared jaw + proportion guides — bone structure.
  masculinity: {
    d: ['M5.6 7.2c0-2.6 2.6-4 6.4-4s6.4 1.4 6.4 4v5c0 4.4-3 8.4-6.4 8.4s-6.4-4-6.4-8.4z'],
    dim: ['M12 4.4v15', 'M6.1 9.6h11.8', 'M6.4 14.4h11.2'],
  },
  // Hair cap sitting on the head, with a swept part.
  hair: {
    d: [
      'M5.2 12.6C5.2 7 8.2 3.6 12 3.6s6.8 3.4 6.8 9c-1-3.2-3.4-5-6.8-5s-5.8 1.8-6.8 5z',
      'M13.6 4.1c-1.6 1.2-3 2.4-3.8 3.6',
    ],
    dim: ['M6.2 11.6c0 4.8 2.4 8.6 5.8 8.6s5.8-3.8 5.8-8.6'],
  },
};

// Lottie is loaded lazily (same guard as react-native-purchases) so a build
// without the native module degrades to the vector icon instead of crashing.
let LottieView: any = null;
try {
  const mod = require('lottie-react-native');
  LottieView = mod?.default ?? mod;
} catch {
  LottieView = null;
}

// ⚠️ DROP LOTTIE FILES HERE.
// Put a .json per category in assets/lottie/ and uncomment its line below.
// Any category without a file keeps the vector icon — the card layout is
// pixel-identical either way, so these can land one at a time.
const CATEGORY_LOTTIE: Partial<Record<string, unknown>> = {
  // skin:        require('@/assets/lottie/skin.json'),
  // jawline:     require('@/assets/lottie/jawline.json'),
  // eyes:        require('@/assets/lottie/eyes.json'),
  // cheekbones:  require('@/assets/lottie/cheekbones.json'),
  // masculinity: require('@/assets/lottie/masculinity.json'),
  // hair:        require('@/assets/lottie/hair.json'),
};

// Category art: sits still at its first frame, then plays once as the reward
// the moment its task is checked off (looping five of these on a scrolling
// list is a real battery/jank cost for no benefit).
function CategoryArt({ category, done, size = 38 }: { category: string; done: boolean; size?: number }) {
  const { color } = catStyle(category);
  const src = CATEGORY_LOTTIE[category];
  const ref = useRef<any>(null);
  const wasDone = useRef(done);

  useEffect(() => {
    if (done && !wasDone.current) ref.current?.play?.();
    wasDone.current = done;
  }, [done]);

  return (
    <View style={[styles.catArt, { width: size, height: size, borderRadius: size / 2, backgroundColor: `${color}1f`, borderColor: `${color}55` }]}>
      {src && LottieView ? (
        <LottieView
          ref={ref}
          source={src}
          autoPlay={false}
          loop={false}
          progress={done ? 1 : 0}
          style={{ width: size * 0.7, height: size * 0.7 }}
        />
      ) : (
        <CategoryGlyph category={category} size={size * 0.62} color={color} />
      )}
    </View>
  );
}

// Renders one category glyph from CATEGORY_GLYPH. Stroke width is scaled off
// the render size so the line weight stays optically even at any size.
function CategoryGlyph({ category, size, color }: { category: string; size: number; color: string }) {
  const g = CATEGORY_GLYPH[category];
  if (!g) return <Ionicons name="ellipse-outline" size={size} color={color} />;
  const sw = Math.max(1.25, (size / 24) * 1.6);
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <G fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {g.dim?.map((d, i) => <Path key={`m${i}`} d={d} opacity={0.4} />)}
        {g.d.map((d, i) => <Path key={`d${i}`} d={d} />)}
        {g.dot?.map(([cx, cy, r], i) => <Circle key={`c${i}`} cx={cx} cy={cy} r={r} />)}
      </G>
    </Svg>
  );
}

// Task-specific instructional illustration (see TASK_ILLUSTRATION), used in
// the task detail sheet. Falls back to the task's category glyph until every
// task in TASK_LIBRARY has its own drawn illustration.
function TaskIllustration({ templateId, category, size, color }: { templateId: string; category: string; size: number; color: string }) {
  const g = TASK_ILLUSTRATION[templateId];
  if (!g) return <CategoryGlyph category={category} size={size} color={color} />;
  const sw = Math.max(1.25, (size / 24) * 1.6);
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <G fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {g.dim?.map((d, i) => <Path key={`m${i}`} d={d} opacity={0.4} />)}
        {g.d.map((d, i) => <Path key={`d${i}`} d={d} />)}
        {g.dot?.map(([cx, cy, r], i) => <Circle key={`c${i}`} cx={cx} cy={cy} r={r} />)}
      </G>
    </Svg>
  );
}

// One task on the timeline: rail circle (tap to complete) + card (tap to
// open the how-to detail sheet — see TaskDetailReader).
function TimelineTask({ task, done, locked, onToggle, onOpenDetail, isLast }: {
  task: ProgramTask;
  done: boolean;
  locked: boolean;
  onToggle: () => void;
  onOpenDetail: () => void;
  isLast: boolean;
}) {
  const { color } = catStyle(task.category);
  return (
    <View style={[styles.tlRow, locked && { opacity: 0.4 }]}>
      <View style={styles.tlRail}>
        {!isLast && <View style={styles.tlLine} />}
        <TouchableOpacity
          disabled={locked}
          onPress={onToggle}
          activeOpacity={0.7}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={[styles.tlCircle, done && { backgroundColor: color, borderColor: color }]}
        >
          {done && <Ionicons name="checkmark" size={15} color={BG} />}
        </TouchableOpacity>
      </View>

      <PressScale
        disabled={locked}
        onPress={onOpenDetail}
        activeOpacity={0.75}
        style={[styles.tlCard, done && { borderColor: `${color}4d`, backgroundColor: `${color}10` }]}
      >
        <CategoryArt category={task.category} done={done} />
        <View style={styles.tlCardText}>
          <Text style={[styles.tlTitle, done && { color: TEXT_DIM, textDecorationLine: 'line-through' }]} numberOfLines={2}>
            {task.label}
          </Text>
          {task.desc ? (
            <Text style={[styles.tlDesc, done && { opacity: 0.45 }]} numberOfLines={3}>{task.desc}</Text>
          ) : null}
        </View>
        <Text style={[styles.tlXp, done && { color }]}>+{task.xp ?? 20}</Text>
      </PressScale>
    </View>
  );
}

// The day's insight — one Concern & its Protocol, folded into the timeline as
// a READ row rather than a checkbox, so the scan's findings arrive a day at a
// time instead of as one overwhelming wall on Results.
function InsightRow({ cat, trait }: { cat: { key: string; label: string }; trait: SubTrait }) {
  const [open, setOpen] = useState(false);
  const c = tierColor(trait.tier);
  return (
    <View style={styles.tlRow}>
      <View style={styles.tlRail}>
        <View style={[styles.tlInsightNode, { borderColor: c, backgroundColor: `${c}22` }]}>
          <Ionicons name="bulb" size={12} color={c} />
        </View>
      </View>
      <PressScale
        style={[styles.tlInsightCard, { borderColor: `${c}40` }]}
        onPress={() => { setOpen(v => !v); Haptics.selectionAsync().catch(() => {}); }}
        activeOpacity={0.8}
      >
        <View style={styles.tlInsightHead}>
          <Text style={[styles.tlInsightKicker, { color: c }]}>TODAY&apos;S INSIGHT  ·  {cat.label}</Text>
          <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={15} color={c} />
        </View>
        <Text style={styles.tlInsightTitle}>{trait.name}</Text>
        <Text style={styles.tlInsightMeta}>
          {tierLabel(trait.tier)}{typeof trait.percentile === 'number' ? `  ·  ${ordinal(trait.percentile)} percentile` : ''}
        </Text>
        {open && (
          <>
            {trait.why ? <Text style={styles.tlInsightWhy}>{trait.why}</Text> : null}
            {trait.protocol ? (
              <View style={styles.protocolBox}>
                <Text style={styles.protocolFix}>{trait.protocol.fix}</Text>
                <View style={styles.protocolTagRow}>
                  <View style={[styles.protocolTag, { flexDirection: 'row', alignItems: 'center', gap: 5 }]}>
                    <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: difficultyColor(trait.protocol.difficulty) }} />
                    <Text style={styles.protocolTagText}>{trait.protocol.difficulty}</Text>
                  </View>
                  {trait.protocol.timeline ? (
                    <View style={[styles.protocolTag, { flexDirection: 'row', alignItems: 'center', gap: 5 }]}>
                      <Ionicons name="time-outline" size={12} color={TEXT_DIM} />
                      <Text style={styles.protocolTagText}>{trait.protocol.timeline}</Text>
                    </View>
                  ) : null}
                  {trait.protocol.cost ? (
                    <View style={[styles.protocolTag, { flexDirection: 'row', alignItems: 'center', gap: 5 }]}>
                      <Ionicons name="cash-outline" size={12} color={TEXT_DIM} />
                      <Text style={styles.protocolTagText}>{trait.protocol.cost}</Text>
                    </View>
                  ) : null}
                </View>
              </View>
            ) : null}
          </>
        )}
      </PressScale>
    </View>
  );
}

// Horizontal week-of-real-dates strip. Tapping a past day loads that day's
// tasks below so a missed day can still be caught up; future days are locked.
function CalendarStrip({ program, completed, selected, onSelect }: {
  program: Program;
  completed: Record<string, boolean>;
  selected: number;      // absolute day index since the program started
  onSelect: (absDay: number) => void;
}) {
  const { weekIdx, dayInWeek } = programCurrentDay(program.generatedAt);
  const todayAbs = weekIdx * 7 + (dayInWeek - 1);

  // Show the calendar week that contains the selected day, Monday-first, so it
  // lines up with a real calendar rather than an arbitrary 7-day slice.
  const today = new Date();
  const selOffset = selected - todayAbs;
  const selDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + selOffset);
  const mondayShift = (selDate.getDay() + 6) % 7;
  const weekStart = new Date(selDate.getFullYear(), selDate.getMonth(), selDate.getDate() - mondayShift);

  const dayTasksFor = (abs: number) => {
    const w = Math.floor(abs / 7);
    const d = (abs % 7) + 1;
    if (w < 0 || w >= program.weeks.length) return [];
    return program.weeks[w].tasks.filter(t => t.day === d);
  };

  return (
    <View style={styles.calStrip}>
      {Array.from({ length: 7 }, (_, i) => {
        const date = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i);
        const abs = todayAbs + Math.round((date.getTime() - new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()) / 86400000);
        const isToday = abs === todayAbs;
        const isSelected = abs === selected;
        const isFuture = abs > todayAbs;
        const inProgram = abs >= 0 && abs < program.weeks.length * 7;
        const dTasks = inProgram ? dayTasksFor(abs) : [];
        const allDone = dTasks.length > 0 && dTasks.every(t => completed[t.instanceId]);
        const someDone = dTasks.some(t => completed[t.instanceId]);

        return (
          <TouchableOpacity
            key={i}
            disabled={isFuture || !inProgram}
            onPress={() => { onSelect(abs); Haptics.selectionAsync().catch(() => {}); }}
            activeOpacity={0.7}
            style={styles.calDay}
          >
            <Text style={[styles.calDow, isSelected && { color: TEXT }]}>
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
            </Text>
            <View style={[
              styles.calNumWrap,
              isSelected && { backgroundColor: ACCENT },
              !isSelected && isToday && { borderColor: ACCENT, borderWidth: 1 },
            ]}>
              <Text style={[
                styles.calNum,
                isSelected && { color: BG },
                !isSelected && isFuture && { color: TEXT_FAINT },
                !isSelected && !inProgram && { color: TEXT_FAINT },
              ]}>
                {date.getDate()}
              </Text>
            </View>
            <View style={[
              styles.calDot,
              allDone && { backgroundColor: ACCENT },
              !allDone && someDone && { backgroundColor: `${ACCENT}70` },
            ]} />
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// HOME — the landing tab (Daily Streak concept, now "Home absorbs plan").
// Greets the user with the streak flame, today's progress ring, and the day's
// tasks, then the full 8-week plan (embedded ProgramTab) on the same scroll.
// The scan lives in the nav bar's center button — the camera never greets you.
function HomeTab({ userName, streakDays, overall, potential, tasks, completed, toggleTask, program, isSubscribed, accent, onScan, onUpgrade, onNewProgram, recommendations, subtraits, freezeAvailable, freezeNotice, onDismissFreezeNotice, rescans }: {
  userName: string;
  streakDays: number;
  overall: number | null;
  potential: number | null;
  tasks: ProgramTask[];
  completed: Record<string, boolean>;
  toggleTask: (instanceId: string) => void;
  program: Program | null;
  isSubscribed: boolean;
  accent: string;
  onScan: () => void;
  onUpgrade: () => void;
  onNewProgram: () => void;
  recommendations: Recommendation[];
  subtraits: Record<string, SubTrait[]>;
  freezeAvailable: boolean;
  freezeNotice: number | null;
  onDismissFreezeNotice: () => void;
  rescans: number;
}) {
  const hasProgram = !!program;
  const done = tasks.filter(t => completed[t.instanceId]).length;
  const total = tasks.length;
  const allDone = total > 0 && done === total;
  const showTasks = hasProgram && isSubscribed;

  // ── Inline program state ──
  // Which day the timeline is showing, as an absolute index since the program
  // began. Defaults to today; tapping a past date on the calendar strip lets a
  // missed day be caught up. `weeksOpen` swaps in the week browser.
  const todayAbs = program
    ? (() => { const { weekIdx, dayInWeek } = programCurrentDay(program.generatedAt); return weekIdx * 7 + (dayInWeek - 1); })()
    : 0;
  const [selectedDay, setSelectedDay] = useState(todayAbs);
  const [weeksOpen, setWeeksOpen] = useState(false);
  // Keep the selection pinned to today as real days roll over (the app can sit
  // open past midnight), but never yank the user off a day they chose.
  const lastTodayAbs = useRef(todayAbs);
  useEffect(() => {
    if (todayAbs !== lastTodayAbs.current) {
      if (selectedDay === lastTodayAbs.current) setSelectedDay(todayAbs);
      lastTodayAbs.current = todayAbs;
    }
  }, [todayAbs, selectedDay]);
  // Which task's how-to detail sheet is open, if any (see TaskDetailReader).
  const [activeDetailTask, setActiveDetailTask] = useState<ProgramTask | null>(null);

  const selWeekIdx = Math.floor(selectedDay / 7);
  const selWeek = program && selWeekIdx >= 0 && selWeekIdx < program.weeks.length ? program.weeks[selWeekIdx] : null;
  const selDayTasks = selWeek ? selWeek.tasks.filter(t => t.day === (selectedDay % 7) + 1) : [];
  const selDone = selDayTasks.filter(t => completed[t.instanceId]).length;
  const selIsFuture = selectedDay > todayAbs;
  const isSelToday = selectedDay === todayAbs;

  // One concern per day, cycled by absolute day so the scan's findings arrive
  // gradually instead of as one wall of text.
  const dayInsight = (() => {
    const list: { cat: typeof CATEGORIES[number]; trait: SubTrait }[] = [];
    for (const cat of CATEGORIES) {
      for (const t of (subtraits[cat.key] || [])) {
        if (isConcern(t.tier)) list.push({ cat, trait: t });
      }
    }
    if (list.length === 0) return null;
    list.sort((a, b) => (a.trait.tier === 'priority' ? 0 : 1) - (b.trait.tier === 'priority' ? 0 : 1));
    return list[selectedDay % list.length];
  })();

  // Gentle flame pulse — alive, but not distracting.
  const flamePulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(flamePulse, { toValue: 1.09, duration: 900, useNativeDriver: true }),
        Animated.timing(flamePulse, { toValue: 1, duration: 900, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [flamePulse]);

  // Micro-win: the progress ring does a little celebratory bounce the moment
  // the last task of the day is checked off.
  const ringPulse = useRef(new Animated.Value(1)).current;
  const wasAllDone = useRef(allDone);
  useEffect(() => {
    if (allDone && !wasAllDone.current) {
      ringPulse.setValue(0.88);
      Animated.spring(ringPulse, { toValue: 1, friction: 3, tension: 140, useNativeDriver: true }).start();
    }
    wasAllDone.current = allDone;
  }, [allDone, ringPulse]);

  if (activeDetailTask) {
    return (
      <TaskDetailReader
        task={activeDetailTask}
        done={!!completed[activeDetailTask.instanceId]}
        onToggle={() => toggleTask(activeDetailTask.instanceId)}
        onBack={() => setActiveDetailTask(null)}
      />
    );
  }

  return (
    <ScrollView style={styles.homeContainer} contentContainerStyle={{ paddingBottom: 40 }} showsVerticalScrollIndicator={false}>
      {/* ── Streak-saved banner — shows once after a freeze absorbs a miss ── */}
      {freezeNotice !== null && (
        <TouchableOpacity style={styles.homeFreezeBanner} onPress={onDismissFreezeNotice} activeOpacity={0.8}>
          <Ionicons name="snow-outline" size={22} color="#9BDCFF" />
          <View style={{ flex: 1 }}>
            <Text style={styles.homeFreezeTitle}>Streak freeze used!</Text>
            <Text style={styles.homeFreezeSub}>
              You missed a day, but your {freezeNotice}-day streak survived. Tap to dismiss.
            </Text>
          </View>
        </TouchableOpacity>
      )}

      {/* ── Compact score row. Shrunk from the old full-height hero so the
          day's tasks start near the fold — the score stays glanceable, but
          the program is what Home is actually for. ── */}
      <StaggerIn index={0}>
      <View style={styles.miniHero}>
        {overall != null ? (
          <RingGauge size={62} stroke={4} progress={overall / 100} gradient glowColor={CYAN}>
            <Text style={styles.miniHeroNum}>{overall}</Text>
          </RingGauge>
        ) : (
          <TouchableOpacity onPress={onScan} style={styles.miniScanRing} activeOpacity={0.8}>
            <Ionicons name="scan" size={22} color={CYAN} />
          </TouchableOpacity>
        )}
        <View style={{ flex: 1 }}>
          <Text style={styles.microLabel}>OVERALL  ·  POTENTIAL {potential != null ? potential : '--'}</Text>
          <Text style={styles.miniHeroCaption}>
            {overall != null && potential != null
              ? `+${Math.max(0, potential - overall)} points within reach`
              : 'Scan your face to reveal your score'}
          </Text>
        </View>
        <View style={styles.miniStreak}>
          <Ionicons name={streakDays > 0 ? 'flame' : 'flame-outline'} size={15} color={streakDays > 0 ? MAGENTA : TEXT_FAINT} />
          <Text style={[styles.miniStreakNum, streakDays > 0 && { color: MAGENTA }]}>{streakDays}</Text>
          {streakDays > 0 && freezeAvailable && (
            <Ionicons name="snow-outline" size={11} color="#9BDCFF" />
          )}
        </View>
      </View>
      </StaggerIn>

      {/* ── Not-yet-Pro / no-plan CTAs. Today's tasks are no longer listed
          separately here — they live inside the current week of YOUR PROGRAM
          below, so there's only one place to check them off. ── */}
      {!showTasks && (hasProgram ? (
        <TouchableOpacity style={styles.homeInfoCard} onPress={onUpgrade} activeOpacity={0.8}>
          <Ionicons name="lock-closed" size={22} color={TEXT_DIM} style={styles.homeInfoIcon} />
          <View style={{ flex: 1 }}>
            <Text style={styles.homeInfoTitle}>Daily tasks are Pro</Text>
            <Text style={styles.homeInfoSub}>Unlock your personalized program with daily habits, XP, and streaks</Text>
          </View>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity style={styles.homeInfoCard} onPress={onScan} activeOpacity={0.8}>
          <Ionicons name="scan-outline" size={24} color={ACCENT} style={styles.homeInfoIcon} />
          <View style={{ flex: 1 }}>
            <Text style={styles.homeInfoTitle}>No plan yet</Text>
            <Text style={styles.homeInfoSub}>Scan your face and we&apos;ll build a personalized program around it</Text>
          </View>
        </TouchableOpacity>
      ))}

      {/* ── First-scan CTA — once a program exists, the nav bar's center scan
          button is the entry point and this big CTA disappears ── */}
      {!hasProgram && (
        <PressScale style={styles.homeScanBtn} onPress={onScan} activeOpacity={0.85}>
          <Text style={styles.homeScanBtnText}>START YOUR FIRST SCAN</Text>
        </PressScale>
      )}

      {/* ── YOUR PROGRAM — no longer behind a week bubble. The selected day's
          tasks render inline as a timeline; "All weeks" swaps in the browser
          (which still owns week detail, skip + rescan gating). ── */}
      {showTasks && program && (weeksOpen ? (
        <>
          <BackHeader label="Back to today" onPress={() => setWeeksOpen(false)} />
          <ProgramTab
            program={program}
            completed={completed}
            toggleTask={toggleTask}
            onNewProgram={onNewProgram}
            subtraits={subtraits}
            rescans={rescans}
          />
        </>
      ) : (
        <>
          <StaggerIn index={1}>
            <CalendarStrip
              program={program}
              completed={completed}
              selected={selectedDay}
              onSelect={setSelectedDay}
            />
          </StaggerIn>

          {/* Program header — "YOUR PROGRAM" leads (serif, prominent); the
              week name is demoted to a quiet subline instead of a whole card. */}
          <StaggerIn index={2}>
          <View style={styles.dayHeadRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.dayHeadTitle}>Your Program</Text>
              <Text style={styles.dayHeadSub}>
                {selWeek ? `Week ${selWeek.week} · ${selWeek.name}` : 'Week —'}
                {isSelToday ? '  ·  Today' : ''}
              </Text>
            </View>
            <TouchableOpacity onPress={() => setWeeksOpen(true)} style={styles.allWeeksBtn} activeOpacity={0.7}>
              <Text style={styles.allWeeksText}>All weeks</Text>
              <Ionicons name="chevron-forward" size={13} color={TEXT_DIM} />
            </TouchableOpacity>
          </View>
          </StaggerIn>

          {/* Day progress */}
          {selDayTasks.length > 0 && (
            <View style={styles.dayProgressRow}>
              <View style={styles.dayProgressTrack}>
                <View style={[styles.dayProgressFill, { width: `${(selDone / selDayTasks.length) * 100}%` }]} />
              </View>
              <Text style={styles.dayProgressText}>{selDone}/{selDayTasks.length}</Text>
            </View>
          )}

          {/* The timeline itself */}
          <StaggerIn index={3}>
          {selDayTasks.length === 0 ? (
            <View style={styles.restDayCard}>
              <Text style={styles.restDayText}>Rest day — recovery is part of the glow-up</Text>
            </View>
          ) : (
            <View style={styles.timeline}>
              {selDayTasks.map((task, i) => (
                <TimelineTask
                  key={task.instanceId}
                  task={task}
                  done={!!completed[task.instanceId]}
                  locked={selIsFuture}
                  onToggle={() => toggleTask(task.instanceId)}
                  onOpenDetail={() => setActiveDetailTask(task)}
                  isLast={i === selDayTasks.length - 1 && !dayInsight}
                />
              ))}
              {dayInsight && <InsightRow cat={dayInsight.cat} trait={dayInsight.trait} />}
            </View>
          )}
          </StaggerIn>
        </>
      ))}
    </ScrollView>
  );
}

// The 8-week plan, concerns & style fixes. No longer a nav tab — rendered
// EMBEDDED inside HomeTab's ScrollView ("Home absorbs plan"), so every view
// here is a plain View (no nested ScrollViews) and the stats/today sections
// that Home already renders were removed.
function ProgramTab({ program, completed, toggleTask, onNewProgram, subtraits, rescans }: {
  program: Program | null;
  completed: Record<string, boolean>;
  toggleTask: (instanceId: string) => void;
  onNewProgram: () => void;
  subtraits: Record<string, SubTrait[]>;
  rescans: number;
}) {
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [skipTarget, setSkipTarget] = useState<number | null>(null);
  const [unlockedWeeks, setUnlockedWeeks] = useState<number[]>([]);
  // Week the user tapped that's gated behind "finish this week + rescan" — shows
  // an explainer (no skip-ahead escape hatch, unlike the ordinary order lock).
  const [rescanNote, setRescanNote] = useState<number | null>(null);
  const [weeksListOpen, setWeeksListOpen] = useState(false);
  // Declutter: future/past weeks, extra concerns, and extra style fixes are
  // collapsed behind toggles so the tab leads with today.
  const [showAllWeeks, setShowAllWeeks] = useState(false);
  // Which task's how-to detail sheet is open, if any (see TaskDetailReader).
  const [activeDetailTask, setActiveDetailTask] = useState<ProgramTask | null>(null);

  // Home shows its own "no plan yet / scan first" card — nothing to add here.
  if (!program) return null;

  if (activeDetailTask) {
    return (
      <TaskDetailReader
        task={activeDetailTask}
        done={!!completed[activeDetailTask.instanceId]}
        onToggle={() => toggleTask(activeDetailTask.instanceId)}
        onBack={() => setActiveDetailTask(null)}
      />
    );
  }

  if (selectedWeek !== null) {
    const weekData = program.weeks[selectedWeek];
    const accent = weekData.gradient[0];
    const { weekIdx: currentWeekIdx, dayInWeek: currentDayInWeek } = programCurrentDay(program.generatedAt);
    const isThisWeekCurrent = selectedWeek === currentWeekIdx;
    // For past weeks any day is fair game; for the current week use the real calendar day.
    const todayDay = isThisWeekCurrent ? currentDayInWeek : 8; // 8 = no "today" highlight for past/future weeks
    const maxInteractiveDay = isThisWeekCurrent ? currentDayInWeek : 7;

    // Tasks may lack a `day` field if the program was generated before this update.
    // Fall back to treating them as one flat list on day 1 so nothing breaks.
    const hasDays = weekData.tasks.every(t => typeof t.day === 'number');
    const tasksByDay: ProgramTask[][] = hasDays
      ? Array.from({ length: 7 }, (_, i) => weekData.tasks.filter(t => t.day === i + 1))
      : [weekData.tasks, [], [], [], [], [], []];

    const weekCompleted = weekData.tasks.filter(t => completed[t.instanceId]).length;
    const weekTotal = weekData.tasks.length;
    const progress = weekTotal ? (weekCompleted / weekTotal) * 100 : 0;

    return (
      <View>
        <BackHeader label="Back to plan" onPress={() => setSelectedWeek(null)} />

        <Text style={[styles.programWeekNum, { color: accent }]}>WEEK {weekData.week}</Text>
        <Text style={styles.programWeekTitle}>{weekData.name}</Text>
        <View style={[styles.prioritiesBadge, { borderColor: accent }]}>
          <Text style={[styles.prioritiesBadgeText, { color: accent }]}>
            {weekData.priorities.join('  ·  ')}
          </Text>
        </View>

        <View style={styles.progressBarTrack}>
          <View style={[styles.progressBarFill, { width: `${progress}%`, backgroundColor: accent, shadowColor: accent, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.9, shadowRadius: 8 }]} />
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text style={styles.progressText}>{weekCompleted} / {weekTotal} missions done</Text>
          <Text style={[styles.progressText, { color: accent }]}>
            {weekData.tasks.filter(t => completed[t.instanceId]).reduce((s, t) => s + (t.xp ?? 20), 0)} / {weekData.tasks.reduce((s, t) => s + (t.xp ?? 20), 0)} XP
          </Text>
        </View>

        {tasksByDay.map((dayTasks, idx) => {
          const dayNum = idx + 1;
          const isToday = dayNum === todayDay;
          const isPast = dayNum < todayDay;
          const isFuture = dayNum > maxInteractiveDay;
          const dayXpDone = dayTasks.filter(t => completed[t.instanceId]).reduce((s, t) => s + (t.xp ?? 20), 0);
          const dayXpTotal = dayTasks.reduce((s, t) => s + (t.xp ?? 20), 0);
          const dayAllDone = dayTasks.length > 0 && dayTasks.every(t => completed[t.instanceId]);

          return (
            <View key={dayNum} style={{ marginTop: 20, opacity: isFuture ? 0.45 : 1 }}>
              {/* Day header */}
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                <View style={[
                  styles.dayHeaderPill,
                  isToday && { backgroundColor: accent, borderColor: accent },
                  isPast && dayAllDone && { borderColor: accent, backgroundColor: `${accent}22` },
                ]}>
                  <Text style={[
                    styles.dayHeaderText,
                    isToday && { color: '#000' },
                    isPast && dayAllDone && { color: accent },
                  ]}>
                    {isToday ? `DAY ${dayNum}  ·  TODAY` : isPast ? `DAY ${dayNum}` : `DAY ${dayNum}`}
                  </Text>
                </View>
                {isPast && dayAllDone && (
                  <Text style={[styles.dayDoneTag, { color: accent }]}>Done  ·  +{dayXpDone} XP</Text>
                )}
                {isToday && dayTasks.length > 0 && (
                  <Text style={[styles.dayDoneTag, { color: accent }]}>{dayTasks.filter(t => completed[t.instanceId]).length}/{dayTasks.length}  ·  {dayXpDone}/{dayXpTotal} XP</Text>
                )}
                {isFuture && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 10, gap: 4 }}>
                    <Ionicons name="lock-closed" size={11} color="rgba(255,255,255,0.35)" />
                    <Text style={styles.dayLockedTag}>Unlocks day {dayNum}</Text>
                  </View>
                )}
              </View>

              {dayTasks.length === 0 ? (
                <View style={styles.restDayCard}>
                  <Text style={styles.restDayText}>Rest day — recovery is part of the glow-up</Text>
                </View>
              ) : (
                dayTasks.map((task) => {
                  const isDone = !!completed[task.instanceId];
                  const taskXp = task.xp ?? 20;
                  return (
                    <PressScale
                      key={task.instanceId}
                      disabled={isFuture}
                      style={[styles.taskCard, isDone && { borderColor: `${accent}55`, backgroundColor: `${accent}12` }]}
                      onPress={() => toggleTask(task.instanceId)}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.taskDot, isDone && { backgroundColor: accent }]} />
                      <View style={styles.taskTextCol}>
                        <Text style={[styles.taskLabel, isDone && { color: accent, textDecorationLine: 'line-through', opacity: 0.7 }]}>
                          {task.label}
                        </Text>
                        {task.desc ? (
                          <Text style={[styles.taskDesc, isDone && { opacity: 0.45 }]}>{task.desc}</Text>
                        ) : null}
                      </View>
                      {isDone ? (
                        <View style={[styles.taskXpBadgeDone, { backgroundColor: `${accent}25`, borderColor: `${accent}60` }]}>
                          <Text style={[styles.taskXpDoneText, { color: accent }]}>+{taskXp} XP</Text>
                        </View>
                      ) : (
                        <View style={styles.taskXpBadge}>
                          <Text style={styles.taskXpText}>+{taskXp}</Text>
                        </View>
                      )}
                      <TouchableOpacity
                        onPress={() => { Haptics.selectionAsync().catch(() => {}); setActiveDetailTask(task); }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                        style={{ marginLeft: 8 }}
                      >
                        <Ionicons name="information-circle-outline" size={18} color="rgba(255,255,255,0.4)" />
                      </TouchableOpacity>
                    </PressScale>
                  );
                })
              )}
            </View>
          );
        })}
      </View>
    );
  }

  // Weeks unlock in order. The "current" week is the first one with unfinished
  // tasks; everything after it is locked until the user finishes — or chooses
  // to skip ahead (which we allow, but advise against).
  const firstActiveIdx = program.weeks.findIndex(w => w.tasks.some(t => !completed[t.instanceId]));
  const currentIdx = firstActiveIdx === -1 ? program.weeks.length - 1 : firstActiveIdx;
  // Rescans gate progression: each rescan (after the program is built) unlocks
  // one more week — but ONLY once you've also finished the week before it. So
  // Week 2 opens after you complete Week 1 AND rescan. Week 1 (index 0) is
  // always open. This lock is deliberately NOT skippable (finish + rescan is
  // the only way through), unlike the ordinary in-order lock below.
  const rescanCeil = 1 + Math.max(0, rescans); // how many weeks the rescans have opened
  const isRescanLocked = (i: number) => i >= rescanCeil;
  const isWeekLocked = (i: number) => isRescanLocked(i) || (i > currentIdx && !unlockedWeeks.includes(i));
  const openWeek = (i: number) => {
    Haptics.selectionAsync().catch(() => {});
    if (isRescanLocked(i)) { setRescanNote(i); }
    else if (isWeekLocked(i)) { setSkipTarget(i); }
    else { setSelectedWeek(i); }
  };
  const confirmSkip = () => {
    if (skipTarget === null) return;
    const target = skipTarget;
    setUnlockedWeeks(prev => prev.includes(target) ? prev : [...prev, target]);
    setSkipTarget(null);
    setSelectedWeek(target);
  };

  // ── Weeks list view: opened by tapping the active-week bubble ──
  if (weeksListOpen) {
    const { weekIdx: calWeekIdx, dayInWeek: calDayInWeek } = programCurrentDay(program.generatedAt);
    return (
      <View>
        <View>
          <BackHeader label="Back" onPress={() => setWeeksListOpen(false)} />

          <View style={styles.weeksHeaderRow}>
            <Text style={styles.weeksHeader}>YOUR PROGRAM</Text>
            <TouchableOpacity onPress={onNewProgram} style={[styles.refreshButton, { flexDirection: 'row', alignItems: 'center', gap: 4 }]} activeOpacity={0.7}>
              <Ionicons name="refresh" size={13} color="rgba(255,255,255,0.6)" />
              <Text style={styles.refreshButtonText}>New Program</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.weeksHelper}>
            Finish your current week, then rescan to unlock the next one.
          </Text>

          {program.weeks.map((week, idx) => {
            const weekDone = week.tasks.filter(t => completed[t.instanceId]).length;
            const weekTotal = week.tasks.length;
            const isComplete = weekTotal > 0 && weekDone === weekTotal;
            const isCurrent = idx === currentIdx;
            const locked = isWeekLocked(idx);
            const accent = locked ? '#8088a0' : week.gradient[0];
            return (
              <TouchableOpacity
                key={week.week}
                style={[styles.weekRowItem, {
                  borderColor: locked ? 'rgba(255,255,255,0.08)' : isCurrent ? `${accent}60` : `${accent}30`,
                  backgroundColor: isCurrent ? `${accent}18` : locked ? 'rgba(255,255,255,0.02)' : `${accent}0a`,
                }, locked && { opacity: 0.55 }]}
                onPress={() => openWeek(idx)}
                activeOpacity={0.8}
              >
                <Ionicons
                  name={locked ? 'lock-closed' : isComplete ? 'checkmark-circle' : 'ellipse-outline'}
                  size={20}
                  color={locked ? 'rgba(255,255,255,0.35)' : accent}
                  style={styles.weekRowIcon}
                />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.weekRowTitle, { color: locked ? 'rgba(255,255,255,0.4)' : '#fff' }]}>
                    Week {week.week} — {week.name}
                  </Text>
                  <Text style={[styles.weekRowSub, { color: locked ? 'rgba(255,255,255,0.25)' : `${accent}90` }]}>
                    {isRescanLocked(idx) ? 'Locked — finish this week & rescan'
                      : locked ? 'Locked — tap to skip ahead'
                      : isComplete ? 'Completed'
                      : idx === calWeekIdx ? `Day ${calDayInWeek} of 7  ·  Active`
                      : `${weekDone}/${weekTotal} done`}
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={18} color={locked ? 'rgba(255,255,255,0.2)' : `${accent}cc`} />
              </TouchableOpacity>
            );
          })}
        </View>

        {skipTarget !== null && (
          <View style={[styles.skipOverlay, styles.skipOverlayEmbed]}>
            <View style={styles.skipCard}>
              <Ionicons name="lock-closed" size={30} color="#FFB84D" style={styles.skipEmoji} />
              <Text style={styles.skipTitle}>Skip to Week {program.weeks[skipTarget].week}?</Text>
              <Text style={styles.skipBody}>
                Your plan is built to be done in order — each week builds on the last for the best glow-up. We really recommend finishing your current week first.
              </Text>
              <TouchableOpacity style={styles.skipPrimaryBtn} onPress={() => setSkipTarget(null)} activeOpacity={0.85}>
                <Text style={styles.skipPrimaryText}>Stay on track</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.skipSecondaryBtn} onPress={confirmSkip} activeOpacity={0.7}>
                <Text style={styles.skipSecondaryText}>Skip ahead anyway</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {rescanNote !== null && (
          <View style={[styles.skipOverlay, styles.skipOverlayEmbed]}>
            <View style={styles.skipCard}>
              <Ionicons name="lock-closed" size={30} color={CYAN} style={styles.skipEmoji} />
              <Text style={styles.skipTitle}>Week {program.weeks[rescanNote].week} is locked</Text>
              <Text style={styles.skipBody}>
                Finish your current week, then run a new scan to unlock Week {program.weeks[rescanNote].week}. Every rescan opens the next week — and shows how far you&apos;ve come.
              </Text>
              <TouchableOpacity style={styles.skipPrimaryBtn} onPress={() => setRescanNote(null)} activeOpacity={0.85}>
                <Text style={styles.skipPrimaryText}>Got it</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    );
  }

  return (
    <View>
    <View>
      {/* ── Plan header — stats/focus/daily-tasks live on Home now ── */}
      <View style={styles.weeksHeaderRow}>
        <Text style={styles.weeksHeader}>YOUR PROGRAM</Text>
        <TouchableOpacity onPress={onNewProgram} style={styles.refreshButton} activeOpacity={0.7}>
          <Text style={styles.refreshButtonText}>⟳ New Program</Text>
        </TouchableOpacity>
      </View>

      {/* Between weeks: the current week is finished but the next is still gated
          behind a rescan. Without this the program view would go empty (the
          active-week card only shows for an UNlocked current week). */}
      {isRescanLocked(currentIdx) && (
        <View style={styles.rescanPromptCard}>
          <Ionicons name="checkmark-done-circle" size={26} color={CYAN} style={{ marginBottom: 8 }} />
          <Text style={styles.rescanPromptTitle}>Week {program.weeks[Math.max(0, currentIdx - 1)].week} complete</Text>
          <Text style={styles.rescanPromptBody}>
            Rescan your face to unlock Week {program.weeks[currentIdx].week} and see how much you&apos;ve improved.
          </Text>
        </View>
      )}

      {(() => {
        const { weekIdx: calWeekIdx, dayInWeek: calDayInWeek } = programCurrentDay(program.generatedAt);
        return program.weeks.map((week, idx) => {
          const weekDone = week.tasks.filter(t => completed[t.instanceId]).length;
          const weekTotal = week.tasks.length;
          const isComplete = weekTotal > 0 && weekDone === weekTotal;
          const isCurrent = idx === currentIdx;
          const locked = isWeekLocked(idx);
          const accent = locked ? '#8088a0' : week.gradient[0];

          // The active week renders as the rich day-bubble card (moved inline
          // from the old standalone ACTIVE WEEK section). All other weeks stay
          // as compact rows.
          if (isCurrent && !locked) {
            const dayForCard = idx === calWeekIdx ? calDayInWeek : 1;
            const daysCompletedInWeek = Array.from({ length: 7 }, (_, i) => i + 1).filter(d => {
              const dayTasks = week.tasks.filter(t => t.day === d);
              return dayTasks.length > 0 && dayTasks.every(t => completed[t.instanceId]);
            }).length;
            return (
              <TouchableOpacity
                key={week.week}
                style={[styles.activeWeekCard, { borderColor: accent, shadowColor: accent }]}
                onPress={() => openWeek(idx)}
                activeOpacity={0.8}
              >
                <View style={[styles.weekCardAccentBar, { backgroundColor: accent }]} />
                <View style={{ flex: 1, paddingLeft: 14 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View>
                      <Text style={[styles.activeWeekMeta, { color: accent }]}>WEEK {week.week}  ·  DAY {dayForCard} OF 7</Text>
                      <Text style={styles.activeWeekTitle}>{week.name}</Text>
                    </View>
                    <Ionicons name="chevron-forward" size={20} color={accent} />
                  </View>
                  <View style={styles.weekPriorityRow}>
                    {week.priorities.map((p, pi) => (
                      <View key={pi} style={[styles.priorityTag, { borderColor: `${accent}45`, backgroundColor: `${accent}18` }]}>
                        <Text style={[styles.priorityTagText, { color: accent }]}>{p}</Text>
                      </View>
                    ))}
                  </View>
                  {/* Day progress dots */}
                  <View style={styles.dayDotsRow}>
                    {Array.from({ length: 7 }, (_, i) => {
                      const d = i + 1;
                      const dayTasks = week.tasks.filter(t => t.day === d);
                      const dayDone = dayTasks.length > 0 && dayTasks.every(t => completed[t.instanceId]);
                      const isActiveDay = d === dayForCard;
                      const isPastDay = d < dayForCard;
                      return (
                        <View key={d} style={[
                          styles.dayDot,
                          dayDone && { backgroundColor: accent, borderColor: accent },
                          isActiveDay && !dayDone && { borderColor: accent, backgroundColor: 'transparent' },
                          isPastDay && !dayDone && { borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.06)' },
                        ]}>
                          {isActiveDay && <View style={[styles.dayDotInner, { backgroundColor: accent }]} />}
                        </View>
                      );
                    })}
                    <Text style={[styles.dayDotsLabel, { color: `${accent}90` }]}>{daysCompletedInWeek}/7 days</Text>
                  </View>
                </View>
              </TouchableOpacity>
            );
          }

          // Collapsed by default — only the active week shows until expanded.
          if (!showAllWeeks) return null;
          return (
            <TouchableOpacity
              key={week.week}
              style={[styles.weekRowItem, {
                borderColor: locked ? 'rgba(255,255,255,0.08)' : `${accent}30`,
                backgroundColor: locked ? 'rgba(255,255,255,0.02)' : `${accent}0a`,
              }, locked && { opacity: 0.55 }]}
              onPress={() => openWeek(idx)}
              activeOpacity={0.8}
            >
              <Ionicons
                name={locked ? 'lock-closed' : isComplete ? 'checkmark-circle' : 'ellipse-outline'}
                size={20}
                color={locked ? 'rgba(255,255,255,0.35)' : accent}
                style={styles.weekRowIcon}
              />
              <View style={{ flex: 1 }}>
                <Text style={[styles.weekRowTitle, { color: locked ? 'rgba(255,255,255,0.4)' : '#fff' }]}>
                  Week {week.week} — {week.name}
                </Text>
                <Text style={[styles.weekRowSub, { color: locked ? 'rgba(255,255,255,0.25)' : `${accent}90` }]}>
                  {isRescanLocked(idx) ? 'Finish this week & rescan' : locked ? 'Locked' : isComplete ? 'Completed' : 'Upcoming'}
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color={locked ? 'rgba(255,255,255,0.2)' : `${accent}cc`} />
            </TouchableOpacity>
          );
        });
      })()}

      {/* Expand/collapse the other 7 weeks */}
      <TouchableOpacity
        style={styles.expandToggle}
        onPress={() => { setShowAllWeeks(v => !v); Haptics.selectionAsync().catch(() => {}); }}
        activeOpacity={0.7}
      >
        <Text style={styles.expandToggleText}>
          {showAllWeeks ? 'HIDE WEEKS  ▴' : `VIEW ALL ${program.weeks.length} WEEKS  ▾`}
        </Text>
      </TouchableOpacity>

      {/* QUICK WINS was removed from the program — the AI recommendations now
          live in the Guides tab, and Concerns & Protocol surface one-per-day as
          insight rows on Home's timeline. */}
    </View>

      {skipTarget !== null && (
        <View style={[styles.skipOverlay, styles.skipOverlayEmbed]}>
          <View style={styles.skipCard}>
            <Ionicons name="lock-closed" size={30} color="#FFB84D" style={styles.skipEmoji} />
            <Text style={styles.skipTitle}>Skip to Week {program.weeks[skipTarget].week}?</Text>
            <Text style={styles.skipBody}>
              Your plan is built to be done in order — each week builds on the last for the best glow-up. We really recommend finishing your current week first.
            </Text>
            <TouchableOpacity style={styles.skipPrimaryBtn} onPress={() => setSkipTarget(null)} activeOpacity={0.85}>
              <Text style={styles.skipPrimaryText}>Stay on track</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.skipSecondaryBtn} onPress={confirmSkip} activeOpacity={0.7}>
              <Text style={styles.skipSecondaryText}>Skip ahead anyway</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {rescanNote !== null && (
        <View style={[styles.skipOverlay, styles.skipOverlayEmbed]}>
          <View style={styles.skipCard}>
            <Ionicons name="lock-closed" size={30} color={CYAN} style={styles.skipEmoji} />
            <Text style={styles.skipTitle}>Week {program.weeks[rescanNote].week} is locked</Text>
            <Text style={styles.skipBody}>
              Finish your current week, then run a new scan to unlock Week {program.weeks[rescanNote].week}. Every rescan opens the next week — and shows how far you&apos;ve come.
            </Text>
            <TouchableOpacity style={styles.skipPrimaryBtn} onPress={() => setRescanNote(null)} activeOpacity={0.85}>
              <Text style={styles.skipPrimaryText}>Got it</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

type ChatMessage = { role: 'user' | 'assistant'; text: string };

const SUPPORT_GREETING: ChatMessage = {
  role: 'assistant',
  text: "Welcome to Maxify Support! I'm here to help with anything — your scores, your program, subscriptions, or how the app works. What can I help you with today?",
};

// Keeps the support conversation alive across closing/reopening the chat for the
// whole session. Cleared only on a full app refresh (a fresh JS load resets it).
let persistedChat: ChatMessage[] | null = null;

function SupportChat({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>(persistedChat ?? [SUPPORT_GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  // Mirror the conversation into module scope so it survives this component
  // unmounting when the user swipes/closes out of the chat.
  useEffect(() => { persistedChat = messages; }, [messages]);

  // Swipe right on the header to close (matches the ✕ button).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const swipeBack = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) => g.dx > 22 && Math.abs(g.dx) > Math.abs(g.dy) * 1.6,
      onPanResponderRelease: (_, g) => { if (g.dx > 70) { Haptics.selectionAsync().catch(() => {}); closeRef.current(); } },
    })
  ).current;

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const updated: ChatMessage[] = [...messages, { role: 'user', text }];
    setMessages(updated);
    setLoading(true);
    try {
      const data = await callClaude({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 400,
          system: `You are a friendly support assistant for Maxify, a facial analysis and self-improvement app for iOS.

Key facts about Maxify:
- Uses AI (Claude) to analyze facial features via the front camera and score them 0-100 across 6 categories: Skin, Jawline, Eyes, Cheekbones, Structure, Hair
- Scores are slightly boosted to be encouraging (a raw 60 shows as ~70)
- After a scan, users get a personalized 8-week program with daily tasks targeting their weakest areas
- Tasks reset daily; completing tasks builds a streak
- "Begin New Program" generates a fresh plan (resets streak and progress)
- Free tier: basic scan. Pro tier: full breakdown, history, advanced features (coming soon)
- App is iOS only for now

Tone: friendly, upbeat, and genuinely supportive with a touch of playful energy — but stay polished and professional, a notch more formal than casual texting (no slang, complete sentences). Keep replies short and clear, use an emoji now and then but not in every line, and stay encouraging since users are actively working on improving themselves. If you're unsure about something specific, say so honestly and offer another way to help.

Legal/compliance boundary: you are a public-facing representative of Maxify, so never say anything that could read as an admission of wrongdoing, illegal conduct, or non-compliance — don't speculate, hedge, or joke about the app or company being "creepy," "sketchy," "illegal," or "a scam," even if a user frames the question that way. If asked whether the app is legal, whether it's allowed to scan faces, or how biometric data is handled, answer confidently and factually: face photos are processed only after the user's explicit in-app consent, are used solely to generate their scores/program, and are sent to Anthropic for scoring — disclosed in the Privacy Policy — and users can delete all their data anytime from Profile > Delete Account. Point users to the Privacy Policy / Terms of Service (Profile tab) for the full legal text instead of improvising legal conclusions, and never give legal advice about their personal situation.`,
          // The API requires the first message to be role "user" — the canned
          // greeting is local-only, so drop everything before the first user turn.
          messages: updated
            .slice(updated.findIndex(m => m.role === 'user'))
            .map(m => ({ role: m.role, content: m.text })),
      });
      const reply = data?.content?.find((b: any) => b.type === 'text')?.text ?? "Sorry, I couldn't connect right now. Try again in a moment!";
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: "Having trouble connecting. Check your internet and try again." }]);
    } finally {
      setLoading(false);
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.supportOverlay}
    >
      <View style={styles.supportHeader} {...swipeBack.panHandlers}>
        <Text style={styles.supportTitle}>Support</Text>
        <TouchableOpacity onPress={onClose} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
          <Ionicons name="close" size={24} color="rgba(255,255,255,0.6)" />
        </TouchableOpacity>
      </View>

      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        contentContainerStyle={styles.supportMessagesList}
        onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: true })}
      >
        {messages.map((m, i) => (
          <View key={i} style={[styles.chatBubble, m.role === 'user' ? styles.chatBubbleUser : styles.chatBubbleBot]}>
            <Text style={[styles.chatBubbleText, m.role === 'user' && { color: '#000' }]}>{m.text}</Text>
          </View>
        ))}
        {loading && (
          <View style={[styles.chatBubble, styles.chatBubbleBot]}>
            <Text style={styles.chatBubbleText}>thinking…</Text>
          </View>
        )}
      </ScrollView>

      <View style={styles.supportInputRow}>
        <TextInput
          style={styles.supportInput}
          value={input}
          onChangeText={setInput}
          placeholder="Ask anything…"
          placeholderTextColor="rgba(255,255,255,0.3)"
          returnKeyType="send"
          onSubmitEditing={send}
          editable={!loading}
        />
        <TouchableOpacity style={[styles.supportSendBtn, loading && { opacity: 0.4 }]} onPress={send} disabled={loading}>
          <Text style={styles.supportSendText}>↑</Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

function LegalScreen({ doc, onClose }: { doc: LegalDoc; onClose: () => void }) {
  return (
    <View style={styles.legalScreen}>
      <View style={styles.legalHeader}>
        <TouchableOpacity
          onPress={onClose}
          activeOpacity={0.7}
          hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
          style={styles.legalBackBtn}
        >
          <Text style={styles.legalClose}>‹ Back</Text>
        </TouchableOpacity>
        <Text style={styles.legalHeaderTitle} numberOfLines={1}>{doc.title}</Text>
        <View style={{ width: 64 }} />
      </View>
      <ScrollView contentContainerStyle={styles.legalScroll} showsVerticalScrollIndicator={false}>
        <Text style={styles.legalEffective}>Effective {LEGAL_EFFECTIVE_DATE}</Text>
        {doc.sections.map((s, i) => (
          <View key={i} style={styles.legalSection}>
            <Text style={styles.legalHeading}>{s.heading}</Text>
            <Text style={styles.legalBody}>{s.body}</Text>
          </View>
        ))}
        <Text style={styles.legalFooter}>{LEGAL_ENTITY} · Maxify</Text>
      </ScrollView>
    </View>
  );
}

function ProfileTab({ scanCount, bestScore, streakDays, xp, badges, isSubscribed, onSubscribe, onRestore, onRedeemPromo, userName, onLogout, onOpenLegal, onDeleteAccount }: {
  scanCount: number;
  bestScore: number | null;
  streakDays: number;
  badges: string[];
  xp: number;
  isSubscribed: boolean;
  onSubscribe: () => void;
  onRestore: () => void;
  onRedeemPromo: () => void;
  userName: string;
  onLogout: () => void;
  onOpenLegal: (doc: 'privacy' | 'terms') => void;
  onDeleteAccount: () => void;
}) {
  const [showSupport, setShowSupport] = useState(false);

  if (showSupport) return <SupportChat onClose={() => setShowSupport(false)} />;

  // Global level from lifetime XP + progress into the next level.
  const level = levelForXp(xp);
  const cur = xpAtLevel(level);
  const next = xpAtLevel(level + 1);
  const levelPct = Math.max(0.04, Math.min(1, (xp - cur) / Math.max(1, next - cur)));

  return (
    <ScrollView style={styles.programContainer} contentContainerStyle={{ paddingBottom: 40, paddingTop: 8 }}>
      <StaggerIn index={0}>
      <View style={styles.profileAvatarSection}>
        <View style={styles.profileAvatarCircle}>
          <Ionicons name="person" size={42} color="rgba(255,255,255,0.7)" />
        </View>
        <View style={[styles.profileTierBadge, isSubscribed && { borderColor: CYAN }]}>
          <Text style={[styles.profileTierText, isSubscribed && { color: CYAN }]}>
            {isSubscribed ? 'PRO' : 'FREE'}
          </Text>
        </View>
        <Text style={styles.profileName}>{userName ? userName : 'Guest'}</Text>
      </View>
      </StaggerIn>

      {/* Global Level — lifetime XP from tasks + guides */}
      <StaggerIn index={1}>
      <View style={styles.levelCard}>
        <View style={styles.levelBadgeSm}>
          <Text style={styles.levelBadgeSmText}>{level}</Text>
        </View>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={styles.levelTitle}>LEVEL {level}</Text>
            <Text style={styles.levelXpText}>{xp - cur} / {next - cur} XP</Text>
          </View>
          <View style={styles.levelTrack}>
            <View style={[styles.levelFill, { width: `${levelPct * 100}%` }]} />
          </View>
        </View>
      </View>
      </StaggerIn>

      <StaggerIn index={2}>
      <View style={styles.statsRow}>
        <View style={styles.statBox}>
          <Ionicons name="scan-outline" size={20} color={PRIMARY_BLUE} style={styles.statIcon} />
          <Text style={[styles.statNum, { color: PRIMARY_BLUE }]}>{scanCount}</Text>
          <Text style={[styles.statLabel, { color: PRIMARY_BLUE }]}>SCANS</Text>
        </View>
        <View style={styles.statBox}>
          <Ionicons name="star" size={20} color={PURPLE_MID} style={styles.statIcon} />
          <Text style={[styles.statNum, { color: PURPLE_MID }]}>{bestScore ?? '--'}</Text>
          <Text style={[styles.statLabel, { color: PURPLE_MID }]}>BEST SCORE</Text>
        </View>
        <View style={styles.statBox}>
          <Ionicons name="flame" size={20} color={FLAME} style={styles.statIcon} />
          <Text style={[styles.statNum, { color: FLAME }]}>{streakDays}</Text>
          <Text style={[styles.statLabel, { color: FLAME }]}>STREAK</Text>
        </View>
      </View>
      </StaggerIn>

      {/* ── Achievements — collectible badges, locked ones dimmed ── */}
      <Text style={styles.settingsSectionHeader}>Achievements  ·  {badges.length}/{BADGES.length}</Text>
      <View style={styles.badgeGrid}>
        {BADGES.map((b) => {
          const on = badges.includes(b.id);
          return (
            <View
              key={b.id}
              style={[styles.badgeCell, on && { borderColor: `${b.color}55`, backgroundColor: `${b.color}12` }]}
            >
              <Ionicons
                name={on ? b.icon : 'lock-closed'}
                size={26}
                color={on ? b.color : 'rgba(255,255,255,0.28)'}
                style={styles.badgeCellIcon}
              />
              <Text style={[styles.badgeCellTitle, !on && { color: 'rgba(255,255,255,0.35)' }]}>{b.title}</Text>
              <Text style={styles.badgeCellDesc}>{b.desc}</Text>
            </View>
          );
        })}
      </View>

      {!isSubscribed && (
        <PressScale style={styles.upgradeCard} onPress={onSubscribe} activeOpacity={0.85}>
          <Text style={styles.upgradeCardTitle}>Upgrade to Pro</Text>
          <Text style={styles.upgradeCardSub}>Unlock full AI analysis, detailed breakdowns, and progress tracking over time.</Text>
          <View style={styles.upgradeCardBtn}>
            <Text style={styles.upgradeCardBtnText}>VIEW PLANS</Text>
          </View>
        </PressScale>
      )}

      <Text style={styles.settingsSectionHeader}>Settings</Text>
      {([
        { icon: 'chatbubble-ellipses-outline', label: 'Contact Support', onPress: () => setShowSupport(true) },
        { icon: 'refresh-outline', label: 'Restore Purchases', onPress: onRestore },
        // Promo code sits right under Restore Purchases; hidden once Pro.
        ...(!isSubscribed ? [{ icon: 'pricetag-outline' as const, label: 'Redeem Promo Code', onPress: onRedeemPromo }] : []),
        { icon: 'lock-closed-outline', label: 'Privacy Policy', onPress: () => onOpenLegal('privacy') },
        { icon: 'document-text-outline', label: 'Terms of Service', onPress: () => onOpenLegal('terms') },
      ] as const).map((row, i) => (
        <PressScale key={i} style={styles.settingsRow} onPress={row.onPress} activeOpacity={0.7}>
          <Ionicons name={row.icon} size={20} color={TEXT_DIM} style={styles.settingsIcon} />
          <Text style={styles.settingsLabel}>{row.label}</Text>
          <Ionicons name="chevron-forward" size={18} color="rgba(255,255,255,0.3)" />
        </PressScale>
      ))}

      <TouchableOpacity style={styles.logoutRow} onPress={onLogout} activeOpacity={0.7}>
        <Text style={styles.logoutText}>Log Out</Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.deleteAccountRow} onPress={onDeleteAccount} activeOpacity={0.7}>
        <Text style={styles.deleteAccountText}>Delete Account &amp; Data</Text>
      </TouchableOpacity>

      <Text style={styles.appVersion}>Maxify v1.0.0</Text>
    </ScrollView>
  );
}

// ---------------------------------------------------------------------------
// Guides — "The Maxify Playbook" (gamified knowledge library)
// ---------------------------------------------------------------------------
type GuideSection = { heading: string; body: string };
type Guide = {
  id: string;
  cat: string;
  emoji: string;
  title: string;
  hook: string;
  tier: 'S' | 'A' | 'B';
  minutes: number;
  xp: number;
  free: boolean;
  sections: GuideSection[];
};

// PULSE: category accents spread along the cyan→magenta spectrum (no rainbow).
const GUIDE_CATS: { key: string; label: string; icon: keyof typeof Ionicons.glyphMap; color: string }[] = [
  { key: 'all', label: 'All', icon: 'sparkles', color: CYAN },
  { key: 'skin', label: 'Skin', icon: 'water-outline', color: '#43e0ff' },
  { key: 'jaw', label: 'Jaw', icon: 'body-outline', color: '#6f9dff' },
  { key: 'eyes', label: 'Eyes', icon: 'eye-outline', color: '#9a6bff' },
  { key: 'hair', label: 'Hair', icon: 'cut-outline', color: '#c26bff' },
  { key: 'body', label: 'Body', icon: 'barbell-outline', color: '#ff4dd2' },
  { key: 'style', label: 'Style', icon: 'shirt-outline', color: '#ff6ea8' },
  { key: 'mind', label: 'Mind', icon: 'bulb-outline', color: '#5ad2ff' },
];

const CAT_COLOR: Record<string, string> = Object.fromEntries(GUIDE_CATS.map(c => [c.key, c.color]));
const CAT_ICON: Record<string, keyof typeof Ionicons.glyphMap> = Object.fromEntries(GUIDE_CATS.map(c => [c.key, c.icon]));
const TIER_COLOR: Record<string, string> = { S: MAGENTA, A: CYAN, B: '#8A9099' };

const GUIDES: Guide[] = [
  {
    id: 'skin-routine', cat: 'skin', emoji: '🧴', title: 'The 3-Step Skin Routine That Actually Works',
    hook: 'Clear skin is the #1 looksmax. Start here.', tier: 'S', minutes: 4, xp: 50, free: true,
    sections: [
      { heading: 'Why skin comes first', body: 'Skin is the largest, most visible feature of your face. Clear, even skin instantly reads as healthy, young, and high-status — before anyone even notices your jaw or eyes. Fix this and everything else pops.' },
      { heading: 'The only 3 steps you need', body: '1) Cleanse — a gentle face wash, morning and night. 2) Moisturize — lock in hydration so skin isn’t oily or flaky. 3) Sunscreen every morning (SPF 30+). That’s it. Skip the 10-step TikTok routines.' },
      { heading: 'The rules', body: 'Wash with lukewarm water, never scrub hard. Don’t touch your face during the day. Change your pillowcase twice a week. Give any new product 4–6 weeks before you judge it.' },
      { heading: 'Common mistakes', body: 'Over-washing strips your skin and triggers MORE oil. Popping pimples leaves scars and dark marks that last months. Harsh alcohol toners wreck your skin barrier. Keep it simple and stay consistent.' },
    ],
  },
  {
    id: 'mewing', cat: 'jaw', emoji: '🦴', title: 'Mewing: The Right Way (Most Guys Do It Wrong)',
    hook: 'Your tongue is a free jawline tool. Use it right.', tier: 'S', minutes: 4, xp: 50, free: true,
    sections: [
      { heading: 'What mewing actually is', body: 'Resting your entire tongue — including the back third — flat against the roof of your mouth, lips sealed, teeth lightly touching. It’s just correct oral posture, held all day.' },
      { heading: 'Do it right', body: 'Say the word “sing” and hold the “-ng”. Feel the back of your tongue rise? That’s the spot. Breathe through your nose and keep your tongue there until it becomes automatic.' },
      { heading: 'Real vs hype', body: 'Better resting posture, nasal breathing, and less bloat are real and noticeable. It will NOT rebuild adult bone structure — anyone promising a new jaw in 30 days is lying. The benefit comes from months of consistency.' },
      { heading: 'Stack it', body: 'Combine with nasal breathing, staying lean, and good posture: tongue up, lips sealed, chin slightly tucked, shoulders back. That single combo upgrades how your whole face and neck read.' },
    ],
  },
  {
    id: 'lean-face', cat: 'body', emoji: '🔪', title: 'Lean = Sharp: Why Body Fat Runs Your Face',
    hook: 'The fastest path to a sharper face: lose what’s hiding it.', tier: 'S', minutes: 4, xp: 50, free: true,
    sections: [
      { heading: 'Your face is downstream of body fat', body: 'Cheekbones, jawline, and hollow cheeks don’t show until the fat covering them is gone. Most people already HAVE good structure — it’s just buried. Getting lean is the single biggest facial unlock.' },
      { heading: 'The target', body: 'For visible facial definition, most people need to reach a lean body-fat range. That’s lean but achievable — abs starting to show is a solid mirror check.' },
      { heading: 'How to get there', body: 'Small calorie deficit (eat slightly less than you burn), high protein (~1g per lb of bodyweight), lift weights to keep muscle, and walk daily. Slow and steady beats crash diets that wreck your face.' },
      { heading: 'Don’t overshoot', body: 'Too lean for too long (sub-8%) makes your face gaunt, tired, and older. The goal is sharp and healthy, not skeletal. Find your sweet spot and hold it year-round.' },
    ],
  },
  {
    id: 'dark-circles', cat: 'eyes', emoji: '👁️', title: 'Kill Dark Circles & Eye Bags',
    hook: 'Tired eyes age you 5 years. Here’s the fix.', tier: 'A', minutes: 4, xp: 40, free: false,
    sections: [
      { heading: 'Know your enemy', body: 'Dark circles come in types: purple/blue (blood vessels, from poor sleep), brown (pigment, often genetic or sun), and hollow (shadow from lost volume). The fix depends on which you have.' },
      { heading: 'The daily fixes', body: 'Sleep 7–9 hours, sleep on your back with your head slightly elevated, cut salt before bed, and stay hydrated. Cold helps — a chilled spoon or roller in the morning shrinks the vessels.' },
      { heading: 'Products that work', body: 'An eye cream with caffeine (de-puffs) plus your daily sunscreen (stops pigment getting worse). Vitamin C in the morning gradually brightens brown circles.' },
      { heading: 'Reality check', body: 'Genetic hollows won’t fully vanish from creams — but sleep, hydration, sun protection, and less salt will visibly reduce 80% of what most people see in the mirror.' },
    ],
  },
  {
    id: 'sunscreen', cat: 'skin', emoji: '☀️', title: 'Sunscreen: The #1 Anti-Aging Cheat Code',
    hook: 'One habit that keeps you young for decades.', tier: 'S', minutes: 3, xp: 50, free: false,
    sections: [
      { heading: 'The #1 anti-aging move, full stop', body: 'Up to 80–90% of visible facial aging — wrinkles, sagging, dark spots, leathery texture — comes from UV, not age. Daily sunscreen is the closest thing to a real-life youth cheat code.' },
      { heading: 'How to use it', body: 'SPF 30–50, applied every morning as your last step — even when cloudy or sitting near windows. Use about two fingers’ length for face and neck. Reapply if you’re outside for hours.' },
      { heading: 'Find one you’ll actually wear', body: 'Modern “fluid” and Korean/Japanese sunscreens feel like nothing and leave no white cast. If it feels greasy or chalky you won’t use it — try samples until one disappears into your skin.' },
      { heading: 'Bonus', body: 'Daily SPF also helps acne marks and dark circles fade faster, since UV darkens pigment. It’s protection AND active improvement in one step.' },
    ],
  },
  {
    id: 'hairline', cat: 'hair', emoji: '💇', title: 'Hairline Defense: Protect What You’ve Got',
    hook: 'The best time to protect your hair is before you lose it.', tier: 'A', minutes: 4, xp: 40, free: false,
    sections: [
      { heading: 'Catch it early', body: 'Hair is far easier to keep than to regrow. Compare photos of your hairline every few months. Watch the temples and crown — thinning there is the early signal.' },
      { heading: 'What’s proven', body: 'Two treatments have real science: topical minoxidil (regrows/thickens) and finasteride (slows the hormone driving loss). Talk to a doctor before finasteride. Most other products are just supportive.' },
      { heading: 'Support habits', body: 'Avoid tight hats/styles that pull (traction loss is real), manage stress, and get enough protein, iron, and vitamin D. Be gentle — no aggressive towel-scrubbing or scalding water.' },
      { heading: 'Ignore the scams', body: 'Most “hair growth” gummies, oils, and shampoos do little. Spend on what’s proven and start early — that’s the whole game.' },
    ],
  },
  {
    id: 'debloat', cat: 'jaw', emoji: '💧', title: 'Debloat Your Face in 7 Days',
    hook: 'Wake up sharper and less puffy within a week.', tier: 'A', minutes: 3, xp: 40, free: false,
    sections: [
      { heading: 'Why your face is puffy', body: 'Water retention from salt, alcohol, bad sleep, and stress makes your face look softer and rounder than it really is. This is fixable fast — often within days.' },
      { heading: 'The 7-day reset', body: 'Cut added salt and processed food, drink MORE water (it flushes retention), zero alcohol, sleep 7–9 hours, and move daily. Watch late-day caffeine too.' },
      { heading: 'Morning de-puff', body: 'Splash cold water, use a chilled roller or cold spoons under the eyes, and do light cardio to get circulation going. The puffiness drains within minutes.' },
      { heading: 'What to expect', body: 'This reveals the lean, defined face you already have — it doesn’t create new structure. Pair it with staying lean year-round for permanent results instead of a 7-day peak.' },
    ],
  },
  {
    id: 'eyebrows', cat: 'style', emoji: '🪒', title: 'Eyebrows: The 5-Minute Frame Upgrade',
    hook: 'Brows frame your eyes. 5 minutes = a different face.', tier: 'A', minutes: 3, xp: 40, free: false,
    sections: [
      { heading: 'Why brows matter more than you think', body: 'Eyebrows frame your eyes and drive your whole expression — one of the first things people read on a face. Clean brows look intentional and sharp; neglected ones drag everything down.' },
      { heading: 'Clean up, don’t redesign', body: 'Only remove obvious strays — the unibrow gap and hairs well below the natural line. Trim long hairs by brushing them up and snipping the tips. Never over-pluck or you’ll look surprised and thin.' },
      { heading: 'Keep them natural', body: 'Full, fairly straight brows with a slight natural arch read best on most faces. Don’t over-thin them or build a high, sharp arch — the goal is groomed, not done.' },
      { heading: 'Tools', body: 'A good pair of tweezers, a small brow brush, and tiny scissors. Do it in bright light after a shower when pores are open. Two minutes a week keeps them sharp.' },
    ],
  },
  {
    id: 'posture', cat: 'body', emoji: '🧍', title: 'Posture Fix = Instant Status',
    hook: 'Stand right and look taller, leaner, and confident — instantly.', tier: 'B', minutes: 3, xp: 30, free: false,
    sections: [
      { heading: 'Posture is free status', body: 'Slouching shortens your neck, doubles your chin, and screams low confidence. Standing tall lengthens your neck, sharpens the jaw-to-neck line, and adds presence — no gym required.' },
      { heading: 'The reset cue', body: 'Chin slightly tucked (not jutting), shoulders down and back, chest open, ears stacked over shoulders. Imagine a string pulling the crown of your head to the ceiling.' },
      { heading: 'Fix the cause', body: 'Tech-neck from phones and desks is the #1 culprit. Strengthen your upper back (face pulls, rows), stretch your chest, and raise screens to eye level. Take a posture break every 30 minutes.' },
      { heading: 'Bonus for photos', body: 'Good posture plus chin slightly forward and down kills the double-chin in every photo. Practice in the mirror until it’s your default.' },
    ],
  },
  {
    id: 'sleep', cat: 'mind', emoji: '😴', title: 'Sleep Like Your Looks Depend On It (They Do)',
    hook: 'The cheapest, most powerful looksmax there is.', tier: 'S', minutes: 4, xp: 50, free: false,
    sections: [
      { heading: 'Beauty sleep is literally real', body: 'In deep sleep your body releases growth hormone, repairs skin, and clears the puffiness and dark circles that wreck your face. Skimp on it and you look tired, older, and softer — every single day.' },
      { heading: 'The targets', body: '7–9 hours, consistent sleep and wake times (even weekends), in a cool, fully dark room. Consistency matters as much as total hours.' },
      { heading: 'The wind-down', body: 'No screens 30–60 min before bed (blue light blocks melatonin), no caffeine after early afternoon, no big meals or alcohol late. Morning sunlight sets your clock for better sleep that night.' },
      { heading: 'Compounding payoff', body: 'Good sleep lowers cortisol (the stress hormone that ages skin and stores face fat), sharpens your eyes, and speeds recovery from training. It’s the foundation everything else is built on.' },
    ],
  },
  {
    id: 'fit-check', cat: 'style', emoji: '👕', title: 'Fit Check: Clothes That Build Your Frame',
    hook: 'The right clothes build the frame you’re working on.', tier: 'B', minutes: 4, xp: 30, free: false,
    sections: [
      { heading: 'Fit beats brand, always', body: 'Clothes that actually fit make you look leaner, broader, and taller. A cheap shirt that fits beats an expensive one that doesn’t. Shoulders are key — the seam should sit right at the edge of your shoulder.' },
      { heading: 'Build a V-taper', body: 'Tops that fit the shoulders and taper at the waist signal an athletic frame. Avoid baggy boxy fits that hide your shape, and skin-tight that looks try-hard. Aim for clean and fitted.' },
      { heading: 'The starter palette', body: 'Master fitted basics first: plain tees, a couple of overshirts, dark straight jeans, clean sneakers, one good jacket. Neutrals — black, white, navy, olive, grey — all mix and look intentional.' },
      { heading: 'Details that read rich', body: 'Clothes steamed/ironed, no loud logos, shoes clean. Grooming + fit + posture together is the actual “aura” people talk about.' },
    ],
  },
  {
    id: 'hydration', cat: 'skin', emoji: '🚰', title: 'Hydration & The Glow Nobody Talks About',
    hook: 'The simplest glow-up almost nobody actually does.', tier: 'B', minutes: 3, xp: 30, free: false,
    sections: [
      { heading: 'Water shows on your face', body: 'Even mild dehydration makes skin look dull and tired and shows fine lines more. Well-hydrated skin looks plump and bright — the “lit from within” glow people pay for in serums.' },
      { heading: 'How much', body: 'Roughly 2–3 liters a day for most active people, more if you train or it’s hot. Quick check: pale-yellow urine = good, dark = drink more. Spread it through the day, don’t chug it all at once.' },
      { heading: 'Hydrate from outside too', body: 'A lightweight moisturizer twice a day seals water into your skin. Look for hyaluronic acid and glycerin — they pull in and hold moisture for that plump look.' },
      { heading: 'Don’t sabotage it', body: 'Excess alcohol, super-salty food, and tons of caffeine dry you out and puff you up. Balance them with extra water and you keep the glow.' },
    ],
  },
  {
    id: 'hair-shape', cat: 'hair', emoji: '✂️', title: 'Hair That Frames Your Face Shape',
    hook: 'The right cut can fake a better face shape.', tier: 'B', minutes: 4, xp: 30, free: false,
    sections: [
      { heading: 'Hair is framing', body: 'Your cut frames your face and can balance its shape — height makes a round face look longer, the right sides make a narrow face look fuller. The wrong cut fights your face; the right one completes it.' },
      { heading: 'Match it up', body: 'Round/soft face → height on top, tight sides for length. Long/narrow → less height, a bit more on the sides for width. Square/strong → most styles work; lean into it. Unsure? A good barber will tell you your shape.' },
      { heading: 'Talk to your barber', body: 'Bring 2–3 reference photos, learn the terms (taper vs fade, length in guards), and ask what suits your hair type and face. A great barber is worth more than any product.' },
      { heading: 'Maintain the shape', body: 'Most cuts look best for 2–4 weeks. Book regular trims, style with the right product (matte clay/paste for most), and keep your hairline and neckline clean between cuts.' },
    ],
  },
  {
    id: 'mindset', cat: 'mind', emoji: '🧠', title: 'The Glow-Up Mindset: Consistency > Intensity',
    hook: 'Why most people quit — and how you won’t.', tier: 'A', minutes: 4, xp: 40, free: false,
    sections: [
      { heading: 'Consistency beats intensity', body: 'The guy who does the basics every day for a year beats the guy who goes 100% for two weeks and quits. Looksmaxxing compounds — small daily habits stack into a different face and body over months.' },
      { heading: 'Play the long game', body: 'Skin clears over weeks, leanness over months, hair and habits over a year. Take monthly progress photos in the same lighting — the mirror lies day to day, but photos show the trend.' },
      { heading: 'Avoid the traps', body: 'Don’t fall for blackpill doom, expensive scams, or comparison spirals. Focus only on what you control: sleep, food, training, skin, grooming, posture, style. That list alone changes everything.' },
      { heading: 'Make it a game', body: 'Stack streaks, hit your daily tasks, level up your knowledge here, and watch your scores climb. You’re not “fixing flaws” — you’re leveling up a character, and that character is you. Keep going.' },
    ],
  },
];

function guideRank(count: number, total: number): { name: string; icon: keyof typeof Ionicons.glyphMap; color: string } {
  if (count >= total && total > 0) return { name: 'MAXXER', icon: 'trophy', color: '#FFD700' };
  if (count >= 12) return { name: 'Expert', icon: 'flame', color: '#FF6600' };
  if (count >= 8) return { name: 'Adept', icon: 'flash', color: '#ff4dd2' };
  if (count >= 4) return { name: 'Grinder', icon: 'barbell', color: '#00BFFF' };
  if (count >= 1) return { name: 'Rookie', icon: 'leaf', color: '#43e0ff' };
  return { name: 'Newbie', icon: 'egg-outline', color: 'rgba(255,255,255,0.6)' };
}

// Full-screen detail sheet for a single program task — numbered how-to steps
// + the mechanism behind it (TASK_HOWTO), or just the one-line desc for tasks
// that don't have full how-to content yet (most of TASK_LIBRARY, for now).
// Mirrors GuideReader's slide-in/swipe-back pattern and reuses its styles.
function TaskDetailReader({ task, done, onToggle, onBack }: {
  task: ProgramTask; done: boolean; onToggle: () => void; onBack: () => void;
}) {
  const { color } = catStyle(task.category);
  const howto = TASK_HOWTO[task.templateId];
  const backRef = useRef(onBack);
  backRef.current = onBack;
  const slideX = useRef(new Animated.Value(width)).current;
  useEffect(() => {
    Animated.timing(slideX, { toValue: 0, duration: 260, useNativeDriver: true }).start();
  }, [slideX]);

  const dismiss = () => {
    Haptics.selectionAsync().catch(() => {});
    Animated.timing(slideX, { toValue: width, duration: 220, useNativeDriver: true }).start(() => backRef.current());
  };

  const swipeBack = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) => g.dx > 14 && Math.abs(g.dx) > Math.abs(g.dy) * 1.4,
      onPanResponderMove: (_, g) => { if (g.dx > 0) slideX.setValue(g.dx); },
      onPanResponderRelease: (_, g) => {
        if (g.dx > 90 || g.vx > 0.5) {
          Haptics.selectionAsync().catch(() => {});
          Animated.timing(slideX, { toValue: width, duration: 200, useNativeDriver: true }).start(() => backRef.current());
        } else {
          Animated.spring(slideX, { toValue: 0, useNativeDriver: true, friction: 9, tension: 70 }).start();
        }
      },
    })
  ).current;

  return (
    <Animated.View style={[gstyles.readerContainer, { transform: [{ translateX: slideX }] }]} {...swipeBack.panHandlers}>
      <ScrollView contentContainerStyle={{ paddingBottom: 140, paddingTop: TOP_BAR_HEIGHT + 8 }} showsVerticalScrollIndicator={false}>
        <View style={{ paddingHorizontal: 20 }}>
          <BackHeader label="Program" onPress={dismiss} />
        </View>

        <View style={[gstyles.readerHero, { borderColor: color + '55' }]}>
          <TaskIllustration templateId={task.templateId} category={task.category} size={40} color={color} />
          <View style={gstyles.readerBadgeRow}>
            <Text style={gstyles.readerMeta}>+{task.xp} XP</Text>
          </View>
          <Text style={gstyles.readerTitle}>{task.label}</Text>
          {howto ? <Text style={[gstyles.readerHook, { color }]}>{howto.why}</Text> : (
            task.desc ? <Text style={[gstyles.readerHook, { color }]}>{task.desc}</Text> : null
          )}
        </View>

        {howto ? (
          <View style={gstyles.section}>
            <View style={gstyles.sectionHeadRow}>
              <View style={[gstyles.sectionNum, { backgroundColor: color + '22', borderColor: color + '66' }]}>
                <Ionicons name="list" size={14} color={color} />
              </View>
              <Text style={gstyles.sectionHeading}>How to</Text>
            </View>
            {howto.steps.map((s, i) => (
              <View key={i} style={styles.taskStepRow}>
                <Text style={[styles.taskStepNum, { color }]}>{i + 1}</Text>
                <Text style={styles.taskStepText}>{s}</Text>
              </View>
            ))}
          </View>
        ) : (
          <View style={gstyles.section}>
            <Text style={gstyles.sectionBody}>Full step-by-step instructions for this one are coming soon — for now, use the description above.</Text>
          </View>
        )}
      </ScrollView>

      <View style={gstyles.readerFooter}>
        {done ? (
          <View style={[gstyles.completeBtn, gstyles.completeBtnDone]}>
            <Text style={gstyles.completeDoneText}>DONE  ·  +{task.xp} XP EARNED</Text>
          </View>
        ) : (
          <TouchableOpacity style={[gstyles.completeBtn, { backgroundColor: color, shadowColor: color }]} onPress={onToggle} activeOpacity={0.85}>
            <Text style={gstyles.completeText}>MARK AS DONE  ·  +{task.xp} XP</Text>
          </TouchableOpacity>
        )}
      </View>
    </Animated.View>
  );
}

// Full-screen reader for a single guide.
function GuideReader({ guide, isRead, onComplete, onBack }: {
  guide: Guide; isRead: boolean; onComplete: () => void; onBack: () => void;
}) {
  const color = CAT_COLOR[guide.cat] ?? PRIMARY_BLUE;
  const backRef = useRef(onBack);
  backRef.current = onBack;
  // Slide the reader in from the right, and let a right-swipe drag it back out
  // smoothly (follows the finger, then animates the rest of the way).
  const slideX = useRef(new Animated.Value(width)).current;
  useEffect(() => {
    Animated.timing(slideX, { toValue: 0, duration: 260, useNativeDriver: true }).start();
  }, [slideX]);

  const dismiss = () => {
    Haptics.selectionAsync().catch(() => {});
    Animated.timing(slideX, { toValue: width, duration: 220, useNativeDriver: true }).start(() => backRef.current());
  };

  const swipeBack = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) => g.dx > 14 && Math.abs(g.dx) > Math.abs(g.dy) * 1.4,
      onPanResponderMove: (_, g) => { if (g.dx > 0) slideX.setValue(g.dx); },
      onPanResponderRelease: (_, g) => {
        if (g.dx > 90 || g.vx > 0.5) {
          Haptics.selectionAsync().catch(() => {});
          Animated.timing(slideX, { toValue: width, duration: 200, useNativeDriver: true }).start(() => backRef.current());
        } else {
          Animated.spring(slideX, { toValue: 0, useNativeDriver: true, friction: 9, tension: 70 }).start();
        }
      },
    })
  ).current;

  return (
    <Animated.View style={[gstyles.readerContainer, { transform: [{ translateX: slideX }] }]} {...swipeBack.panHandlers}>
      <ScrollView contentContainerStyle={{ paddingBottom: 140, paddingTop: TOP_BAR_HEIGHT + 8 }} showsVerticalScrollIndicator={false}>
        <View style={{ paddingHorizontal: 20 }}>
          <BackHeader label="Playbook" onPress={dismiss} />
        </View>

        <View style={[gstyles.readerHero, { borderColor: color + '55' }]}>
          <Ionicons name={CAT_ICON[guide.cat] ?? 'sparkles'} size={40} color={color} style={gstyles.readerEmoji} />
          <View style={gstyles.readerBadgeRow}>
            <View style={[gstyles.tierPill, { borderColor: TIER_COLOR[guide.tier], backgroundColor: TIER_COLOR[guide.tier] + '22' }]}>
              <Text style={[gstyles.tierPillText, { color: TIER_COLOR[guide.tier] }]}>{guide.tier}-TIER</Text>
            </View>
            <Text style={gstyles.readerMeta}>{guide.minutes} min   ·   +{guide.xp} XP</Text>
          </View>
          <Text style={gstyles.readerTitle}>{guide.title}</Text>
          <Text style={[gstyles.readerHook, { color }]}>{guide.hook}</Text>
        </View>

        {guide.sections.map((s, i) => (
          <View key={i} style={gstyles.section}>
            <View style={gstyles.sectionHeadRow}>
              <View style={[gstyles.sectionNum, { backgroundColor: color + '22', borderColor: color + '66' }]}>
                <Text style={[gstyles.sectionNumText, { color }]}>{i + 1}</Text>
              </View>
              <Text style={gstyles.sectionHeading}>{s.heading}</Text>
            </View>
            <Text style={gstyles.sectionBody}>{s.body}</Text>
          </View>
        ))}
      </ScrollView>

      <View style={gstyles.readerFooter}>
        {isRead ? (
          <View style={[gstyles.completeBtn, gstyles.completeBtnDone]}>
            <Text style={gstyles.completeDoneText}>MASTERED  ·  +{guide.xp} XP EARNED</Text>
          </View>
        ) : (
          <TouchableOpacity style={[gstyles.completeBtn, { backgroundColor: color, shadowColor: color }]} onPress={onComplete} activeOpacity={0.85}>
            <Text style={gstyles.completeText}>MARK AS MASTERED  ·  +{guide.xp} XP</Text>
          </TouchableOpacity>
        )}
      </View>
    </Animated.View>
  );
}

function GuidesTab({ isSubscribed, onSubscribe, onXp, recommendations = [] }: {
  isSubscribed: boolean;
  onSubscribe: () => void;
  onXp?: (n: number) => void;
  recommendations?: Recommendation[];
}) {
  const [readIds, setReadIds] = useState<string[]>([]);
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState<Guide | null>(null);
  const [showAllFixes, setShowAllFixes] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(STORAGE.guidesRead);
        if (raw) setReadIds(JSON.parse(raw));
      } catch { /* ignore */ }
      loaded.current = true;
    })();
  }, []);

  const markRead = (g: Guide) => {
    if (readIds.includes(g.id)) return;
    const next = [...readIds, g.id];
    setReadIds(next);
    AsyncStorage.setItem(STORAGE.guidesRead, JSON.stringify(next)).catch(() => {});
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    onXp?.(g.xp); // guides feed the global Level too
  };

  const readCount = readIds.length;
  const total = GUIDES.length;
  const totalXp = GUIDES.filter(g => readIds.includes(g.id)).reduce((s, g) => s + g.xp, 0);
  const rank = guideRank(readCount, total);
  const pct = total ? readCount / total : 0;

  // Point the user at their next guide: first unread one they can open, else
  // the first unread overall, else just the first guide.
  const featured =
    GUIDES.find(g => !readIds.includes(g.id) && (g.free || isSubscribed)) ??
    GUIDES.find(g => !readIds.includes(g.id)) ??
    GUIDES[0];
  const allRead = readCount >= total;

  const openGuide = (g: Guide) => {
    if (!g.free && !isSubscribed) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
      onSubscribe();
      return;
    }
    Haptics.selectionAsync().catch(() => {});
    setActive(g);
  };

  if (active) {
    return (
      <GuideReader
        guide={active}
        isRead={readIds.includes(active.id)}
        onComplete={() => markRead(active)}
        onBack={() => setActive(null)}
      />
    );
  }

  const topics = GUIDE_CATS.filter(c => c.key !== 'all');
  const catStats = (key: string) => {
    const gs = GUIDES.filter(g => g.cat === key);
    return { total: gs.length, read: gs.filter(g => readIds.includes(g.id)).length };
  };

  const GuideCard = ({ g }: { g: Guide }) => {
    const color = CAT_COLOR[g.cat] ?? PRIMARY_BLUE;
    const done = readIds.includes(g.id);
    const locked = !g.free && !isSubscribed;
    return (
      <PressScale
        style={[gstyles.card, {
          borderColor: done ? `${color}55` : color + '40',
          backgroundColor: done ? `${color}18` : color + '0e',
        }]}
        onPress={() => openGuide(g)} activeOpacity={0.85}>
        <View style={[gstyles.cardIcon, { backgroundColor: color + '1A', borderColor: color + '55' }]}>
          <Ionicons name={CAT_ICON[g.cat] ?? 'sparkles'} size={20} color={color} />
        </View>
        <View style={{ flex: 1 }}>
          <View style={gstyles.cardTopRow}>
            <Text style={gstyles.cardMeta}>{g.minutes}m  ·  +{g.xp} XP</Text>
            {done && <Ionicons name="checkmark-circle" size={15} color={color} />}
            {locked && <Ionicons name="lock-closed" size={13} color="rgba(255,255,255,0.5)" />}
          </View>
          <Text style={[gstyles.cardTitle, locked && { opacity: 0.7 }]} numberOfLines={2}>{g.title}</Text>
          <Text style={gstyles.cardHook} numberOfLines={1}>{g.hook}</Text>
        </View>
      </PressScale>
    );
  };

  const UnlockBanner = () => !isSubscribed ? (
    <PressScale style={gstyles.unlockBanner} onPress={onSubscribe} activeOpacity={0.85}>
      <Text style={gstyles.unlockTitle}>Unlock the full Playbook</Text>
      <Text style={gstyles.unlockSub}>Get all {total} guides, every drop, and your full breakdown with Pro.</Text>
      <View style={gstyles.unlockBtn}><Text style={gstyles.unlockBtnText}>GO PRO</Text></View>
    </PressScale>
  ) : null;

  // ── Level 2: one area's guides (opened from a tile) ──
  if (filter !== 'all') {
    const cat = GUIDE_CATS.find(c => c.key === filter) ?? GUIDE_CATS[0];
    const list = GUIDES.filter(g => g.cat === filter);
    const st = catStats(filter);
    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 40, paddingTop: 6 }} showsVerticalScrollIndicator={false}>
        <BackHeader label="All areas" onPress={() => { setFilter('all'); Haptics.selectionAsync().catch(() => {}); }} />
        <View style={[gstyles.catDetailHead, { borderColor: cat.color + '55', backgroundColor: cat.color + '14' }]}>
          <Ionicons name={cat.icon} size={34} color={cat.color} />
          <View style={{ flex: 1, marginLeft: 14 }}>
            <Text style={[gstyles.catDetailTitle, { color: cat.color }]}>{cat.label}</Text>
            <Text style={gstyles.catDetailSub}>{st.read === st.total ? 'All mastered' : `${st.read} of ${st.total} mastered`}</Text>
          </View>
        </View>
        {list.map(g => <GuideCard key={g.id} g={g} />)}
        <UnlockBanner />
      </ScrollView>
    );
  }

  // ── Level 1: landing — mastery, one clear next step, browse-by-area tiles ──
  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 40, paddingTop: 6 }} showsVerticalScrollIndicator={false}>
      <StaggerIn index={0}>
        <Text style={gstyles.playbookTitle}>THE PLAYBOOK</Text>
      </StaggerIn>

      {/* Compact mastery bar */}
      <StaggerIn index={1}>
      <View style={[gstyles.masteryCard, { borderColor: rank.color + '55', shadowColor: rank.color }]}>
        <View style={gstyles.masteryTopRow}>
          <Ionicons name={rank.icon} size={30} color={rank.color} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[gstyles.masteryRank, { color: rank.color }]}>{rank.name}</Text>
            <Text style={gstyles.masteryCount}>
              {allRead ? 'All guides mastered' : `${readCount} of ${total} mastered`}
            </Text>
          </View>
          <View style={gstyles.masteryXpBox}>
            <Text style={gstyles.masteryXp}>{totalXp}</Text>
            <Text style={gstyles.masteryXpLabel}>XP</Text>
          </View>
        </View>
        <View style={gstyles.progressTrack}>
          <View style={[gstyles.progressFill, { width: `${Math.max(4, pct * 100)}%`, backgroundColor: rank.color, shadowColor: rank.color }]} />
        </View>
      </View>
      </StaggerIn>

      {/* One clear next step */}
      {!allRead && (
        <StaggerIn index={2}>
          <Text style={gstyles.sectionLabel}>{readCount === 0 ? 'START HERE' : 'CONTINUE'}</Text>
          <PressScale
            style={[gstyles.featuredCard, {
              borderColor: (CAT_COLOR[featured.cat] ?? PRIMARY_BLUE) + '88',
              shadowColor: CAT_COLOR[featured.cat] ?? PRIMARY_BLUE,
              backgroundColor: (CAT_COLOR[featured.cat] ?? PRIMARY_BLUE) + '1E',
            }]}
            onPress={() => openGuide(featured)} activeOpacity={0.9}>
            <Ionicons name={CAT_ICON[featured.cat] ?? 'sparkles'} size={30} color={CAT_COLOR[featured.cat] ?? PRIMARY_BLUE} style={{ marginBottom: 10 }} />
            <Text style={gstyles.featuredTitle}>{featured.title}</Text>
            <Text style={gstyles.featuredHook}>{featured.hook}</Text>
            <View style={gstyles.featuredCta}>
              <Text style={gstyles.featuredCtaText}>OPEN  ·  +{featured.xp} XP</Text>
              <Ionicons name="chevron-forward" size={14} color={CAT_COLOR[featured.cat] ?? PRIMARY_BLUE} />
            </View>
          </PressScale>
        </StaggerIn>
      )}

      {/* Quick wins — the scan's own AI recommendations. Moved here out of the
          program (which is now purely the daily timeline): these are one-off
          tweaks to read, not habits to check off, so they belong with guides. */}
      {isSubscribed && recommendations.length > 0 && (
        <>
          <Text style={gstyles.sectionLabel}>QUICK WINS FOR YOUR FACE</Text>
          {(showAllFixes ? recommendations : recommendations.slice(0, 3)).map((rec, i) => (
            <View key={i} style={gstyles.quickWinCard}>
              <View style={gstyles.quickWinIcon}>
                <Ionicons name={recIcon(rec.feature)} size={19} color={ACCENT} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={gstyles.quickWinTitle}>{rec.title}</Text>
                <Text style={gstyles.quickWinAction}>{rec.action}</Text>
                {rec.reason ? <Text style={gstyles.quickWinReason}>{rec.reason}</Text> : null}
              </View>
            </View>
          ))}
          {recommendations.length > 3 && (
            <TouchableOpacity
              style={styles.expandToggle}
              onPress={() => { setShowAllFixes(v => !v); Haptics.selectionAsync().catch(() => {}); }}
              activeOpacity={0.7}
            >
              <Text style={styles.expandToggleText}>
                {showAllFixes ? 'SHOW LESS  ▴' : `+${recommendations.length - 3} MORE  ▾`}
              </Text>
            </TouchableOpacity>
          )}
        </>
      )}

      {/* Browse by area — tiles, one topic at a time */}
      <Text style={gstyles.sectionLabel}>BROWSE BY AREA</Text>
      <View style={gstyles.tileGrid}>
        {topics.map(c => {
          const st = catStats(c.key);
          const done = st.total > 0 && st.read === st.total;
          return (
            <PressScale key={c.key}
              style={[gstyles.tile, { borderColor: c.color + '40', backgroundColor: c.color + '10' }]}
              onPress={() => { setFilter(c.key); Haptics.selectionAsync().catch(() => {}); }} activeOpacity={0.85}>
              <Ionicons name={c.icon} size={24} color={c.color} style={gstyles.tileEmoji} />
              <Text style={gstyles.tileLabel}>{c.label}</Text>
              <View style={gstyles.tileBar}>
                <View style={[gstyles.tileBarFill, { width: `${st.total ? (st.read / st.total) * 100 : 0}%`, backgroundColor: c.color }]} />
              </View>
              <Text style={[gstyles.tileProgress, done && { color: c.color }]}>{done ? 'Mastered' : `${st.read}/${st.total}`}</Text>
            </PressScale>
          );
        })}
      </View>

      <UnlockBanner />
    </ScrollView>
  );
}

const gstyles = StyleSheet.create({
  // Quick wins (moved here from the program) — read-once tweaks, not habits.
  quickWinCard: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 12,
    backgroundColor: SURFACE_2, borderRadius: 15, borderWidth: 1, borderColor: BORDER,
    padding: 14, marginBottom: 10,
  },
  quickWinIcon: {
    width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
    backgroundColor: `${ACCENT}1f`, borderWidth: 1, borderColor: `${ACCENT}55`,
  },
  quickWinTitle: { color: TEXT, fontSize: 14.5, fontFamily: FONT_SEMI },
  quickWinAction: { color: TEXT, fontSize: 12.5, fontFamily: FONT, lineHeight: 18, marginTop: 4, opacity: 0.85 },
  quickWinReason: { color: TEXT_DIM, fontSize: 11.5, fontFamily: FONT, lineHeight: 16, marginTop: 5, fontStyle: 'italic' },

  playbookTitle: {
    color: '#fff', fontSize: 28, fontFamily: SERIF_SEMI, letterSpacing: 0, marginTop: 4,
    textShadowColor: PRIMARY_BLUE, textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 14,
  },
  playbookSub: { color: 'rgba(255,255,255,0.45)', fontSize: 13, fontFamily: FONT, marginTop: 4, marginBottom: 18 },

  masteryCard: {
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 20, borderWidth: 1.5,
    padding: 18, marginBottom: 26, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.35, shadowRadius: 18,
  },
  masteryTopRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 14 },
  masteryRankLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 2 },
  // The rank name is the one word the eye lands on in this card — serif, and
  // NO positive tracking. Letter-spaced bold sans at display size is what made
  // this read generic; a serif with slight negative tracking reads authored.
  masteryRank: { fontSize: 26, fontFamily: SERIF_BOLD, letterSpacing: -0.4, marginTop: 2 },
  masteryXpBox: { alignItems: 'flex-end' },
  // Numerals stay sans — Fraunces' figures are stylistic and hurt scannability.
  masteryXp: { color: '#fff', fontSize: 22, fontFamily: FONT_BOLD },
  masteryXpLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: FONT_BOLD },
  progressTrack: { height: 10, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
  progressFill: { height: 10, borderRadius: 6, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.9, shadowRadius: 8 },
  masteryCount: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: FONT_SEMI, marginTop: 10, textAlign: 'center' },

  sectionLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 2, marginBottom: 12, marginTop: 6 },

  // Browse-by-area tiles (landing)
  tileGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  tile: {
    width: '48%', borderRadius: 18, borderWidth: 1.5, paddingVertical: 18, paddingHorizontal: 14,
    marginBottom: 12, alignItems: 'center',
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.28, shadowRadius: 12,
  },
  tileEmoji: { fontSize: 34, marginBottom: 8 },
  tileLabel: { color: '#fff', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 0.5, marginBottom: 10 },
  tileBar: { width: '80%', height: 5, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.12)', overflow: 'hidden' },
  tileBarFill: { height: '100%', borderRadius: 3 },
  tileProgress: { color: 'rgba(255,255,255,0.55)', fontSize: 11, fontFamily: FONT_BOLD, marginTop: 7, letterSpacing: 0.5 },

  // Category detail (level 2)
  catDetailHead: {
    flexDirection: 'row', alignItems: 'center', borderRadius: 18, borderWidth: 1.5,
    padding: 16, marginBottom: 18,
  },
  catDetailTitle: { fontSize: 22, fontFamily: FONT_BOLD, letterSpacing: 1 },
  catDetailSub: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: FONT_SEMI, marginTop: 3 },

  featuredCard: {
    backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 22, borderWidth: 1.5,
    padding: 20, marginBottom: 24, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.4, shadowRadius: 20,
  },
  // Matches the guide reader's own title treatment, so opening a guide feels
  // like the same headline growing rather than switching typeface mid-flow.
  featuredTitle: { color: '#fff', fontSize: 21, fontFamily: SERIF_BOLD, lineHeight: 27, letterSpacing: -0.3, marginBottom: 6 },
  featuredHook: { color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 20, marginBottom: 16 },
  featuredCta: { alignSelf: 'flex-start', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 50, paddingHorizontal: 18, paddingVertical: 9 },
  featuredCtaText: { color: '#fff', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 1 },

  chipsRow: { marginBottom: 18 },
  chip: {
    paddingHorizontal: 14, paddingVertical: 9, borderRadius: 50,
    backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
  },
  chipText: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: FONT_BOLD },

  card: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    borderRadius: 18, borderWidth: 1.5,
    padding: 14, marginBottom: 12,
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.45, shadowRadius: 14, elevation: 6,
  },
  cardDone: {},
  cardIcon: { width: 44, height: 44, borderRadius: 12, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  cardTopRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 5 },
  cardMeta: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: FONT_BOLD, flex: 1 },
  cardTitle: { color: '#fff', fontSize: 15, fontFamily: FONT_BOLD, lineHeight: 19, marginBottom: 3 },
  cardHook: { color: 'rgba(255,255,255,0.45)', fontSize: 12 },

  unlockBanner: {
    marginTop: 14, backgroundColor: 'rgba(191,0,255,0.08)', borderRadius: 18,
    borderWidth: 1.5, borderColor: 'rgba(191,0,255,0.4)', padding: 20, alignItems: 'center',
  },
  unlockTitle: { color: '#ff4dd2', fontSize: 17, fontFamily: FONT_BOLD, marginBottom: 6 },
  unlockSub: { color: 'rgba(255,255,255,0.6)', fontSize: 13, textAlign: 'center', lineHeight: 19, marginBottom: 16 },
  unlockBtn: { backgroundColor: '#ff4dd2', borderRadius: 50, paddingHorizontal: 30, paddingVertical: 12 },
  unlockBtnText: { color: '#fff', fontSize: 14, fontFamily: FONT_BOLD, letterSpacing: 1.5 },

  // Reader
  readerContainer: { flex: 1, backgroundColor: '#000' },
  readerHero: {
    marginHorizontal: 20, marginTop: 8, marginBottom: 24, padding: 22, borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1.5,
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.4, shadowRadius: 20,
  },
  readerEmoji: { fontSize: 52, marginBottom: 12 },
  readerBadgeRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12 },
  tierPill: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, borderWidth: 1 },
  tierPillText: { fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 1 },
  readerMeta: { color: 'rgba(255,255,255,0.5)', fontSize: 12, fontFamily: FONT_BOLD },
  // Serif here on purpose: the guide/task title is a display moment at 24px
  // (see the SERIF_* note at the top), which is exactly where Fraunces earns
  // its character — Inter read flat and generic on these headers.
  readerTitle: { color: '#fff', fontSize: 25, fontFamily: SERIF_BOLD, lineHeight: 32, letterSpacing: -0.3, marginBottom: 8 },
  readerHook: { fontSize: 15, fontFamily: FONT_BOLD, lineHeight: 21 },

  section: { marginHorizontal: 20, marginBottom: 22 },
  sectionHeadRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 9 },
  sectionNum: { width: 28, height: 28, borderRadius: 9, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  sectionNumText: { fontSize: 14, fontFamily: FONT_BOLD },
  // Section headings echo the title's serif one step down, so a guide reads as
  // one typographic voice instead of sans headings under a serif title.
  sectionHeading: { color: '#fff', fontSize: 18, fontFamily: SERIF_SEMI, letterSpacing: -0.2, flex: 1 },
  // Body stays Inter — long-form legibility beats character below ~15px.
  sectionBody: { color: 'rgba(255,255,255,0.72)', fontSize: 15, lineHeight: 23 },

  readerFooter: {
    position: 'absolute', left: 0, right: 0, bottom: 0, paddingHorizontal: 20,
    paddingTop: 14, paddingBottom: 30, backgroundColor: 'rgba(0,0,0,0.92)',
    borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)',
  },
  completeBtn: {
    borderRadius: 16, paddingVertical: 17, alignItems: 'center',
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.8, shadowRadius: 20,
  },
  completeBtnDone: { backgroundColor: 'rgba(67,224,255,0.12)', borderWidth: 1.5, borderColor: '#43e0ff' },
  completeText: { color: '#000', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 1 },
  completeDoneText: { color: '#43e0ff', fontSize: 14, fontFamily: FONT_BOLD, letterSpacing: 1 },
});

// ---------------------------------------------------------------------------
// First-launch tutorial — dims the screen and spotlights one area at a time
// ---------------------------------------------------------------------------
type TutorialStep = {
  badge: string;
  title: string;
  body: string;
  color: string;
  target: { x: number; y: number; w: number; h: number; r: number } | null;
};

function TutorialOverlay({ steps, onDone }: { steps: TutorialStep[]; onDone: () => void }) {
  const [i, setI] = useState(0);
  const step = steps[i];
  const t = step.target;
  const last = i === steps.length - 1;

  const next = () => {
    Haptics.selectionAsync().catch(() => {});
    if (last) onDone();
    else setI(i + 1);
  };

  // Place the tooltip away from whatever is spotlighted.
  let cardTop: number;
  if (!t) cardTop = height / 2 - 150;
  else if (t.y > height / 2) cardTop = Math.max(90, t.y - 250);
  else cardTop = Math.min(height - 320, t.y + t.h + 28);

  return (
    <View style={tut.overlay} pointerEvents="auto">
      <Svg width={width} height={height} style={StyleSheet.absoluteFill}>
        <Defs>
          <Mask id="tutMask">
            <Rect x={0} y={0} width={width} height={height} fill="white" />
            {t && <Rect x={t.x} y={t.y} width={t.w} height={t.h} rx={t.r} fill="black" />}
          </Mask>
        </Defs>
        <Rect x={0} y={0} width={width} height={height} fill="rgba(0,0,0,0.86)" mask="url(#tutMask)" />
        {t && (
          <Rect x={t.x} y={t.y} width={t.w} height={t.h} rx={t.r}
            fill="none" stroke={step.color} strokeWidth={3} />
        )}
      </Svg>

      <View style={[tut.card, { top: cardTop, borderColor: step.color + '66', shadowColor: step.color }]}>
        <Text style={[tut.badge, { color: step.color }]}>{step.badge}</Text>
        <Text style={tut.title}>{step.title}</Text>
        <Text style={tut.body}>{step.body}</Text>
        <View style={tut.dots}>
          {steps.map((_, di) => (
            <View key={di} style={[tut.dot, di === i && { backgroundColor: step.color, width: 18 }]} />
          ))}
        </View>
        <TouchableOpacity style={[tut.nextBtn, { backgroundColor: step.color }]} onPress={next} activeOpacity={0.85}>
          <Text style={tut.nextText}>{last ? "LET'S GO" : 'NEXT'}</Text>
        </TouchableOpacity>
        {!last && (
          <TouchableOpacity onPress={onDone} activeOpacity={0.7} style={tut.skipBtn}>
            <Text style={tut.skipText}>Skip tour</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const tut = StyleSheet.create({
  overlay: { ...StyleSheet.absoluteFillObject, zIndex: 999 },
  card: {
    position: 'absolute', left: 22, right: 22, backgroundColor: '#0b0b12',
    borderRadius: 22, borderWidth: 1.5, padding: 22,
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.7, shadowRadius: 24, elevation: 12,
  },
  badge: { fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 2, marginBottom: 8 },
  title: { color: '#fff', fontSize: 22, fontFamily: FONT_BOLD, marginBottom: 8 },
  body: { color: 'rgba(255,255,255,0.7)', fontSize: 15, lineHeight: 22, marginBottom: 18 },
  dots: { flexDirection: 'row', gap: 6, marginBottom: 18 },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.25)' },
  nextBtn: { borderRadius: 14, paddingVertical: 15, alignItems: 'center' },
  nextText: { color: '#000', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 1.5 },
  skipBtn: { alignItems: 'center', marginTop: 12 },
  skipText: { color: 'rgba(255,255,255,0.4)', fontSize: 13, fontFamily: FONT_SEMI },
});

// ---------------------------------------------------------------------------
// Login / Sign-up gate
// ---------------------------------------------------------------------------
// Local-only auth for now: the account + session live in AsyncStorage so the
// user stays signed in across launches. Swap this for a real backend (or Sign
// in with Apple) before App Store launch — the surface is just onAuthed().
// The looping poses demoed in the Wii-style how-to card — mirror the guided
// scan positions so first-timers know exactly what's coming.
const HOWTO_POSES: { caption: string; dir: 'front' | 'left' | 'right' | 'down' }[] = [
  { caption: 'Face the camera', dir: 'front' },
  { caption: 'Turn your head fully left', dir: 'left' },
  { caption: 'Turn your head fully right', dir: 'right' },
];

// Friendly coach mascot (Duolingo-style): a simple geometric character that bobs.
// Kept intentionally minimal/geometric so it reads clean rather than janky.
function CoachMascot({ size = 40 }: { size?: number }) {
  const bob = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(Animated.sequence([
      Animated.timing(bob, { toValue: 1, duration: 700, useNativeDriver: true }),
      Animated.timing(bob, { toValue: 0, duration: 700, useNativeDriver: true }),
    ]));
    loop.start();
    return () => loop.stop();
  }, [bob]);
  const ty = bob.interpolate({ inputRange: [0, 1], outputRange: [0, -4] });
  return (
    <Animated.View style={{ transform: [{ translateY: ty }] }}>
      <Svg width={size} height={size} viewBox="0 0 48 48">
        <Circle cx="24" cy="25" r="18" fill={PRIMARY_BLUE} />
        <Circle cx="24" cy="25" r="18" fill="none" stroke="#fff" strokeWidth="2" opacity={0.85} />
        {/* antenna */}
        <Line x1="24" y1="7" x2="24" y2="2" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <Circle cx="24" cy="2.5" r="2" fill="#43e0ff" />
        {/* eyes */}
        <Circle cx="18" cy="22" r="4.2" fill="#fff" />
        <Circle cx="30" cy="22" r="4.2" fill="#fff" />
        <Circle cx="18.5" cy="22.5" r="2" fill="#0a2540" />
        <Circle cx="30.5" cy="22.5" r="2" fill="#0a2540" />
        {/* smile */}
        <Path d="M17 31 Q24 37 31 31" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
      </Svg>
    </Animated.View>
  );
}

// Wii-style head silhouette. Distinct shapes per pose so left/right read as a
// real ~90° HEAD TURN (profile: nose out one side, back-of-head ear on the
// other) — not an in-plane tilt. Down = a chin-down nod.
function HowToSilhouette({ dir }: { dir: 'front' | 'left' | 'right' | 'down' }) {
  const FILL = '#1c6fb0';
  const FACE = '#bfe3f5';
  const ARROW = '#ff7a00';
  return (
    <Svg width={150} height={160} viewBox="0 0 120 140">
      {/* shoulders + neck (static) */}
      <Path d="M16 140 C16 110 40 99 60 99 C80 99 104 110 104 140 Z" fill={FILL} opacity={0.92} />
      <Rect x="50" y="82" width="20" height="20" rx="6" fill={FILL} />

      {dir === 'front' && (
        <>
          <Circle cx="60" cy="52" r="34" fill={FILL} />
          <Circle cx="24" cy="54" r="6" fill={FILL} />
          <Circle cx="96" cy="54" r="6" fill={FILL} />
          <Circle cx="50" cy="48" r="3.2" fill={FACE} />
          <Circle cx="70" cy="48" r="3.2" fill={FACE} />
        </>
      )}

      {dir === 'left' && (
        <>
          {/* turned left: face/nose on the left, back-of-head ear on the right */}
          <Circle cx="62" cy="52" r="34" fill={FILL} />
          <Circle cx="92" cy="54" r="6" fill={FILL} />
          <Path d="M30 45 L30 61 L13 53 Z" fill={FILL} />
          <Circle cx="48" cy="47" r="3.2" fill={FACE} />
          <Path d="M48 12 L22 12 M22 12 l9 -6 m-9 6 l9 6" fill="none" stroke={ARROW} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}

      {dir === 'right' && (
        <>
          <Circle cx="58" cy="52" r="34" fill={FILL} />
          <Circle cx="28" cy="54" r="6" fill={FILL} />
          <Path d="M90 45 L90 61 L107 53 Z" fill={FILL} />
          <Circle cx="72" cy="47" r="3.2" fill={FACE} />
          <Path d="M72 12 L98 12 M98 12 l-9 -6 m9 6 l-9 6" fill="none" stroke={ARROW} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}

      {dir === 'down' && (
        <>
          {/* chin-down: crown of the head faces the camera and the eyes sit low
              and foreshortened — the read is "looking down", not up. */}
          <Circle cx="60" cy="56" r="34" fill={FILL} />
          <Circle cx="26" cy="58" r="6" fill={FILL} />
          <Circle cx="94" cy="58" r="6" fill={FILL} />
          {/* lighter crown cap = top of head toward camera */}
          <Path d="M28 48 Q60 16 92 48 Z" fill={FACE} opacity={0.5} />
          {/* eyes pushed low */}
          <Circle cx="50" cy="72" r="3" fill={FACE} />
          <Circle cx="70" cy="72" r="3" fill={FACE} />
          <Path d="M60 12 L60 34 M60 34 l-7 -8 m7 8 l7 -8" fill="none" stroke={ARROW} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </Svg>
  );
}

// First-time scan tutorial — a nostalgic Wii-style instruction card that loops
// through the head positions, narrated by the coach mascot.
function ScanHowToCard({ onDismiss }: { onDismiss: () => void }) {
  const [frame, setFrame] = useState(0);
  const pop = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const id = setInterval(() => {
      setFrame(f => (f + 1) % HOWTO_POSES.length);
      pop.setValue(0.7);
      Animated.spring(pop, { toValue: 1, friction: 5, tension: 120, useNativeDriver: true }).start();
    }, 1200);
    return () => clearInterval(id);
  }, [pop]);
  const pose = HOWTO_POSES[frame];
  return (
    <View style={styles.howToOverlay}>
      <View style={styles.howToCard}>
        <View style={styles.howToHeader}>
          <CoachMascot size={38} />
          <Text style={styles.howToHeaderText}>HOW TO SCAN</Text>
        </View>
        <View style={styles.howToBody}>
          <Animated.View style={{ transform: [{ scale: pop }] }}>
            <HowToSilhouette dir={pose.dir} />
          </Animated.View>
          <Text style={styles.howToCaption}>{pose.caption}</Text>
          <View style={styles.howToDots}>
            {HOWTO_POSES.map((_, i) => (
              <View key={i} style={[styles.howToDot, i === frame && styles.howToDotActive]} />
            ))}
          </View>
        </View>
        <View style={styles.howToTips}>
          <Text style={styles.howToTipsTitle}>FOR AN ACCURATE SCAN</Text>
          {[
            'Face a window or light — never behind you',
            'Wipe your camera lens',
            'Remove glasses, hats, hair off your face',
            'Phone at eye level, about arm’s length',
          ].map((tip) => (
            <View key={tip} style={styles.howToTipRow}>
              <Ionicons name="checkmark-circle" size={16} color="#43e0ff" style={styles.howToTipCheck} />
              <Text style={styles.howToTipText}>{tip}</Text>
            </View>
          ))}
        </View>
        <Text style={styles.howToHint}>Hold still — each angle is captured automatically.</Text>
        <TouchableOpacity style={styles.howToButton} onPress={onDismiss} activeOpacity={0.85}>
          <Text style={styles.howToButtonText}>GOT IT</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// Data-driven onboarding questions. Order = step order. Swap/extend freely —
// the component renders whatever is here. `single` = pick one, `multi` = pick many.
type OnbQuestion = {
  key: keyof Profile | 'goals';
  type: 'single' | 'multi';
  title: string;
  subtitle?: string;
  options: { label: string; value: string }[];
};
const ONBOARDING_QUESTIONS: OnbQuestion[] = [
  {
    key: 'gender', type: 'single',
    title: "What's your gender?",
    subtitle: 'We tailor your analysis and plan to you.',
    options: [
      { label: 'Male', value: 'male' },
      { label: 'Female', value: 'female' },
      { label: 'Other', value: 'other' },
    ],
  },
  {
    key: 'skinType', type: 'single',
    title: "What's your current routine?",
    subtitle: 'Helps us pitch recs at the right level.',
    options: [
      { label: 'No routine yet', value: 'no routine' },
      { label: 'Basic grooming', value: 'basic' },
      { label: 'Consistent routine', value: 'consistent' },
      { label: 'Advanced', value: 'advanced' },
    ],
  },
  {
    key: 'ethnicity', type: 'single',
    title: 'Which best describes you?',
    subtitle: 'Helps us compare your features fairly.',
    options: [
      { label: 'East Asian', value: 'East Asian' },
      { label: 'South Asian', value: 'South Asian' },
      { label: 'Black / African', value: 'Black or African' },
      { label: 'White / European', value: 'White or European' },
      { label: 'Hispanic / Latino', value: 'Hispanic or Latino' },
      { label: 'Middle Eastern', value: 'Middle Eastern' },
      { label: 'Mixed / Other', value: 'Mixed or Other' },
    ],
  },
  {
    key: 'goals', type: 'multi',
    title: "What's your goal?",
    subtitle: 'Pick all that apply.',
    options: [
      { label: 'Sharper jawline', value: 'sharper jawline' },
      { label: 'Clearer skin', value: 'clearer skin' },
      { label: 'Better hair', value: 'better hair' },
      { label: 'Defined cheekbones', value: 'defined cheekbones' },
      { label: 'Brighter eyes', value: 'brighter eyes' },
      { label: 'Overall harmony', value: 'overall harmony' },
    ],
  },
];

// Full-screen, multi-step onboarding shown before login on first launch. Collects
// the Profile that feeds the Claude scoring prompt + tailored recommendations.
function OnboardingQuestionnaire({ onDone }: { onDone: (p: Profile) => void }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const q = ONBOARDING_QUESTIONS[step];
  const isLast = step === ONBOARDING_QUESTIONS.length - 1;

  const selected = answers[q.key as string];
  const answered = q.type === 'multi'
    ? Array.isArray(selected) && selected.length > 0
    : typeof selected === 'string' && selected.length > 0;

  const choose = (value: string) => {
    Haptics.selectionAsync().catch(() => {});
    setAnswers(prev => {
      if (q.type === 'multi') {
        const cur = Array.isArray(prev[q.key as string]) ? (prev[q.key as string] as string[]) : [];
        const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
        return { ...prev, [q.key as string]: next };
      }
      return { ...prev, [q.key as string]: value };
    });
  };

  const next = () => {
    if (!answered) return;
    Haptics.selectionAsync().catch(() => {});
    if (!isLast) { setStep(s => s + 1); return; }
    const profile: Profile = {
      gender: (answers.gender as Profile['gender']) ?? 'other',
      skinType: (answers.skinType as string) ?? '',
      ethnicity: (answers.ethnicity as string) ?? '',
      goals: Array.isArray(answers.goals) ? (answers.goals as string[]) : [],
      completedAt: Date.now(),
    };
    AsyncStorage.setItem(STORAGE.profile, JSON.stringify(profile)).catch(() => {});
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    onDone(profile);
  };

  const isSel = (value: string) =>
    q.type === 'multi'
      ? Array.isArray(selected) && selected.includes(value)
      : selected === value;

  return (
    <View style={styles.onbContainer}>
      {/* Progress bar */}
      <View style={styles.onbProgressTrack}>
        <View style={[styles.onbProgressFill, { width: `${((step + 1) / ONBOARDING_QUESTIONS.length) * 100}%` }]} />
      </View>

      <View style={styles.onbHeaderRow}>
        {step > 0 ? (
          <TouchableOpacity onPress={() => { Haptics.selectionAsync().catch(() => {}); setStep(s => s - 1); }} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Text style={styles.onbBack}>←</Text>
          </TouchableOpacity>
        ) : <View style={{ width: 24 }} />}
        <Text style={styles.onbStepCount}>{step + 1} / {ONBOARDING_QUESTIONS.length}</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView contentContainerStyle={styles.onbScroll} showsVerticalScrollIndicator={false}>
        <Text style={styles.onbTitle}>{q.title}</Text>
        {q.subtitle ? <Text style={styles.onbSubtitle}>{q.subtitle}</Text> : null}

        <View style={styles.onbOptions}>
          {q.options.map(opt => {
            const sel = isSel(opt.value);
            return (
              <TouchableOpacity
                key={opt.value}
                style={[styles.onbOption, sel && styles.onbOptionSel]}
                onPress={() => choose(opt.value)}
                activeOpacity={0.85}
              >
                <Text style={[styles.onbOptionText, sel && styles.onbOptionTextSel]}>{opt.label}</Text>
                {sel && <Ionicons name="checkmark" size={18} color={ACCENT} style={styles.onbCheck} />}
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>

      <TouchableOpacity
        style={[styles.onbContinue, !answered && styles.onbContinueDisabled]}
        onPress={next}
        disabled={!answered}
        activeOpacity={0.85}
      >
        <Text style={[styles.onbContinueText, !answered && { color: 'rgba(255,255,255,0.4)' }]}>
          {isLast ? 'FINISH' : 'CONTINUE'}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

// `postScan` = the account prompt shown AFTER the results teaser (new
// scan-first onboarding): same auth mechanics, but the copy sells saving the
// scan they just invested in, and "guest" reads as a low-key "Skip for now".
function LoginScreen({ onAuthed, postScan = false }: { onAuthed: (name: string) => void; postScan?: boolean }) {
  const [mode, setMode] = useState<'login' | 'signup'>('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [appleAvailable, setAppleAvailable] = useState(false);

  // Sign in with Apple is iOS 13+ only — check before showing the button.
  useEffect(() => {
    AppleAuthentication.isAvailableAsync().then(setAppleAvailable).catch(() => setAppleAvailable(false));
  }, []);

  const signInWithApple = async () => {
    setError('');
    try {
      const cred = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });
      // Apple only returns name/email on the FIRST sign-in; reuse stored values
      // on later sign-ins (Apple gives us just the stable user id then).
      const existingRaw = await AsyncStorage.getItem(STORAGE.account);
      const existing = existingRaw ? JSON.parse(existingRaw) : null;
      const appleName = cred.fullName?.givenName ?? existing?.name ?? '';
      const account = {
        name: appleName,
        email: cred.email ?? existing?.email ?? '',
        appleUserId: cred.user,
        provider: 'apple',
      };
      await AsyncStorage.setItem(STORAGE.account, JSON.stringify(account));
      await AsyncStorage.setItem(STORAGE.session, cred.user);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
      onAuthed(appleName);
    } catch (e) {
      // User tapped cancel on the Apple sheet — not an error worth showing.
      if ((e as { code?: string }).code === 'ERR_REQUEST_CANCELED') return;
      setError('Apple sign-in failed. Please try again.');
    }
  };

  const submit = async () => {
    setError('');
    const em = email.trim().toLowerCase();
    if (mode === 'signup' && name.trim().length < 2) { setError('Enter your name.'); return; }
    if (!em || !em.includes('@') || !em.includes('.')) { setError('Enter a valid email.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setBusy(true);
    try {
      if (mode === 'signup') {
        const account = { name: name.trim(), email: em, password };
        await AsyncStorage.setItem(STORAGE.account, JSON.stringify(account));
        await AsyncStorage.setItem(STORAGE.session, em);
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
        onAuthed(account.name);
      } else {
        const raw = await AsyncStorage.getItem(STORAGE.account);
        const acct = raw ? JSON.parse(raw) : null;
        if (!acct || acct.email !== em) { setError('No account found for that email. Sign up first.'); setBusy(false); return; }
        if (acct.password !== password) { setError('Incorrect password.'); setBusy(false); return; }
        await AsyncStorage.setItem(STORAGE.session, em);
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
        onAuthed(acct.name);
      }
    } catch {
      setError('Something went wrong. Try again.');
      setBusy(false);
    }
  };

  const continueAsGuest = async () => {
    await AsyncStorage.setItem(STORAGE.session, 'guest').catch(() => {});
    Haptics.selectionAsync().catch(() => {});
    onAuthed('');
  };

  return (
    <View style={lstyles.container}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          contentContainerStyle={lstyles.scroll}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <Text style={lstyles.brand}>MAXIFY</Text>
          <Text style={lstyles.tagline}>{postScan ? 'Your scan is done — save your results.' : 'Unlock your full potential.'}</Text>
          {postScan && (
            <View style={lstyles.benefitList}>
              {[
                'Keep your scores & scan history',
                'Track your progress week over week',
                'Sync your streak and 8-week plan',
              ].map((b, i) => (
                <View key={i} style={lstyles.benefitRow}>
                  <Ionicons name="checkmark-circle" size={15} color={ACCENT} />
                  <Text style={lstyles.benefitText}>{b}</Text>
                </View>
              ))}
            </View>
          )}

          {appleAvailable && (
            <>
              <AppleAuthentication.AppleAuthenticationButton
                buttonType={AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN}
                buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.WHITE}
                cornerRadius={14}
                style={lstyles.appleBtn}
                onPress={signInWithApple}
              />
              <View style={lstyles.dividerRow}>
                <View style={lstyles.dividerLine} />
                <Text style={lstyles.dividerText}>or use email</Text>
                <View style={lstyles.dividerLine} />
              </View>
            </>
          )}

          <View style={lstyles.toggleRow}>
            <TouchableOpacity
              style={[lstyles.toggleBtn, mode === 'signup' && lstyles.toggleBtnActive]}
              onPress={() => { setMode('signup'); setError(''); Haptics.selectionAsync().catch(() => {}); }}
              activeOpacity={0.8}>
              <Text style={[lstyles.toggleText, mode === 'signup' && lstyles.toggleTextActive]}>SIGN UP</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[lstyles.toggleBtn, mode === 'login' && lstyles.toggleBtnActive]}
              onPress={() => { setMode('login'); setError(''); Haptics.selectionAsync().catch(() => {}); }}
              activeOpacity={0.8}>
              <Text style={[lstyles.toggleText, mode === 'login' && lstyles.toggleTextActive]}>LOG IN</Text>
            </TouchableOpacity>
          </View>

          {mode === 'signup' && (
            <TextInput
              style={lstyles.input}
              placeholder="Name"
              placeholderTextColor="rgba(255,255,255,0.35)"
              value={name}
              onChangeText={setName}
              autoCapitalize="words"
              returnKeyType="next"
            />
          )}
          <TextInput
            style={lstyles.input}
            placeholder="Email"
            placeholderTextColor="rgba(255,255,255,0.35)"
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            autoCorrect={false}
            returnKeyType="next"
          />
          <TextInput
            style={lstyles.input}
            placeholder="Password"
            placeholderTextColor="rgba(255,255,255,0.35)"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            returnKeyType="done"
            onSubmitEditing={submit}
          />

          {error ? <Text style={lstyles.error}>{error}</Text> : null}

          <TouchableOpacity
            style={[lstyles.primaryBtn, busy && { opacity: 0.6 }]}
            onPress={submit}
            disabled={busy}
            activeOpacity={0.85}>
            <Text style={lstyles.primaryBtnText}>
              {mode === 'signup' ? 'CREATE ACCOUNT' : 'LOG IN'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity onPress={continueAsGuest} activeOpacity={0.7} style={lstyles.guestBtn}>
            <Text style={lstyles.guestText}>{postScan ? 'Skip for now' : 'Continue as guest'}</Text>
          </TouchableOpacity>

          <Text style={lstyles.legal}>
            By continuing you agree to our Terms & Privacy Policy.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const lstyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  scroll: { flexGrow: 1, justifyContent: 'center', paddingHorizontal: 32, paddingVertical: 60 },
  brand: {
    color: PRIMARY_BLUE, fontSize: 44, fontFamily: FONT_BOLD, letterSpacing: 12, textAlign: 'center',
    textShadowColor: PRIMARY_BLUE, textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 28,
  },
  tagline: {
    color: 'rgba(255,255,255,0.55)', fontSize: 15, fontFamily: FONT_SEMI,
    textAlign: 'center', marginTop: 10, marginBottom: 38,
  },
  appleBtn: { height: 52, width: '100%', marginBottom: 18 },
  dividerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 22 },
  dividerLine: { flex: 1, height: 1, backgroundColor: 'rgba(255,255,255,0.12)' },
  dividerText: { color: 'rgba(255,255,255,0.4)', fontSize: 12, fontFamily: FONT_SEMI, marginHorizontal: 12 },
  // Post-scan account prompt: why creating an account is worth it.
  benefitList: { alignSelf: 'center', gap: 8, marginTop: -14, marginBottom: 26 },
  benefitRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  benefitText: { color: 'rgba(255,255,255,0.75)', fontSize: 13.5, fontFamily: FONT_SEMI },
  toggleRow: {
    flexDirection: 'row', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 14,
    padding: 4, marginBottom: 24,
  },
  toggleBtn: { flex: 1, paddingVertical: 12, borderRadius: 11, alignItems: 'center' },
  toggleBtnActive: {
    backgroundColor: 'rgba(0,191,255,0.16)',
    borderWidth: 1, borderColor: 'rgba(0,191,255,0.5)',
  },
  toggleText: { color: 'rgba(255,255,255,0.45)', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 2 },
  toggleTextActive: { color: PRIMARY_BLUE },
  input: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 14, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)', paddingHorizontal: 18, paddingVertical: 16,
    color: '#fff', fontSize: 16, marginBottom: 14,
  },
  error: { color: '#FF5A5A', fontSize: 13, fontFamily: FONT_SEMI, marginBottom: 10, marginLeft: 4 },
  primaryBtn: {
    backgroundColor: PRIMARY_BLUE, borderRadius: 16, paddingVertical: 18, alignItems: 'center',
    marginTop: 8, shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8, shadowRadius: 22,
  },
  primaryBtnText: { color: '#000', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 2 },
  guestBtn: { marginTop: 22, alignItems: 'center' },
  guestText: { color: 'rgba(255,255,255,0.5)', fontSize: 14, fontFamily: FONT_SEMI, textDecorationLine: 'underline' },
  legal: {
    color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center',
    marginTop: 34, lineHeight: 16, paddingHorizontal: 10,
  },
});

export default function ScannerScreen() {
  const { hasPermission, requestPermission } = useCameraPermission();
  // useCameraDevice can transiently report `undefined` on a re-render (camera
  // interruption, Control Center takeover, device-list refresh) even after
  // already resolving a real device. If that momentary `undefined` reaches the
  // `!device` early-return below, the whole persistent <VisionCamera> tree
  // unmounts while VisionCamera's own session-teardown thread is mid
  // `stopRunning` — the two racing teardowns trip an AVFoundation assertion
  // (`detachFromFigCaptureSession`) and SIGABRTs. Caching the last resolved
  // device means a transient blip never tears the camera down.
  const rawDevice = useCameraDevice('front');
  const deviceRef = useRef(rawDevice);
  if (rawDevice) deviceRef.current = rawDevice;
  const device = rawDevice ?? deviceRef.current;
  const isFocused = useIsFocused();
  // Fire Apple's native camera prompt automatically the first time we need the
  // camera — no custom "grant" button tap required. iOS only shows the system
  // dialog once; if already denied, this resolves silently and the fallback
  // screen sends the user to Settings instead.
  const askedCamRef = useRef(false);
  useEffect(() => {
    if (!hasPermission && !askedCamRef.current) {
      askedCamRef.current = true;
      requestPermission().catch(() => {});
    }
  }, [hasPermission, requestPermission]);
  const [appActive, setAppActive] = useState(true);
  const [scanError, setScanError] = useState(false); // flashes when analysis fails (no fake scores)
  const [badScan, setBadScan] = useState(false); // flashes when no clear face was found in the photo
  const [badScanReason, setBadScanReason] = useState(''); // specific coaching for the bad scan (dark/blurry/etc.)
  const [tab, setTab] = useState('home');
  // Directional tab transition: the incoming tab slides ~14px in from the side
  // it sits on (per TAB_ORDER) while fading up. Values are shared by the four
  // non-scan tab wrappers; the Scan tab is exempt (its camera layer is a
  // persistent root-mounted view — see "Persistent camera layer").
  const tabSlide = useRef(new Animated.Value(0)).current;
  const tabFade = useRef(new Animated.Value(1)).current;
  const switchTab = (next: string) => {
    if (next === tab) return;
    const dir = Math.sign(TAB_ORDER.indexOf(next) - TAB_ORDER.indexOf(tab));
    tabSlide.setValue(14 * dir);
    tabFade.setValue(0);
    setTab(next);
    Animated.parallel([
      Animated.timing(tabSlide, { toValue: 0, duration: 200, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.timing(tabFade, { toValue: 1, duration: 200, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
    ]).start();
  };
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [showResults, setShowResults] = useState(false);
  // The results sheet is kept mounted (empty) whenever there's a scan so an
  // upward drag has something to live-follow — but its HEAVY content (base64
  // photos, SVG breakdown, before/after slider) mounts only once the sheet is
  // actually open or being dragged. That keeps switching TO the Scan tab snappy
  // (the old always-mounted content cost ~a couple seconds and a chunk of
  // memory on every tab switch) instead of decoding full-res images off-screen.
  const [sheetContentMounted, setSheetContentMounted] = useState(false);
  // When opened via the Results tab's "View full breakdown" button we hide the
  // score bubbles (the Results tab already leads with the scores) — swipe-up on
  // the Scan page still shows the full sheet, bubbles included.
  const [hideResultBubbles, setHideResultBubbles] = useState(false);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [subtraits, setSubtraits] = useState<Record<string, SubTrait[]>>({});
  const [harmony, setHarmony] = useState<HarmonyMetric[]>([]);
  const [frontPhotoBase64, setFrontPhotoBase64] = useState<string | null>(null);
  // Real detected face position/size for the front photo (normalized 0-1
  // fractions of the capture frame), snapshotted at the moment of capture —
  // lets the biometric face map anchor its mesh/pins to the actual face
  // instead of assuming a fixed framing. Null when detection missed that shot.
  const [frontFaceBox, setFrontFaceBox] = useState<FaceBox | null>(null);
  // $40 human-edited photo order — a one-time RevenueCat purchase (not the
  // "pro" entitlement). `photoOrdered` mirrors hasOrderedHumanPhoto() so the
  // UI shows "order received" instead of the purchase CTA after buying, even
  // across relaunches/reinstalls (RevenueCat is the source of truth, not
  // local storage). Fulfillment is manual email-out — see
  // app/api/order-photo+api.ts.
  const [photoOrdered, setPhotoOrdered] = useState(false);
  // Whether the $40 product actually exists in the store yet. Apple requires a
  // first non-consumable IAP to be reviewed with a new app version, so this
  // code can ship over-the-air before the product is purchasable — in that
  // window we hide the offer entirely rather than show a CTA that can only
  // fail. Flips to true by itself once the IAP is approved.
  const [photoOfferAvailable, setPhotoOfferAvailable] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderErr, setOrderErr] = useState('');
  const [showPotentialAgeGate, setShowPotentialAgeGate] = useState(false);
  const potentialAgeOkRef = useRef(false); // one-time 18+ confirmation, persisted
  const [scanStep, setScanStep] = useState(0); // which guided position (0-3) is active
  const [countdown, setCountdown] = useState<number | null>(null); // 3-2-1 before a manual capture; null = idle
  const [capturing, setCapturing] = useState(false); // true from tap → capture (disables the button)
  const shotsRef = useRef<string[]>([]); // captures accumulated across the manual tap-to-capture steps
  const [analyzing, setAnalyzing] = useState(false);

  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  // Which task instances have already paid out XP/lifetime credit TODAY. Separate
  // from `completed` (which stays freely toggleable, e.g. to undo a mis-tap) so
  // unchecking then rechecking the same task can never earn rewards twice.
  const [awardedTasks, setAwardedTasks] = useState<Record<string, boolean>>({});
  const [streakDays, setStreakDays] = useState(0);
  const [program, setProgram] = useState<Program | null>(null);
  // Rescans completed since the current program was built. A rescan no longer
  // wipes the program — it keeps your progress and bumps this counter, which
  // (together with finishing the prior week) is what unlocks the next week.
  const [programRescans, setProgramRescans] = useState(0);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false); // true while a RevenueCat purchase is in flight (spinner on the CTA)
  const [showNewProgramModal, setShowNewProgramModal] = useState(false);
  const [showWeeklyLimitModal, setShowWeeklyLimitModal] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showLegal, setShowLegal] = useState<'privacy' | 'terms' | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Which flow the consent modal should resume into after "I agree".
  const pendingConsentAction = useRef<'scan' | 'import'>('scan');
  // Consent is gated through a ref so startScan() can re-check it synchronously
  // right after the user agrees, without waiting for a state re-render.
  const consentRef = useRef(false);
  // Regional availability (BIPA — see verifyRegion). 'ok' once verified this
  // session; the ref lets startScan re-check synchronously after a retry.
  const [showRegionBlock, setShowRegionBlock] = useState(false);
  const [regionStatus, setRegionStatus] = useState<'blocked' | 'denied'>('blocked');
  const [checkingRegion, setCheckingRegion] = useState(false);
  const regionRef = useRef<'unknown' | 'ok' | 'blocked' | 'denied'>('unknown');
  const [streakCelebration, setStreakCelebration] = useState<number | null>(null);
  // Conversion flow: skippable post-teaser account prompt + the paywall's
  // selected tier. accountNudgedRef makes the automatic prompt fire once, ever.
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);
  // Plain login screen shown after Log Out (the login GATE is gone in the
  // scan-first flow, so without this a logout had no visible effect at all).
  // Distinct from showAccountPrompt: no postScan copy, no paywall chaining.
  const [showLoginScreen, setShowLoginScreen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ProPlan>('weekly');
  const accountNudgedRef = useRef(false);
  // Promo code — a small link on the paywall for comp/reviewer access.
  // Grants Pro locally (persisted, bypasses RevenueCat entirely) on a match.
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const PROMO_CODE = 'ASIGTOUSE';
  // Achievement badges: unlocked ids + the pending unlock celebration.
  const [badges, setBadges] = useState<string[]>([]);
  const badgesLoadedRef = useRef(false);
  const [badgeUnlock, setBadgeUnlock] = useState<{ def: BadgeDef; extra: number } | null>(null);
  // Streak freeze: one missed day per week is forgiven. Notice = streak count
  // that was just saved by a freeze (shows a one-time banner on Home).
  const [freezeAvailable, setFreezeAvailable] = useState(false);
  const [freezeNotice, setFreezeNotice] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  // The slot-machine score reveal shown right after a scan finishes.
  const [revealData, setRevealData] = useState<{ score: number; delta: number; isFirst: boolean; isBest: boolean } | null>(null);
  // Lifetime XP → global Level (tasks + guides). levelUp holds the overlay's target.
  const [xp, setXp] = useState(0);
  const [levelUp, setLevelUp] = useState<number | null>(null);
  // Daily tip on the scan screen — dismissed state persists per calendar day.
  const [tipDismissed, setTipDismissed] = useState(true);
  // Day-1 photo (first scan ever) for the before/after journey on Results.
  const [day1Photo, setDay1Photo] = useState<string | null>(null);
  // Sunday recap / 30-60-90 milestone overlay (one at a time).
  const [statsMoment, setStatsMoment] = useState<StatsMoment | null>(null);
  const [showScanHowTo, setShowScanHowTo] = useState(false);
  const scanHowToSeen = useRef(false); // guards the one-time auto-show
  const [scanCount, setScanCount] = useState(0);
  const [bestScore, setBestScore] = useState<number | null>(null);
  const [journeyStart, setJourneyStart] = useState<number | null>(null); // anchors over-time boost
  const [lifetimeTasks, setLifetimeTasks] = useState(0); // cumulative tasks completed
  const [scanHistory, setScanHistory] = useState<ScanRecord[]>([]);
  const [isSubscribed, setIsSubscribed] = useState(false); // set true by isProActive() below once RevenueCat confirms an active entitlement
  const [authName, setAuthName] = useState<string | null>(null); // null = not signed in
  const [authReady, setAuthReady] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null); // onboarding answers; null = not done
  const [profileReady, setProfileReady] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const hydrated = useRef(false);
  const todayKey = dateKey();

  // Pause the camera when the app is backgrounded (saves battery, avoids the
  // detector running off-screen).
  useEffect(() => {
    const sub = AppState.addEventListener('change', (s) => setAppActive(s === 'active'));
    return () => sub.remove();
  }, []);

  // Set up RevenueCat and restore real Pro status. If RevenueCat isn't
  // configured yet, this is a no-op and the app stays on the free tier.
  useEffect(() => {
    configurePurchases();
    isProActive().then((active) => { if (active) setIsSubscribed(true); });
    hasOrderedHumanPhoto().then((ordered) => { if (ordered) setPhotoOrdered(true); });
    isHumanPhotoAvailable().then(setPhotoOfferAvailable).catch(() => {});
    AsyncStorage.getItem(STORAGE.promoUnlocked)
      .then(v => { if (v === '1') setIsSubscribed(true); })
      .catch(() => {});
  }, []);

  // Promo code submit — a hardcoded comp code that grants Pro locally,
  // persisted so it survives relaunch, entirely independent of RevenueCat.
  const submitPromoCode = () => {
    if (promoCode.trim().toUpperCase() === PROMO_CODE) {
      setIsSubscribed(true);
      AsyncStorage.setItem(STORAGE.promoUnlocked, '1').catch(() => {});
      setShowPromoInput(false);
      setPromoCode('');
      setPromoError('');
      setShowSubscribeModal(false);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    } else {
      setPromoError('Invalid code');
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(() => {});
    }
  };

  // Live face detection was removed: running the detector every frame forced a
  // full-screen re-render on each detection change, which caused a visible
  // glitch over the camera preview. Instead we capture freely and let Claude
  // judge the photo after the fact — if there's no clear face it returns a
  // sentinel and we show a "bad scan, try again" message (see analyzeWithClaude).
  // JPEG capped at ~2.8 MP: full-res 12 MP photos exceed the Claude API's 5 MB
  // image limit, which 400'd every analysis and looked like a network failure.
  // 'jpeg' (not 'native') — iOS native is HEIC, which the API payload mislabels.
  const photoOutput = usePhotoOutput({
    targetResolution: SCAN_PHOTO_RESOLUTION,
    containerFormat: 'jpeg',
    quality: 0.85,
  });

  // ── Hands-free auto-capture (head-pose driven) ──
  // Latest scan state, mirrored into a ref so the (stable) detection callback
  // reads current values without re-subscribing the native output every render.
  const scanLive = useRef<{ scanning: boolean; capturing: boolean; scanStep: number; countdown: number | null }>({
    scanning: false, capturing: false, scanStep: 0, countdown: null,
  });
  const holdStartRef = useRef<number | null>(null); // when the target pose began being held
  const armedRef = useRef(false);                   // gate: one auto-fire per pose
  const captureStepRef = useRef<() => void>(() => {}); // latest captureCurrentStep
  // Most recent detected face bounds (screen px), updated on every detection
  // frame regardless of pose/centering gates — snapshotted into frontFaceBox
  // state when the front-facing shot (step 0) actually captures.
  const lastFaceBoundsRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  // Scan generation counter — bumped on every scan start/cancel/abort. The
  // async capture loop snapshots it at entry and re-checks after every await,
  // so a cancelled/restarted scan can never fire a stale capture, advance a
  // dead step, or analyze a half-cancelled shot set.
  const scanGenRef = useRef(0);
  // Synchronous re-entrancy lock for captureCurrentStep — `capturing` state
  // updates async, so a manual "Capture now" tap landing in the same frame as
  // the head-pose auto-fire could start TWO countdown loops without this.
  const capturingRef = useRef(false);
  // Camera remount key + throttle for the native-error recovery path.
  const [cameraGen, setCameraGen] = useState(0);
  const lastCamRecoverRef = useRef(0);

  // Stable callback (lazy-init ref, not useCallback — reactCompiler is on). Runs
  // ~30x/sec from native but does NO setState: it only tracks a hold timer in a
  // ref and, on a completed hold, fires the capture. That's what keeps this from
  // re-rendering the preview the way the old frame processor did.
  const onFacesDetectedRef = useRef<(faces: Face[]) => void>(undefined);
  if (!onFacesDetectedRef.current) {
    onFacesDetectedRef.current = (faces: Face[]) => {
      const s = scanLive.current;
      if (!s.scanning || s.capturing || s.countdown !== null || !armedRef.current) {
        holdStartRef.current = null;
        return;
      }
      const f = faces[0];
      if (f) lastFaceBoundsRef.current = f.bounds;
      if (!f || !poseMet(SCAN_STEPS[s.scanStep].dir, f.yawAngle, f.pitchAngle)) {
        holdStartRef.current = null;
        return;
      }
      // Centering gate: never auto-fire on a face that's clearly off-center or
      // too far away — off-center captures produce garbage scores. (Manual
      // "Capture now" still works as the escape hatch if detection is off.)
      if (!faceInOval(f.bounds)) {
        holdStartRef.current = null;
        return;
      }
      const now = Date.now();
      if (holdStartRef.current == null) { holdStartRef.current = now; return; }
      if (now - holdStartRef.current >= HEAD_HOLD_MS) {
        armedRef.current = false;      // prevent a second trigger before capturing flips
        holdStartRef.current = null;
        captureStepRef.current();
      }
    };
  }

  // NOTE: useFaceDetectorOutput memoizes on a rest-spread `options` object that
  // is reallocated every render, so it hands back a NEW output object each time.
  // Feeding that to the camera would reconfigure the session on every render
  // (glitch risk). Freeze the first instance in a ref — its callbacks delegate
  // through the library's own stable useRef to our stable onFacesDetected, so
  // the frozen output stays fully live.
  const faceOutputRaw = useFaceDetectorOutput({
    performanceMode: 'fast',
    runLandmarks: false,
    runContours: false,
    runClassifications: false,
    trackingEnabled: false,
    // Scale face bounds to SCREEN coordinates on the native side so the
    // centering gate (faceInOval) can compare against the drawn oval directly.
    autoMode: true,
    windowWidth: width,
    windowHeight: height,
    cameraFacing: 'front',
    onFacesDetected: onFacesDetectedRef.current,
    onError: (e) => console.error('Face detect error:', e),
  });
  const faceOutputRef = useRef(faceOutputRaw);
  const faceOutput = faceOutputRef.current;

  // The camera's outputs array is FROZEN for the app's lifetime. The old
  // `outputs={scanning ? [photo, face] : [photo]}` added/removed an output on
  // every scan start/end, and the library responds to that with a full ASYNC
  // session.configure() teardown/rebuild (each output even contributes a
  // resolution constraint, so the whole format could change). That reconfigure
  // raced the countdown/capture that starts right after — the root cause of
  // mid-scan preview glitches and native crashes. With a constant array the
  // session is configured exactly once. The face detector now runs whenever
  // the camera is active, but its callback early-returns unless a scan is
  // live, so the only cost is some idle MLKit CPU on the scan tab.
  const cameraOutputs = useRef([photoOutput, faceOutput]).current;

  // Re-arm the auto-capture for each new pose; disarm when the scan ends.
  useEffect(() => {
    armedRef.current = scanning;
    holdStartRef.current = null;
  }, [scanStep, scanning]);

  // Restore an existing login session on launch (keeps the user signed in).
  useEffect(() => {
    (async () => {
      try {
        const session = await AsyncStorage.getItem(STORAGE.session);
        if (session) {
          if (session === 'guest') {
            setAuthName('');
          } else {
            const raw = await AsyncStorage.getItem(STORAGE.account);
            const acct = raw ? JSON.parse(raw) : null;
            setAuthName(acct?.name ?? '');
          }
        }
      } catch {
        // ignore — fall through to the login screen
      } finally {
        setAuthReady(true);
      }
      // Restore the onboarding profile (captured before login on first launch).
      try {
        const profRaw = await AsyncStorage.getItem(STORAGE.profile);
        if (profRaw) {
          const p = JSON.parse(profRaw);
          if (p && p.gender) setProfile(p);
        }
      } catch {
        // ignore — fall through to the onboarding questionnaire
      } finally {
        setProfileReady(true);
      }
    })();
  }, []);

  // TODAY stat: tasks for today's day within the week the user is actually on.
  // The program now persists across rescans (progress isn't wiped), so the
  // active week is driven by completion + rescans — NOT the raw calendar week,
  // which would otherwise drift into a still-locked week and leave the Home
  // ring/streak pointing at tasks the user can't reach.
  const todayTasksForStats = (() => {
    if (!program) return [] as ProgramTask[];
    const { dayInWeek } = programCurrentDay(program.generatedAt);
    const firstIncomplete = program.weeks.findIndex(w => w.tasks.some(t => !completed[t.instanceId]));
    const frontier = firstIncomplete === -1 ? program.weeks.length - 1 : firstIncomplete;
    const unlockedCeil = Math.max(0, programRescans); // max 0-indexed week the rescans have opened
    const activeIdx = Math.max(0, Math.min(frontier, unlockedCeil, program.weeks.length - 1));
    const week = program.weeks[activeIdx];
    const hasDays = week.tasks.every(t => typeof t.day === 'number');
    return hasDays ? week.tasks.filter(t => t.day === dayInWeek) : week.tasks;
  })();
  const totalTasks = todayTasksForStats.length;

  // Init sound players + restore unlocked badges once at launch. Detection is
  // gated on badgesLoadedRef so a slow read can't double-award earned badges.
  useEffect(() => {
    initSounds();
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(STORAGE.badges);
        if (raw) {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) setBadges(arr);
        }
      } catch { /* fresh install */ }
      badgesLoadedRef.current = true;
    })();
  }, []);

  // Badge detection — reactive: whenever tracked stats move, unlock anything
  // newly earned, persist, and celebrate (slightly delayed so the overlay
  // never collides with task confetti / the streak celebration).
  useEffect(() => {
    if (!badgesLoadedRef.current) return;
    const perfectDay = totalTasks > 0 && todayTasksForStats.every(t => completed[t.instanceId]);
    const weekConquered = !!program && program.weeks.some(w => w.tasks.length > 0 && w.tasks.every(t => completed[t.instanceId]));
    const ctx: BadgeCtx = {
      scanCount, streakDays, lifetimeTasks,
      level: levelForXp(xp),
      hasPotential: photoOrdered,
      perfectDay, weekConquered,
    };
    const newly = BADGES.filter(b => !badges.includes(b.id) && b.earned(ctx));
    if (newly.length === 0) return;
    const next = [...badges, ...newly.map(b => b.id)];
    setBadges(next);
    AsyncStorage.setItem(STORAGE.badges, JSON.stringify(next)).catch(() => {});
    const star = newly[newly.length - 1]; // celebrate the most impressive (last-listed)
    setTimeout(() => setBadgeUnlock({ def: star, extra: newly.length - 1 }), 1100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanCount, streakDays, lifetimeTasks, xp, photoOrdered, completed, program, badges]);

  // At least one task done today extends the streak. A single missed day can
  // be absorbed by the weekly streak freeze; otherwise the streak resets.
  const bumpStreak = async () => {
    try {
      const raw = await AsyncStorage.getItem(STORAGE.streak);
      const stored = raw ? JSON.parse(raw) : { count: 0, lastDate: null };
      if (stored.lastDate === todayKey) { setStreakDays(stored.count); return; }
      const gap = stored.lastDate ? daysBetweenKeys(stored.lastDate, todayKey) : null;
      let count = 1;
      if (gap === 1) {
        count = stored.count + 1;
      } else if (gap === 2 && stored.count > 0) {
        // One missed day — consume the freeze if it's still available.
        // (Normally the launch-time repair already handled this; this path
        // covers the app staying alive in the background across midnights.)
        try {
          const fRaw = await AsyncStorage.getItem(STORAGE.streakFreeze);
          const f = fRaw ? JSON.parse(fRaw) : { available: true, usedWeek: null };
          const available = f.available || (f.usedWeek && f.usedWeek !== weekAnchorKey(todayKey));
          if (available) {
            count = stored.count + 1;
            await AsyncStorage.setItem(STORAGE.streakFreeze, JSON.stringify({ available: false, usedWeek: weekAnchorKey(todayKey) }));
            setFreezeAvailable(false);
            setFreezeNotice(stored.count);
          }
        } catch { /* no freeze — falls through to reset */ }
      }
      await AsyncStorage.setItem(STORAGE.streak, JSON.stringify({ count, lastDate: todayKey }));
      setStreakDays(count);
      setStreakCelebration(count); // 🎉 Duolingo-style celebration on a new streak day
      // Streak just extended → tonight's flame pings go quiet and future copy
      // carries the new count.
      AsyncStorage.getItem(STORAGE.lastScanDate)
        .then((ls) => scheduleReminders({ isSubscribed, lastScanDate: ls, streakDays: count, streakSafeToday: true }))
        .catch(() => {});
    } catch (e) {
      console.error('Streak error:', e);
    }
  };

  // Add lifetime XP; crossing a level bound queues the level-up overlay
  // (slightly delayed so it never collides with task haptics/confetti).
  const addXp = (amount: number) => {
    setXp(prev => {
      const next = prev + amount;
      AsyncStorage.setItem(STORAGE.xp, JSON.stringify(next)).catch(() => {});
      const after = levelForXp(next);
      if (after > levelForXp(prev)) setTimeout(() => setLevelUp(after), 700);
      return next;
    });
  };

  const dismissTip = () => {
    setTipDismissed(true);
    AsyncStorage.setItem(STORAGE.tipDismissed, todayKey).catch(() => {});
    Haptics.selectionAsync().catch(() => {});
  };

  const toggleTask = (instanceId: string) => {
    // Light tap feedback on every toggle (the big celebration fires via bumpStreak)
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    const nowDone = !completed[instanceId];
    setCompleted(prev => ({ ...prev, [instanceId]: !prev[instanceId] }));

    // Reward XP / the lifetime tally / the all-done celebration only the FIRST
    // time a given task instance is completed each day. `completed` stays freely
    // toggleable (unchecking never decrements), but without this guard,
    // unchecking then rechecking the same task paid out again every time.
    if (nowDone && !awardedTasks[instanceId]) {
      playSound('task');
      setAwardedTasks(prev => {
        const next = { ...prev, [instanceId]: true };
        AsyncStorage.setItem(STORAGE.tasksAwarded(todayKey), JSON.stringify(next)).catch(() => {});
        return next;
      });
      setLifetimeTasks(n => {
        const next = n + 1;
        AsyncStorage.setItem(STORAGE.lifetimeTasks, JSON.stringify(next)).catch(() => {});
        return next;
      });

      // Award the task's XP toward the global Level.
      const taskXp = program?.weeks.flatMap(w => w.tasks).find(t => t.instanceId === instanceId)?.xp ?? 20;
      addXp(taskXp);

      // Confetti once — the moment the LAST never-yet-awarded task of the day
      // earns its first completion. Keyed off awardedTasks (not `completed`) so
      // toggling a task off and back on can't re-trigger the celebration.
      if (totalTasks > 0) {
        const wasAllAwarded = todayTasksForStats.every(t => awardedTasks[t.instanceId]);
        const allAwardedNow = todayTasksForStats.every(t => t.instanceId === instanceId || awardedTasks[t.instanceId]);
        if (allAwardedNow && !wasAllAwarded) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 2600);
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
        }
      }
    }
  };

  // Generate + persist a fresh program from raw scan scores.
  const applyScores = (s: Record<string, number>) => {
    setScores(s);
    // Persist raw scores so the bubbles + results sheet survive an app restart.
    AsyncStorage.setItem(STORAGE.lastScores, JSON.stringify(s)).catch(() => {});
    // First scan builds the program. A RESCAN no longer regenerates it (which
    // used to wipe week progress and made a "finish week 1, then rescan to
    // unlock week 2" ladder impossible) — instead it keeps the program and
    // counts the rescan, which is what unlocks the next week once the current
    // one is finished. Rebuilding from scratch is the explicit "New Program".
    if (!program) {
      const prog = generateProgram(s);
      setProgram(prog);
      AsyncStorage.setItem(STORAGE.program, JSON.stringify(prog)).catch(() => {});
      setProgramRescans(0);
      AsyncStorage.setItem(STORAGE.programRescans, '0').catch(() => {});
    } else {
      setProgramRescans(prev => {
        const next = prev + 1;
        AsyncStorage.setItem(STORAGE.programRescans, String(next)).catch(() => {});
        return next;
      });
    }

    // Anchor the over-time boost to the first scan (persists across the program
    // regenerating on every scan). Use the effective anchor for this scan's boost.
    const anchor = journeyStart ?? Date.now();
    if (journeyStart === null) {
      setJourneyStart(anchor);
      AsyncStorage.setItem(STORAGE.journeyStart, JSON.stringify(anchor)).catch(() => {});
    }
    const boostAtScan = computeBoost(anchor, lifetimeTasks);

    // Track scan count
    setScanCount(prev => {
      const next = prev + 1;
      AsyncStorage.setItem(STORAGE.scanCount, JSON.stringify(next)).catch(() => {});
      return next;
    });

    // Track best overall score (displayed + boosted). Doubles as the ratchet
    // floor so the headline number never appears to drop on a later scan. Also
    // drives the score-reveal overlay: the shown score never drops, so the
    // delta vs the previous shown score is always >= 0 (no demoralizing red).
    if (typeof s.overall === 'number') {
      const display = displayScore(s.overall, boostAtScan);
      const prevBest = bestScore;                       // previously shown overall
      const shown = prevBest === null ? display : Math.max(display, prevBest);
      const isFirst = prevBest === null;
      const isNewBest = prevBest !== null && display > prevBest;
      const delta = prevBest === null ? 0 : Math.max(0, shown - prevBest);

      const next = prevBest === null || display > prevBest ? display : prevBest;
      setBestScore(next);
      AsyncStorage.setItem(STORAGE.bestScore, JSON.stringify(next)).catch(() => {});

      // Fire the reveal event (owns its own confetti when it's a new best).
      setRevealData({ score: shown, delta, isFirst, isBest: isNewBest });

      if (isNewBest) {
        // Ask for App Store review 4s after a new best, max once per 30 days.
        AsyncStorage.getItem(STORAGE.lastReviewPrompt).then(last => {
          const daysSince = last ? daysBetweenKeys(last, todayKey) : 999;
          if (daysSince >= 30) {
            setTimeout(() => {
              StoreReview?.requestReview().catch(() => {});
              AsyncStorage.setItem(STORAGE.lastReviewPrompt, todayKey).catch(() => {});
            }, 4000);
          }
        }).catch(() => {});
      }
    }

    // Append to scan history (display scores) for the Results graph
    if (typeof s.overall === 'number') {
      const rec: ScanRecord = {
        ts: Date.now(),
        overall: displayScore(s.overall, boostAtScan),
        potential: typeof s.potential === 'number' ? s.potential : displayScore(s.overall, boostAtScan),
        cats: Object.fromEntries(CATEGORIES.map(c => [c.key, typeof s[c.key] === 'number' ? displayScore(s[c.key], boostAtScan) : 0])),
      };
      setScanHistory(prev => {
        const next = [...prev, rec].slice(-60); // keep last 60 scans
        AsyncStorage.setItem(STORAGE.history, JSON.stringify(next)).catch(() => {});
        return next;
      });
    }

    // Save last scan date (used for weekly free-tier gate)
    AsyncStorage.setItem(STORAGE.lastScanDate, todayKey).catch(() => {});

    // Refresh reminders so the "scan ready" alert points at the new 7-day window.
    // For free users, also tease the locked score breakdown ~2h later.
    scheduleReminders({
      isSubscribed,
      lastScanDate: todayKey,
      streakDays,
      streakSafeToday: Object.values(completed).some(Boolean),
    }).then(() => {
      if (!isSubscribed) scheduleScoreTease();
    });

    // (No auto-popup here anymore: the new conversion flow lets the user view
    // their teaser first, then chains account prompt → paywall on close/CTA.)
  };

  // Free-user upgrade entry point (all locked cards, CTAs, and nudges route
  // here). Users who never created an account get the skippable account
  // prompt first; its onAuthed/skip then opens the paywall.
  const openUpgradeFlow = () => {
    if (authName === null) setShowAccountPrompt(true);
    else setShowSubscribeModal(true);
  };

  const onUnlock = async () => {
    if (purchasing) return; // guard against double-taps while the sheet is open
    // Run a real purchase of the SELECTED paywall tier. If RevenueCat isn't
    // ready (missing key, module failed to load), production FAILS CLOSED —
    // the old fallthrough silently granted Pro for free, a revenue landmine
    // if RevenueCat ever misconfigured. Dev builds keep the free unlock so
    // the paid UI stays testable before payments exist.
    if (purchasesReady()) {
      setPurchasing(true);
      const res = await buyPro(selectedPlan);
      setPurchasing(false);
      if (res.status === 'cancelled') return; // user backed out — stay on free, silently
      if (res.status === 'unavailable') {
        Alert.alert(
          'Subscription unavailable',
          'This subscription isn’t available to purchase right now. You have not been charged. Please try again in a little while.'
        );
        return;
      }
      if (res.status === 'error') {
        Alert.alert(
          'Purchase didn’t complete',
          res.message || 'Something went wrong and you have not been charged. Please try again.'
        );
        return;
      }
      // res.status === 'success' → fall through and unlock
    } else if (!__DEV__) {
      Alert.alert(
        'Purchases Unavailable',
        'The App Store can’t be reached right now. Check your connection and try again in a moment.'
      );
      return;
    }
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    setIsSubscribed(true);
    setShowSubscribeModal(false);
    setShowWeeklyLimitModal(false);
    // The isSubscribed/streak effect below reschedules to the Pro flame
    // reminders (with the real streak count) — no manual call needed here.
  };

  const onRestore = async () => {
    // If RevenueCat isn't configured yet, there's nothing real to restore.
    if (!purchasesReady()) {
      Alert.alert('Restore Purchases', 'Purchases aren’t available in this build yet.');
      return;
    }
    const ok = await restorePro();
    if (ok) {
      setIsSubscribed(true);
      Alert.alert('You’re all set', 'Your Pro subscription has been restored.');
    } else {
      Alert.alert('Nothing to restore', 'We couldn’t find an active Pro subscription on this Apple ID.');
    }
  };

  // Sign out — clears the session (keeps the saved account so the user can log
  // back in) and presents the login screen. There's no login GATE anymore, so
  // the screen must be shown explicitly or logout appears to do nothing.
  const handleLogout = async () => {
    await AsyncStorage.removeItem(STORAGE.session).catch(() => {});
    Haptics.selectionAsync().catch(() => {});
    switchTab('home');
    setAuthName(null);
    setShowLoginScreen(true);
  };

  // User agreed to the scan consent. Persist it, then continue whichever flow
  // they were starting — live scan or photo import (the ref lets the gates see
  // consent immediately without waiting for a re-render).
  const acceptConsent = async () => {
    consentRef.current = true;
    await AsyncStorage.setItem(STORAGE.scanConsent, '1').catch(() => {});
    Haptics.selectionAsync().catch(() => {});
    setShowConsent(false);
    if (pendingConsentAction.current === 'import') startImport();
    else startScan();
  };

  // Re-run the region check (used by the "Try Again" button on the block screen,
  // e.g. after the user enables location in Settings). On success, dismiss the
  // block and continue into the scan flow.
  const retryRegionCheck = async () => {
    setCheckingRegion(true);
    const result = await verifyRegion();
    setCheckingRegion(false);
    regionRef.current = result;
    if (result === 'ok') {
      setShowRegionBlock(false);
      startScan();
    } else {
      setRegionStatus(result);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(() => {});
    }
  };

  // Delete account + ALL local data (Apple requires in-app account deletion for
  // apps that support account creation). Wipes every Maxify key, resets state,
  // and returns to the login screen.
  const confirmDeleteAccount = async () => {
    try {
      const keys = await AsyncStorage.getAllKeys();
      const ours = keys.filter(k => k.startsWith('maxify_'));
      if (ours.length) await AsyncStorage.multiRemove(ours);
    } catch (e) {
      console.error('Account deletion error:', e);
    }
    consentRef.current = false;
    setProgram(null);
    setCompleted({});
    setAwardedTasks({});
    setScores({});
    setScanned(false);
    setScanHistory([]);
    setScanCount(0);
    setBestScore(null);
    setXp(0);
    setDay1Photo(null);
    setFrontPhotoBase64(null);
    setFrontFaceBox(null);
    setJourneyStart(null);
    setLifetimeTasks(0);
    setStreakDays(0);
    setIsSubscribed(false);
    setShowDeleteModal(false);
    // The modal promises this "permanently erases everything on this device" —
    // these were being wiped from storage but left stale in memory, so a fresh
    // sign-in in the same app session would skip onboarding (old profile still
    // set) and show achievements already unlocked (old badges array intact).
    setProfile(null);
    setBadges([]);
    setRecommendations([]);
    setSubtraits({});
    setHarmony([]);
    setFreezeAvailable(false);
    setFreezeNotice(null);
    switchTab('home');
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    setAuthName(null);
  };

  const confirmNewProgram = () => {
    const base = program?.rawScores ?? scores;
    if (!base || Object.keys(base).length === 0) return;
    const newProg = generateProgram(base);
    setProgram(newProg);
    setProgramRescans(0);
    setCompleted({});
    setAwardedTasks({});
    setStreakDays(0);
    AsyncStorage.setItem(STORAGE.program, JSON.stringify(newProg)).catch(() => {});
    AsyncStorage.setItem(STORAGE.programRescans, '0').catch(() => {});
    AsyncStorage.setItem(STORAGE.streak, JSON.stringify({ count: 0, lastDate: null })).catch(() => {});
    AsyncStorage.setItem(STORAGE.tasks(todayKey), JSON.stringify({})).catch(() => {});
    AsyncStorage.setItem(STORAGE.tasksAwarded(todayKey), JSON.stringify({})).catch(() => {});
    setShowNewProgramModal(false);
  };

  // Restore saved program, today's completed tasks, and streak on launch.
  useEffect(() => {
    (async () => {
      try {
        const [progRaw, doneRaw, awardedRaw, streakRaw, countRaw, bestRaw, histRaw, scoresRaw, consentRaw, journeyRaw, lifeTasksRaw, lastScanRaw, recsRaw, subsRaw, harmRaw, ageOkRaw, frontPhotoRaw, frontFaceBoxRaw, xpRaw, tipRaw, guidesReadRaw, day1Raw, recapRaw, milestonesRaw] = await Promise.all([
          AsyncStorage.getItem(STORAGE.program),
          AsyncStorage.getItem(STORAGE.tasks(todayKey)),
          AsyncStorage.getItem(STORAGE.tasksAwarded(todayKey)),
          AsyncStorage.getItem(STORAGE.streak),
          AsyncStorage.getItem(STORAGE.scanCount),
          AsyncStorage.getItem(STORAGE.bestScore),
          AsyncStorage.getItem(STORAGE.history),
          AsyncStorage.getItem(STORAGE.lastScores),
          AsyncStorage.getItem(STORAGE.scanConsent),
          AsyncStorage.getItem(STORAGE.journeyStart),
          AsyncStorage.getItem(STORAGE.lifetimeTasks),
          AsyncStorage.getItem(STORAGE.lastScanDate),
          AsyncStorage.getItem(STORAGE.recommendations),
          AsyncStorage.getItem(STORAGE.subtraits),
          AsyncStorage.getItem(STORAGE.harmony),
          AsyncStorage.getItem(STORAGE.potentialAgeOk),
          AsyncStorage.getItem(STORAGE.frontPhoto),
          AsyncStorage.getItem(STORAGE.frontFaceBox),
          AsyncStorage.getItem(STORAGE.xp),
          AsyncStorage.getItem(STORAGE.tipDismissed),
          AsyncStorage.getItem(STORAGE.guidesRead),
          AsyncStorage.getItem(STORAGE.day1Photo),
          AsyncStorage.getItem(STORAGE.lastRecap),
          AsyncStorage.getItem(STORAGE.milestonesShown),
        ]);
        hydrated.current = true;
        if (consentRaw === '1') consentRef.current = true;
        if (ageOkRaw === '1') potentialAgeOkRef.current = true;
        // Restore the saved scan photo so the results pictures (face map,
        // trait crops) survive a relaunch.
        if (frontPhotoRaw) setFrontPhotoBase64(frontPhotoRaw);
        if (frontFaceBoxRaw) {
          try { setFrontFaceBox(JSON.parse(frontFaceBoxRaw)); } catch { /* ignore corrupt value */ }
        }
        // XP: restore, or seed once from historical tasks/guides so existing
        // users don't restart at Level 1 when Levels launch.
        if (xpRaw != null) {
          setXp(JSON.parse(xpRaw));
        } else {
          let seeded = (lifeTasksRaw ? JSON.parse(lifeTasksRaw) : 0) * 20;
          try {
            const ids: string[] = guidesReadRaw ? JSON.parse(guidesReadRaw) : [];
            seeded += GUIDES.filter(g => ids.includes(g.id)).reduce((s, g) => s + g.xp, 0);
          } catch { /* ignore */ }
          if (seeded > 0) {
            setXp(seeded);
            AsyncStorage.setItem(STORAGE.xp, JSON.stringify(seeded)).catch(() => {});
          }
        }
        // Show today's tip unless it was dismissed today.
        setTipDismissed(tipRaw === todayKey);
        if (day1Raw) setDay1Photo(day1Raw);

        // ── Journey milestones (30/60/90 days) and the Sunday recap ──────────
        // At most one stats moment per launch; milestones outrank the recap.
        try {
          const journeyTs: number | null = journeyRaw ? JSON.parse(journeyRaw) : null;
          const hist: ScanRecord[] = histRaw ? JSON.parse(histRaw) : [];
          const lifeTasks: number = lifeTasksRaw ? JSON.parse(lifeTasksRaw) : 0;
          const scans: number = countRaw ? JSON.parse(countRaw) : 0;
          const shownMs: number[] = milestonesRaw ? JSON.parse(milestonesRaw) : [];
          const daysIn = journeyTs ? Math.floor((Date.now() - journeyTs) / 86400000) : 0;
          const hitMs = [90, 60, 30].find(m => daysIn >= m && !shownMs.includes(m));

          if (journeyTs && hitMs) {
            const first = hist[0]?.overall;
            const latest = hist[hist.length - 1]?.overall;
            setStatsMoment({
              title: `${hitMs} DAYS ON MAXIFY`,
              sub: 'Look how far you’ve come.',
              confetti: true,
              rows: [
                { icon: 'scan', label: 'Scans', value: String(scans) },
                { icon: 'checkmark-circle', label: 'Tasks completed', value: String(lifeTasks) },
                ...(typeof first === 'number' && typeof latest === 'number' && latest > first
                  ? [{ icon: 'trending-up' as const, label: 'Score since Day 1', value: `+${latest - first}` }]
                  : []),
              ],
            });
            AsyncStorage.setItem(STORAGE.milestonesShown, JSON.stringify([...shownMs, hitMs])).catch(() => {});
          } else if (new Date().getDay() === 0 && recapRaw !== todayKey && (scans > 0 || lifeTasks > 0)) {
            // Sunday recap — tasks done in the past 7 days + streak + score move.
            const weekKeys: string[] = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date();
              d.setDate(d.getDate() - i);
              weekKeys.push(STORAGE.tasks(dateKey(d)));
            }
            const weekVals = await AsyncStorage.multiGet(weekKeys);
            let weekTasks = 0;
            for (const [, v] of weekVals) {
              if (v) { try { weekTasks += Object.values(JSON.parse(v)).filter(Boolean).length; } catch { /* ignore */ } }
            }
            const cutoff = Date.now() - 7 * 86400000;
            const older = [...hist].reverse().find(h => h.ts <= cutoff) ?? hist[0];
            const latest = hist[hist.length - 1];
            const wkDelta = older && latest && latest.overall > older.overall ? latest.overall - older.overall : 0;
            const streakNow = computeStreakOnLoad(streakRaw ? JSON.parse(streakRaw) : null, todayKey);
            setStatsMoment({
              title: 'YOUR WEEK IN REVIEW',
              sub: 'Every week compounds. Here’s yours.',
              rows: [
                { icon: 'checkmark-circle', label: 'Tasks this week', value: String(weekTasks) },
                { icon: 'flame', label: 'Current streak', value: `${streakNow} day${streakNow === 1 ? '' : 's'}` },
                ...(wkDelta > 0 ? [{ icon: 'trending-up' as const, label: 'Score this week', value: `+${wkDelta}` }] : []),
              ],
            });
            AsyncStorage.setItem(STORAGE.lastRecap, todayKey).catch(() => {});
          }
        } catch { /* stats moments are best-effort — never block launch */ }
        if (progRaw) setProgram(JSON.parse(progRaw));
        // Rescans-since-program-built — gates week unlocking. Read standalone so
        // the big multiGet above stays untouched.
        AsyncStorage.getItem(STORAGE.programRescans)
          .then(r => { const n = r ? parseInt(r, 10) : 0; if (Number.isFinite(n)) setProgramRescans(n); })
          .catch(() => {});
        if (doneRaw) setCompleted(JSON.parse(doneRaw));
        // Fall back to treating already-completed tasks as already-awarded (only
        // relevant right after this fix ships, before an awarded-set has ever
        // been persisted) so existing users can't re-earn today's XP for free.
        if (awardedRaw) setAwardedTasks(JSON.parse(awardedRaw));
        else if (doneRaw) setAwardedTasks(JSON.parse(doneRaw));
        // Restore the last scan so scores + results stay visible after relaunch.
        if (scoresRaw) {
          const s = JSON.parse(scoresRaw);
          if (s && typeof s.overall === 'number') {
            setScores(s);
            setScanned(true);
          }
        }
        // Streak restore — freeze-aware. A single missed day is forgiven when
        // the weekly freeze is available: consume it, backdate lastDate to
        // yesterday (so today's task completion continues the streak
        // naturally), and queue the "streak saved" banner on Home.
        {
          const storedStreak = streakRaw ? JSON.parse(streakRaw) : null;
          let freeze = { available: true, usedWeek: null as string | null };
          try {
            const fRaw = await AsyncStorage.getItem(STORAGE.streakFreeze);
            if (fRaw) freeze = { ...freeze, ...JSON.parse(fRaw) };
          } catch { /* first run — default freeze granted */ }
          // Weekly refill: a freeze used in a previous week comes back.
          if (!freeze.available && freeze.usedWeek && freeze.usedWeek !== weekAnchorKey(todayKey)) {
            freeze = { available: true, usedWeek: null };
          }
          const gap = storedStreak?.lastDate ? daysBetweenKeys(storedStreak.lastDate, todayKey) : null;
          if (gap === 2 && storedStreak.count > 0 && freeze.available) {
            freeze = { available: false, usedWeek: weekAnchorKey(todayKey) };
            const yesterday = dateKey(new Date(Date.now() - 86400000));
            await AsyncStorage.setItem(STORAGE.streak, JSON.stringify({ count: storedStreak.count, lastDate: yesterday }));
            setStreakDays(storedStreak.count);
            setFreezeNotice(storedStreak.count);
          } else {
            setStreakDays(computeStreakOnLoad(storedStreak, todayKey));
          }
          AsyncStorage.setItem(STORAGE.streakFreeze, JSON.stringify(freeze)).catch(() => {});
          setFreezeAvailable(freeze.available);
        }
        if (countRaw) setScanCount(JSON.parse(countRaw));
        // Scan-first onboarding: a brand-new user (never scanned, nothing
        // stored) lands directly on the Scan tab — the scan IS the onboarding.
        // Returning users keep landing on Home.
        if (!countRaw && !scoresRaw && !progRaw) setTab('scan');
        if (bestRaw) setBestScore(JSON.parse(bestRaw));
        if (histRaw) setScanHistory(JSON.parse(histRaw));
        if (journeyRaw) setJourneyStart(JSON.parse(journeyRaw));
        if (lifeTasksRaw) setLifetimeTasks(JSON.parse(lifeTasksRaw));
        if (recsRaw) {
          const r = JSON.parse(recsRaw);
          if (Array.isArray(r)) setRecommendations(r);
        }
        if (subsRaw) {
          const s = JSON.parse(subsRaw);
          if (s && typeof s === 'object') setSubtraits(s);
        }
        if (harmRaw) {
          const h = JSON.parse(harmRaw);
          if (Array.isArray(h)) setHarmony(h);
        }
        // Subscription intentionally NOT restored — always start free until
        // the user taps "Start Free Trial". Swap to RevenueCat before launch.

        // First-launch tutorial: show once, the first time the app opens.
        const tutDone = await AsyncStorage.getItem(STORAGE.tutorialDone).catch(() => null);
        if (!tutDone) {
          setShowTutorial(true); // new users get the notification ask after the tour
        } else {
          // Returning users: reschedule. Sub state always starts free on launch,
          // so free-tier reminders are correct until they tap subscribe.
          scheduleReminders({ isSubscribed: false, lastScanDate: lastScanRaw });
        }
      } catch (e) {
        hydrated.current = true;
        console.error('Load error:', e);
      }
    })();
  }, [todayKey]);

  // Persist today's completion (keyed by date) and advance the streak.
  useEffect(() => {
    if (!hydrated.current) return;
    AsyncStorage.setItem(STORAGE.tasks(todayKey), JSON.stringify(completed)).catch(() => {});
    if (Object.values(completed).some(Boolean)) bumpStreak();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed]);

  // Keep the Pro flame reminders in sync: RevenueCat flips isSubscribed
  // asynchronously after launch (the launch-time schedule assumed free), and
  // the restored streak count also lands async — reschedule when either does.
  // Idempotent (cancelAll first), so overlapping calls are harmless.
  useEffect(() => {
    if (!isSubscribed || !hydrated.current) return;
    AsyncStorage.getItem(STORAGE.lastScanDate)
      .then((ls) => scheduleReminders({
        isSubscribed: true,
        lastScanDate: ls,
        streakDays,
        streakSafeToday: Object.values(completed).some(Boolean),
      }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscribed, streakDays]);

  // Load whether the first-time how-to has been seen — checked as a gate when
  // the user presses Scan (not auto-shown), so scanning never starts behind it.
  useEffect(() => {
    AsyncStorage.getItem(STORAGE.scanHowTo)
      .then(v => { if (v === '1') scanHowToSeen.current = true; })
      .catch(() => {});
    AsyncStorage.getItem(STORAGE.accountNudged)
      .then(v => { if (v === '1') accountNudgedRef.current = true; })
      .catch(() => {});
  }, []);

  // "Got it" — mark seen and close. We deliberately do NOT start the scan here;
  // the user presses Scan again to begin (so they're ready for the capture).
  const dismissScanHowTo = () => {
    scanHowToSeen.current = true;
    setShowScanHowTo(false);
    AsyncStorage.setItem(STORAGE.scanHowTo, '1').catch(() => {});
    Haptics.selectionAsync().catch(() => {});
  };

  const scanLineAnim = useRef(new Animated.Value(0)).current;
  const zoomAnim = useRef(new Animated.Value(1)).current;
  const flashAnim = useRef(new Animated.Value(0)).current;
  const resultsSlide = useRef(new Animated.Value(height)).current;
  // Tracks resultsSlide's live numeric value (incl. mid-animation) so a drag
  // gesture can grab it from wherever it currently sits without a jump.
  const resultsSlideValueRef = useRef(height);
  useEffect(() => {
    const id = resultsSlide.addListener(({ value }) => { resultsSlideValueRef.current = value; });
    return () => resultsSlide.removeListener(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pulseAnim = useRef(new Animated.Value(0)).current;
  const swipeHintAnim = useRef(new Animated.Value(0)).current;
  const ringGlowAnim = useRef(new Animated.Value(0)).current;
  const guideProgress = useRef(new Animated.Value(0)).current; // guided-scan ring fill (0-1)
  const ringFlash = useRef(new Animated.Value(0)).current;     // green pulse on each capture
  const pulseLoopRef = useRef<Animated.CompositeAnimation | null>(null);
  const swipeLoopRef = useRef<Animated.CompositeAnimation | null>(null);
  const ringLoopRef = useRef<Animated.CompositeAnimation | null>(null);

  const faceHeight = FACE_OVAL_H;
  const faceWidth = FACE_OVAL_W;
  const faceTop = FACE_OVAL_TOP;


  // Continuous ring glow loop (always running)
  useEffect(() => {
    ringLoopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(ringGlowAnim, { toValue: 1, duration: 1400, useNativeDriver: false }),
        Animated.timing(ringGlowAnim, { toValue: 0, duration: 1400, useNativeDriver: false }),
      ])
    );
    ringLoopRef.current.start();
    return () => { if (ringLoopRef.current) ringLoopRef.current.stop(); };
  }, []);

  const startPulse = () => {
    pulseAnim.setValue(0);
    if (pulseLoopRef.current) pulseLoopRef.current.stop();
    pulseLoopRef.current = Animated.loop(
      Animated.sequence([
        // JS-driven (not native): the same ScoreBubble node also runs a
        // JS-driven shadow glow, and a single node can't mix native + JS
        // drivers. Scale on a small bubble is cheap enough on the JS driver.
        Animated.timing(pulseAnim, { toValue: 1, duration: 900, useNativeDriver: false }),
        Animated.timing(pulseAnim, { toValue: 0, duration: 900, useNativeDriver: false }),
      ])
    );
    pulseLoopRef.current.start();
  };

  const startSwipeHintPulse = () => {
    swipeHintAnim.setValue(0);
    if (swipeLoopRef.current) swipeLoopRef.current.stop();
    swipeLoopRef.current = Animated.loop(
      Animated.sequence([
        // JS driver required: this value animates shadowOpacity on the swipe
        // hint, which the native animated module doesn't support.
        Animated.timing(swipeHintAnim, { toValue: 1, duration: 700, useNativeDriver: false }),
        Animated.timing(swipeHintAnim, { toValue: 0, duration: 700, useNativeDriver: false }),
      ])
    );
    swipeLoopRef.current.start();
  };

  const stopAllAnimations = () => {
    if (pulseLoopRef.current) pulseLoopRef.current.stop();
    if (swipeLoopRef.current) swipeLoopRef.current.stop();
    pulseAnim.setValue(0);
    swipeHintAnim.setValue(0);
  };

  useEffect(() => () => stopAllAnimations(), []);

  const extractJSON = (text: string): any => {
    let clean = text.replace(/```json|```/g, '').trim();
    const match = clean.match(/\{[\s\S]*\}/);
    if (match) clean = match[0];
    try { return JSON.parse(clean); }
    catch {
      const fixed = clean.replace(/'/g, '"').replace(/(\w+)\s*:/g, '"$1":').replace(/""(\w+)""/g, '"$1"').replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');
      return JSON.parse(fixed);
    }
  };

  const analyzeWithClaude = async (images: string[]) => {
    setAnalyzing(true);

    // A failed analysis never shows made-up scores — surface an error and let
    // the user rescan. Nothing is persisted on failure, so a failed attempt
    // doesn't burn the free tier's weekly scan either.
    const fail = () => {
      setAnalyzing(false);
      setScanned(false);
      // If the sheet was swiped open mid-analysis, unmounting it while its
      // slide position is 0 would re-mount it fully open on the next scan.
      setShowResults(false);
      setSheetContentMounted(false);
      resultsSlide.setValue(height);
      setScanError(true);
      setTimeout(() => setScanError(false), 3500);
    };

    // No clear face in the photo — distinct from a technical failure. Nothing is
    // persisted, so it doesn't burn the free tier's weekly scan.
    const failBadScan = (issue?: string) => {
      setAnalyzing(false);
      setScanned(false);
      setShowResults(false);
      setSheetContentMounted(false);
      resultsSlide.setValue(height);
      setBadScanReason(badScanMessage(issue));
      setBadScan(true);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(() => {});
      setTimeout(() => setBadScan(false), 4500);
    };

    const finish = (raw: Record<string, any>) => {
      const safe: Record<string, number> = {};
      for (const k of ['overall', ...CATEGORY_KEYS, 'potential']) {
        if (typeof raw[k] === 'number') safe[k] = raw[k];
      }
      safe.potential = Math.min(Math.max(typeof safe.potential === 'number' ? safe.potential : 94, 89), 99);
      const newReasons: Record<string, string> = {};
      for (const cat of CATEGORY_KEYS) {
        if (typeof raw[`${cat}_reason`] === 'string') newReasons[cat] = raw[`${cat}_reason`];
      }
      setReasons(newReasons);

      // Tailored style fixes — keep only well-formed entries; never fail the scan
      // over them (scores are the gate). Persist so they survive a relaunch.
      const recs: Recommendation[] = Array.isArray(raw.recommendations)
        ? raw.recommendations
            .filter((r: any) => r && typeof r.title === 'string' && typeof r.action === 'string')
            .slice(0, 6)
            .map((r: any) => ({
              title: r.title,
              action: r.action,
              reason: typeof r.reason === 'string' ? r.reason : '',
              feature: typeof r.feature === 'string' ? r.feature : '',
            }))
        : [];
      setRecommendations(recs);
      AsyncStorage.setItem(STORAGE.recommendations, JSON.stringify(recs)).catch(() => {});

      // Per-category sub-traits (Qoves-style named-trait report). Keep only
      // well-formed entries; never fail the scan over them.
      const subs: Record<string, SubTrait[]> = {};
      if (raw.subtraits && typeof raw.subtraits === 'object') {
        for (const cat of CATEGORY_KEYS) {
          const arr = raw.subtraits[cat];
          if (Array.isArray(arr)) {
            const cleaned = arr
              .filter((t: any) => t && typeof t.name === 'string')
              .slice(0, 5)
              .map((t: any) => {
                const tier = normTier(t.tier);
                const st: SubTrait = { name: t.name, tier };
                if (typeof t.percentile === 'number') st.percentile = Math.max(1, Math.min(99, Math.round(t.percentile)));
                // why + protocol only matter on concerns (developing/priority)
                if (isConcern(tier)) {
                  if (typeof t.why === 'string') st.why = t.why;
                  const p = t.protocol;
                  if (p && typeof p === 'object' && typeof p.fix === 'string') {
                    st.protocol = {
                      fix: p.fix,
                      difficulty: normDifficulty(p.difficulty),
                      timeline: typeof p.timeline === 'string' ? p.timeline : '',
                      cost: typeof p.cost === 'string' ? p.cost : '',
                    };
                  }
                }
                return st;
              });
            if (cleaned.length) subs[cat] = cleaned;
          }
        }
      }
      setSubtraits(subs);
      AsyncStorage.setItem(STORAGE.subtraits, JSON.stringify(subs)).catch(() => {});

      // Facial harmony / ratio metrics (the "this is scientific" layer).
      const harm: HarmonyMetric[] = Array.isArray(raw.harmony)
        ? raw.harmony
            .filter((m: any) => m && typeof m.name === 'string' && typeof m.value === 'string')
            .slice(0, 5)
            .map((m: any) => ({ name: m.name, value: m.value, status: normStatus(m.status) }))
        : [];
      setHarmony(harm);
      AsyncStorage.setItem(STORAGE.harmony, JSON.stringify(harm)).catch(() => {});

      // Persist this scan's photo locally so the face map and trait crops
      // survive leaving the app. Stays on-device only.
      if (images[0]) AsyncStorage.setItem(STORAGE.frontPhoto, images[0]).catch(() => {});
      // Keep the very FIRST scan photo forever — it anchors the Day-1 journey.
      if (images[0]) {
        AsyncStorage.getItem(STORAGE.day1Photo).then(v => {
          if (!v) {
            AsyncStorage.setItem(STORAGE.day1Photo, images[0]).catch(() => {});
            setDay1Photo(images[0]);
          }
        }).catch(() => {});
      }

      applyScores(safe);
      startPulse();
      startSwipeHintPulse();
      setAnalyzing(false);
    };

    // Keep only real captures that are safely under the API's 5 MB-per-image
    // limit (the camera caps resolution, so this is just a guard). The front
    // shot leads so Claude treats it as the primary reference.
    const validImages = images.filter(img => img && img.length <= 6_500_000);
    if (validImages.length === 0) { console.error('Scan failed: no usable photos captured'); fail(); return; }

    // Demographic/goal context from onboarding — makes scoring + recommendations
    // account for the user's stated sex, age, ethnicity and goals.
    const goalsText = profile?.goals?.length ? profile.goals.join(', ') : 'overall improvement';
    const profileCtx = profile
      ? ` The user is ${profile.gender}, ethnicity: ${profile.ethnicity}, current grooming routine: ${profile.skinType}. Their stated goals: ${goalsText}. From the photos, estimate their approximate age range and factor it into scoring and recommendations. Judge facial structure and harmony appropriately for their stated sex, estimated age, and demographic (compare within their group where relevant). Tailor every recommendation to their goals and routine level — if they have no routine, start with fundamentals; if advanced, skip basics and go deeper.`
      : '';

    try {
      const data = await callClaude({
        model: 'claude-sonnet-4-6',
        max_tokens: 3200,
        // Speed: face scoring is perception + structured JSON extraction, not a
        // deep-reasoning task — so skip extended thinking and run low effort.
        // This cuts analysis latency substantially without downgrading the model
        // (both are GA on Sonnet 4.6, no beta header needed through the proxy).
        // If scores start to feel less accurate, bump effort to 'medium'.
        thinking: { type: 'disabled' },
        output_config: { effort: 'low' },
        messages: [{
          role: 'user',
          content: [
            ...validImages.map((img) => ({ type: 'image', source: { type: 'base64', media_type: b64MediaType(img), data: img } })),
            { type: 'text', text: `You are the scoring engine for Maxify, a facial self-improvement app.${profileCtx} You are given up to 3 photos of the SAME person captured during a guided scan: front-facing, head turned left, and head turned right. Use all angles together to judge facial structure more accurately than a single photo would allow.\n\nFIRST, check photo quality: the first (front-facing) photo must clearly show a single human face that is reasonably well-lit, in focus, and unobstructed enough to rate. If it does NOT, respond with ONLY {"reject":true,"issue":X} and nothing else, where X names the single biggest problem — one of exactly: "dark" (too dark, backlit, or heavy shadow), "blurry" (out of focus or motion blur), "framing" (face off-center, too far, or mostly cut off), "obstructed" (covered by hair, hand, glasses, or mask), or "no_face" (no clear human face, or multiple people).\n\nOtherwise, rate each feature from 0-100 based on what you actually see across the angles: skin (clarity, texture), jawline (definition, sharpness — judged from the profile angles too), eyes (eye area, brows, symmetry), cheekbones (prominence — clearer from the turned angles), masculinity (NOTE: this key reports OVERALL FACIAL STRUCTURE, balance & harmony — i.e. sex-typical attractiveness for the user's stated gender, NOT maleness specifically), hair (density, hairline, style). Score decisively and use the FULL realistic range — do not cluster everyone in a safe 60-85 band out of politeness. An unremarkable, average feature should land around 45-55, not 65-75. Genuinely weak features (a rounder/fuller face with little jaw definition, visible acne or uneven texture, thinning hair, poor symmetry) should score in the 20-40 range. Reserve 85+ for features that are genuinely exceptional. Two people who look noticeably different from each other must NOT receive similar scores — if their bone structure, facial fullness, skin, or proportions clearly differ, the numbers must reflect that gap clearly, not converge toward a safe middle. Facial fullness/weight is a real signal: a fuller/rounder face with soft jaw definition should score meaningfully lower on jawline and cheekbones than a lean, angular face with sharp definition, independent of symmetry. Judge the face, not the photos: account for lighting/angle/quality without letting them drag scores down. "overall" is your holistic rating and should also span the full range — it is not an average pulled toward the middle. "potential" is the realistic ceiling after 8 weeks of consistent self-improvement, and it must ALSO vary genuinely from person to person: someone who already has strong bone structure and few real weaknesses should land 95-99, while someone with more realistic room to close (weaker jawline, fuller face, more developing traits) should land lower, around 89-94. It must be an integer 89-99 — do not default to the same number for everyone.\n\nFor each scored category also include a "reason": one blunt sentence under 12 words naming the specific strength or weakness you observed — no generic platitudes.\n\nAlso include "subtraits": an object keyed by each scored category (skin, jawline, eyes, cheekbones, masculinity, hair). Each value is an array of 3-5 specific named anatomical sub-traits you assessed for that category. Go granular with real facial-anatomy terms (eyes: "Canthal tilt", "Eye spacing", "Under-eye hollowing", "Upper-lid exposure"; jawline: "Gonial angle", "Chin projection", "Ramus length", "Submental fullness"; cheekbones: "Zygomatic prominence", "Ogee curve", "Malar fat"; skin: "Skin clarity", "Even tone", "Pore visibility", "Texture"; hair: "Density", "Hairline maturity", "Temple recession"; masculinity/structure: "Facial width", "Bone structure", "Facial thirds balance"). Each sub-trait object: {"name": the trait term, "tier": one of exactly "ideal", "strong", "balanced", "developing", or "priority" (best to worst), "percentile": an integer 1-99 estimating where this trait ranks vs others of the user's stated sex and ethnicity (higher = better)}. For any sub-trait whose tier is "developing" or "priority" (a concern), ALSO include "why": one blunt sentence under 16 words on why the trait matters aesthetically, and "protocol": {"fix": the single most effective NON-SURGICAL action to improve it, "difficulty": "Easy"|"Medium"|"Hard", "timeline": realistic e.g. "6-8 weeks", "cost": e.g. "Free", "$", "$$", or a short range}. Do NOT include why/protocol on ideal/strong/balanced traits.\n\nCRITICAL on sub-trait variety: the tiers and percentiles in the example JSON below are STRUCTURAL PLACEHOLDERS ONLY — they show the shape of the response, not typical values. Do NOT reuse them. In particular, do not default "Skin clarity" (or any other named trait) to a low tier just because the example does; judge each trait purely from THIS person's photos. Across users, every named trait must be capable of landing anywhere from "priority" to "ideal" — if you find yourself assigning the same trait the same tier for most faces, you are pattern-matching the example instead of looking. Within a single scan, spread the tiers: a typical face should have a genuine mix (roughly 1-2 ideal/strong, 1-2 balanced, 1-2 developing/priority), not a uniform block. Percentiles must span the full 1-99 range across users and correlate with what you actually see — a genuinely excellent trait belongs in the 80s-90s, a genuinely poor one in the 5-25 range. Do not cluster percentiles in the 35-60 band.\n\nAlso include "harmony": an array of 3-4 facial proportion/ratio metrics you can estimate from the photos — each {"name": the metric (e.g. "Facial thirds", "Facial width-to-height ratio", "Facial symmetry", "Eye spacing ratio"), "value": a short reading (e.g. "Balanced", "1.9", "Slightly wide"), "status": one of exactly "ideal", "balanced", or "flag"}.\n\nAlso include "appearance": a short FACTUAL description of what is on their face right now, exactly as photographed: {"hairstyle": their current haircut — style, length, texture, color — in under 12 words (e.g. "short wavy dark hair, mid fade, medium length on top"), "facialHair": their current facial hair in under 8 words (e.g. "light goatee, no mustache", or "clean-shaven")}. Describe only what you actually see — no suggestions or improvements here.\n\nAlso include "recommendations": an array of 4-6 CONCRETE, CHANGEABLE styling/grooming tactics tailored to THIS face and their goals — things they can act on now (facial hair shape, hairstyle, brow grooming, glasses/frames, styling, body composition), NOT habits-only and NOT surgery. Each item: {"title": short name, "action": exactly what to do, "reason": why it suits THEIR specific face (reference the actual feature, e.g. "rounder face" or "weak chin"), "feature": which area it targets (jawline, hair, eyes, skin, cheekbones, structure, style)}. Example reasoning: a rounder/softer face → a sharp angular goatee to add length and sharpen the jaw.\n\nRespond with ONLY this exact JSON format, no other text, no markdown:\n{"overall":72,"skin":61,"skin_reason":"Good texture but minor acne scarring visible","jawline":78,"jawline_reason":"Good definition, chin slightly soft","eyes":74,"eyes_reason":"Strong brow ridge, slightly uneven alignment","cheekbones":66,"cheekbones_reason":"Moderate prominence, could be more defined","masculinity":75,"masculinity_reason":"Solid bone structure and facial width","hair":70,"hair_reason":"Good density, hairline slightly receding","potential":96,"subtraits":{"skin":[{"name":"Skin clarity","tier":"developing","percentile":42,"why":"Uneven clarity reads as fatigue and lowers perceived skin health.","protocol":{"fix":"Nightly adapalene + AM SPF for 8 weeks","difficulty":"Easy","timeline":"6-8 weeks","cost":"$"}},{"name":"Texture","tier":"strong","percentile":71},{"name":"Even tone","tier":"balanced","percentile":55}],"jawline":[{"name":"Gonial angle","tier":"strong","percentile":74},{"name":"Chin projection","tier":"priority","percentile":31,"why":"A recessed chin shortens the lower third and softens the jaw.","protocol":{"fix":"Chin-tuck + mewing tongue posture daily","difficulty":"Medium","timeline":"3-6 months","cost":"Free"}},{"name":"Submental fullness","tier":"developing","percentile":38,"why":"Fullness under the chin blurs the jaw's edge.","protocol":{"fix":"Lower body-fat via cardio + lower sodium","difficulty":"Medium","timeline":"8-12 weeks","cost":"Free"}}],"eyes":[{"name":"Canthal tilt","tier":"ideal","percentile":82},{"name":"Eye spacing","tier":"balanced","percentile":54},{"name":"Under-eye hollowing","tier":"developing","percentile":36,"why":"Dark hollows add perceived age and tiredness.","protocol":{"fix":"Caffeine eye cream + 8h sleep + hydration","difficulty":"Easy","timeline":"4-6 weeks","cost":"$"}}],"cheekbones":[{"name":"Zygomatic prominence","tier":"balanced","percentile":58},{"name":"Ogee curve","tier":"strong","percentile":69}],"masculinity":[{"name":"Facial width","tier":"strong","percentile":72},{"name":"Bone structure","tier":"balanced","percentile":60}],"hair":[{"name":"Density","tier":"ideal","percentile":80},{"name":"Hairline maturity","tier":"developing","percentile":40,"why":"Slight temple recession weakens the frame of the face.","protocol":{"fix":"Textured fringe to cover temples; consider dermatologist","difficulty":"Easy","timeline":"1 week (styling)","cost":"$"}}]},"harmony":[{"name":"Facial thirds","value":"Balanced","status":"ideal"},{"name":"Facial width-to-height ratio","value":"1.9","status":"balanced"},{"name":"Facial symmetry","value":"High","status":"ideal"}],"appearance":{"hairstyle":"short wavy dark hair, low fade","facialHair":"clean-shaven"},"recommendations":[{"title":"Grow an angular goatee","action":"Trim a short goatee with sharp, defined edges","reason":"Adds vertical length to sharpen a rounder face","feature":"jawline"},{"title":"Textured crop with volume on top","action":"Ask your barber for a textured crop, height on top","reason":"Balances a wide face and frames the eyes","feature":"hair"}]}` },
          ],
        }],
      });

      const text = data?.content
        ? data.content.filter((i: any) => i.type === 'text').map((i: any) => i.text).join('')
        : '';
      if (!text) { console.error('Scan failed: empty response from API', JSON.stringify(data).slice(0, 300)); fail(); return; }

      let parsed: any;
      try { parsed = extractJSON(text); } catch { fail(); return; }
      // Claude flagged the photo as too low-quality to rate — show a SPECIFIC
      // nudge (dark/blurry/framing/etc.) so the retry actually improves.
      if (parsed && (parsed.reject === true || parsed.no_face === true)) {
        failBadScan(typeof parsed.issue === 'string' ? parsed.issue : undefined);
        return;
      }
      // Only accept a complete, numeric score set — anything else is a failure.
      const required = ['overall', ...CATEGORY_KEYS];
      if (!parsed || !required.every(k => typeof parsed[k] === 'number')) { fail(); return; }
      finish(parsed);
    } catch (err) {
      console.error('Claude API error:', err);
      fail();
    }
  };

  // Import a photo instead of scanning — same consent/region/business gates as
  // a live scan (it's the same biometric processing), then the system picker.
  // The picked photo runs through the exact same analysis path as a capture.
  const startImport = async () => {
    if (scanning || analyzing || checkingRegion) return;
    pendingConsentAction.current = 'import';

    if (regionRef.current !== 'ok') {
      setCheckingRegion(true);
      const result = await verifyRegion();
      setCheckingRegion(false);
      regionRef.current = result;
      if (result !== 'ok') {
        setRegionStatus(result);
        setShowRegionBlock(true);
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(() => {});
        return;
      }
    }

    if (!consentRef.current) {
      Haptics.selectionAsync().catch(() => {});
      setShowConsent(true);
      return;
    }

    if (!isSubscribed && scanCount > 0) {
      setShowWeeklyLimitModal(true);
      return;
    }

    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.6, // keeps even 48MP shots under the API's per-image size cap
      base64: true,
    }).catch(() => null);
    if (!res || res.canceled || !res.assets?.[0]?.base64) return;

    // Reset the previous scan's results, then analyze the imported photo.
    setScanned(false);
    setScanError(false);
    setShowResults(false);
    setOrderErr('');
    setReasons({});
    setRecommendations([]);
    setSubtraits({});
    setHarmony([]);
    const b64 = res.assets[0].base64;
    setFrontPhotoBase64(b64);
    setScanned(true);
    Haptics.selectionAsync().catch(() => {});
    await analyzeWithClaude([b64]);
  };

  const startScan = async () => {
    if (scanning || checkingRegion) return;
    pendingConsentAction.current = 'scan';

    // (The old first-scan "how to scan" card gate was removed — the guided scan
    // now teaches the poses inline with per-step arrows and a countdown, so we no
    // longer interrupt the first scan with a separate dialog. The card is still
    // reachable any time via the ⓘ "How to scan" button.)

    // Regional availability gate. Face scanning is disabled in Illinois (BIPA).
    // We verify once per session via GPS; after a successful check regionRef is
    // 'ok' and we skip straight through. iOS remembers the permission grant, so
    // this won't re-prompt every time.
    if (regionRef.current !== 'ok') {
      setCheckingRegion(true);
      const result = await verifyRegion();
      setCheckingRegion(false);
      regionRef.current = result;
      if (result !== 'ok') {
        setRegionStatus(result);
        setShowRegionBlock(true);
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(() => {});
        return;
      }
    }

    // Biometric/AI-processing consent gate. The first time the user scans, we
    // must get explicit, documented consent before any photo is captured — this
    // is what makes the consent language in the Privacy Policy real.
    if (!consentRef.current) {
      Haptics.selectionAsync().catch(() => {});
      setShowConsent(true);
      return;
    }

    // Lifetime gate: free users get ONE scan ever — rescanning is a Pro perk.
    // (Replaced the old 1-per-week free gate per the cost-guard decision.)
    if (!isSubscribed && scanCount > 0) {
      setShowWeeklyLimitModal(true);
      return;
    }

    // Begin the guided scan at step 0. Capture is HANDS-FREE from here: the
    // head-pose detector (onFacesDetected → poseMet) fires the 3-2-1 countdown
    // once you hold the target pose. A small "Capture now" link is the manual
    // fallback if detection ever can't see your face.
    scanGenRef.current++;        // invalidate any capture loop from a previous scan
    capturingRef.current = false;
    shotsRef.current = [];
    lastFaceBoundsRef.current = null;
    setCountdown(null);
    setCapturing(false);
    setScanning(true);
    setScanned(false);
    setScanError(false);
    setBadScan(false);
    setShowResults(false);
    resultsSlide.setValue(height); // sheet parks closed, wherever it was left
    setFrontPhotoBase64(null);
    setFrontFaceBox(null);
    setOrderErr('');
    setReasons({});
    setRecommendations([]);
    setSubtraits({});
    setHarmony([]);
    setScanStep(0);
    guideProgress.setValue(0);
    ringFlash.setValue(0);
    scanLineAnim.setValue(0);
    flashAnim.setValue(0);
    stopAllAnimations();
    Haptics.selectionAsync().catch(() => {});
  };

  // In-memory capture: JPEG bytes → base64, Photo disposed immediately so nothing
  // is ever written to disk (consistent with the privacy policy).
  const captureOne = async (): Promise<string> => {
    try {
      const photo = await photoOutput.capturePhoto({ enableShutterSound: false }, {});
      try {
        const buffer = await photo.getFileDataAsync();
        return bytesToBase64(new Uint8Array(buffer));
      } finally {
        photo.dispose();
      }
    } catch (e) { console.error('Camera capture error:', e); return ''; }
  };

  // Capture the current guided step: run a 3-2-1 countdown (so the user knows
  // exactly when the shot lands and can hold the pose), take the photo, play
  // the lock-in feedback, then advance — or finish + analyze after the final
  // pose. Guarded against cancellation/restart at every await via scanGenRef.
  const captureCurrentStep = async () => {
    if (!scanning || capturing || capturingRef.current) return;
    capturingRef.current = true;
    const gen = scanGenRef.current;
    const i = scanStep;
    setCapturing(true);

    // Sweep the scan line while we count down and capture, so the frame reads as
    // actively scanning.
    scanLineAnim.setValue(0);
    const sweep = Animated.loop(
      Animated.timing(scanLineAnim, { toValue: 1, duration: 1400, useNativeDriver: true })
    );
    sweep.start();

    // True whenever the scan this loop belongs to was cancelled or replaced
    // while we were awaiting — bail out without capturing/advancing/analyzing.
    const stale = () => gen !== scanGenRef.current;
    const bail = () => {
      sweep.stop();
      scanLineAnim.setValue(0);
      setCountdown(null);
      setCapturing(false);
      capturingRef.current = false;
    };

    // 3 · 2 · 1 countdown — snappy ~0.25s ticks (halved for the 2×-faster scan),
    // and a deliberately SHORT beat after "1" so the shutter lands right on the
    // count ("3, 2, 1-click"), not "3, 2, 1 ... click" (the capture itself adds
    // its own latency).
    for (let n = 3; n >= 1; n--) {
      if (stale()) { bail(); return; }
      setCountdown(n);
      Haptics.selectionAsync().catch(() => {});
      await wait(n === 1 ? 130 : 250);
    }
    if (stale()) { bail(); return; }
    setCountdown(null);

    let b64 = await captureOne();
    if (!b64 && !stale()) {
      // One retry — a transient capture failure shouldn't hole the shot set.
      await wait(180);
      if (!stale()) b64 = await captureOne();
    }
    if (stale()) { bail(); return; }
    // Index-addressed (not push) so a failed shot leaves a hole instead of
    // shifting later poses down — shots[0] must always be the FRONT photo.
    if (b64) shotsRef.current[i] = b64;
    // Snapshot the face box for the FRONT shot only (step 0) — normalized to
    // 0-1 fractions of the screen so the biometric face map can anchor its
    // mesh/pins to the real detected face instead of a fixed template.
    if (b64 && i === 0) {
      const b = lastFaceBoundsRef.current;
      const box: FaceBox | null = b ? { x: b.x / width, y: b.y / height, w: b.width / width, h: b.height / height } : null;
      setFrontFaceBox(box);
      if (box) AsyncStorage.setItem(STORAGE.frontFaceBox, JSON.stringify(box)).catch(() => {});
      else AsyncStorage.removeItem(STORAGE.frontFaceBox).catch(() => {});
    }
    sweep.stop();
    scanLineAnim.setValue(0);

    // Lock-in feedback: advance the ring, flash it green, quick screen flash + buzz.
    Animated.timing(guideProgress, { toValue: (i + 1) / SCAN_STEPS.length, duration: 450, useNativeDriver: false }).start();
    Animated.sequence([
      Animated.timing(ringFlash, { toValue: 1, duration: 140, useNativeDriver: false }),
      Animated.timing(ringFlash, { toValue: 0, duration: 360, useNativeDriver: false }),
    ]).start();
    Animated.sequence([
      Animated.timing(flashAnim, { toValue: 1, duration: 100, useNativeDriver: true }),
      Animated.timing(flashAnim, { toValue: 0, duration: 320, useNativeDriver: true }),
    ]).start();
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
    await wait(250);
    if (stale()) { bail(); return; }

    if (i + 1 >= SCAN_STEPS.length) {
      // Done — the front-facing shot (step 0) is annotated on the results sheet.
      const shots = shotsRef.current;
      if (shots[0]) setFrontPhotoBase64(shots[0]);
      setCapturing(false);
      capturingRef.current = false;
      setScanning(false);
      setScanned(true);
      await analyzeWithClaude(shots); // always runs; falls back if no photos
    } else {
      setScanStep(i + 1);
      setCapturing(false);
      capturingRef.current = false;
    }
  };

  // Keep the detection callback's refs pointing at the latest values every render
  // (cheap, and the recommended pattern for a "latest value" read from a stable
  // native callback).
  captureStepRef.current = captureCurrentStep;
  scanLive.current = { scanning, capturing, scanStep, countdown };

  // Bail out of an in-progress guided scan without analyzing.
  const cancelScan = () => {
    scanGenRef.current++;        // stale-out any in-flight countdown/capture loop
    capturingRef.current = false;
    setScanning(false);
    setCapturing(false);
    setCountdown(null);
    setScanStep(0);
    shotsRef.current = [];
    guideProgress.setValue(0);
    Haptics.selectionAsync().catch(() => {});
  };

  // Abort the guided scan without haptics/analysis — used when the CAMERA is
  // the thing that failed (native session error, interruption by a phone call
  // or another app). Safe to call when no scan is running.
  const abortScanCleanly = (reason: string) => {
    if (!scanLive.current.scanning) return;
    console.warn(`Scan aborted: ${reason}`);
    scanGenRef.current++;
    capturingRef.current = false;
    setScanning(false);
    setCapturing(false);
    setCountdown(null);
    setScanStep(0);
    shotsRef.current = [];
    guideProgress.setValue(0);
  };

  // Native camera-session error: abort any scan, then recover by remounting
  // the camera (key bump) — throttled so a persistent failure can't remount-loop.
  const onCameraError = (e: unknown) => {
    console.error('Camera error:', e);
    abortScanCleanly('camera session error');
    const now = Date.now();
    if (now - lastCamRecoverRef.current < 5000) return;
    lastCamRecoverRef.current = now;
    setCameraGen(g => g + 1);
  };

  // Send the scan photo + the account's email to the order-notification
  // route so the order actually reaches an inbox. Safe to call again on its
  // own (no re-charge) if it failed the first time — see the "resend" button
  // in the order-confirmation UI.
  const sendPhotoOrder = async (): Promise<boolean> => {
    if (!frontPhotoBase64) return false;
    try {
      const accountRaw = await AsyncStorage.getItem(STORAGE.account);
      const account = accountRaw ? JSON.parse(accountRaw) : null;
      const email = typeof account?.email === 'string' ? account.email : '';
      if (!email) throw new Error('No account email on file');
      const res = await fetch(`${process.env.EXPO_PUBLIC_API_BASE}/api/order-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: frontPhotoBase64, email }),
      });
      if (!res.ok) throw new Error(`order-photo ${res.status}`);
      return true;
    } catch (err) {
      console.error('Order notification failed:', err);
      return false;
    }
  };

  // Buy the $40 human-edited-photo order (Pro not required — this is its own
  // one-time purchase, separate from the "pro" entitlement). Explicit button
  // tap only. Fulfillment is manual — see app/api/order-photo+api.ts.
  const startPhotoOrder = async () => {
    if (!frontPhotoBase64 || orderLoading || photoOrdered) return;
    if (!potentialAgeOkRef.current) {
      Haptics.selectionAsync().catch(() => {});
      setShowPotentialAgeGate(true);
      return;
    }
    setOrderErr('');
    setOrderLoading(true);
    try {
      const res = await buyHumanPhoto();
      if (res.status === 'cancelled') return; // backed out — stay as-is, silently
      if (res.status === 'unavailable') {
        Alert.alert(
          'Not available right now',
          'This isn’t available to purchase right now. You have not been charged. Please try again in a little while.'
        );
        return;
      }
      if (res.status === 'error') {
        Alert.alert('Purchase didn’t complete', res.message || 'Something went wrong and you have not been charged. Please try again.');
        return;
      }
      // Purchase succeeded — they're charged regardless of whether the order
      // notification email goes through, so always show "order received" and
      // just flag the relay separately if it failed.
      setPhotoOrdered(true);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
      const sent = await sendPhotoOrder();
      if (!sent) setOrderErr('Order confirmed, but we had trouble sending your photo — tap to retry.');
    } finally {
      setOrderLoading(false);
    }
  };

  const confirmPotentialAge = () => {
    potentialAgeOkRef.current = true;
    AsyncStorage.setItem(STORAGE.potentialAgeOk, '1').catch(() => {});
    setShowPotentialAgeGate(false);
    startPhotoOrder();
  };

  // `hideBubbles` is checked with `=== true` so that call sites wired straight
  // to onPress (which pass a GestureResponderEvent) still show the bubbles —
  // only an explicit openResults(true) from the breakdown button hides them.
  const openResults = (hideBubbles: unknown = false) => {
    if (!scanned) return;
    setHideResultBubbles(hideBubbles === true);
    setSheetContentMounted(true); // mount the heavy content now that it's opening
    setShowResults(true);
    resultsSlide.setValue(height);
    Animated.spring(resultsSlide, { toValue: 0, useNativeDriver: true, friction: 8, tension: 60 }).start();
  };

  const closeResults = () => {
    Animated.timing(resultsSlide, { toValue: height, duration: 300, useNativeDriver: true }).start(() => {
      setShowResults(false);
      setSheetContentMounted(false); // fully closed — free the images/SVG again
      // Post-teaser conversion chain (fires ONCE, ever): a free user who has
      // never created an account just finished viewing their results teaser —
      // prompt account creation (skippable), which then opens the paywall.
      if (!isSubscribed && authName === null && !accountNudgedRef.current) {
        accountNudgedRef.current = true;
        AsyncStorage.setItem(STORAGE.accountNudged, '1').catch(() => {});
        setTimeout(() => setShowAccountPrompt(true), 350);
      }
    });
  };

  // Gesture handlers are created once, so they must read the latest state/
  // callbacks through refs — otherwise they capture stale values from the
  // first render and the swipe silently stops working.
  const gestureLive = useRef({ scanned, showResults, openResults, closeResults });
  gestureLive.current = { scanned, showResults, openResults, closeResults };

  // Where resultsSlide sat when the current drag began — captured on grant so
  // the sheet keeps following the finger even if it grabs mid-animation.
  const dragStartRef = useRef(height);

  const mainSwipePanResponder = useRef(
    PanResponder.create({
      // Claim upward drags ANYWHERE on the scan screen, at the CAPTURE phase.
      // The scan view sits on top of the native VisionCamera surface, which can
      // otherwise swallow the move negotiation so the bubble-phase handler
      // never fires — that's why the advertised "swipe up for results" gesture
      // silently did nothing.
      onMoveShouldSetPanResponderCapture: (_, g) => {
        const { scanned, showResults } = gestureLive.current;
        return scanned && !showResults && g.dy < -6 && Math.abs(g.dy) > Math.abs(g.dx) * 1.1;
      },
      onMoveShouldSetPanResponder: (_, g) => {
        const { scanned, showResults } = gestureLive.current;
        return scanned && !showResults && g.dy < -6 && Math.abs(g.dy) > Math.abs(g.dx) * 1.1;
      },
      onPanResponderGrant: () => {
        // Mount the heavy sheet content as the drag begins so it's there to
        // follow the finger up (it's deferred while closed, keeping tab
        // switches snappy).
        setSheetContentMounted(true);
        dragStartRef.current = resultsSlideValueRef.current;
      },
      // Live-follow: the sheet tracks the finger 1:1 instead of only snapping
      // into place on release — that's what made the old gesture feel unsmooth.
      onPanResponderMove: (_, g) => {
        const next = Math.max(0, Math.min(height, dragStartRef.current + g.dy));
        resultsSlide.setValue(next);
      },
      onPanResponderRelease: (_, g) => {
        const { scanned } = gestureLive.current;
        if (!scanned) return;
        const open = resultsSlideValueRef.current < height * 0.6 || g.vy < -0.5;
        if (open) {
          setHideResultBubbles(false); // swipe-up on Scan always shows the full sheet
          setShowResults(true);
          Animated.spring(resultsSlide, { toValue: 0, useNativeDriver: true, friction: 8, tension: 60 }).start();
        } else {
          Animated.timing(resultsSlide, { toValue: height, duration: 220, useNativeDriver: true }).start(() => {
            setShowResults(false);
            setSheetContentMounted(false); // drag cancelled back to closed — free it
          });
        }
      },
    })
  ).current;

  // Only the sheet's top handle claims downward drags — dragging within the
  // scrollable results content must scroll it, not close the sheet.
  const sheetPanResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) => g.dy > 4 && Math.abs(g.dy) > Math.abs(g.dx) * 1.1,
      onPanResponderGrant: () => {
        // Halt any in-flight open/close animation (native-driven) before the
        // drag takes over — otherwise the settling spring keeps overriding our
        // per-frame setValue and the sheet "jumps" instead of tracking the
        // finger. stopAnimation hands back the exact current value to drag from.
        dragStartRef.current = resultsSlideValueRef.current;
        resultsSlide.stopAnimation((v) => { if (typeof v === 'number') dragStartRef.current = v; });
      },
      onPanResponderMove: (_, g) => {
        if (g.dy < 0) return; // the handle only closes — no re-dragging further open from here
        const next = Math.max(0, Math.min(height, dragStartRef.current + g.dy));
        resultsSlide.setValue(next);
      },
      onPanResponderRelease: (_, g) => {
        const shouldClose = resultsSlideValueRef.current > height * 0.22 || g.vy > 0.6;
        if (shouldClose) {
          // Route through closeResults (via the live ref) so the post-teaser
          // account-prompt nudge also fires on a swipe-down close.
          gestureLive.current.closeResults();
        } else {
          Animated.spring(resultsSlide, { toValue: 0, useNativeDriver: true, friction: 8, tension: 60 }).start();
        }
      },
    })
  ).current;

  const scanLineY = scanLineAnim.interpolate({ inputRange: [0, 1], outputRange: [0, faceHeight] });

  // authReady prevents a flash while the stored session/profile load.
  if (!authReady || !profileReady) return <View style={styles.container} />;
  // Onboarding questionnaire on a fresh install — it feeds the AI scoring
  // prompt, so it stays BEFORE the scan. There is deliberately NO login gate
  // anymore (scan-first onboarding): the account prompt appears after the
  // results teaser instead, and it's skippable.
  if (!profile) {
    return <OnboardingQuestionnaire onDone={(p) => setProfile(p)} />;
  }

  if (!hasPermission) {
    // The native prompt auto-fires on mount (effect above). If we're still here,
    // the user dismissed/denied it — and iOS won't show the system dialog again,
    // so the only way back is the Settings app.
    return (
      <View style={[styles.container, { alignItems: 'center', justifyContent: 'center', padding: 32 }]}>
        <Text style={styles.permissionText}>Maxify needs camera access to scan your face.</Text>
        <Text style={[styles.permissionText, { fontSize: 14, opacity: 0.7, marginBottom: 24 }]}>
          Enable it in Settings, then come back.
        </Text>
        <TouchableOpacity style={styles.permissionButton} onPress={() => Linking.openSettings()}>
          <Text style={styles.permissionButtonText}>OPEN SETTINGS</Text>
        </TouchableOpacity>
      </View>
    );
  }
  if (!device) {
    return (
      <View style={[styles.container, { alignItems: 'center', justifyContent: 'center' }]}>
        <Text style={styles.permissionText}>Preparing camera…</Text>
      </View>
    );
  }

  const potentialScore = scores.potential ?? '--';
  // The small over-time assist, recomputed live so it ticks up as weeks pass and
  // tasks get completed. Applied to displayed scores (not potential — that's the
  // ceiling). bestScore is the ratchet floor for the headline OVERALL.
  const scoreBoost = computeBoost(journeyStart, lifetimeTasks);
  // After a relaunch `scores` is empty but history persists — fall back to the
  // last recorded scan (history stores display values already).
  const lastScan = scanHistory.length > 0 ? scanHistory[scanHistory.length - 1] : null;
  // No displayScore lift here — potential is already the shown ceiling value
  // (clamped 89-99 in finish()), not a raw score that needs the friendliness
  // curve. Lifting it on top used to compress everyone into a ~96-99 band.
  const potentialDisplay: number | string = typeof scores.potential === 'number'
    ? scores.potential
    : lastScan ? lastScan.potential : '--';
  // Keep the camera warm on Home too (not just Scan) so tapping the center
  // scan button jumps straight to a live preview instead of a ~1s cold start.
  // Deliberately NOT warmed on Results/Guides/Profile — no reason to run the
  // camera while the user is just browsing those. The persistent camera layer
  // stays opacity:0 off the Scan tab, so warming it on Home is invisible.
  const cameraActive = isFocused && appActive && (tab === 'scan' || tab === 'home');

  // Accent for the Home tab's task cards — follows the active program week's
  // gradient so Home and Program feel like one system.
  const homeAccent = program
    ? program.weeks[Math.min(programCurrentDay(program.generatedAt).weekIdx, program.weeks.length - 1)].gradient[0]
    : PRIMARY_BLUE;

  // First-launch tour. Trimmed to three quick steps (Welcome → Scan → Program)
  // so onboarding doesn't interrupt the user repeatedly — the scores/swipe-up
  // hint is folded into the Scan step, and the redundant closing step is gone.
  const tutorialSteps: TutorialStep[] = [
    {
      badge: 'WELCOME', color: PRIMARY_BLUE, target: null,
      title: 'Welcome to Maxify',
      body: "Your AI glow-up coach. Here's a 15-second tour so you know where everything is.",
    },
    {
      badge: 'STEP 1 · SCAN', color: '#43e0ff',
      // Spotlights the raised center scan button in the nav bar — the app now
      // lands on Home, so the camera oval isn't on screen.
      target: { x: width / 2 - 36, y: height - BOTTOM_INSET - NAV_BAR_HEIGHT - 30, w: 72, h: 72, r: 36 },
      title: 'Scan your face',
      body: "Tap the camera button when you're ready — arrows and a countdown guide each pose. Our AI rates 6 features in seconds, then swipe up for your full results.",
    },
    {
      badge: 'MOST IMPORTANT', color: '#ff4dd2',
      // Home (first of the nav items) — where the streak, daily tasks, and the
      // full 8-week plan all live now.
      target: { x: 8, y: height - BOTTOM_INSET - NAV_BAR_HEIGHT - 8, w: 84, h: NAV_BAR_HEIGHT + 14, r: 16 },
      title: 'Your daily plan',
      body: 'Home is where the real change happens — your streak, daily habits, and full 8-week plan, all on one screen. Open it every day and check off your tasks.',
    },
  ];

  const tabSubtitles: Record<string, string | null> = {
    home: null,
    scan: null,
    guides: 'GUIDES',
    results: 'RESULTS',
    profile: 'PROFILE',
  };

  return (
    <View style={styles.container}>
      {/* ── Persistent camera layer ──────────────────────────────────────
          Mounted ONCE for the whole lifetime of this screen. It used to live
          inside `{tab === 'scan' && (...)}`, so every time the user left the
          Scan tab (which happens automatically right after a scan finishes)
          the <Camera> unmounted — deallocating the AVCaptureSession and
          tripping an AVFoundation teardown assertion (`__assert_rtn` in
          `-[AVCaptureOutput detachFromFigCaptureSession:]`) → SIGABRT. That is
          the "random crash during/after scans."
          The fix is VisionCamera's intended pattern: keep it mounted and use
          `isActive` to pause it. `cameraActive` is already false off the Scan
          tab, so the session stops there; opacity hides the frozen frame; and
          it renders behind the opaque top/nav bars (zIndex 10) and every
          opaque tab screen, so it's invisible unless you're scanning.
          `key={cameraGen}` still allows a deliberate remount for native-error
          recovery (throttled in onCameraError); `outputs` is the frozen array
          so the session is configured exactly once. */}
      <Animated.View
        pointerEvents="none"
        style={[StyleSheet.absoluteFill, { opacity: tab === 'scan' ? 1 : 0, transform: [{ scale: zoomAnim }] }]}
      >
        <VisionCamera
          key={cameraGen}
          style={StyleSheet.absoluteFill}
          device={device}
          isActive={cameraActive}
          outputs={cameraOutputs}
          onError={onCameraError}
          onInterruptionStarted={() => abortScanCleanly('camera interrupted')}
        />
      </Animated.View>

      <View style={styles.topBar}>
        {/* Gradient-clipped wordmark (SVG). Replaces the old glowing Text —
            textShadowRadius renders as a rectangular box on the New
            Architecture, and PULSE wants a gradient wordmark, not a glow. */}
        <GradientText text="MAXIFY" size={26} width={250} height={40} letterSpacing={9} align="center" />
        {/* Streak chip — small, corner-placed. Hidden on Home (its hero already
            leads with the streak, no need to show it twice). */}
        {streakDays > 0 && tab !== 'home' && (
          <View style={styles.streakChip}>
            <Ionicons name="flame" size={13} color={FLAME} style={styles.streakChipFlame} />
            <Text style={styles.streakChipText}>{streakDays}</Text>
          </View>
        )}
        {tabSubtitles[tab] && (
          <Text style={styles.tabSubtitle}>{tabSubtitles[tab]}</Text>
        )}
      </View>

      {tab === 'home' && (
        <Animated.View style={[styles.tabContent, { opacity: tabFade, transform: [{ translateX: tabSlide }] }]}>
          <HomeTab
            userName={authName ?? ''}
            streakDays={streakDays}
            overall={bestScore != null ? bestScore : (typeof scores.overall === 'number' ? Math.round(scores.overall) : null)}
            potential={typeof potentialDisplay === 'number' ? potentialDisplay : null}
            tasks={todayTasksForStats}
            completed={completed}
            toggleTask={toggleTask}
            program={program}
            isSubscribed={isSubscribed}
            accent={homeAccent}
            onScan={() => switchTab('scan')}
            onUpgrade={openUpgradeFlow}
            onNewProgram={() => setShowNewProgramModal(true)}
            recommendations={recommendations}
            subtraits={subtraits}
            freezeAvailable={freezeAvailable}
            freezeNotice={freezeNotice}
            onDismissFreezeNotice={() => { setFreezeNotice(null); Haptics.selectionAsync().catch(() => {}); }}
            rescans={programRescans}
          />
        </Animated.View>
      )}

      {tab === 'scan' && (
        <View style={{ flex: 1 }} {...mainSwipePanResponder.panHandlers}>
          {/* The live camera preview is the persistent layer mounted at the
              root of this component (see "Persistent camera layer" above) and
              renders behind these overlays. It is deliberately NOT mounted
              here — remounting it per-tab is what deallocated the capture
              session and crashed the app. */}
          <Animated.View
            pointerEvents="none"
            style={[StyleSheet.absoluteFill, { backgroundColor: '#43e0ff', opacity: flashAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 0.3] }) }]}
          />

          {/* Full-screen spotlight — single SVG, no rectangular boundary */}
          <View pointerEvents="none" style={StyleSheet.absoluteFill}>
            <SilhouetteSpotlight
              screenW={width} screenH={height}
              faceLeft={(width - faceWidth) / 2} faceTop={faceTop}
              faceW={faceWidth} faceH={faceHeight}
            />
          </View>

          <View pointerEvents="none" style={{ position: 'absolute', top: faceTop, left: (width - faceWidth) / 2, width: faceWidth, height: faceHeight }}>
            <FaceSilhouette width={faceWidth} height={faceHeight} />
            {capturing && (
              <Animated.View style={[styles.scanLine, { transform: [{ translateY: scanLineY }] }]} />
            )}
          </View>

          {/* Guided-scan progress ring — wraps the oval frame, fills as the 4
              positions are captured, pulses green on each lock-in. */}
          {scanning && (
            <View
              pointerEvents="none"
              style={{ position: 'absolute', top: faceTop - 14, left: (width - faceWidth) / 2 - 17, width: faceWidth + 34, height: faceHeight + 28 }}
            >
              <GuideRing width={faceWidth + 34} height={faceHeight + 28} progress={guideProgress} flash={ringFlash} />
            </View>
          )}

          {/* Directional cue — a big arrow beside the oval pointing the way the
              user should turn their head (front-camera preview is mirrored, so
              "your left" points screen-left). Shown only while waiting for the
              CAPTURE tap; the countdown takes over the oval once tapped. */}
          {scanning && !capturing && (() => {
            const dir = SCAN_STEPS[scanStep].dir;
            const ovalLeft = (width - faceWidth) / 2;
            const cy = faceTop + faceHeight / 2;
            if (dir === 'front') {
              return (
                <View pointerEvents="none" style={{ position: 'absolute', top: cy - 30, left: width / 2 - 30 }}>
                  <ScanDirectionCue dir="front" />
                </View>
              );
            }
            if (dir === 'down') {
              return (
                <View pointerEvents="none" style={{ position: 'absolute', top: faceTop + faceHeight + 4, left: width / 2 - 30 }}>
                  <ScanDirectionCue dir="down" />
                </View>
              );
            }
            const left = dir === 'left' ? ovalLeft - 64 : ovalLeft + faceWidth + 4;
            return (
              <View pointerEvents="none" style={{ position: 'absolute', top: cy - 34, left }}>
                <ScanDirectionCue dir={dir} />
              </View>
            );
          })()}

          {/* 3-2-1 countdown — big number centered in the oval so the user knows
              exactly when the shot lands. */}
          {scanning && countdown !== null && (
            <View
              pointerEvents="none"
              style={{ position: 'absolute', top: faceTop, left: (width - faceWidth) / 2, width: faceWidth, height: faceHeight, alignItems: 'center', justifyContent: 'center' }}
            >
              <View style={styles.countdownCircle}>
                <Text style={styles.countdownText}>{countdown}</Text>
              </View>
            </View>
          )}

          {!scanned && !scanning && (
            <View style={[styles.statusWrapper, { top: TOP_BAR_HEIGHT + 16 }]}>
              <View style={styles.statusPill}>
                <Text style={styles.scanText}>ALIGN YOUR FACE</Text>
              </View>
              <TouchableOpacity style={[styles.howToReplay, { flexDirection: 'row', alignItems: 'center', gap: 5 }]} onPress={() => setShowScanHowTo(true)} activeOpacity={0.7}>
                <Ionicons name="information-circle-outline" size={15} color="rgba(255,255,255,0.7)" />
                <Text style={styles.howToReplayText}>How to scan</Text>
              </TouchableOpacity>
              {/* Daily tip — one per day, dismissible, a reason to open the app */}
              {!tipDismissed && (
                <View style={styles.tipCard}>
                  <Ionicons name="bulb-outline" size={18} color="#FFD700" />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.tipLabel}>DAILY TIP</Text>
                    <Text style={styles.tipText}>{DAILY_TIPS[Math.floor(Date.now() / 86400000) % DAILY_TIPS.length]}</Text>
                  </View>
                  <TouchableOpacity onPress={dismissTip} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons name="close" size={16} color="rgba(255,255,255,0.5)" />
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}

          {scanning && (
            <View style={[styles.statusWrapper, { top: TOP_BAR_HEIGHT + 16 }]}>
              <Text style={styles.guideStepCount}>STEP {scanStep + 1} OF {SCAN_STEPS.length}</Text>
              <View style={styles.guidePill}>
                <Text style={styles.guideInstruction}>{SCAN_STEPS[scanStep].instruction}</Text>
              </View>
              <Text style={styles.guideHint}>
                {countdown !== null ? 'Hold that pose…' : capturing ? 'Capturing…' : SCAN_STEPS[scanStep].hint}
              </Text>
            </View>
          )}

          {scanned && !scanning && (
            <View style={[styles.statusWrapper, { top: TOP_BAR_HEIGHT + 16 }]}>
              <TouchableOpacity onPress={openResults} activeOpacity={0.7}>
                <Animated.View style={[
                  styles.swipeHint,
                  {
                    shadowOpacity: swipeHintAnim.interpolate({ inputRange: [0, 1], outputRange: [0.3, 0.9] }),
                    transform: [{ scale: swipeHintAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.03] }) }],
                  },
                ]}>
                  <Text style={styles.swipeHintArrow}>↑</Text>
                  <Text style={styles.swipeHintText}>
                    {analyzing ? 'ANALYZING...' : 'SWIPE UP FOR FULL RESULTS'}
                  </Text>
                  <Text style={styles.swipeHintArrow}>↑</Text>
                </Animated.View>
              </TouchableOpacity>
            </View>
          )}

          {/* Post-scan warning only — shown when a scan came back with no clear
              face or a technical failure. No live per-frame feedback anymore. */}
          {!scanning && (badScan || scanError) && (
            <View style={[styles.scanFeedbackBanner, styles.scanFeedbackBannerWarn]}>
              <Text style={[styles.scanFeedbackText, styles.scanFeedbackTextWarn]}>
                {badScan
                  ? badScanReason
                  : 'Analysis failed — tap RESCAN to try again'}
              </Text>
            </View>
          )}

          <View style={[styles.scanBarWrapper, { bottom: SCAN_BAR_BOTTOM }]}>
            {scanning ? (
              <>
                {/* Auto-capture: the head-pose detector fires the countdown once
                    you hold the pose — this pill is a status readout, not a button. */}
                <View style={[styles.scanBar, (capturing || countdown !== null) && styles.scanBarActive]}>
                  <Text style={[styles.scanBarText, (capturing || countdown !== null) && styles.scanBarTextActive]}>
                    {countdown !== null
                      ? 'HOLD STILL…'
                      : capturing
                        ? 'CAPTURING…'
                        : scanStep === 0
                          ? 'CENTER YOUR FACE'
                          : 'HOLD THE POSE'}
                  </Text>
                </View>
                <View style={styles.scanBarLinksRow}>
                  {/* Manual fallback if detection can't see the face. */}
                  <TouchableOpacity
                    onPress={captureCurrentStep}
                    disabled={capturing || countdown !== null}
                    activeOpacity={0.7}
                    hitSlop={{ top: 6, bottom: 6, left: 10, right: 10 }}
                  >
                    <Text style={styles.importLink}>Capture now</Text>
                  </TouchableOpacity>
                  <Text style={styles.scanBarLinkDot}>·</Text>
                  <TouchableOpacity
                    onPress={cancelScan}
                    disabled={capturing || countdown !== null}
                    activeOpacity={0.7}
                    hitSlop={{ top: 6, bottom: 6, left: 10, right: 10 }}
                  >
                    <Text style={styles.importLink}>Cancel scan</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <>
                <TouchableOpacity style={styles.scanBar} onPress={startScan} activeOpacity={0.8}>
                  <Text style={styles.scanBarText}>{scanned ? 'RESCAN' : 'SCAN'}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={startImport} activeOpacity={0.7} hitSlop={{ top: 6, bottom: 6, left: 10, right: 10 }}>
                  <Text style={styles.importLink}>Can&apos;t scan? Import from Photos</Text>
                </TouchableOpacity>
              </>
            )}
          </View>

          <View style={[styles.bubblesRow, { bottom: BUBBLES_BOTTOM }]}>
            <View style={styles.sideBubble}>
              {isSubscribed ? (
                <ScoreBubble label="SKIN" score={scores.skin ?? '--'} size="small" onPress={scanned ? openResults : undefined} pulse={scanned ? pulseAnim : undefined} glowRing={ringGlowAnim} boost={scoreBoost} />
              ) : (
                <TouchableOpacity style={styles.lockedSideBubble} onPress={openUpgradeFlow} activeOpacity={0.8}>
                  <Ionicons name="lock-closed" size={18} color="rgba(255,255,255,0.55)" style={styles.lockedSideLock} />
                  <Text style={styles.lockedSideLabel}>SKIN</Text>
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.centerBubble}>
              <ScoreBubble label="OVERALL" score={scores.overall ?? '--'} arcColor={CYAN} size="large" onPress={scanned ? openResults : undefined} pulse={scanned ? pulseAnim : undefined} glowRing={ringGlowAnim} boost={scoreBoost} floor={bestScore} />
            </View>
            <View style={styles.sideBubble}>
              {isSubscribed ? (
                <ScoreBubble label="JAWLINE" score={scores.jawline ?? '--'} size="small" onPress={scanned ? openResults : undefined} pulse={scanned ? pulseAnim : undefined} glowRing={ringGlowAnim} boost={scoreBoost} />
              ) : (
                <TouchableOpacity style={styles.lockedSideBubble} onPress={openUpgradeFlow} activeOpacity={0.8}>
                  <Ionicons name="lock-closed" size={18} color="rgba(255,255,255,0.55)" style={styles.lockedSideLock} />
                  <Text style={styles.lockedSideLabel}>JAWLINE</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Analysis loading state — rendered last so it sits over the scan UI,
              but the nav bar (higher zIndex, rendered at root) stays on top and
              tappable so the user can leave while analysis runs. */}
          {analyzing && <ScanAnalyzingOverlay />}
        </View>
      )}

      {tab === 'guides' && (
        <Animated.View style={[styles.tabContent, { opacity: tabFade, transform: [{ translateX: tabSlide }] }]}>
          <GuidesTab
            isSubscribed={isSubscribed}
            onSubscribe={openUpgradeFlow}
            onXp={addXp}
            recommendations={recommendations}
          />
        </Animated.View>
      )}
      {tab === 'results' && (
        <Animated.View style={[styles.tabContent, { opacity: tabFade, transform: [{ translateX: tabSlide }] }]}>
          <ResultsTab
            history={scanHistory}
            isSubscribed={isSubscribed}
            onSubscribe={openUpgradeFlow}
            day1Photo={day1Photo}
            latestPhoto={frontPhotoBase64}
            canViewBreakdown={scanned}
            onViewBreakdown={() => { switchTab('scan'); setTimeout(() => openResults(true), 40); }}
            subtraits={subtraits}
          />
        </Animated.View>
      )}
      {tab === 'profile' && (
        <Animated.View style={[styles.tabContent, { opacity: tabFade, transform: [{ translateX: tabSlide }] }]}>
          <ProfileTab
            scanCount={scanCount}
            bestScore={bestScore}
            streakDays={streakDays}
            xp={xp}
            badges={badges}
            isSubscribed={isSubscribed}
            onSubscribe={openUpgradeFlow}
            onRestore={onRestore}
            onRedeemPromo={() => { setPromoCode(''); setPromoError(''); setShowPromoInput(true); }}
            userName={authName ?? ''}
            onLogout={handleLogout}
            onOpenLegal={(doc) => setShowLegal(doc)}
            onDeleteAccount={() => setShowDeleteModal(true)}
          />
        </Animated.View>
      )}

      <View style={styles.bottomBar}>
        <NavBar tab={tab} setTab={(t) => switchTab(t)} />
      </View>

      {revealData !== null && (
        <ScoreRevealOverlay
          score={revealData.score}
          delta={revealData.delta}
          isFirst={revealData.isFirst}
          isBest={revealData.isBest}
          onClose={() => { setRevealData(null); openResults(); }}
        />
      )}

      {streakCelebration !== null && (
        <StreakCelebration count={streakCelebration} onClose={() => setStreakCelebration(null)} />
      )}

      {levelUp !== null && (
        <LevelUpOverlay level={levelUp} onClose={() => setLevelUp(null)} />
      )}

      {badgeUnlock !== null && (
        <BadgeUnlockOverlay
          badge={badgeUnlock.def}
          extra={badgeUnlock.extra}
          onClose={() => setBadgeUnlock(null)}
        />
      )}

      {statsMoment !== null && (
        <StatsMomentOverlay moment={statsMoment} onClose={() => setStatsMoment(null)} />
      )}

      {showConfetti && <ConfettiBurst />}

      {/* ── Premium paywall — full-screen, 3 tiers, weekly trial preselected ── */}
      {showSubscribeModal && (
        <View style={styles.paywallOverlay}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.paywallScroll} showsVerticalScrollIndicator={false}>
            <TouchableOpacity style={styles.paywallClose} onPress={() => setShowSubscribeModal(false)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={22} color="rgba(255,255,255,0.45)" />
            </TouchableOpacity>

            <Text style={styles.paywallEyebrow}>MAXIFY PRO</Text>
            <Text style={styles.paywallTitle}>Unlock Your Complete Analysis</Text>
            <Text style={styles.paywallSub}>
              You&apos;ve already completed your scan — and it uncovered insights you haven&apos;t seen yet. Don&apos;t let your progress stop here.
            </Text>

            {/* Value comparison — anchor the price against traditional options
                BEFORE the tiers appear, so $6.99/week reads as a bargain by
                contrast the moment they see it. */}
            <View style={styles.compareCard}>
              <Text style={styles.compareTitle}>A SMALL INVESTMENT COMPARED TO TRADITIONAL OPTIONS</Text>
              {[
                { label: 'Rhinoplasty', value: '$5,000–$15,000+' },
                { label: 'Chin implant', value: '$3,000–$8,000+' },
                { label: 'Orthodontic treatment', value: '$3,000–$7,000+' },
              ].map((r, i) => (
                <View key={i} style={styles.compareRow}>
                  <Text style={styles.compareLabel}>{r.label}</Text>
                  <Text style={styles.compareValue}>{r.value}</Text>
                </View>
              ))}
              <View style={[styles.compareRow, styles.compareRowMaxify]}>
                <Text style={styles.compareLabelMaxify}>Maxify Pro</Text>
                <Text style={styles.compareValueMaxify}>from $6.99/week</Text>
              </View>
              <Text style={styles.compareFinePrint}>
                Typical U.S. price ranges, shown for general comparison only. Maxify is a self-improvement app — not a medical service, and not a substitute for professional advice or procedures.
              </Text>
            </View>

            {/* Tier selector — weekly (trial) preselected, yearly spotlighted */}
            {([
              { id: 'weekly' as const, title: 'Weekly', price: '$6.99', per: '/week', badge: '3-DAY FREE TRIAL', badgeColor: '#43e0ff' },
              { id: 'monthly' as const, title: 'Monthly', price: '$14.99', per: '/month' },
              { id: 'annual' as const, title: 'Yearly', price: '$49.99', per: '/year', badge: 'BEST VALUE · SAVE 86%', badgeColor: '#FFD700', note: 'Less than $1/week' },
            ]).map((t) => {
              const sel = selectedPlan === t.id;
              return (
                <TouchableOpacity
                  key={t.id}
                  style={[styles.tierRow, sel && styles.tierRowSelected]}
                  onPress={() => { setSelectedPlan(t.id); Haptics.selectionAsync().catch(() => {}); }}
                  activeOpacity={0.85}
                >
                  <View style={[styles.tierRadio, sel && { borderColor: ACCENT }]}>
                    {sel && <View style={styles.tierRadioInner} />}
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text style={styles.tierTitle}>{t.title}</Text>
                      {t.badge && (
                        <View style={[styles.tierBadge, { borderColor: `${t.badgeColor}66`, backgroundColor: `${t.badgeColor}14` }]}>
                          <Text style={[styles.tierBadgeText, { color: t.badgeColor }]}>{t.badge}</Text>
                        </View>
                      )}
                    </View>
                    {t.note ? <Text style={styles.tierNote}>{t.note}</Text> : null}
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={styles.tierPrice}>{t.price}</Text>
                    <Text style={styles.tierPer}>{t.per}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}

            {purchasing ? (
              <View style={styles.paywallCtaLoading}>
                <ActivityIndicator color={BG} />
              </View>
            ) : (
              <GradientPill
                label={selectedPlan === 'weekly' ? 'START MY 3-DAY FREE TRIAL' : 'UNLOCK MAXIFY PRO'}
                onPress={onUnlock}
                height={54}
                glowColor={MAGENTA}
                style={{ marginTop: 8 }}
              />
            )}
            <Text style={styles.modalPriceHint}>
              {selectedPlan === 'weekly'
                ? '3 days free, then $6.99/week · auto-renews · cancel anytime'
                : selectedPlan === 'annual'
                  ? '$49.99/year · auto-renews · cancel anytime'
                  : '$14.99/month · auto-renews · cancel anytime'}
            </Text>

            {/* Feature reinforcement — right before the exit points, as a final
                "here's everything you get" reminder after they've seen price. */}
            <View style={styles.paywallFeatures}>
              {[
                'All 6 scores + your full trait breakdown',
                '8-week personalized plan built on your scan',
                'Unlimited scans — track every change',
                'AI coaching, daily habits & streaks',
              ].map((f, i) => (
                <View key={i} style={styles.modalFeatureRow}>
                  <Ionicons name="checkmark-circle" size={16} color={ACCENT} />
                  <Text style={styles.modalFeatureText}>{f}</Text>
                </View>
              ))}
            </View>

            <TouchableOpacity onPress={onRestore} activeOpacity={0.7}>
              <Text style={styles.paywallRestore}>Restore purchases</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowSubscribeModal(false)} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>not right now</Text>
            </TouchableOpacity>
            {/* Apple Guideline 3.1.2 — required auto-renewable subscription
                disclosure. Keep this verbatim-ish; reviewers look for it. */}
            <Text style={styles.paywallDisclosure}>
              Payment is charged to your Apple ID at confirmation of purchase. Your subscription automatically renews unless it is canceled at least 24 hours before the end of the current period, and your account is charged for renewal within 24 hours before the end of the current period. Any unused portion of a free trial is forfeited when you purchase a subscription. Manage or cancel anytime in your App Store account settings.
            </Text>
            <View style={styles.modalLegalRow}>
              <Text style={styles.modalLegalLink} onPress={() => { setShowSubscribeModal(false); setShowLegal('terms'); }}>Terms (EULA)</Text>
              <Text style={styles.modalLegalDot}> · </Text>
              <Text style={styles.modalLegalLink} onPress={() => { setShowSubscribeModal(false); setShowLegal('privacy'); }}>Privacy Policy</Text>
            </View>
          </ScrollView>
        </View>
      )}

      {/* ── Post-logout login screen — plain auth, no paywall chaining ── */}
      {showLoginScreen && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 320, elevation: 320 }]}>
          <LoginScreen
            onAuthed={(n) => {
              setAuthName(n || '');
              setShowLoginScreen(false);
            }}
          />
        </View>
      )}

      {/* ── Post-teaser account prompt (skippable) — then the paywall ── */}
      {showAccountPrompt && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 320, elevation: 320 }]}>
          <LoginScreen
            postScan
            onAuthed={(n) => {
              setAuthName(n || '');
              setShowAccountPrompt(false);
              if (!isSubscribed) setTimeout(() => setShowSubscribeModal(true), 320);
            }}
          />
        </View>
      )}

      {showWeeklyLimitModal && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Ionicons name="sparkles" size={30} color={ACCENT} style={styles.modalEmoji} />
            <Text style={styles.modalTitle}>Free Scan Used</Text>
            <Text style={styles.modalSubtitle}>
              You&apos;ve used your free scan.{'\n'}Go Pro to rescan anytime, track your progress week over week, and unlock your full breakdown.
            </Text>
            <TouchableOpacity
              style={styles.modalSubscribeBtn}
              onPress={() => { setShowWeeklyLimitModal(false); setTimeout(openUpgradeFlow, 250); }}
              activeOpacity={0.85}
            >
              <Text style={styles.modalSubscribeBtnText}>UPGRADE TO PRO</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowWeeklyLimitModal(false)} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>close</Text>
            </TouchableOpacity>
            <View style={styles.modalLegalRow}>
              <Text style={styles.modalLegalLink} onPress={() => { setShowWeeklyLimitModal(false); setShowLegal('terms'); }}>Terms</Text>
              <Text style={styles.modalLegalDot}> · </Text>
              <Text style={styles.modalLegalLink} onPress={() => { setShowWeeklyLimitModal(false); setShowLegal('privacy'); }}>Privacy Policy</Text>
            </View>
          </View>
        </View>
      )}

      {/* New Program confirmation modal */}
      {showNewProgramModal && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Ionicons name="warning-outline" size={30} color="#FFB84D" style={styles.modalEmoji} />
            <Text style={styles.modalTitle}>Start Fresh?</Text>
            <Text style={styles.modalSubtitle}>
              Your <Text style={{ color: '#FF6600', fontFamily: FONT_BOLD }}>{streakDays}-day streak</Text> and all daily task progress will be permanently lost.{'\n\n'}A brand new 8-week program will be generated from your last scan.
            </Text>
            <TouchableOpacity style={[styles.modalSubscribeBtn, { backgroundColor: '#FF3B30' }]} onPress={confirmNewProgram} activeOpacity={0.85}>
              <Text style={styles.modalSubscribeBtnText}>YES, START FRESH</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowNewProgramModal(false)} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Scan consent — shown before the first scan (biometric/AI consent) */}
      {showConsent && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Ionicons name="camera-outline" size={30} color={ACCENT} style={styles.modalEmoji} />
            <Text style={styles.modalTitle}>Before Your First Scan</Text>
            <Text style={styles.modalSubtitle}>
              To rate your face, Maxify takes a photo and sends it to our AI provider for analysis.{'\n\n'}Your photo isn&apos;t stored by us after your scores come back and is never used to identify you. A copy stays only on your device so your results survive a restart — deleting your account erases it.{'\n\n'}By continuing you confirm you&apos;re 13 or older — and if you&apos;re under 18, that your parent or guardian agrees.
            </Text>
            <TouchableOpacity style={styles.modalSubscribeBtn} onPress={acceptConsent} activeOpacity={0.85}>
              <Text style={styles.modalSubscribeBtnText}>I AGREE — SCAN MY FACE</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowConsent(false)} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>not now</Text>
            </TouchableOpacity>
            <View style={styles.modalLegalRow}>
              <Text style={styles.modalLegalLink} onPress={() => { setShowConsent(false); setShowLegal('terms'); }}>Terms</Text>
              <Text style={styles.modalLegalDot}> · </Text>
              <Text style={styles.modalLegalLink} onPress={() => { setShowConsent(false); setShowLegal('privacy'); }}>Privacy Policy</Text>
            </View>
          </View>
        </View>
      )}

      {/* One-time 18+ confirmation for the hand-edited photo order */}
      {showPotentialAgeGate && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Ionicons name="sparkles" size={30} color="#ff4dd2" style={styles.modalEmoji} />
            <Text style={styles.modalTitle}>Adults Only</Text>
            <Text style={styles.modalSubtitle}>
              This is a photo edited by a real person, so it&apos;s only available to users 18 and older.{'\n\n'}Your photo is sent securely to be edited and the finished result is emailed back to you — it isn&apos;t stored beyond fulfilling your order.
            </Text>
            <TouchableOpacity style={styles.modalSubscribeBtn} onPress={confirmPotentialAge} activeOpacity={0.85}>
              <Text style={styles.modalSubscribeBtnText}>I&apos;M 18 OR OLDER — CONTINUE</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowPotentialAgeGate(false)} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>not yet</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Regional availability — face scanning disabled in Illinois (BIPA) */}
      {showRegionBlock && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Ionicons name={regionStatus === 'blocked' ? 'location-outline' : 'navigate-outline'} size={30} color="#FFB84D" style={styles.modalEmoji} />
            <Text style={styles.modalTitle}>
              {regionStatus === 'blocked' ? 'Not Available in Your Region' : 'Location Needed'}
            </Text>
            <Text style={styles.modalSubtitle}>
              {regionStatus === 'blocked'
                ? 'Due to Illinois biometric privacy law, face analysis isn’t available to users in Illinois.\n\nYou can still use your full program, daily tasks, and progress tracking — everything except the face scan.'
                : 'Maxify needs your location once to confirm face analysis is available in your region. Your location is used only for this check and is never saved.\n\nEnable location in Settings, then try again.'}
            </Text>
            {regionStatus === 'denied' && (
              <TouchableOpacity style={styles.modalSubscribeBtn} onPress={retryRegionCheck} activeOpacity={0.85} disabled={checkingRegion}>
                <Text style={styles.modalSubscribeBtnText}>{checkingRegion ? 'CHECKING…' : 'TRY AGAIN'}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={() => setShowRegionBlock(false)} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>{regionStatus === 'blocked' ? 'OK' : 'not now'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Promo code (opened from Profile → Settings → Redeem Promo Code) */}
      {showPromoInput && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Ionicons name="pricetag-outline" size={30} color={ACCENT} style={styles.modalEmoji} />
            <Text style={styles.modalTitle}>Redeem Promo Code</Text>
            <Text style={styles.modalSubtitle}>Enter your code to unlock Maxify Pro.</Text>
            <TextInput
              style={styles.promoInput}
              placeholder="Promo code"
              placeholderTextColor="rgba(255,255,255,0.3)"
              value={promoCode}
              onChangeText={(t) => { setPromoCode(t); setPromoError(''); }}
              autoCapitalize="characters"
              autoCorrect={false}
              returnKeyType="done"
              onSubmitEditing={submitPromoCode}
            />
            {promoError ? <Text style={styles.promoError}>{promoError}</Text> : null}
            <TouchableOpacity style={styles.modalSubscribeBtn} onPress={submitPromoCode} activeOpacity={0.85}>
              <Text style={styles.modalSubscribeBtnText}>APPLY CODE</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { setShowPromoInput(false); setPromoError(''); }} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Delete account confirmation */}
      {showDeleteModal && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Ionicons name="trash-outline" size={30} color="#FF3B30" style={styles.modalEmoji} />
            <Text style={styles.modalTitle}>Delete Account &amp; Data?</Text>
            <Text style={styles.modalSubtitle}>
              This permanently erases everything on this device — your account, scores, scan history, streak, and program.{'\n\n'}This can&apos;t be undone.
            </Text>
            <TouchableOpacity style={[styles.modalSubscribeBtn, { backgroundColor: '#FF3B30' }]} onPress={confirmDeleteAccount} activeOpacity={0.85}>
              <Text style={styles.modalSubscribeBtnText}>DELETE EVERYTHING</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowDeleteModal(false)} activeOpacity={0.7}>
              <Text style={styles.modalDismissText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Privacy Policy / Terms full-screen viewer. Needs an explicit high
          zIndex/elevation: the top bar and nav bar carry zIndex:10, so without
          this the nav bar stacks ABOVE this overlay (RN honors zIndex over JSX
          order) — it covered the "‹ Back" button and left the user trapped. */}
      {showLegal && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 300, elevation: 300 }]}>
          <LegalScreen doc={showLegal === 'privacy' ? PRIVACY_POLICY : TERMS_OF_SERVICE} onClose={() => setShowLegal(null)} />
        </View>
      )}

      {/* Mounted whenever there's a scan to show (not just while open) so an
          upward drag from the closed position has something to live-follow —
          it just sits translated fully below the screen until dragged/opened. */}
      {scanned && tab === 'scan' && (
        <Animated.View style={[styles.resultsSheet, { transform: [{ translateY: resultsSlide }] }]}>
          {/* Only this header claims downward drags — dragging inside the
              scrollable content below must scroll it, not close the sheet. */}
          <View {...sheetPanResponder.panHandlers}>
            <View style={styles.dragHandle} />
            <Text style={styles.resultsTitle}>YOUR RESULTS</Text>
            <Text style={styles.resultsSubtitle}>{analyzing ? 'AI is analyzing your face...' : 'Swipe down to close'}</Text>
          </View>

          {/* Heavy content (base64 photos, SVG breakdown, before/after slider)
              mounts only once the sheet is open or being dragged — see
              sheetContentMounted. While closed it stays unmounted so switching
              onto the Scan tab doesn't pay to decode off-screen images. */}
          {sheetContentMounted && (
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.resultsScroll}>
            {/* Score bubbles — hidden when opened from the Results tab's "View
                full breakdown" (the Results tab already leads with the scores). */}
            {!hideResultBubbles && (
              <>
            <View style={styles.resultsTopRow}>
              <View style={{ alignItems: 'center' }}>
                <ScoreBubble label="OVERALL" score={scores.overall ?? '--'} arcColor={CYAN} size="xlarge" glowRing={ringGlowAnim} boost={scoreBoost} floor={bestScore} />
                {/* PB tag — only when the LATEST scan beat every previous one */}
                {scanHistory.length > 1 &&
                  scanHistory[scanHistory.length - 1].overall > Math.max(...scanHistory.slice(0, -1).map(h => h.overall)) && (
                  <View style={[styles.pbTag, { flexDirection: 'row', alignItems: 'center', gap: 5 }]}>
                    <Ionicons name="trophy" size={12} color="#FFD700" />
                    <Text style={styles.pbTagText}>PERSONAL BEST</Text>
                  </View>
                )}
              </View>
              {isSubscribed ? (
                <ScoreBubble label="POTENTIAL" score={potentialScore} arcColor="#ff4dd2" size="xlarge" glowRing={ringGlowAnim} />
              ) : (
                /* Teaser: the potential score exists but stays locked — the
                   overall number above is the free payoff, this is the gap. */
                <TouchableOpacity style={styles.lockedPotential} onPress={openUpgradeFlow} activeOpacity={0.8}>
                  <Ionicons name="lock-closed" size={24} color="#ff9ae8" />
                  <Text style={styles.lockedPotentialLabel}>POTENTIAL</Text>
                  <Text style={styles.lockedPotentialHint}>Tap to reveal</Text>
                </TouchableOpacity>
              )}
            </View>

            <View style={{ position: 'relative', width: '100%' }}>
              <View style={[styles.resultsGrid, !isSubscribed && { opacity: 0.12 }]}>
                {CATEGORIES.map((cat) => {
                  const pct = typeof scores[cat.key] === 'number'
                    ? topPercentLabel(displayScore(scores[cat.key], scoreBoost))
                    : null;
                  return (
                    <View key={cat.key} style={styles.catBubbleWrap}>
                      <ScoreBubble label={cat.label} score={scores[cat.key] ?? '--'} size="medium" glowRing={ringGlowAnim} boost={scoreBoost} />
                      {isSubscribed && pct ? (
                        <Text style={styles.catPercentile}>↑ {pct}</Text>
                      ) : null}
                      {isSubscribed && reasons[cat.key] ? (
                        <Text style={styles.catReason}>{reasons[cat.key]}</Text>
                      ) : null}
                    </View>
                  );
                })}
              </View>
              {!isSubscribed && (
                <ProLock
                  title="Full Breakdown"
                  sub="See your detailed scores across all 6 categories"
                  onUnlock={() => { closeResults(); setTimeout(openUpgradeFlow, 320); }}
                />
              )}
            </View>
              </>
            )}

            {/* Biometric Face Map — interactive numbered trait pins + callout */}
            {frontPhotoBase64 && (
              <View style={styles.faceMapContainer}>
                <SectionHeader title="BIOMETRIC FACE MAP" sub="Tap any point to inspect that trait" accent={PRIMARY_BLUE} />
                {isSubscribed && Object.keys(subtraits).length > 0 ? (
                  <InteractiveFaceMap photo={frontPhotoBase64} pins={buildPins(subtraits)} box={frontFaceBox} />
                ) : (
                  <View style={[styles.faceMapWrapper, { width: FACE_MAP_W, height: FACE_MAP_H }]}>
                    <ExpoImage
                      source={{ uri: `data:image/jpeg;base64,${frontPhotoBase64}` }}
                      style={[styles.faceMapPhoto, !isSubscribed && { opacity: 0.12 }]}
                      contentFit="cover"
                      cachePolicy="memory"
                    />
                    <View style={[StyleSheet.absoluteFill, !isSubscribed && { opacity: 0.35 }]} pointerEvents="none">
                      <FaceMesh w={FACE_MAP_W} h={FACE_MAP_H} scores={scores} boost={scoreBoost} box={frontFaceBox} />
                    </View>
                    <View style={[styles.mapCorner, styles.mapCornerTL]} pointerEvents="none" />
                    <View style={[styles.mapCorner, styles.mapCornerTR]} pointerEvents="none" />
                    <View style={[styles.mapCorner, styles.mapCornerBL]} pointerEvents="none" />
                    <View style={[styles.mapCorner, styles.mapCornerBR]} pointerEvents="none" />
                    {!isSubscribed && (
                      <ProLock
                        title="Biometric Map"
                        sub="Tap-to-inspect pins on every feature, rated and ranked"
                        cta="UNLOCK YOUR FACIAL MAP"
                        onUnlock={() => { closeResults(); setTimeout(openUpgradeFlow, 320); }}
                      />
                    )}
                  </View>
                )}
              </View>
            )}

            {/* Trait Breakdown — per-category named sub-traits, Qoves-style */}
            {Object.keys(subtraits).length > 0 && (
              <View style={styles.breakdownSection}>
                <SectionHeader title="TRAIT BREAKDOWN" sub="Every feature, rated and ranked" accent="#43e0ff" />
                <View style={{ position: 'relative', width: '100%' }}>
                  <View style={!isSubscribed ? { opacity: 0.12 } : undefined}>
                    {CATEGORIES.map((cat) => {
                      const traits = subtraits[cat.key];
                      if (!traits || traits.length === 0) return null;
                      const disp = typeof scores[cat.key] === 'number' ? displayScore(scores[cat.key], scoreBoost) : null;
                      return (
                        <View key={cat.key} style={styles.breakdownCard}>
                          <View style={styles.breakdownHeader}>
                            {frontPhotoBase64 && CAT_ANCHOR[cat.key] && (() => {
                              const a = mapPoint(CAT_ANCHOR[cat.key], frontFaceBox);
                              return <FaceCrop photo={frontPhotoBase64} ax={a.x} ay={a.y} size={44} />;
                            })()}
                            <Text style={[styles.breakdownCat, { flex: 1, marginLeft: frontPhotoBase64 ? 10 : 0 }]}>{cat.label}</Text>
                            {disp !== null && (
                              <Text style={[styles.breakdownScore, { color: getScoreColor(disp) }]}>{disp}</Text>
                            )}
                          </View>
                          {traits.map((t, i) => (
                            <View key={i} style={styles.traitRow2}>
                              <View style={styles.traitRowTop}>
                                <Text style={styles.traitName} numberOfLines={1}>{t.name}</Text>
                                <View style={[styles.tierTag, { borderColor: tierColor(t.tier), backgroundColor: tierColor(t.tier) + '22' }]}>
                                  <Text style={[styles.tierTagText, { color: tierColor(t.tier) }]}>{tierLabel(t.tier)}</Text>
                                </View>
                              </View>
                              {typeof t.percentile === 'number' && (
                                <View style={styles.traitBarRow}>
                                  <PercentBar pct={t.percentile} color={tierColor(t.tier)} />
                                  <Text style={[styles.traitPct, { color: tierColor(t.tier) }]}>{ordinal(t.percentile)}</Text>
                                </View>
                              )}
                            </View>
                          ))}
                        </View>
                      );
                    })}
                  </View>
                  {!isSubscribed && (
                    <ProLock
                      title="Trait Breakdown"
                      sub="See every feature broken into named traits, clinically rated"
                      onUnlock={() => { closeResults(); setTimeout(openUpgradeFlow, 320); }}
                    />
                  )}
                </View>
              </View>
            )}

            {/* Facial Harmony — proportion/ratio metrics (credibility layer) */}
            {harmony.length > 0 && (
              <View style={styles.breakdownSection}>
                <SectionHeader title="FACIAL HARMONY" sub="Proportions & symmetry from your scan" accent="#FFD700" />
                <View style={{ position: 'relative', width: '100%' }}>
                  <View style={[styles.harmonyCard, !isSubscribed && { opacity: 0.12 }]}>
                    {harmony.map((m, i) => (
                      <View key={i} style={[styles.harmonyRow, i === harmony.length - 1 && { borderBottomWidth: 0 }]}>
                        <Text style={styles.harmonyName}>{m.name}</Text>
                        <View style={styles.harmonyRight}>
                          <Text style={styles.harmonyValue}>{m.value}</Text>
                          <View style={[styles.harmonyDot, { backgroundColor: tierColor(m.status === 'flag' ? 'weak' : m.status) }]} />
                        </View>
                      </View>
                    ))}
                  </View>
                  {!isSubscribed && (
                    <ProLock
                      title="Facial Harmony"
                      sub="Anthropometric ratios and symmetry, measured from your scan"
                      onUnlock={() => { closeResults(); setTimeout(openUpgradeFlow, 320); }}
                    />
                  )}
                </View>
              </View>
            )}

            {/* Hand-edited photo order — deliberately placed AFTER the free/Pro
                value (face map, trait breakdown, harmony), not leading the
                sheet. A one-time $40 purchase (not tied to Pro), asked right
                after someone just paid for a subscription reads as a second
                money-grab and risks buyer's-remorse cancellations — better to
                let them sit with what they already got first. A real
                retoucher's edit of the front capture; delivered by email,
                fulfillment is manual — see app/api/order-photo+api.ts.
                Hidden until the IAP is actually purchasable (see
                photoOfferAvailable) — but still shown to anyone who already
                ordered, so their confirmation never disappears. */}
            {frontPhotoBase64 && (photoOfferAvailable || photoOrdered) && (
              <View style={styles.faceMapContainer}>
                <SectionHeader title="HAND-EDITED PHOTO" sub="A real retoucher's take on your best self" accent="#ff4dd2" />
                <View style={[styles.faceMapWrapper, { width: FACE_MAP_W, height: FACE_MAP_H }]}>
                  <ExpoImage
                    source={{ uri: `data:image/jpeg;base64,${frontPhotoBase64}` }}
                    style={[styles.faceMapPhoto, { opacity: 0.35 }]}
                    contentFit="cover"
                    cachePolicy="memory"
                  />
                  <View style={styles.potentialCta}>
                    {photoOrdered ? (
                      <>
                        <Ionicons name="checkmark-circle" size={24} color="#ff4dd2" />
                        <Text style={styles.potentialSub}>
                          Order received — your hand-edited photo will be emailed to you within a few days.
                        </Text>
                        {orderErr ? (
                          <TouchableOpacity
                            style={[styles.potentialRegenBtn, { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }]}
                            onPress={async () => {
                              setOrderErr('');
                              const sent = await sendPhotoOrder();
                              if (!sent) setOrderErr('Still having trouble — tap to retry.');
                            }}
                            activeOpacity={0.7}
                          >
                            <Ionicons name="refresh" size={14} color="#ff4dd2" />
                            <Text style={styles.potentialRegenText}>{orderErr}</Text>
                          </TouchableOpacity>
                        ) : null}
                      </>
                    ) : orderLoading ? (
                      <>
                        <Ionicons name="sparkles" size={24} color="#ff4dd2" />
                        <Text style={styles.potentialLoadingText}>PLACING YOUR ORDER…</Text>
                      </>
                    ) : (
                      <>
                        <Ionicons name="sparkles" size={24} color="#ff4dd2" />
                        <Text style={styles.potentialSub}>
                          Get this photo hand-edited by a real retoucher — clearer skin, sharper jaw, better hair — delivered to your email in a few days.
                        </Text>
                        <TouchableOpacity style={styles.potentialBtn} onPress={startPhotoOrder} activeOpacity={0.85}>
                          <Text style={styles.potentialBtnText}>ORDER FOR $40</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>
                </View>
              </View>
            )}

            <TouchableOpacity
              style={[styles.unlockButton, { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }]}
              onPress={() => {
                closeResults();
                // Pro → their plan on Home; free → the upgrade flow.
                setTimeout(() => { if (isSubscribed) switchTab('home'); else openUpgradeFlow(); }, 320);
              }}
            >
              <Ionicons name={isSubscribed ? 'clipboard-outline' : 'lock-open'} size={16} color="#00131c" />
              <Text style={styles.unlockButtonText}>{isSubscribed ? 'VIEW MY PLAN' : 'UNLOCK YOUR POTENTIAL'}</Text>
            </TouchableOpacity>
            <Text style={styles.unlockSubtext}>
              {isSubscribed
                ? 'See your personalized program and style fixes'
                : <>Get your personalized program and reach your{' '}<Text style={{ color: '#ff4dd2' }}>{potentialDisplay} potential</Text></>
              }
            </Text>

            {/* Blurred plan teaser — free users only. Pro users own the real
                plan (the CTA above already routes them to it); showing them a
                "unlock to reveal" lock on something they've paid for was a bug. */}
            {!isSubscribed && (
              <View style={styles.samplePlanContainer}>
                <Text style={styles.samplePlanTitle}>YOUR PERSONALIZED PLAN</Text>
                <View style={styles.samplePlanBlur}>
                  {['Week 1: Jawmaxxing', 'Week 2: Skinmaxxing', 'Week 3: Eyemaxxing', 'Week 4: Gymmaxxing'].map((item, i) => (
                    <View key={i} style={styles.samplePlanItem}>
                      <Text style={styles.samplePlanItemText}>{item}</Text>
                    </View>
                  ))}
                  <View style={styles.blurOverlay} />
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Ionicons name="lock-closed" size={13} color="rgba(255,255,255,0.6)" />
                  <Text style={styles.samplePlanLock}>Unlock to reveal your full plan</Text>
                </View>
              </View>
            )}
          </ScrollView>
          )}
        </Animated.View>
      )}

      {showTutorial && (
        <TutorialOverlay
          steps={tutorialSteps}
          onDone={() => {
            setShowTutorial(false);
            AsyncStorage.setItem(STORAGE.tutorialDone, '1').catch(() => {});
            // Ask for notification permission with context; new user is free + no scan yet.
            scheduleReminders({ isSubscribed: false, lastScanDate: null });
          }}
        />
      )}

      {showScanHowTo && <ScanHowToCard onDismiss={dismissScanHowTo} />}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  topBar: {
    position: 'absolute', top: 0, left: 0, right: 0, height: TOP_BAR_HEIGHT,
    backgroundColor: BG, justifyContent: 'flex-end', alignItems: 'center',
    paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: 'rgba(67,224,255,0.12)', zIndex: 10,
  },
  // Legacy wordmark style — no longer rendered (replaced by GradientText).
  header: {
    color: PRIMARY_BLUE, fontSize: 30, fontFamily: FONT_BOLD, letterSpacing: 10,
  },
  tabSubtitle: {
    color: CYAN, fontSize: 11, fontFamily: FONT_SEMI, letterSpacing: 4,
    marginTop: 4, opacity: 0.75,
  },
  streakChip: {
    position: 'absolute', left: 12, bottom: 8, flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(255,138,0,0.14)', borderRadius: 14, borderWidth: 1, borderColor: 'rgba(255,138,0,0.5)',
    paddingHorizontal: 9, paddingVertical: 5, gap: 3,
  },
  streakChipFlame: { fontSize: 13 },
  streakChipText: { color: '#FFB05A', fontSize: 13, fontFamily: FONT_BOLD },
  // Duolingo-style streak celebration
  streakOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(8,4,0,0.94)',
    justifyContent: 'center', alignItems: 'center', zIndex: 200,
  },
  streakRays: { position: 'absolute', alignItems: 'center', justifyContent: 'center' },
  streakFlameWrap: { alignItems: 'center', justifyContent: 'center' },
  streakFlare: {
    position: 'absolute', width: 150, height: 150, borderRadius: 75,
    backgroundColor: '#FF8A00',
  },
  streakNumber: {
    color: '#fff', fontSize: 96, fontFamily: FONT_BOLD, marginTop: -6,
    textShadowColor: '#FF8A00', textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 28,
  },
  streakDayLabel: {
    color: '#FF8A00', fontSize: 22, fontFamily: FONT_BOLD, letterSpacing: 8, marginTop: -4,
  },
  streakSubtext: {
    color: 'rgba(255,255,255,0.75)', fontSize: 15, fontFamily: FONT_SEMI,
    textAlign: 'center', marginTop: 18, paddingHorizontal: 40, lineHeight: 22,
  },
  streakContinueBtn: {
    marginTop: 40, backgroundColor: '#FF8A00', paddingHorizontal: 44, paddingVertical: 16,
    borderRadius: 50, shadowColor: '#FF8A00', shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9, shadowRadius: 24,
  },
  streakContinueText: { color: '#fff', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 2 },

  // Score reveal overlay ("pull the lever")
  revealOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.96)',
    justifyContent: 'center', alignItems: 'center', zIndex: 210, paddingHorizontal: 32,
  },
  revealPulseRing: {
    width: 150, height: 150, borderRadius: 75, borderWidth: 3, marginBottom: 26,
  },
  revealSuspenseText: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 4 },
  revealLabel: { color: 'rgba(255,255,255,0.55)', fontSize: 14, fontFamily: FONT_BOLD, letterSpacing: 5, marginBottom: 6 },
  revealNumber: { fontSize: 120, fontFamily: FONT_BOLD, lineHeight: 132 },
  revealDelta: { fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 0.5, marginTop: 6, marginBottom: 30 },
  revealBtn: {
    backgroundColor: PRIMARY_BLUE, borderRadius: 50, paddingVertical: 15, paddingHorizontal: 40,
    shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.8, shadowRadius: 18,
  },
  revealBtnText: { color: '#000', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 1.5 },

  // Level-up overlay + Profile level card
  levelUpOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(6,0,10,0.95)',
    justifyContent: 'center', alignItems: 'center', zIndex: 205, paddingHorizontal: 32,
  },
  levelUpLabel: { color: '#ff9ae8', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 6, marginBottom: 22 },
  levelUpBadge: {
    width: 150, height: 150, borderRadius: 75, borderWidth: 4, borderColor: '#ff4dd2',
    backgroundColor: 'rgba(191,0,255,0.12)', alignItems: 'center', justifyContent: 'center',
    shadowColor: '#ff4dd2', shadowOffset: { width: 0, height: 0 }, shadowRadius: 30, elevation: 10,
  },
  levelUpNumber: { color: '#fff', fontSize: 64, fontFamily: FONT_BOLD },
  levelUpSub: { color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center', marginTop: 22, marginBottom: 28, lineHeight: 20 },
  levelCard: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(191,0,255,0.08)', borderRadius: 18, borderWidth: 1.5, borderColor: 'rgba(191,0,255,0.4)',
    padding: 14, marginBottom: 16,
  },
  levelBadgeSm: {
    width: 44, height: 44, borderRadius: 22, borderWidth: 2, borderColor: '#ff4dd2',
    backgroundColor: 'rgba(191,0,255,0.15)', alignItems: 'center', justifyContent: 'center',
  },
  levelBadgeSmText: { color: '#fff', fontSize: 19, fontFamily: SERIF_BOLD },
  levelTitle: { color: '#ff9ae8', fontSize: 17, fontFamily: SERIF_SEMI, letterSpacing: -0.1 },
  levelXpText: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: FONT_BOLD },
  levelTrack: { height: 7, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.1)', overflow: 'hidden', marginTop: 8 },
  levelFill: { height: '100%', borderRadius: 4, backgroundColor: '#ff4dd2' },

  // Today's Focus hero card (Program tab)
  focusCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 20, borderWidth: 1.5,
    padding: 18, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.35, shadowRadius: 14,
  },
  focusIcon: { fontSize: 34 },
  focusLabel: { color: '#fff', fontSize: 16, fontFamily: FONT_BOLD, lineHeight: 22 },
  focusMeta: { fontSize: 12, fontFamily: FONT_BOLD, marginTop: 4, letterSpacing: 0.4 },
  focusGo: { borderRadius: 50, paddingHorizontal: 16, paddingVertical: 10 },
  focusGoText: { color: '#000', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 1 },

  // Expand/collapse toggles (weeks, concerns, fixes)
  expandToggle: { alignItems: 'center', paddingVertical: 12, marginBottom: 4 },
  expandToggleText: { color: 'rgba(255,255,255,0.45)', fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 1.5 },

  // Daily tip card (scan screen)
  tipCard: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: 'rgba(0,0,0,0.72)', borderRadius: 16, borderWidth: 1, borderColor: 'rgba(0,191,255,0.35)',
    paddingHorizontal: 14, paddingVertical: 11, marginTop: 12, marginHorizontal: 24, maxWidth: 480,
  },
  tipLabel: { color: PRIMARY_BLUE, fontSize: 9.5, fontFamily: FONT_BOLD, letterSpacing: 2, marginBottom: 3 },
  tipText: { color: 'rgba(255,255,255,0.85)', fontSize: 12.5, lineHeight: 17 },

  // Personal-best tag under the OVERALL bubble
  pbTag: {
    marginTop: 8, backgroundColor: 'rgba(255,215,0,0.12)', borderWidth: 1, borderColor: 'rgba(255,215,0,0.55)',
    borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4,
  },
  pbTagText: { color: '#FFD700', fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 1.2 },

  // Stats moment overlay (Sunday recap + 30/60/90 milestones)
  momentOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.93)',
    justifyContent: 'center', alignItems: 'center', zIndex: 204, paddingHorizontal: 26,
  },
  momentCard: {
    width: '100%', maxWidth: 400, backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 24, borderWidth: 1.5, borderColor: 'rgba(0,191,255,0.4)',
    padding: 24, alignItems: 'center',
  },
  momentTitle: { color: '#fff', fontSize: 20, fontFamily: FONT_BOLD, letterSpacing: 2.5, textAlign: 'center' },
  momentSub: { color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 6, marginBottom: 18, textAlign: 'center' },
  momentRow: {
    flexDirection: 'row', alignItems: 'center', width: '100%',
    backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 14, padding: 13, marginBottom: 9,
  },
  momentRowIcon: { fontSize: 20, marginRight: 10 },
  momentRowLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 13, fontFamily: FONT_SEMI, flex: 1 },
  momentRowValue: { color: '#fff', fontSize: 17, fontFamily: FONT_BOLD },

  // Day-1 vs Today journey card (Results tab)
  journeyCard: {
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 20, borderWidth: 1,
    borderColor: 'rgba(67,224,255,0.3)', padding: 16, marginBottom: 18,
  },
  journeyTitle: { color: 'rgba(255,255,255,0.88)', fontSize: 19, fontFamily: SERIF_SEMI, letterSpacing: -0.2, textAlign: 'center', marginBottom: 12 },
  journeyRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 },
  journeyCol: { alignItems: 'center', flex: 1, maxWidth: 130 },
  journeyPhoto: { width: '100%', aspectRatio: 3 / 4, borderRadius: 14, backgroundColor: '#111' },
  journeyTag: { color: 'rgba(255,255,255,0.55)', fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 1.5, marginTop: 8 },
  journeyScore: { color: 'rgba(255,255,255,0.9)', fontSize: 24, fontFamily: SERIF_BOLD, marginTop: 2 },
  journeyMid: { alignItems: 'center', paddingHorizontal: 2 },
  journeyDelta: { color: '#43e0ff', fontSize: 13, fontFamily: FONT_BOLD, marginTop: 4 },
  journeyTeaser: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: FONT, lineHeight: 19, textAlign: 'center', paddingHorizontal: 6, paddingBottom: 4 },

  // Results tab
  resultsTabHeadline: {
    color: '#fff', fontSize: 30, fontFamily: SERIF_BOLD, letterSpacing: -0.3, textAlign: 'center', marginTop: 14,
    textShadowColor: '#43e0ff', textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 16,
  },
  resultsTabSub: { color: 'rgba(255,255,255,0.45)', fontSize: 13, fontFamily: FONT, textAlign: 'center', marginTop: 5, marginBottom: 22 },
  graphCard: {
    backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 18, borderWidth: 1,
    borderColor: 'rgba(67,224,255,0.25)', padding: 16, marginBottom: 26,
    shadowColor: '#43e0ff', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.3, shadowRadius: 14,
  },
  graphTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  graphTitle: { color: '#43e0ff', fontSize: 17, fontFamily: SERIF_SEMI, letterSpacing: -0.1 },
  graphHint: { color: 'rgba(255,255,255,0.35)', fontSize: 10, fontFamily: FONT, fontStyle: 'italic' },
  historyHeader: { color: 'rgba(255,255,255,0.82)', fontSize: 19, fontFamily: SERIF_SEMI, letterSpacing: -0.2, marginBottom: 12 },
  historyRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 16, marginBottom: 8,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)',
  },
  historyDateCol: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  historyDate: { color: '#fff', fontSize: 15, fontFamily: FONT_BOLD },
  historyLatestTag: {
    color: '#43e0ff', fontSize: 9, fontFamily: FONT_BOLD, letterSpacing: 1,
    backgroundColor: 'rgba(67,224,255,0.12)', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2,
    overflow: 'hidden',
  },
  historyScoreWrap: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  historyScore: { color: '#fff', fontSize: 22, fontFamily: FONT_BOLD },
  historyDelta: { fontSize: 13, fontFamily: FONT_BOLD, minWidth: 34, textAlign: 'right' },
  resultsUpsell: {
    marginTop: 18, backgroundColor: 'rgba(191,0,255,0.08)', borderRadius: 16,
    borderWidth: 1, borderColor: 'rgba(191,0,255,0.35)', padding: 18, alignItems: 'center',
  },
  resultsUpsellTitle: { color: '#ff4dd2', fontSize: 19, fontFamily: SERIF_SEMI, letterSpacing: -0.2, marginBottom: 5 },
  resultsUpsellSub: { color: 'rgba(255,255,255,0.55)', fontSize: 13, fontFamily: FONT, textAlign: 'center' },

  // Spotlight dark overlays around silhouette
  darkOverlay: {
    position: 'absolute', left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 1,
  },
  darkOverlaySide: {
    position: 'absolute',
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 1,
  },

  scanLine: {
    position: 'absolute', left: 10, right: 10, height: 3,
    backgroundColor: '#43e0ff', shadowColor: '#43e0ff',
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 1, shadowRadius: 12,
  },
  statusWrapper: { position: 'absolute', left: 0, right: 0, alignItems: 'center', zIndex: 5 },

  // ── Scan analysis loading overlay ──
  // Covers the scan UI (blocks its touches) but NOT the nav bar — the bottom
  // bar carries zIndex 10 and is a root sibling, so it stays on top/tappable.
  analyzeOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(6,8,11,0.94)',
    alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 40, paddingBottom: NAV_TOTAL, zIndex: 6,
  },
  analyzeRingWrap: { width: 132, height: 132, alignItems: 'center', justifyContent: 'center', marginBottom: 34 },
  analyzeCore: {
    width: 60, height: 60, borderRadius: 30, alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(0,191,255,0.08)', borderWidth: 1, borderColor: 'rgba(0,191,255,0.3)',
  },
  analyzeTitle: {
    color: '#fff', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 4, textAlign: 'center',
  },
  analyzeStatus: {
    color: ACCENT, fontSize: 14, fontFamily: FONT_SEMI, textAlign: 'center', marginTop: 14, minHeight: 20,
  },
  analyzeLeaveHint: {
    color: 'rgba(255,255,255,0.45)', fontSize: 12.5, lineHeight: 18, textAlign: 'center',
    marginTop: 28, maxWidth: 300,
  },
  statusPill: {
    backgroundColor: 'rgba(0,0,0,0.7)', paddingHorizontal: 18, paddingVertical: 10,
    borderRadius: 22, borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)',
  },
  scanText: { color: 'rgba(255,255,255,0.85)', fontSize: 11, letterSpacing: 3, fontFamily: FONT_BOLD },
  guideStepCount: { color: PRIMARY_BLUE, fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 3, marginBottom: 8, opacity: 0.9 },
  guidePill: {
    backgroundColor: 'rgba(0,0,0,0.78)', paddingHorizontal: 22, paddingVertical: 12,
    borderRadius: 24, borderWidth: 1.5, borderColor: PRIMARY_BLUE,
    shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.7, shadowRadius: 14,
    maxWidth: width - 48,
  },
  guideInstruction: { color: '#fff', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 0.5, textAlign: 'center' },
  guideHint: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 8, letterSpacing: 0.5, textAlign: 'center' },
  // Directional turn cue shown beside the oval during a guided scan.
  scanCueArrow: {
    width: 60, height: 68, borderRadius: 34,
    backgroundColor: 'rgba(67,224,255,0.16)', borderWidth: 2, borderColor: '#43e0ff',
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#43e0ff', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.8, shadowRadius: 14,
  },
  scanCueArrowText: { color: '#43e0ff', fontSize: 44, fontFamily: FONT_BOLD, lineHeight: 48, marginTop: -2 },
  scanCueReticle: {
    width: 60, height: 60, alignItems: 'center', justifyContent: 'center',
  },
  scanCueReticleText: { color: '#43e0ff', fontSize: 52, fontFamily: FONT, opacity: 0.9 },
  // 3-2-1 countdown number centered in the oval.
  countdownCircle: {
    width: 108, height: 108, borderRadius: 54,
    backgroundColor: 'rgba(0,0,0,0.55)', borderWidth: 3, borderColor: '#43e0ff',
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#43e0ff', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.9, shadowRadius: 20,
  },
  countdownText: { color: '#fff', fontSize: 60, fontFamily: FONT_BOLD, textAlign: 'center' },
  swipeHint: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: 'rgba(0,0,0,0.85)', paddingHorizontal: 22, paddingVertical: 12,
    borderRadius: 26, borderWidth: 1.5, borderColor: PRIMARY_BLUE,
    shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 }, shadowRadius: 14,
  },
  swipeHintArrow: { color: PRIMARY_BLUE, fontSize: 16, fontFamily: FONT_BOLD },
  swipeHintText: { color: PRIMARY_BLUE, fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 2 },
  bubblesRow: {
    position: 'absolute', left: 0, right: 0,
    flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-end',
    gap: 16, paddingHorizontal: 16, zIndex: 5,
  },
  sideBubble: { marginBottom: 0 },
  centerBubble: { marginBottom: 20 },
  bubble: {
    backgroundColor: 'rgba(0,0,0,0.8)', borderRadius: 100,
    alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
  },
  bubbleTextContainer: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' },
  bubbleLabel: { color: '#fff', letterSpacing: 1.2, opacity: 0.7 },

  scanBarWrapper: {
    position: 'absolute', left: 0, right: 0, alignItems: 'center',
    zIndex: 5,
  },
  importLink: {
    color: 'rgba(255,255,255,0.55)', fontSize: 12, fontFamily: FONT_BOLD,
    marginTop: 9, textDecorationLine: 'underline',
    textShadowColor: '#000', textShadowOffset: { width: 0, height: 1 }, textShadowRadius: 3,
  },
  scanBarLinksRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  scanBarLinkDot: { color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 9 },
  scanBar: {
    paddingHorizontal: 50, paddingVertical: 12,
    borderRadius: 50, borderWidth: 2, borderColor: '#ff4dd2',
    backgroundColor: 'rgba(0,0,0,0.6)',
    shadowColor: '#ff4dd2', shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1, shadowRadius: 20,
    alignItems: 'center', justifyContent: 'center',
  },
  scanBarActive: { borderColor: '#43e0ff', shadowColor: '#43e0ff' },
  scanBarDisabled: { borderColor: 'rgba(255,255,255,0.25)', shadowOpacity: 0.15 },
  scanBarText: {
    color: '#ff4dd2', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 4,
    textShadowColor: '#ff4dd2', textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 10,
    textAlign: 'center',
  },
  scanBarTextActive: { color: '#43e0ff', textShadowColor: '#43e0ff' },
  scanBarTextDisabled: { color: 'rgba(255,255,255,0.4)', textShadowRadius: 0 },

  bottomBar: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    paddingBottom: BOTTOM_INSET, backgroundColor: SURFACE,
    borderTopWidth: 1, borderTopColor: BORDER, zIndex: 10,
  },
  navBar: {
    height: NAV_BAR_HEIGHT,
    flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-around',
    paddingHorizontal: 8, paddingBottom: 0,
  },
  navItem: { alignItems: 'center', justifyContent: 'flex-end', flex: 1, gap: 3 },
  // Sliding active-tab indicator (see NavBar): glides between item centers,
  // hidden while Scan (the FAB) is active. Cyan glow to match PULSE.
  navIndicator: {
    position: 'absolute', top: 0, left: 0, width: 16, height: 2.5, borderRadius: 1.25,
    backgroundColor: CYAN,
    shadowColor: CYAN, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.8, shadowRadius: 6, elevation: 4,
  },
  navLabel: { color: TEXT_FAINT, fontSize: 9, fontFamily: FONT_SEMI, marginTop: 1, letterSpacing: 0.7 },
  navLabelActive: { color: CYAN },
  // Shared back affordance for every drill-in view (see BackHeader component).
  backHeader: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingVertical: 8, marginBottom: 4, alignSelf: 'flex-start' },
  backHeaderText: { fontSize: 14, fontFamily: FONT_BOLD, letterSpacing: 0.3 },
  // Raised center scan button — bottom-aligned in the bar, tall enough to poke
  // above it (the overflowing sliver is decorative; the tappable core sits in
  // the bar).
  navScanFab: {
    width: 56, height: 56, borderRadius: 28, flex: 0,
    backgroundColor: MAGENTA, alignItems: 'center', justifyContent: 'center',
    marginHorizontal: 6,
    shadowColor: MAGENTA, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.5, shadowRadius: 16, elevation: 8,
  },
  // Scan-active cue: intensify the magenta glow.
  navScanFabActive: { shadowOpacity: 0.85, shadowRadius: 22 },

  // ── HOME tab ──
  homeContainer: { flex: 1, paddingHorizontal: 20 },
  homeDate: { color: TEXT_DIM, fontSize: 11, letterSpacing: 2.5, fontFamily: FONT_BOLD, marginTop: 10 },
  homeGreeting: { color: TEXT, fontSize: 26, fontFamily: SERIF_SEMI, marginTop: 4, marginBottom: 18, letterSpacing: -0.3 },
  // ── PULSE hero (OVERALL ring + POTENTIAL) ──
  pulseHero: {
    flexDirection: 'row', alignItems: 'center', gap: 18,
    backgroundColor: 'rgba(122,150,255,0.045)', // faint cyan↔magenta duotone wash
    borderWidth: 1, borderColor: BORDER, borderRadius: 16,
    paddingVertical: 18, paddingHorizontal: 20, marginTop: 12, marginBottom: 14,
  },
  microLabel: { color: TEXT_DIM, fontSize: 9, fontFamily: FONT_SEMI, letterSpacing: 1.6, marginTop: 2 },
  heroRingNum: { color: TEXT, fontSize: 30, fontFamily: FONT_BOLD, lineHeight: 34, textAlign: 'center' },
  heroPotentialMuted: { color: TEXT_FAINT, fontSize: 38, fontFamily: FONT_BOLD, lineHeight: 44 },
  heroCaption: { color: TEXT_DIM, fontSize: 11, fontFamily: FONT, marginTop: 5, lineHeight: 15 },
  heroScanRing: {
    width: 104, height: 104, borderRadius: 52, borderWidth: 1.5, borderColor: 'rgba(67,224,255,0.4)',
    alignItems: 'center', justifyContent: 'center',
  },
  homeStreakRow: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 20, marginTop: 2 },
  homeStreakLine: { color: TEXT_DIM, fontSize: 12, fontFamily: FONT_MED, flexShrink: 1 },
  homeStreakCard: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER,
    borderRadius: 18, padding: 16, marginBottom: 10,
  },
  homeStreakNum: { color: FLAME, fontSize: 40, fontFamily: FONT_BOLD, letterSpacing: -1, lineHeight: 44 },
  homeStreakUnit: { color: FLAME, fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 2 },
  homeStreakMsg: { color: TEXT_DIM, fontSize: 12.5, marginTop: 2, lineHeight: 17 },
  homeRingCard: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER,
    borderRadius: 18, padding: 16, marginBottom: 18,
  },
  homeRingTitle: { color: TEXT, fontSize: 14.5, fontFamily: FONT_BOLD },
  homeRingSub: { color: TEXT_DIM, fontSize: 12, marginTop: 3, lineHeight: 16 },
  homeInfoCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER,
    borderRadius: 18, padding: 16,
  },
  homeInfoIcon: { fontSize: 26 },
  homeInfoTitle: { color: TEXT, fontSize: 14, fontFamily: FONT_BOLD },
  homeInfoSub: { color: TEXT_DIM, fontSize: 12, marginTop: 2, lineHeight: 16 },
  homeScanBtn: {
    backgroundColor: ACCENT, borderRadius: 16, paddingVertical: 16, alignItems: 'center', marginTop: 16,
  },
  homeScanBtnText: { color: '#00131c', fontSize: 14, fontFamily: FONT_BOLD, letterSpacing: 1.2 },
  homeFreezeHint: { color: TEXT_DIM, fontSize: 11, marginTop: 6, fontFamily: FONT_SEMI },
  homeFreezeBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: ACCENT_SOFT, borderWidth: 1, borderColor: 'rgba(0,191,255,0.28)',
    borderRadius: 16, padding: 14, marginBottom: 12,
  },
  homeFreezeTitle: { color: '#9BDCFF', fontSize: 14, fontFamily: FONT_BOLD },
  homeFreezeSub: { color: TEXT_DIM, fontSize: 12, marginTop: 2, lineHeight: 16 },
  // "Your 8-week plan" divider — makes the embedded plan a clearly labeled
  // section instead of blending into the Home scroll.
  homePlanHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginTop: 28, marginBottom: 6, paddingTop: 18,
    borderTopWidth: 1, borderTopColor: BORDER,
  },
  homePlanHeaderText: { color: ACCENT, fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 2 },
  // Results tab → open the full breakdown sheet (nav fix: not swipe-only).
  viewBreakdownBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: ACCENT, borderRadius: 14, paddingVertical: 14, marginBottom: 18,
  },
  viewBreakdownText: { color: '#00131c', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 1.2 },
  // Task micro-win overlay: anchored over the check (right edge of the card).
  taskWinAnchor: {
    position: 'absolute', right: 26, top: '50%',
    alignItems: 'center', justifyContent: 'center',
  },
  taskWinDot: { position: 'absolute', width: 6, height: 6, borderRadius: 3 },
  taskWinXp: {
    position: 'absolute', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 0.5,
    textShadowColor: '#000', textShadowOffset: { width: 0, height: 1 }, textShadowRadius: 4,
  },

  // ── Achievement badges ──
  badgeGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 24 },
  badgeCell: {
    width: (width - 40 - 20) / 3, // 3 per row inside the 20px-padded scroll
    backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER,
    borderRadius: 14, paddingVertical: 12, paddingHorizontal: 6, alignItems: 'center',
  },
  badgeCellIcon: { marginBottom: 6 },
  badgeCellTitle: { color: '#fff', fontSize: 12, fontFamily: SERIF_SEMI, letterSpacing: -0.1, textAlign: 'center' },
  badgeCellDesc: { color: 'rgba(255,255,255,0.4)', fontSize: 8.5, fontFamily: FONT, textAlign: 'center', marginTop: 3, lineHeight: 11 },
  badgeUnlockRing: {
    width: 130, height: 130, borderRadius: 65, borderWidth: 3,
    alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.05)',
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.9, shadowRadius: 24, elevation: 10,
  },
  badgeUnlockTitle: { color: '#fff', fontSize: 26, fontFamily: FONT_BOLD, letterSpacing: 0.5, marginTop: 18 },

  permissionText: { color: '#fff', fontSize: 18, textAlign: 'center', marginBottom: 20 },
  permissionButton: { backgroundColor: '#43e0ff', padding: 16, borderRadius: 50 },
  permissionButtonText: { color: '#000', fontFamily: FONT_BOLD, fontSize: 16 },

  tabContent: { flex: 1, paddingTop: TOP_BAR_HEIGHT, paddingBottom: NAV_BAR_HEIGHT + BOTTOM_INSET },
  placeholderTab: {
    flex: 1, justifyContent: 'center', alignItems: 'center',
    paddingTop: TOP_BAR_HEIGHT, paddingBottom: NAV_BAR_HEIGHT + BOTTOM_INSET,
  },
  placeholderText: { color: 'rgba(255,255,255,0.5)', fontSize: 20, fontFamily: FONT_BOLD, letterSpacing: 2 },

  programContainer: { flex: 1, paddingHorizontal: 20 },
  programLocked: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 30 },
  programLockIcon: { fontSize: 60, marginBottom: 20 },
  programLockTitle: { color: PRIMARY_BLUE, fontSize: 22, fontFamily: FONT_BOLD, letterSpacing: 4, marginBottom: 10 },
  programLockText: { color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center', lineHeight: 22 },
  programHeadline: {
    color: '#fff', fontSize: 30, fontFamily: FONT_BOLD, textAlign: 'center',
    marginTop: 16, marginBottom: 22, lineHeight: 36,
    textShadowColor: PRIMARY_BLUE, textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 22,
  },

  statsRow: { flexDirection: 'row', gap: 10, marginBottom: 24 },
  statBox: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.55)',
    paddingVertical: 14, paddingHorizontal: 8, borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center', justifyContent: 'center',
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.45, shadowRadius: 12,
  },
  statIcon: { fontSize: 22, marginBottom: 4 },
  statNum: { fontSize: 23, fontFamily: SERIF_BOLD },
  statLabel: { fontSize: 8, fontFamily: FONT_BOLD, letterSpacing: 1.5, marginTop: 2 },

  weeksHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  weeksHeader: { color: PRIMARY_BLUE, fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 3, flex: 1, opacity: 0.85 },
  weeksHelper: { color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 19, marginBottom: 16 },
  rescanPromptCard: { borderWidth: 1, borderColor: `${CYAN}45`, backgroundColor: `${CYAN}12`, borderRadius: 16, padding: 18, marginBottom: 16, alignItems: 'center' },
  rescanPromptTitle: { color: '#fff', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 0.3, marginBottom: 5, textAlign: 'center' },
  rescanPromptBody: { color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 19, textAlign: 'center' },
  weekLockedLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 9, fontFamily: FONT_BOLD, letterSpacing: 2, marginBottom: 2 },
  weekLockedHint: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: FONT_SEMI, fontStyle: 'italic', marginTop: 2 },

  // Skip-ahead confirmation
  // Embedded in Home's tall scroll, a centered card can land offscreen — pin
  // the confirm near the top of the plan section (where the tapped row is).
  skipOverlayEmbed: { justifyContent: 'flex-start', paddingTop: 70 },
  skipOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.8)', alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 32, zIndex: 50,
  },
  skipCard: {
    backgroundColor: '#0b0b12', borderRadius: 24, borderWidth: 1.5,
    borderColor: 'rgba(0,191,255,0.3)', padding: 26, alignItems: 'center', width: '100%',
    shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.5, shadowRadius: 24,
  },
  skipEmoji: { fontSize: 44, marginBottom: 10 },
  skipTitle: { color: '#fff', fontSize: 21, fontFamily: FONT_BOLD, textAlign: 'center', marginBottom: 10 },
  skipBody: { color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 21, textAlign: 'center', marginBottom: 22 },
  skipPrimaryBtn: {
    backgroundColor: PRIMARY_BLUE, borderRadius: 14, paddingVertical: 16, alignItems: 'center', width: '100%',
    shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.7, shadowRadius: 16,
  },
  skipPrimaryText: { color: '#000', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 1 },
  skipSecondaryBtn: { paddingVertical: 14, alignItems: 'center', marginTop: 6 },
  skipSecondaryText: { color: 'rgba(255,255,255,0.45)', fontSize: 14, fontFamily: FONT_SEMI },
  refreshButton: {
    paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20,
    borderWidth: 1, borderColor: PRIMARY_BLUE, backgroundColor: 'rgba(0,191,255,0.08)',
  },
  refreshButtonText: { color: PRIMARY_BLUE, fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 1 },

  scanFeedbackBanner: {
    position: 'absolute', bottom: SCAN_BAR_BOTTOM + 80,
    left: 24, right: 24, zIndex: 20,
    backgroundColor: 'rgba(0,0,0,0.88)', borderRadius: 16,
    paddingVertical: 14, paddingHorizontal: 20,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
  },
  scanFeedbackText: { color: 'rgba(255,255,255,0.85)', fontSize: 14, fontFamily: FONT_BOLD, textAlign: 'center' },
  scanFeedbackBannerOk: { borderColor: '#43e0ff', backgroundColor: 'rgba(67,224,255,0.12)' },
  scanFeedbackBannerWarn: { borderColor: '#FF5A5A', backgroundColor: 'rgba(255,90,90,0.16)' },
  scanFeedbackTextWarn: { color: '#FF8A8A' },
  scanFeedbackTextOk: { color: '#43e0ff' },

  profileAvatarSection: { alignItems: 'center', paddingVertical: 28, gap: 12 },
  profileAvatarCircle: {
    width: 90, height: 90, borderRadius: 45,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 2, borderColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center', justifyContent: 'center',
  },
  profileTierBadge: {
    paddingHorizontal: 16, paddingVertical: 5, borderRadius: 20,
    borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.3)',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  profileTierText: { color: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 2 },
  profileName: { color: '#fff', fontSize: 26, fontFamily: SERIF_BOLD, marginTop: 12, letterSpacing: -0.2 },

  upgradeCard: {
    backgroundColor: 'rgba(255,215,0,0.07)', borderRadius: 20,
    borderWidth: 1.5, borderColor: 'rgba(255,215,0,0.3)',
    padding: 20, marginBottom: 28,
  },
  upgradeCardTitle: { color: '#FFD700', fontSize: 22, fontFamily: SERIF_BOLD, letterSpacing: -0.2, marginBottom: 8 },
  upgradeCardSub: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: FONT, lineHeight: 20, marginBottom: 16 },
  upgradeCardBtn: {
    backgroundColor: '#FFD700', borderRadius: 50,
    paddingVertical: 12, alignItems: 'center',
  },
  upgradeCardBtnText: { color: '#000', fontFamily: FONT_BOLD, fontSize: 13, letterSpacing: 1.5 },

  settingsSectionHeader: {
    color: 'rgba(255,255,255,0.82)', fontSize: 19, fontFamily: SERIF_SEMI,
    letterSpacing: -0.2, marginBottom: 12,
  },
  settingsRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 16, paddingHorizontal: 4,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)', gap: 14,
  },
  settingsIcon: { fontSize: 20, width: 28, textAlign: 'center' },
  settingsLabel: { color: '#fff', fontSize: 15, fontFamily: FONT_SEMI, flex: 1 },
  settingsNote: { color: 'rgba(255,255,255,0.35)', fontSize: 14 },
  logoutRow: {
    marginTop: 26, paddingVertical: 15, borderRadius: 14, alignItems: 'center',
    borderWidth: 1, borderColor: 'rgba(255,90,90,0.35)', backgroundColor: 'rgba(255,90,90,0.06)',
  },
  logoutText: { color: '#FF5A5A', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 1 },
  deleteAccountRow: { marginTop: 14, paddingVertical: 12, alignItems: 'center' },
  deleteAccountText: { color: 'rgba(255,90,90,0.7)', fontSize: 13, fontFamily: FONT_SEMI, textDecorationLine: 'underline' },
  appVersion: {
    color: 'rgba(255,255,255,0.2)', fontSize: 12, textAlign: 'center',
    marginTop: 32, letterSpacing: 1,
  },

  // Paywall / modal legal links
  modalLegalRow: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 14 },
  modalLegalLink: { color: 'rgba(255,255,255,0.45)', fontSize: 12, fontFamily: FONT_SEMI, textDecorationLine: 'underline' },
  modalLegalDot: { color: 'rgba(255,255,255,0.3)', fontSize: 12 },

  // Legal viewer (Privacy Policy / Terms)
  legalScreen: { flex: 1, backgroundColor: '#06080F' },
  legalHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingTop: 72, paddingBottom: 14, paddingHorizontal: 18,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  legalBackBtn: {
    backgroundColor: 'rgba(0,191,255,0.15)',
    borderRadius: 18, paddingVertical: 8, paddingHorizontal: 14,
  },
  legalClose: { color: PRIMARY_BLUE, fontSize: 16, fontFamily: FONT_BOLD },
  legalHeaderTitle: { color: '#fff', fontSize: 17, fontFamily: FONT_BOLD, flex: 1, textAlign: 'center' },
  legalScroll: { padding: 22, paddingBottom: 60 },
  legalEffective: { color: 'rgba(255,255,255,0.4)', fontSize: 12, fontStyle: 'italic', marginBottom: 20 },
  legalSection: { marginBottom: 20 },
  legalHeading: { color: PRIMARY_BLUE, fontSize: 15, fontFamily: FONT_BOLD, marginBottom: 7 },
  legalBody: { color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 21 },
  legalFooter: { color: 'rgba(255,255,255,0.25)', fontSize: 12, textAlign: 'center', marginTop: 16 },

  supportOverlay: {
    flex: 1, backgroundColor: '#0a0a0a',
  },
  supportHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 20, paddingTop: 16, paddingBottom: 14,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  supportTitle: { color: '#fff', fontSize: 18, fontFamily: FONT_BOLD, letterSpacing: 1 },
  supportMessagesList: { padding: 16, gap: 10, paddingBottom: 24 },
  chatBubble: {
    maxWidth: '80%', borderRadius: 18, paddingHorizontal: 14, paddingVertical: 10,
  },
  chatBubbleUser: {
    alignSelf: 'flex-end', backgroundColor: PRIMARY_BLUE,
  },
  chatBubbleBot: {
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
  },
  chatBubbleText: { color: '#fff', fontSize: 15, lineHeight: 22 },
  supportInputRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 16, paddingVertical: 12,
    borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)',
    backgroundColor: '#0a0a0a',
  },
  supportInput: {
    flex: 1, backgroundColor: 'rgba(255,255,255,0.07)',
    borderRadius: 22, paddingHorizontal: 16, paddingVertical: 11,
    color: '#fff', fontSize: 15,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
  },
  supportSendBtn: {
    width: 42, height: 42, borderRadius: 21,
    backgroundColor: PRIMARY_BLUE, alignItems: 'center', justifyContent: 'center',
  },
  supportSendText: { color: '#000', fontSize: 18, fontFamily: FONT_BOLD },

  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.82)',
    justifyContent: 'center', alignItems: 'center',
    zIndex: 100, paddingHorizontal: 24,
  },

  // ── Premium paywall (full-screen) ──
  paywallOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#05070A', zIndex: 310, elevation: 310,
  },
  paywallScroll: { paddingHorizontal: 26, paddingTop: 64, paddingBottom: 48 },
  paywallClose: { position: 'absolute', top: 58, right: 22, zIndex: 5 },
  paywallEyebrow: { color: ACCENT, fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 4, marginBottom: 10 },
  paywallTitle: { color: TEXT, fontSize: 30, fontFamily: SERIF_BOLD, letterSpacing: -0.5, lineHeight: 36, marginBottom: 10 },
  paywallSub: { color: TEXT_DIM, fontSize: 14.5, lineHeight: 21, marginBottom: 22 },
  paywallFeatures: { gap: 10, marginTop: 26, marginBottom: 20 },
  tierRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: SURFACE, borderWidth: 1.5, borderColor: BORDER,
    borderRadius: 16, padding: 16, marginBottom: 10,
  },
  tierRowSelected: { borderColor: ACCENT, backgroundColor: ACCENT_SOFT },
  tierRadio: {
    width: 22, height: 22, borderRadius: 11, borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.25)', alignItems: 'center', justifyContent: 'center',
  },
  tierRadioInner: { width: 11, height: 11, borderRadius: 6, backgroundColor: ACCENT },
  tierTitle: { color: TEXT, fontSize: 16, fontFamily: FONT_BOLD },
  tierPrice: { color: TEXT, fontSize: 17, fontFamily: FONT_BOLD },
  tierPer: { color: TEXT_DIM, fontSize: 11, fontFamily: FONT_SEMI },
  tierBadge: { borderWidth: 1, borderRadius: 7, paddingHorizontal: 7, paddingVertical: 2.5 },
  tierBadgeText: { fontSize: 9.5, fontFamily: FONT_BOLD, letterSpacing: 0.6 },
  tierNote: { color: TEXT_DIM, fontSize: 11.5, marginTop: 3 },
  paywallCtaLoading: {
    height: 54, borderRadius: 27, marginTop: 8, alignItems: 'center', justifyContent: 'center',
    backgroundColor: MAGENTA, opacity: 0.7,
  },
  paywallTitleFont: { fontFamily: FONT_BOLD },
  compareCard: {
    backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER,
    borderRadius: 16, padding: 18, marginTop: 26, marginBottom: 20,
  },
  compareTitle: { color: TEXT_DIM, fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 1.4, marginBottom: 14, lineHeight: 16 },
  compareRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: BORDER,
  },
  compareLabel: { color: 'rgba(255,255,255,0.65)', fontSize: 13.5 },
  compareValue: { color: 'rgba(255,255,255,0.65)', fontSize: 13.5, fontFamily: FONT_BOLD },
  compareRowMaxify: { borderBottomWidth: 0, marginTop: 2 },
  compareLabelMaxify: { color: ACCENT, fontSize: 14.5, fontFamily: FONT_BOLD },
  compareValueMaxify: { color: ACCENT, fontSize: 14.5, fontFamily: FONT_BOLD },
  compareFinePrint: { color: 'rgba(255,255,255,0.3)', fontSize: 10.5, lineHeight: 15, marginTop: 12 },
  paywallRestore: { color: TEXT_DIM, fontSize: 13, fontFamily: FONT_SEMI, textAlign: 'center', marginBottom: 4, padding: 6 },
  promoInput: {
    width: '100%', backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)', borderRadius: 12,
    paddingHorizontal: 16, paddingVertical: 14, marginBottom: 12,
    color: TEXT, fontSize: 15, letterSpacing: 2, textAlign: 'center', fontFamily: FONT_BOLD,
  },
  promoError: { color: '#FF5A5A', fontSize: 13, fontFamily: FONT_SEMI, textAlign: 'center', marginBottom: 10 },
  paywallDisclosure: { color: 'rgba(255,255,255,0.32)', fontSize: 10.5, lineHeight: 15, textAlign: 'center', marginTop: 14, marginBottom: 4, paddingHorizontal: 6 },

  // ── Results teaser (free) ──
  lockedPotential: {
    width: 118, height: 118, borderRadius: 59,
    borderWidth: 2, borderColor: 'rgba(191,0,255,0.45)', borderStyle: 'dashed',
    backgroundColor: 'rgba(191,0,255,0.07)',
    alignItems: 'center', justifyContent: 'center', gap: 3,
  },
  lockedPotentialLabel: { color: '#ff9ae8', fontSize: 10.5, fontFamily: FONT_BOLD, letterSpacing: 1.5 },
  lockedPotentialHint: { color: 'rgba(255,255,255,0.4)', fontSize: 9.5, fontFamily: FONT_SEMI },
  teaserFrost: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(10,12,16,0.35)' },
  modalCard: {
    backgroundColor: '#111', borderRadius: 28,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
    padding: 28, width: '100%', alignItems: 'center',
  },
  modalEmoji: { fontSize: 48, marginBottom: 14 },
  modalTitle: {
    color: '#fff', fontSize: 24, fontFamily: FONT_BOLD, textAlign: 'center',
    letterSpacing: 0.5, marginBottom: 10,
  },
  modalSubtitle: {
    color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center',
    lineHeight: 22, marginBottom: 22,
  },
  modalFeatures: { width: '100%', gap: 10, marginBottom: 28 },
  modalFeatureRow: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  modalFeatureText: { color: 'rgba(255,255,255,0.85)', fontSize: 14, lineHeight: 20, flex: 1 },
  modalSubscribeBtn: {
    backgroundColor: PRIMARY_BLUE, borderRadius: 50,
    paddingVertical: 16, paddingHorizontal: 32,
    width: '100%', alignItems: 'center', marginBottom: 16,
  },
  modalSubscribeBtnText: { color: '#000', fontFamily: FONT_BOLD, fontSize: 15, letterSpacing: 1.5 },
  modalDismissText: { color: 'rgba(255,255,255,0.4)', fontSize: 14, paddingVertical: 4 },
  modalSocialProof: {
    backgroundColor: 'rgba(0,191,255,0.1)', borderRadius: 12, borderWidth: 1,
    borderColor: 'rgba(0,191,255,0.25)', paddingVertical: 8, paddingHorizontal: 14, marginBottom: 4,
  },
  modalSocialProofText: { color: PRIMARY_BLUE, fontSize: 13, fontFamily: FONT_BOLD, textAlign: 'center' },
  modalPriceHint: { color: 'rgba(255,255,255,0.35)', fontSize: 11, textAlign: 'center', marginTop: -2, marginBottom: 6 },

  programTeaserOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center', alignItems: 'center',
    paddingHorizontal: 28,
    backgroundColor: 'rgba(0,0,0,0.72)',
  },
  programTeaserEmoji: { fontSize: 48, marginBottom: 10 },
  programTeaserTitle: {
    color: '#fff', fontSize: 24, fontFamily: FONT_BOLD, letterSpacing: 3,
    textAlign: 'center', marginBottom: 10,
    textShadowColor: PRIMARY_BLUE, textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 18,
  },
  programTeaserSub: {
    color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center',
    lineHeight: 20, marginBottom: 20,
  },
  programTeaserBullets: { alignSelf: 'stretch', gap: 8, marginBottom: 24 },
  programTeaserBullet: { color: 'rgba(255,255,255,0.75)', fontSize: 13, fontFamily: FONT_SEMI },
  programTeaserBtn: {
    alignSelf: 'stretch', backgroundColor: PRIMARY_BLUE,
    paddingVertical: 16, borderRadius: 50, alignItems: 'center',
    shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.9, shadowRadius: 20,
    marginBottom: 10,
  },
  programTeaserBtnText: { color: '#000', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 2 },
  programTeaserHint: { color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center' },

  // Scan quality hint
  scanQualityHint: {
    position: 'absolute', bottom: SCAN_BAR_BOTTOM + 90,
    left: 32, right: 32, alignItems: 'center', zIndex: 4,
  },
  scanQualityHintText: {
    color: 'rgba(200,200,200,0.45)', fontSize: 11,
    fontFamily: FONT_SEMI, letterSpacing: 1, textAlign: 'center',
  },
  scanFeedbackIcon: { fontSize: 18, marginBottom: 4 },

  // Task card XP badges
  taskXpBadge: {
    backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 8, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)', paddingHorizontal: 7, paddingVertical: 3, marginLeft: 8,
  },
  taskXpText: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: FONT_BOLD },
  taskXpBadgeDone: { borderRadius: 8, borderWidth: 1, paddingHorizontal: 7, paddingVertical: 3, marginLeft: 8 },
  taskXpDoneText: { fontSize: 11, fontFamily: FONT_BOLD },

  // Task detail sheet — numbered how-to steps (see TaskDetailReader).
  taskStepRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 14, gap: 12 },
  taskStepNum: { fontSize: 15, fontFamily: FONT_BOLD, width: 18 },
  taskStepText: { flex: 1, color: 'rgba(255,255,255,0.82)', fontSize: 14.5, fontFamily: FONT, lineHeight: 21 },

  // Week card enhancements
  weekCardIconWrap: { position: 'relative', marginRight: 0 },
  weekCurrentDot: {
    position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: 4,
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 1, shadowRadius: 4,
  },
  weekCurrentLabel: { fontSize: 9, fontFamily: FONT_BOLD, letterSpacing: 2, marginBottom: 2 },
  prioritiesBadge: {
    alignSelf: 'center', paddingHorizontal: 14, paddingVertical: 6, borderRadius: 16,
    borderWidth: 1, backgroundColor: 'rgba(255,255,255,0.05)', marginTop: 10, marginBottom: 20,
  },
  prioritiesBadgeText: { fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 1 },

  weekCard: {
    borderRadius: 18, marginBottom: 14, overflow: 'hidden', position: 'relative',
    backgroundColor: 'rgba(6,6,12,0.96)',
    borderWidth: 1.5,
    shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.6, shadowRadius: 20, elevation: 8,
  },
  weekCardAccentBar: {
    position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
  },
  weekCardContent: { flexDirection: 'row', alignItems: 'center', paddingVertical: 16, paddingHorizontal: 18, paddingLeft: 22, gap: 14 },
  weekCardIcon: { fontSize: 34 },
  weekCardTopRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  weekCardTitleNew: { color: '#fff', fontSize: 19, fontFamily: FONT_BOLD },
  weekCardBadge: {
    fontSize: 11, fontFamily: FONT_BOLD,
    borderWidth: 1, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10,
    overflow: 'hidden',
  },
  weekPriorityRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  priorityTag: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10, borderWidth: 1 },
  priorityTagText: { fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 0.8 },
  weekProgressTrack: { height: 5, backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden' },
  weekProgressFill: { height: '100%', borderRadius: 3 },
  weekProgressLabel: { fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 1, marginTop: 5 },
  weekCompleteCircle: {
    width: 26, height: 26, borderRadius: 13, borderWidth: 1.5,
    justifyContent: 'center', alignItems: 'center',
  },
  weekCompleteCheckText: { fontSize: 13, fontFamily: FONT_BOLD },

  programWeekNum: { fontSize: 14, fontFamily: FONT_BOLD, letterSpacing: 3, textAlign: 'center' },
  programWeekTitle: { color: '#fff', fontSize: 29, fontFamily: SERIF_SEMI, textAlign: 'center', marginTop: 6 },
  programWeekFocus: { color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center', marginTop: 6, marginBottom: 20 },
  progressBarTrack: { height: 8, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 },
  progressBarFill: { height: '100%', borderRadius: 4 },
  progressText: { color: 'rgba(255,255,255,0.5)', fontSize: 12, textAlign: 'center', marginBottom: 24 },
  dailyTasksHeader: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 3, marginBottom: 12 },
  sectionLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 3, marginBottom: 10, marginTop: 4 },
  dailyTasksHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  dailyTasksCount: { fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 1 },
  dailyTaskBigCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: CARD_FILL, borderRadius: 14,
    paddingVertical: 13, paddingHorizontal: 14, borderWidth: 1, borderColor: BORDER,
  },
  // PULSE task row: leading check circle (outline idle → filled cyan when done).
  taskCheckCircle: {
    width: 24, height: 24, borderRadius: 12,
    borderWidth: 2, borderColor: 'rgba(236,232,248,0.28)',
    alignItems: 'center', justifyContent: 'center',
  },
  dailyTaskBigLabel: { color: TEXT, fontSize: 13.5, fontFamily: FONT_SEMI, marginBottom: 3 },
  dailyTaskBigMeta: { color: TEXT_FAINT, fontSize: 10.5, fontFamily: FONT },
  // Trailing magenta "+XP" chip (fully rounded, accent border/text).
  xpChip: {
    paddingHorizontal: 9, paddingVertical: 4, borderRadius: 99,
    borderWidth: 1, borderColor: 'rgba(255,77,210,0.4)',
  },
  xpChipText: { color: MAGENTA, fontSize: 11, fontFamily: FONT_SEMI },
  activeWeekCard: {
    flexDirection: 'row', borderRadius: 18, borderWidth: 1.5,
    backgroundColor: 'rgba(255,255,255,0.04)',
    shadowOffset: { width: 0, height: 0 }, shadowRadius: 12, shadowOpacity: 0.5,
    overflow: 'hidden', paddingVertical: 16, paddingRight: 16, marginBottom: 4,
  },
  activeWeekMeta: { fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 2, marginBottom: 3 },
  activeWeekTitle: { color: '#fff', fontSize: 19, fontFamily: SERIF_SEMI },
  dayDotsRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 12 },
  dayDot: {
    width: 20, height: 20, borderRadius: 10,
    borderWidth: 2, borderColor: 'rgba(255,255,255,0.15)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center', justifyContent: 'center',
  },
  dayDotInner: { width: 8, height: 8, borderRadius: 4 },
  dayDotsLabel: { fontSize: 11, fontFamily: FONT_BOLD, marginLeft: 4 },
  weekRowItem: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    borderRadius: 14, borderWidth: 1, padding: 14, marginBottom: 8,
  },
  weekRowIcon: { fontSize: 22, width: 30, textAlign: 'center' },
  weekRowTitle: { fontSize: 14, fontFamily: FONT_BOLD, marginBottom: 2 },
  weekRowSub: { fontSize: 12, fontFamily: FONT_MED },
  dayHeaderPill: {
    paddingHorizontal: 12, paddingVertical: 5, borderRadius: 20,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.06)',
  },
  dayHeaderText: { color: 'rgba(255,255,255,0.7)', fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 2 },
  dayDoneTag: { fontSize: 11, fontFamily: FONT_BOLD, marginLeft: 10 },
  dayLockedTag: { fontSize: 11, color: 'rgba(255,255,255,0.35)' },
  restDayCard: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 14,
    padding: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)',
  },
  restDayText: { color: 'rgba(255,255,255,0.4)', fontSize: 13, fontStyle: 'italic' },
  taskCard: {
    flexDirection: 'row', alignItems: 'flex-start',
    backgroundColor: SURFACE_2, borderRadius: 13,
    padding: 13, marginBottom: 9, borderWidth: 1, borderColor: BORDER, gap: 12,
  },
  taskDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.22)', marginLeft: 3, marginTop: 6 },
  taskTextCol: { flex: 1 },
  taskLabel: { color: TEXT, fontSize: 14, fontFamily: FONT_SEMI },
  taskDesc: { color: TEXT_DIM, fontSize: 12, fontFamily: FONT, lineHeight: 17, marginTop: 3 },

  // ── Compact Home hero (replaces the tall pulseHero) ──
  miniHero: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    backgroundColor: 'rgba(122,150,255,0.045)',
    borderWidth: 1, borderColor: BORDER, borderRadius: 16,
    paddingVertical: 12, paddingHorizontal: 16, marginTop: 10, marginBottom: 14,
  },
  miniHeroNum: { color: TEXT, fontSize: 21, fontFamily: FONT_BOLD, lineHeight: 24, textAlign: 'center' },
  miniHeroCaption: { color: TEXT, fontSize: 13, fontFamily: FONT_MED, marginTop: 3 },
  miniScanRing: {
    width: 62, height: 62, borderRadius: 31, borderWidth: 1.5, borderColor: 'rgba(67,224,255,0.4)',
    alignItems: 'center', justifyContent: 'center',
  },
  miniStreak: { alignItems: 'center', gap: 2 },
  miniStreakNum: { color: TEXT_FAINT, fontSize: 15, fontFamily: FONT_BOLD },

  // ── Calendar day strip ──
  calStrip: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 },
  calDay: { alignItems: 'center', flex: 1, gap: 6 },
  calDow: { color: TEXT_FAINT, fontSize: 11, fontFamily: FONT_MED, letterSpacing: 0.5 },
  calNumWrap: {
    width: 34, height: 34, borderRadius: 17,
    alignItems: 'center', justifyContent: 'center', borderColor: 'transparent', borderWidth: 1,
  },
  calNum: { color: TEXT, fontSize: 15, fontFamily: FONT_SEMI },
  calDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.12)' },

  // ── Day header — "Your Program" is the loud part, the week is a subline ──
  dayHeadRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 14 },
  dayHeadTitle: { color: TEXT, fontSize: 27, fontFamily: SERIF_SEMI, letterSpacing: -0.2 },
  dayHeadSub: { color: TEXT_DIM, fontSize: 12, fontFamily: FONT_MED, marginTop: 3 },
  allWeeksBtn: { flexDirection: 'row', alignItems: 'center', gap: 2, paddingVertical: 6, paddingLeft: 10 },
  allWeeksText: { color: TEXT_DIM, fontSize: 12, fontFamily: FONT_MED },

  dayProgressRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 18 },
  dayProgressTrack: { flex: 1, height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
  dayProgressFill: { height: '100%', borderRadius: 2, backgroundColor: ACCENT },
  dayProgressText: { color: TEXT_DIM, fontSize: 12, fontFamily: FONT_SEMI },

  // ── Timeline: rail (circle + connecting line) beside a task card ──
  timeline: { marginBottom: 8 },
  tlRow: { flexDirection: 'row', marginBottom: 12 },
  tlRail: { width: 30, alignItems: 'center' },
  tlLine: { position: 'absolute', top: 24, bottom: -12, width: 2, backgroundColor: 'rgba(255,255,255,0.10)' },
  tlCircle: {
    width: 24, height: 24, borderRadius: 12, borderWidth: 2, borderColor: 'rgba(255,255,255,0.28)',
    backgroundColor: BG, alignItems: 'center', justifyContent: 'center', marginTop: 14,
  },
  tlCard: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: SURFACE_2, borderRadius: 15, borderWidth: 1, borderColor: BORDER,
    paddingVertical: 13, paddingHorizontal: 13, marginLeft: 8,
  },
  tlCardText: { flex: 1 },
  tlTitle: { color: TEXT, fontSize: 14, fontFamily: FONT_SEMI, lineHeight: 19 },
  tlDesc: { color: TEXT_DIM, fontSize: 11.5, fontFamily: FONT, lineHeight: 16, marginTop: 3 },
  tlXp: { color: TEXT_FAINT, fontSize: 12, fontFamily: FONT_BOLD },
  catArt: { alignItems: 'center', justifyContent: 'center', borderWidth: 1 },

  // ── Insight row (a Concern + Protocol, folded into the day) ──
  tlInsightNode: {
    width: 24, height: 24, borderRadius: 12, borderWidth: 1.5,
    alignItems: 'center', justifyContent: 'center', marginTop: 14,
  },
  tlInsightCard: {
    flex: 1, backgroundColor: 'rgba(255,255,255,0.025)', borderRadius: 15, borderWidth: 1,
    paddingVertical: 13, paddingHorizontal: 14, marginLeft: 8,
  },
  tlInsightHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  tlInsightKicker: { fontSize: 9.5, fontFamily: FONT_BOLD, letterSpacing: 1.4 },
  tlInsightTitle: { color: TEXT, fontSize: 15, fontFamily: SERIF_SEMI },
  tlInsightMeta: { color: TEXT_DIM, fontSize: 11.5, fontFamily: FONT, marginTop: 2 },
  tlInsightWhy: { color: TEXT_DIM, fontSize: 12.5, fontFamily: FONT, lineHeight: 18, marginTop: 9 },
  checkbox: {
    width: 26, height: 26, borderRadius: 13,
    borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)',
    alignItems: 'center', justifyContent: 'center',
  },
  checkmark: { color: '#000', fontSize: 16, fontFamily: FONT_BOLD },

  resultsSheet: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    height: height * 0.92, backgroundColor: 'rgba(0,0,0,0.97)',
    borderTopLeftRadius: 30, borderTopRightRadius: 30,
    borderTopWidth: 1, borderColor: 'rgba(0,191,255,0.3)',
    paddingTop: 16, paddingHorizontal: 20, paddingBottom: 24, zIndex: 20,
  },
  resultsScroll: { alignItems: 'center', paddingBottom: 20 },
  dragHandle: {
    width: 40, height: 4, backgroundColor: 'rgba(255,255,255,0.3)',
    borderRadius: 2, alignSelf: 'center', marginBottom: 12,
  },
  resultsTitle: {
    color: CYAN, fontSize: 20, fontFamily: FONT_BOLD, letterSpacing: 6,
    textAlign: 'center',
  },
  resultsSubtitle: {
    color: 'rgba(255,255,255,0.3)', fontSize: 10, textAlign: 'center',
    letterSpacing: 2, marginTop: 3, marginBottom: 14,
  },
  resultsTopRow: { flexDirection: 'row', gap: 24, marginBottom: 20, justifyContent: 'center', alignItems: 'center' },
  resultsGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 14, marginBottom: 24, width: '100%' },
  unlockButton: {
    backgroundColor: '#ff4dd2', paddingHorizontal: 40, paddingVertical: 18, borderRadius: 50,
    shadowColor: '#ff4dd2', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 1, shadowRadius: 25,
    marginBottom: 10, alignSelf: 'stretch', alignItems: 'center',
  },
  unlockButtonText: { color: '#fff', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 2 },
  unlockSubtext: { color: 'rgba(255,255,255,0.5)', fontSize: 12, textAlign: 'center', marginBottom: 24 },
  samplePlanContainer: { width: '100%', marginTop: 8 },
  samplePlanTitle: { color: PRIMARY_BLUE, fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 3, textAlign: 'center', marginBottom: 12, opacity: 0.7 },
  samplePlanBlur: { position: 'relative', borderRadius: 16, overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(0,191,255,0.2)' },
  samplePlanItem: {
    paddingVertical: 14, paddingHorizontal: 20,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  samplePlanItemText: { color: '#fff', fontSize: 14, fontFamily: FONT_SEMI, letterSpacing: 1 },
  blurOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, height: '70%', backgroundColor: 'rgba(0,0,0,0.85)' },
  samplePlanLock: { color: 'rgba(255,255,255,0.4)', fontSize: 11, textAlign: 'center', marginTop: 10, letterSpacing: 1 },
  categoryLockOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.88)',
    justifyContent: 'center', alignItems: 'center',
    borderRadius: 16, gap: 8,
  },
  categoryLockIcon: { fontSize: 28 },
  categoryLockTitle: { color: '#fff', fontSize: 18, fontFamily: FONT_BOLD, letterSpacing: 2 },
  categoryLockSub: { color: 'rgba(255,255,255,0.5)', fontSize: 13, textAlign: 'center', paddingHorizontal: 24 },
  categoryLockBtn: {
    marginTop: 6, backgroundColor: '#ff4dd2', paddingHorizontal: 28, paddingVertical: 13,
    borderRadius: 50, shadowColor: '#ff4dd2', shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9, shadowRadius: 18,
  },
  categoryLockBtnText: { color: '#fff', fontSize: 14, fontFamily: FONT_BOLD, letterSpacing: 2 },

  catBubbleWrap: { alignItems: 'center', width: '46%' },
  catReason: {
    color: 'rgba(255,255,255,0.45)', fontSize: 10, textAlign: 'center',
    marginTop: 5, paddingHorizontal: 4, lineHeight: 14,
  },
  catPercentile: {
    color: '#43e0ff', fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 0.5,
    textAlign: 'center', marginTop: 5,
  },

  // Trait Breakdown + Facial Harmony report sections
  breakdownSection: { width: '100%', marginBottom: 28, marginTop: 4 },
  breakdownCard: {
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 16,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', padding: 14, marginBottom: 10,
  },
  breakdownHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  breakdownCat: { color: '#fff', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 1.5 },
  breakdownScore: { fontSize: 17, fontFamily: FONT_BOLD },
  traitRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 5,
  },
  traitName: { color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: FONT_SEMI, flex: 1, marginRight: 10 },
  traitRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  traitPct: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: FONT_BOLD },
  tierTag: { borderWidth: 1, borderRadius: 7, paddingHorizontal: 9, paddingVertical: 3 },
  tierTagText: { fontSize: 9, fontFamily: FONT_BOLD, letterSpacing: 1 },
  // Trait row with a percentile bar underneath the name.
  traitRow2: { paddingVertical: 8 },
  traitRowTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  traitBarRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 },
  // Percentile bar.
  pbTrack: { flex: 1, height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.1)', overflow: 'hidden' },
  pbFill: { height: '100%', borderRadius: 3 },
  // Interactive face-map pins + callout.
  mapPin: {
    position: 'absolute', alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.5, shadowRadius: 2, elevation: 3,
  },
  mapPinText: { color: '#000', fontSize: 9, fontFamily: FONT_BOLD },
  mapHint: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: FONT_SEMI, textAlign: 'center', marginTop: 10, letterSpacing: 0.5 },
  pinCallout: {
    width: FACE_MAP_W, marginTop: 14, padding: 14, borderRadius: 14, borderWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  pinCalloutHead: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  pinCalloutNum: { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  pinCalloutNumText: { color: '#000', fontSize: 11, fontFamily: FONT_BOLD },
  pinCalloutName: { color: '#fff', fontSize: 14, fontFamily: FONT_BOLD, flex: 1 },
  pinCalloutBarRow: { flexDirection: 'row', alignItems: 'center', gap: 9, marginTop: 12 },
  pinCalloutPct: { fontSize: 12, fontFamily: FONT_BOLD, width: 42, textAlign: 'right' },
  pinCalloutWhy: { color: 'rgba(255,255,255,0.7)', fontSize: 12.5, lineHeight: 18, marginTop: 10 },
  pinCalloutFix: { color: 'rgba(255,255,255,0.92)', fontSize: 12.5, fontFamily: FONT_SEMI, lineHeight: 18, marginTop: 8 },
  // Before/after potential slider.
  baWrap: {
    borderRadius: 18, overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(191,0,255,0.35)',
    backgroundColor: '#000',
  },
  baClip: { position: 'absolute', left: 0, top: 0, overflow: 'hidden' },
  baDivider: { position: 'absolute', top: 0, width: 2, backgroundColor: 'rgba(255,255,255,0.9)' },
  baHandle: {
    position: 'absolute', width: 34, height: 34, borderRadius: 17,
    backgroundColor: 'rgba(0,0,0,0.55)', borderWidth: 1.5, borderColor: '#fff',
    alignItems: 'center', justifyContent: 'center',
  },
  baHandleText: { color: '#fff', fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: -1 },
  baTag: {
    position: 'absolute', top: 10, paddingHorizontal: 9, paddingVertical: 4,
    borderRadius: 8, backgroundColor: 'rgba(0,0,0,0.55)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.25)',
  },
  baTagText: { color: 'rgba(255,255,255,0.85)', fontSize: 9.5, fontFamily: FONT_BOLD, letterSpacing: 1.2 },
  // Potential generate CTA state.
  potentialCta: {
    ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 26, gap: 10,
  },
  potentialBtn: {
    backgroundColor: '#ff4dd2', borderRadius: 50, paddingVertical: 13, paddingHorizontal: 22,
    marginTop: 4, shadowColor: '#ff4dd2', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.7, shadowRadius: 12, elevation: 6,
  },
  potentialBtnText: { color: '#fff', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 1.5 },
  potentialSub: { color: 'rgba(255,255,255,0.75)', fontSize: 12.5, textAlign: 'center', lineHeight: 18 },
  potentialLoadingText: { color: '#ff9ae8', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 1.5 },
  potentialErrText: { color: '#FF9F1C', fontSize: 11.5, textAlign: 'center', marginTop: 6 },
  potentialRegenBtn: {
    alignSelf: 'center', marginTop: 10, paddingVertical: 8, paddingHorizontal: 18, borderRadius: 50,
    borderWidth: 1, borderColor: 'rgba(191,0,255,0.5)', backgroundColor: 'rgba(191,0,255,0.08)',
  },
  potentialRegenText: { color: '#ff9ae8', fontSize: 11.5, fontFamily: FONT_BOLD, letterSpacing: 1.2 },
  harmonyCard: {
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 16,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', paddingHorizontal: 16,
  },
  harmonyRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: BORDER, gap: 12,
  },
  // Name keeps its natural width (never collapses to one char per line); the
  // value takes the rest and wraps if it's long (e.g. "slight left-right…").
  harmonyName: { color: TEXT, fontSize: 13, fontFamily: FONT_MED, flexShrink: 0 },
  harmonyRight: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, justifyContent: 'flex-end' },
  harmonyValue: { color: TEXT, fontSize: 13, fontFamily: FONT_SEMI, flexShrink: 1, textAlign: 'right' },
  harmonyDot: { width: 9, height: 9, borderRadius: 5 },
  proLockOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.86)', borderRadius: 16,
    justifyContent: 'center', alignItems: 'center', gap: 8, paddingHorizontal: 16,
  },

  faceMapContainer: { width: '100%', alignItems: 'center', marginBottom: 28, marginTop: 4 },
  faceMapTitle: {
    color: PRIMARY_BLUE, fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 4,
    textAlign: 'center', marginBottom: 12, opacity: 0.7,
  },
  // Unified results-sheet section header (title + accent bar + subtitle).
  sectionHead: { alignItems: 'center', marginBottom: 14 },
  sectionHeadTitle: { color: '#fff', fontSize: 12, fontFamily: FONT_BOLD, letterSpacing: 3.5, textAlign: 'center' },
  sectionAccentBar: { width: 26, height: 2.5, borderRadius: 2, marginTop: 7 },
  sectionHeadSub: { color: 'rgba(255,255,255,0.45)', fontSize: 11, textAlign: 'center', marginTop: 7, letterSpacing: 0.3 },
  // Shared lock-card text (used by ProLock everywhere).
  lockTitle: { color: '#fff', fontSize: 17, fontFamily: FONT_BOLD, letterSpacing: 1.5, textAlign: 'center' },
  lockSub: { color: 'rgba(255,255,255,0.5)', fontSize: 12.5, textAlign: 'center', paddingHorizontal: 20, lineHeight: 17 },
  faceMapWrapper: {
    borderRadius: 18, overflow: 'hidden',
    borderWidth: 1, borderColor: 'rgba(0,191,255,0.25)',
    position: 'relative', backgroundColor: '#000',
  },
  faceMapPhoto: { width: '100%', height: '100%' },
  mapCorner: {
    position: 'absolute', width: 22, height: 22,
    borderColor: PRIMARY_BLUE,
  },
  mapCornerTL: { top: 8, left: 8, borderTopWidth: 2, borderLeftWidth: 2 },
  mapCornerTR: { top: 8, right: 8, borderTopWidth: 2, borderRightWidth: 2 },
  mapCornerBL: { bottom: 8, left: 8, borderBottomWidth: 2, borderLeftWidth: 2 },
  mapCornerBR: { bottom: 8, right: 8, borderBottomWidth: 2, borderRightWidth: 2 },
  mapLegend: {
    flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center',
    gap: 14, marginTop: 12,
  },
  mapLegendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  mapLegendDot: { width: 8, height: 8, borderRadius: 4 },
  mapLegendText: { color: 'rgba(255,255,255,0.6)', fontSize: 10, fontFamily: FONT_BOLD, letterSpacing: 0.5 },
  faceMapLockOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.88)',
    justifyContent: 'center', alignItems: 'center',
    gap: 8, paddingHorizontal: 16,
  },
  // Wii-style "how to scan" tutorial card
  howToOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center', alignItems: 'center', zIndex: 120, paddingHorizontal: 28,
  },
  howToCard: {
    width: '100%', maxWidth: 360, borderRadius: 20, overflow: 'hidden',
    backgroundColor: '#dff0f5', borderWidth: 3, borderColor: '#fff',
    shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.5, shadowRadius: 20,
  },
  howToHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#16a3ad', paddingVertical: 12, paddingHorizontal: 18,
  },
  howToHeaderText: { color: '#fff', fontSize: 18, fontFamily: FONT_BOLD, letterSpacing: 1.5 },
  howToBody: { alignItems: 'center', paddingTop: 16, paddingBottom: 6 },
  howToCaption: { color: '#0e5b63', fontSize: 19, fontFamily: FONT_BOLD, marginTop: 4 },
  howToDots: { flexDirection: 'row', gap: 7, marginTop: 14 },
  howToDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: 'rgba(14,91,99,0.25)' },
  howToDotActive: { backgroundColor: '#16a3ad', width: 18 },
  howToTips: { marginHorizontal: 16, marginTop: 6, padding: 14, borderRadius: 14, backgroundColor: 'rgba(22,163,173,0.08)' },
  howToTipsTitle: { color: '#0e5b63', fontSize: 10.5, fontFamily: FONT_BOLD, letterSpacing: 1.5, marginBottom: 8 },
  howToTipRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  howToTipCheck: { color: '#16a3ad', fontSize: 13, fontFamily: FONT_BOLD, width: 18 },
  howToTipText: { color: '#2f5d63', fontSize: 12.5, fontFamily: FONT_SEMI, flex: 1, lineHeight: 17 },
  howToHint: { color: '#3a6e74', fontSize: 12.5, textAlign: 'center', paddingHorizontal: 22, marginTop: 10, lineHeight: 18 },
  howToButton: {
    backgroundColor: '#16a3ad', margin: 16, borderRadius: 50, paddingVertical: 15, alignItems: 'center',
  },
  howToButtonText: { color: '#fff', fontSize: 15, fontFamily: FONT_BOLD, letterSpacing: 2 },
  howToReplay: { marginTop: 10, paddingVertical: 6, paddingHorizontal: 14, borderRadius: 16, backgroundColor: 'rgba(0,0,0,0.45)' },
  howToReplayText: { color: 'rgba(255,255,255,0.7)', fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 1 },

  concernSection: { marginTop: 18, marginBottom: 4 },
  concernIntro: { color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 2, marginBottom: 12, lineHeight: 18 },
  concernCard: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 14,
    borderWidth: 1, padding: 14, marginBottom: 10,
  },
  concernHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  concernName: { color: '#fff', fontSize: 15, fontFamily: FONT_BOLD, flex: 1, marginRight: 10 },
  concernMeta: { color: 'rgba(255,255,255,0.45)', fontSize: 11, fontFamily: FONT_BOLD, letterSpacing: 0.5, marginTop: 5 },
  concernWhy: { color: 'rgba(255,255,255,0.72)', fontSize: 13, lineHeight: 18, marginTop: 8 },
  protocolBox: {
    marginTop: 11, paddingTop: 11, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)',
  },
  protocolFix: { color: '#fff', fontSize: 13, fontFamily: FONT_BOLD, lineHeight: 18, marginBottom: 9 },
  protocolTagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  protocolTag: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 7,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', paddingHorizontal: 9, paddingVertical: 4,
  },
  protocolTagText: { color: 'rgba(255,255,255,0.85)', fontSize: 11, fontFamily: FONT_BOLD },

  styleFixSection: { marginTop: 22, marginBottom: 4 },
  styleFixIntro: { color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 2, marginBottom: 12 },
  styleFixCard: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 12,
    backgroundColor: 'rgba(191,0,255,0.06)', borderRadius: 16,
    borderWidth: 1, borderColor: 'rgba(191,0,255,0.3)',
    padding: 14, marginBottom: 10,
  },
  styleFixIcon: { fontSize: 24, marginTop: 1 },
  styleFixTitle: { color: '#fff', fontSize: 15, fontFamily: FONT_BOLD, marginBottom: 3 },
  styleFixAction: { color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 18 },
  styleFixReason: { color: '#ff4dd2', fontSize: 12, lineHeight: 17, marginTop: 6 },

  onbContainer: { flex: 1, backgroundColor: '#000', paddingTop: 64, paddingHorizontal: 24, paddingBottom: 34 },
  onbProgressTrack: { height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.1)', overflow: 'hidden' },
  onbProgressFill: { height: 4, borderRadius: 2, backgroundColor: PRIMARY_BLUE },
  onbHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 18 },
  onbBack: { color: '#fff', fontSize: 26, fontFamily: FONT_BOLD },
  onbStepCount: { color: 'rgba(255,255,255,0.5)', fontSize: 13, fontFamily: FONT_BOLD, letterSpacing: 2 },
  onbScroll: { paddingTop: 28, paddingBottom: 20 },
  onbTitle: { color: '#fff', fontSize: 28, fontFamily: FONT_BOLD, letterSpacing: 0.3, marginBottom: 8 },
  onbSubtitle: { color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 21, marginBottom: 22 },
  onbOptions: { gap: 12, marginTop: 8 },
  onbOption: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 16,
    borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.12)',
    paddingVertical: 18, paddingHorizontal: 20,
  },
  onbOptionSel: {
    borderColor: PRIMARY_BLUE, backgroundColor: 'rgba(0,191,255,0.12)',
    shadowColor: PRIMARY_BLUE, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.6, shadowRadius: 12,
  },
  onbOptionText: { color: '#fff', fontSize: 16, fontFamily: FONT_BOLD },
  onbOptionTextSel: { color: PRIMARY_BLUE },
  onbCheck: { color: PRIMARY_BLUE, fontSize: 18, fontFamily: FONT_BOLD },
  onbContinue: {
    backgroundColor: PRIMARY_BLUE, borderRadius: 50, paddingVertical: 18,
    alignItems: 'center', marginTop: 8,
  },
  onbContinueDisabled: { backgroundColor: 'rgba(255,255,255,0.08)' },
  onbContinueText: { color: '#000', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 2 },

  faceMapLockTitle: { color: '#fff', fontSize: 16, fontFamily: FONT_BOLD, letterSpacing: 2 },
  faceMapLockSub: { color: 'rgba(255,255,255,0.45)', fontSize: 11, textAlign: 'center', lineHeight: 16 },

  lockedSideBubble: {
    width: 72, height: 72, borderRadius: 36,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.15)',
    justifyContent: 'center', alignItems: 'center',
  },
  lockedSideLock: { fontSize: 16 },
  lockedSideLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 9, fontFamily: FONT_BOLD, letterSpacing: 1.5, marginTop: 2 },
});